import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useNutzerverwaltung } from "./useNutzerverwaltung.ts";
import { useFxZuordnungen } from "./useFxZuordnungen.ts";
import { useFxOverview } from "./useFxOverview.ts";
import { useDrawerDokumente } from "./useDrawerDokumente.ts";
import { useDrawerBestellfelder } from "./useDrawerBestellfelder.ts";
import { useDrawerTasks } from "./useDrawerTasks.ts";
import { useBoardBestellzustand } from "./useBoardBestellzustand.ts";
import { meldeNachladeFehlschlaege, useBoardSammelaktionen, type NachladeErgebnis } from "./useBoardSammelaktionen.ts";
import { useAutoArchivierung } from "./useAutoArchivierung.ts";
import { useDrawerZahlungsplan } from "./useDrawerZahlungsplan.ts";
import { useDrawerZahlungen } from "./useDrawerZahlungen.ts";
import { useDrawerSpedition } from "./useDrawerSpedition.ts";
import { useBestellungAnlegen } from "./useBestellungAnlegen.ts";
export { anlageStarten, ANLAGEMASKE_VERWORFENE_FELDER } from "./useBestellungAnlegen.ts";
import { useBestellgruppen } from "./useBestellgruppen.ts";
import { useZeitstrahlSendungen } from "./useZeitstrahlSendungen.ts";
import { bestellungNachKennung, bestellungNachKennungNachladen } from "./bestellungsAufloesung.ts";
import { ersteErlaubteSeite, hatRecht, SEITEN_RECHTE, type Seite } from "../../state/berechtigungen.ts";
import { istNurLeser } from "../../state/nurLeser.ts";
import { ersteZustandswahl, useSpeicher } from "../../state/Speicher.tsx";
import { meldeFehler, meldeNachladeFehler, meldeNachladeSatz } from "../../state/fehlerStapel.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { useMontiertRef } from "../../state/useMontiertRef.ts";
import type { Konflikt, Nutzer } from "../../state/reduzierer.ts";
import {
  ApiFehler,
  KonfliktFehler,
  SitzungsFehler,
  anfrage,
  beiSitzungsverlust,
  setzeSitzungsToken,
  sitzungAusSpeicherUebernehmen,
} from "../../api/basis.ts";
import { abmelden, eigenesProfil, passwortAendern } from "../../api/auth.ts";
import { nutzerListe } from "../../api/nutzer.ts";
import { ausgangUnklar } from "../../lib/ausgangUnklar.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { setStatusDefinitions } from "../../lib/status.ts";
import { orderListSchreibweg } from "../../lib/orderListEditor.ts";
import type {
  Order,
  OrderItem,
  OrderPayment,
  OrderTask,
  ResponsibilityModule,
  Sendung,
} from "../../lib/types.ts";
import type { JtlLager } from "../../lib/lager.ts";
import { bestellbezeichnung, kennungVon } from "../../lib/bestellkennung.ts";
import { isPaymentRequestTask } from "../../lib/payments.ts";
import { jtlLagerLaden } from "../../api/jtlLager.ts";
import { verknuepfungLoesen } from "../../api/sendungen.ts";
import { aktuelleSammelsendungAusKonflikt, gemeinsameWerteSchreiben } from "../../api/sammelsendungen.ts";

import type { OrderListColumn } from "../../config/board.ts";
import {
  bestellungAendern,
  bestellungLaden,
  bestellungLoeschen,
  boardStatusAendern,
  NachladenNachSchreibenFehler,
  SSE_ENTITAET as BESTELLUNG_ENTITAET,
} from "../../api/bestellungen.ts";
import {
  positionsDokumenteLaden,
  type PositionsDokument,
} from "../../api/dokumente.ts";
import { geladeneDokumente, POSITIONSDOKUMENTE_LAEDT, type PositionsDokumenteZustand } from "../../lib/positionsDokumenteZustand.ts";
import { BestellgruppenBereich } from "../OrderGroups/BestellgruppenBereich.tsx";
import { BestellgruppenAnsicht } from "../OrderGroups/BestellgruppenAnsicht.tsx";
import { positionAendern } from "../../api/positionen.ts";
import { zahlstatusZuruecksetzen, type ZahlungszielRaten } from "../../api/bestellungZahlungsziel.ts";
import { ladeFreigaben } from "../../api/lieferantenZahlungsziele.ts";
import {
  jtlBestellkopfAendern,
  jtlPositionArtikelTauschen,
  jtlPositionMengePreisAendern,
  jtlPositionHinzufuegen,
  jtlPositionLoeschen,
  jtlBestellungLoeschen,
  zusammenfuehren,
  istAbgeleiteteKennung,
  abgeleiteteKennung,
  type JtlBestellungRoh,
} from "../../api/jtlBestellungen.ts";
import { jtlSuche, type JtlArtikel } from "../../api/jtlArtikelSuche.ts";
// Task 9 (Anlagemaske): die GENERISCHE `jtlSuche`-Fassung, unter Alias - `jtlSuche` (ohne Alias, oben)
// ist bereits die Task-17-Fassung fuer `BestellungTab`. Beide Namen bezeichnen ZWEI verschiedene
// Module (`api/jtlSuche.ts` vs. `api/jtlArtikelSuche.ts`) - siehe deren Kopfkommentare, Abschnitt
// "Merge feat/09-spa-seiten".
import { jtlSuche as jtlSucheGenerisch } from "../../api/jtlSuche.ts";
import {
  belegEntfernen,
  belegHochladen,
  kurssicherungAendern,
  kurssicherungAnlegen,
  kurssicherungWeichLoeschen,
  kurssicherungLaden,
  fxKontenLaden,
  type FxAccount,
  SSE_ENTITAET as KURSSICHERUNG_ENTITAET,
} from "../../api/kurssicherung.ts";
import { fxCalculatorValues, type FxCalculatorDraft, type FxDeal } from "../../lib/fx.ts";
import { sicherungsNr } from "../../lib/sicherungsNummer.ts";
import type { FxSicherungAnlegenEingabe } from "../PaymentsPage/SicherungAnlegenDialog.tsx";
import { todayIso } from "../../lib/format.ts";
import {
  leseArtikelBreiten,
  leseBoardAbschnitte,
  leseBreiten,
  leseLaenderFilter,
  leseTransportartFilter,
  kippeAbschnitt,
  schreibeArtikelBreiten,
  schreibeBoardAbschnitte,
  schreibeBreiten,
  schreibeLaenderFilter,
  schreibeTransportartFilter,
  type Abschnitte,
  type Breiten,
  type LaenderFilter,
  type TransportartFilter,
} from "../../state/arbeitsplatz.ts";
import {
  breitenTaste as bediendeBreitenTaste,
  starteBreitenZiehen,
  zurueckgesetzteBreiten,
} from "../Board/SpaltenBreiten.ts";
import { Anmeldung, type AngemeldeterNutzer } from "./Anmeldung.tsx";
import { Kopfzeile } from "./Kopfzeile.tsx";
// Task 11: die Union der SPA-Seiten inklusive der Lieferantenflaeche - siehe Kommentar am
// `seite`-Zustand unten. `LIEFERANTEN_SEITE` ist die EINE Kennung dieser Seite; sie steht in
// `Navigation.tsx`, obwohl dort seit der Fix-Runde 1 kein Reiter mehr dafuer haengt (Begruendung
// dort am Typ `AktiveSeite`: `users` ist derselbe Fall - Seite ohne Reiter, Zugang ueber das
// Marken-Menue).
import { AKTIVITAETEN_SEITE, DASHBOARD_SEITE, KI_ERLAUBEN_SEITE, KI_ZUGAENGE_SEITE, LIEFERANTEN_SEITE, LIQUIDITAET_RECHT, LIQUIDITAET_SEITE, ZAHLUNGSVORLAGEN_SEITE, type AktiveSeite } from "./Navigation.tsx";
// Erlauben-Seite und "Meine KI-Zugaenge" (KI-Zugang, Stufe 1): nachgeladener Chunk, nicht im Eintritt.
import { KiErlaubenSeiteLazy, KiZugaengeSeiteLazy } from "../KiZugang/KiZugangLazy.tsx";
import { LiquiditaetsSeite } from "../LiquiditaetsPage/LiquiditaetsSeite.tsx";
import { AktivitaetenSeite } from "../AktivitaetenPage/AktivitaetenSeite.tsx";
// Dashboard: nur die Huelle liegt im Eintritt, die Seite selbst kommt nachgeladen (siehe `DashboardSeiteLazy`).
import { DashboardSeiteLazy } from "../DashboardPage/DashboardSeiteLazy.tsx";
import { useDateiDownloadMitStapel } from "../../api/useDateiDownload.ts";
import { dashboardStandLaden } from "../../api/dashboard.ts";
import type { SeitenZiel } from "../AktivitaetenPage/aktivitaetenAnzeige.ts";
// `AktivitaetWo` aus `api/aktivitaeten.ts` (die Seite "Aktivitaeten"); `api/historie.ts` fuehrt seit
// Task 9 nur noch den Zeitstrahl einer Bestellung.
import type { AktivitaetWo } from "../../api/aktivitaeten.ts";
import { ZahlungsvorlagenSeite } from "../PaymentTemplates/ZahlungsvorlagenSeite.tsx";
import { vorlagenName } from "../../lib/zahlungsvorlagenTitel.ts";
import { LieferantenSeite } from "../Suppliers/LieferantenSeite.tsx";
import { KonfliktBanner } from "./KonfliktBanner.tsx";
import { LadeHinweis } from "./LadeHinweis.tsx";
import { VerbindungsLuecke } from "./VerbindungsLuecke.tsx";
import { Ladefehler } from "../Ladefehler/Ladefehler.tsx";
import { Toast, useMeldungsAnzeige } from "./Toast.tsx";
import { StehendeHinweise, useStehendeHinweise } from "./StehendeHinweise.tsx";
import { uploadStelle, useUploadStellen } from "./useUploadStellen.ts";
import { BoardSeite } from "../Board/BoardSeite.tsx";
import { KontextMenue, type OrderKontextAktion } from "../Board/KontextMenue.tsx";
import { ListenEditor, bereinigteBestellungen } from "../Board/ListenEditor.tsx";
import { SammelStatusDialog } from "../Board/SammelStatusDialog.tsx";
import { VorlagenDialog } from "../TaskTemplate/VorlagenDialog.tsx";
import { darfBestellungOeffnen, Drawer, type DrawerSprung } from "../Drawer/Drawer.tsx";
// Der Sammelsendungs-Drawer kommt nachgeladen (siehe `SammelsendungDrawerLazy`), im Eintrittsbündel liegt nur die Hülle.
import { SammelsendungDrawerLazy } from "../Drawer/SammelsendungDrawerLazy.tsx";
// NUR als Typ: ein Wert-Import aus `ZollDialog.tsx` zoege den nachgeladenen Chunk ins Eintrittsbuendel.
import type { ZollMelder } from "../Zollinstruktionen/ZollDialog.tsx";
import { JtlLoeschDialog } from "../Drawer/JtlLoeschDialog.tsx";
import type { DrawerTabId } from "../Drawer/drawerRechte.ts";
import type { DrawerZiel } from "../../lib/zellZiel.ts";
import { BestellungTab } from "../Drawer/MainTab/BestellungTab.tsx";
import { SpeditionTab } from "../Drawer/ShipTab/SpeditionTab.tsx";
import { Anlieferungsliste } from "../Drawer/ArrivalTab/Anlieferungsliste.tsx";
import { TasksTab } from "../Drawer/TasksTab/TasksTab.tsx";
import { DokumenteTab, istGezeichneterDokumentTyp } from "../Drawer/DocsTab/DokumenteTab.tsx";
import { dateiZuGrossText, istZuGross, NachladenNachUploadFehler, uploadFehlerText } from "../../lib/upload.ts";
import { ProduktionsbildLightbox } from "../Drawer/DocsTab/ProduktionsbildLightbox.tsx";
// Massenupload: nur diese drei liegen im Eintrittsbündel, der Dialog selbst kommt nachgeladen (siehe `MassenuploadDialogLazy`).
import type { MassenuploadAuftrag } from "../Drawer/DocsTab/Massenupload/auftrag.ts";
import { fokusZurAblage } from "../Drawer/DocsTab/Massenupload/MassenuploadAblage.tsx";
import { MassenuploadDialogLazy } from "../Drawer/DocsTab/Massenupload/MassenuploadDialogLazy.tsx";
import { ZahlungTab } from "../Drawer/PaymentTab/ZahlungTab.tsx";
import { EkTab } from "../Drawer/CalcTab/EkTab.tsx";
import { HistoryTab } from "../Drawer/HistoryTab/HistoryTab.tsx";
import { Ikone } from "../Ikone.tsx";
import { SplitKontextProvider, baueSplitIndizes, type SplitKontextWert } from "../SplitMarke.tsx";
import { KostenSeite } from "../CostsPage/KostenSeite.tsx";
import { ZahlungsSeite, ensureFxCalculatorEntwurf } from "../PaymentsPage/ZahlungsSeite.tsx";
import { ZeitstrahlSeite } from "../TimelinePage/ZeitstrahlSeite.tsx";
import { UebersichtSeite } from "../OverviewPage/UebersichtSeite.tsx";
import { normalisierteRolle } from "../Users/RollenAuswahl.tsx";
import { NutzerSeiteLazy } from "../Users/NutzerSeiteLazy.tsx";
import { PasswortDialog, type PasswortDialogDaten } from "../Users/PasswortDialog.tsx";
import { FxKontenDialog } from "../Settings/FxKontenDialog.tsx";
import {
  Anlagemaske,
  type AnlagemaskeDuplikat,
  type JtlLieferantTreffer,
} from "../NewOrder/Anlagemaske.tsx";
import { TeillieferungDialog, type TeillieferungMenge } from "../NewOrder/TeillieferungDialog.tsx";
import { SplitRueckgaengigDialog } from "../NewOrder/SplitRueckgaengigDialog.tsx";
import { splitRueckgaengigAusfuehren, type RueckfuehrungsAuswahl } from "../../api/splitRueckgaengig.ts";
import type { JtlArtikelTreffer } from "../NewOrder/PositionsEditor.tsx";

/** `THEME_STORAGE_KEY` (index.html:5332) - siehe auch `ThemeSchalter.tsx`. */
const THEME_SPEICHER_SCHLUESSEL = "hubTheme";

/**
 * Rueckfallanzeige fuer einen Aendernden, dessen Anzeigename hier nicht bekannt ist - siehe
 * `anzeigenameZu` unten. Ein Satz, der genau das sagt, was bekannt ist; niemals die rohe id.
 */
const UNBEKANNTER_AENDERNDER = "einem anderen Benutzer";

/**
 * Auftrag C2b: der Platzhalter-Akteur fuer den Konflikt-Banner eines JTL-Kopf-Konflikts (409 von
 * `PATCH /api/jtl/orders/:k`). Anders als der Hub-Overlay-Konflikt (`current.updatedBy`, siehe
 * `backend/src/versioning/rowVersion.ts`) traegt der JTL-409-Koerper KEINEN Aendernden -
 * `aendereBestellkopfTeilweise` (`backend/src/jtl/schreiben/kopfLesen.ts`) liest `kBenutzer` beim
 * Konflikt nicht mit (siehe dortiger Kommentar, "AUFRUFPARAMETER"). Keine echte Nutzer-id trifft
 * jemals genau diese Zeichenkette - `anzeigenameZu` unten loest sie deshalb zuverlaessig auf den
 * Fallback-Text `UNBEKANNTER_AENDERNDER` auf, statt eine erfundene Identitaet zu behaupten.
 */
const JTL_KOPF_KONFLIKT_AKTEUR = "jtl-kopf-konflikt";

/**
 * Der Zustand nach einem gescheiterten Abruf der Positionsdokumente. Die Route antwortet auf jede Nicht-UUID mit 404 - eine JTL-Bestellung
 * ohne Hub-Zeile hat die Kennung `jtl-<n>` und kann keine Positionsdokumente tragen (Normalfall, wie in
 * `Positionsdokumente.tsx#vorschlaegeLaden`). Das ist eine Auskunft ("keine"), kein Ladefehler: sonst stuende bei jeder solchen Bestellung
 * eine Flaeche mit "Erneut laden", das nie helfen kann. Alles andere ist `gescheitert` (Task 13, E4).
 */
function zustandNachFehlschlag(fehler: unknown): PositionsDokumenteZustand {
  if (fehler instanceof ApiFehler && fehler.status === 404) return { art: "geladen", dokumente: [] };
  return { art: "gescheitert", ursache: fehler };
}

/** Wie ein Abruf der Positionsdokumente ausging: die Liste oder der Fehler. */
type PositionsDokumenteAusgang =
  | { art: "dokumente"; dokumente: PositionsDokument[] }
  | { art: "fehlschlag"; fehler: unknown };

/**
 * G7 (Auflagen-Pruefung): EINE Quelle statt vieler wortgleicher Literale - `speditionVerknuepfen` und
 * `speditionLoesen` (`useDrawerSpedition.ts`), `aufgabenNachladen` (`useDrawerTasks.ts`), (seit der
 * Zahlungsraten-Reparatur) `ratenNachladen` (`useDrawerZahlungen.ts`) - dorthin wird der Satz als Parameter
 * gereicht - und (seit Auftrag C2c) `drawerJtlKopfAendern` unten zeigen alle DENSELBEN Satz, wenn der
 * Schreibvorgang gelang, das anschliessende Nachladen aber scheiterte. Dieselbe Bauart wie
 * `drawerRechte.ts#NUR_LESEN_TITEL`: EIN Ort statt einer Kopie je Aufrufer, die auseinanderdriften
 * koennte, ohne dass ein Test es merkt.
 */
const NACHLADEN_FEHLGESCHLAGEN_MELDUNG =
  "Gespeichert - die Ansicht ist aber nicht mehr aktuell. Bitte neu laden.";

/**
 * Der Melder des Dialogs „Zollinstruktionen anstoßen“ (`SpeditionTab`, `SammelsendungDrawer`, Plan „Zollinstruktionen im Hub“,
 * Task 11). Der Dialog liegt in einem nachgeladenen Chunk und darf `state/fehlerStapel.ts` nicht importieren (AGENTS.md, „Rolldown
 * zerlegt das Eintrittsbuendel“); die App liegt im Eintritt und reicht `meldeFehler` herein. Eine Konstante: ein stabiler Verweis,
 * kein neues Objekt je Render.
 */
const ZOLL_MELDER: ZollMelder = { fehler: meldeFehler };

/**
 * Teillieferung (`POST /api/jtl/orders/:k/split`) mit verlorener Antwort (Proxy-502/504, Zeitlimit, Verbindungsabbruch,
 * 500): die Route hat weder bRowversion noch Idempotenzschutz, ein erneuter Versuch kann eine ZWEITE Teillieferung in
 * JTL anlegen (R-T7d, dieselbe Lage wie die Doppelanlage R-T6b). Die Meldung sagt deshalb nur, dass der Ausgang
 * unklar ist - ohne "konnte nicht angelegt werden" davor und ohne Wiederholungsrat dahinter.
 */
const TEILLIEFERUNG_AUSGANG_UNKLAR_MELDUNG =
  "Ob die Teillieferung in JTL angelegt wurde, ist unklar – bitte in JTL prüfen, bevor sie erneut angelegt wird.";

/**
 * Auftrag C2c, M2: die Meldung, wenn `drawerJtlKopfAendern` OHNE `bRowversion` aufgerufen wird - der
 * Datensatz traegt dann keine Sperre (Pfad: `bestellungLaden` faengt einen Fehlschlag der
 * JTL-Anreicherung ab und liefert die reine Hub-Zeile, `api/bestellungen.ts`; seit Fehleranzeige Paket 2,
 * Task 7, lehnt es stattdessen ab). Ein Schreibvorgang ohne Sperre bliebe hier ungeprueft - siehe
 * Kopfkommentar `drawerJtlKopfAendern`.
 */
const JTL_KOPF_OHNE_ROWVERSION_MELDUNG =
  "Lager, Notiz und Bezugsauftrag sind gerade nicht geladen. Bitte die Bestellung neu laden.";

/**
 * Auftrag E: Zeitlimit fuer den JTL-Kopf-Schreibweg (`fuehreKopfSchreibvorgangAus` unten) - ohne
 * dieses Limit haengt `anfrage()` (`api/basis.ts`) bei einer Verbindung, die nie antwortet, FUER
 * IMMER: `fetch` kennt von sich aus keinen Timeout, und die Serialisierung
 * (`schreibeKopfSerialisiert`) haengt JEDEN kuenftigen Schreibvorgang DERSELBEN Bestellung an genau
 * dieses eine, nie abschliessende Promise - GEMESSEN (siehe Bericht): zwei weitere Speicherversuche
 * ohne jeden `fetch`-Aufruf, ohne Banner, der Nutzer tippt ins Leere und haelt seine Aenderung fuer
 * gespeichert.
 *
 * DAUER: 60 Sekunden. Das Backend bindet sein eigenes Zeitbudget an den JTL-MSSQL-Schreibweg
 * (`backend/src/jtl/schreiben/mssqlZiel.ts`: `connectionTimeout` 5 s + `requestTimeout` 30 s) plus
 * ein anschliessendes Zuruecklesen ueber den Lesepool (`backend/src/jtl/pool.ts`:
 * `VERBINDUNGSAUFBAU_ZEITGRENZE_MS` 5 s + `ABFRAGE_ZEITGRENZE_MS` 15 s) - im schlechtesten, aber
 * legitimen Fall bis zu ~55 s, BEVOR das Backend selbst antwortet (Erfolg oder Fehler). 60 s liegen
 * bewusst DARUEBER: ein kuerzeres Limit meldete einen langsamen, aber am Ende erfolgreichen
 * Schreibvorgang faelschlich als abgebrochen, waehrend er beim Server noch liefe - der Nutzer saehe
 * eine falsche Fehlermeldung zu einem Vorgang, der Sekunden spaeter serverseitig gelungen waere.
 */
const JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS = 60_000;

/**
 * Auftrag E: die Meldung, wenn `JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS` ablaeuft (siehe dort) - bewusst
 * NICHT dieselbe wie die generische "Die Bestellung konnte nicht geändert werden." unten: der
 * Ausgang ist hier ECHT ungewiss (der Server hat die Anfrage womoeglich nie gesehen, oder er
 * schreibt dort weiter, waehrend das Frontend laengst aufgegeben hat) - der Nutzer soll es erneut
 * versuchen, statt auf eine Antwort zu warten, die den Client nie mehr erreicht.
 */
const JTL_KOPF_ZEITLIMIT_MELDUNG =
  "Die Bestellung konnte nicht gespeichert werden. Bitte erneut versuchen.";

/**
 * ============ DIE SECHS GEMESSENEN ABWEICHUNGEN DES GANZSEITENVERGLEICHS (Task 13) ============
 *
 * WARUM SIE HIER STEHEN UND NICHT IN EINEM BERICHT (Gesamtreview 2026-08-11, Critical 2): sie
 * standen bis dahin ausschliesslich in `.superpowers/sdd/2026-08-11-phase-06-verdrahtung/` - und
 * dessen `.gitignore` fuehrt `*`. `git ls-files` auf dieses Verzeichnis ist LEER: Fortschrittsbuch
 * und alle dreissig Berichte sind nicht versioniert. Wird der Arbeitsbaum nach dem Merge geloescht,
 * sind sechs gemessene Befunde samt der durchdachten Reihenfolge spurlos weg - genau der stille
 * Verwurf, gegen den dieses Projekt sonst ueberall antritt.
 *
 * Dieselbe "Option C" wie bei `ANLAGEMASKE_VERWORFENE_FELDER` unten: EINE Quelle im versionierten
 * Quelltext, plus ein Eintrag in der Bestandsaufnahme (`test/reference/bundle.test.ts`), der Laenge
 * und Namen prueft - nach dem Muster der `NEGATIVLISTE` dort. Wer eine Abweichung schliesst, setzt
 * `stand` um und macht den Fall rot; das ist der Zweck.
 *
 * Alle sechs sind GEMESSEN, nicht vermutet: blanker Ganzseitenlauf ueber drei Projekte
 * (`hell-1680`, `dunkel-1680`, `hell-1440`) gegen `node server.js` + `vite preview`, 16 bestanden /
 * 20 uebersprungen / 48 gescheitert in 33,2 min - die Ursachen B bis E wurden erst ueber eine
 * Wegwerf-Sonde sichtbar, die A ueberbrueckte (die Sonde ist zurueckgenommen, `harness.ts` ist
 * unveraendert; dauerhaft haette sie A verdeckt).
 *
 * Kein Laufzeitnutzer - anders als `ANLAGEMASKE_VERWORFENE_FELDER`, das der Toast liest. Diese
 * Tabelle ist eine ARTEFAKT-Quelle: sie soll den Merge ueberleben und im Test lesbar sein, nicht
 * gezeichnet werden.
 */
export interface GanzseitenAbweichung {
  /** Die Kennung aus dem Task-13-Bericht - unveraendert, damit Bericht und Quelltext sich treffen. */
  kennung: "A" | "B" | "C" | "D" | "E" | "F";
  /** Was abweicht, in einem Satz. */
  titel: string;
  /** Wo es sitzt - Produktionscode ODER Pruefstand, ausdruecklich unterschieden. */
  ort: string;
  /** Was der Nutzer bzw. der Lauf davon hat. */
  wirkung: string;
  stand: "behoben" | "offen" | "pruefstandsfrage";
}

export const GANZSEITENVERGLEICH_ABWEICHUNGEN: readonly GanzseitenAbweichung[] = Object.freeze([
  {
    kennung: "A",
    titel: "Der Anfangsbestand wurde ausschliesslich VOR der Anmeldung geholt, also mit einem 401.",
    ort: "Produktionscode: state/Speicher.tsx, Ladeeffekt (Abhaengigkeitsliste war [dienste] allein)",
    wirkung:
      "Board, Uebersicht, Kosten, Zahlungen und Zeitstrahl zeigten fuer JEDEN Nutzer in JEDER " +
      "Sitzung '0 Bestellungen' - api/basis.ts haelt das Token nur im Arbeitsspeicher, keine " +
      "Sitzung ueberlebt eine Neuladung. Das Original laedt umgekehrt (index.html:5719/:5794).",
    stand: "behoben",
  },
  {
    kennung: "B",
    titel: "Die fuenf Drawer-Ganzseitenvergleiche klicken in die Zeilenmitte statt an den Rand.",
    ort: "Pruefstand: test/visual/seiten/drawer-*.spec.ts",
    wirkung:
      "page.locator('[data-order-id=…]').click() trifft die Mitte, dort steht " +
      "span.order-list-editable-value, dessen onClick stopPropagation() ruft (Bestellzeile.tsx, " +
      "woertlich aus index.html:7651) - der Zeilen-onClick, der den Drawer oeffnet, wird nie " +
      "erreicht. Fuenf Faelle laufen je in die 90-Sekunden-Grenze: 7,5 min Leerlauf je Projekt, " +
      "bei drei Projekten 22,5 min je Gesamtlauf. Der SPA-Drawer selbst ist in Ordnung - die " +
      "Gegenprobe am Zeilenrand setzt aside#drawer.open.",
    stand: "offen",
  },
  {
    kennung: "C",
    titel:
      "Eine unerwartete Antwortform legt die GESAMTE Oberflaeche stumm still - es gibt keine " +
      "Fehlergrenze; und die beiden Backends sind sich ueber die Form von GET /api/users uneins.",
    ort:
      "Produktionscode (Fehlergrenze): components/App/App.tsx - nirgends eine React-Fehlergrenze. " +
      "Formfrage: backend/src/http/routes/users.ts liefert ein blankes Array, server.js ein " +
      "{ users: [...] }-Objekt; api/nutzer.ts erwartet ein Array und liegt fuer die PRODUKTION richtig.",
    wirkung:
      "Ein Klick auf das Marken-Menue wirft im Baseline-Rundlauf 'TypeError: b.map is not a " +
      "function'; document.body hat danach NULL Kinder. Der Fall dialog-passwort kann in diesem " +
      "Aufbau nicht gruen werden, aus einem Grund, der mit dem Passwortdialog nichts zu tun hat.",
    stand: "offen",
  },
  {
    kennung: "D",
    titel: "Der Fall users-standard navigiert nie zur Nutzerverwaltung - ein Schritt fehlt.",
    ort: "Pruefstand: test/visual/seiten/nutzer.spec.ts",
    wirkung:
      "Gemessen: #page-users ist vorhanden, traegt aber die Klasse 'page' statt 'active', und " +
      ".users-table tbody hat 0 Zeilen - das Nutzerverzeichnis wird erst beim Oeffnen des " +
      "Marken-Menues geladen. Der einzige Weg dorthin liefe durch C.",
    stand: "offen",
  },
  {
    kennung: "E",
    titel: "#board trug einen Knoten zu viel und die Klasse eine Ebene zu tief.",
    ort: "Produktionscode: components/App/App.tsx (#board) und components/Board/BoardSeite.tsx",
    wirkung:
      "index.html:6994 setzt board.className = 'grouped-order-list' AUF #board; die SPA rendert " +
      "<div id=\"board\"> ohne Klasse und darin ein zusaetzliches <div class=\"grouped-order-list\">. " +
      "Das CSS des Bestands ist eindeutig (#board.grouped-order-list, index.html:1975). Weil die " +
      "Signatur keine id fuehrt, war das die ERSTE Abweichung in JEDEM Ganzseitenvergleich - " +
      "board-standard, board-gruppe-zu, board-suche, board-listeneditor, costs-standard, " +
      "overview-standard, payments-standard.",
    stand: "behoben",
  },
  {
    kennung: "F",
    titel: "Eine Zeile der Playwright-Konfiguration war seit jeher wirkungslos.",
    ort: "Pruefstand: playwright.config.ts (use.reducedMotion)",
    wirkung:
      "reducedMotion ist in Playwright 1.62.1 keine use-Option, sondern eine " +
      "BrowserContextOption; unbekannte use-Schluessel werden zur Laufzeit still ignoriert. " +
      "Gemessen: matchMedia('(prefers-reduced-motion: reduce)').matches war false und wurde erst " +
      "unter use.contextOptions true. Bereits in Task 13 erledigt.",
    stand: "behoben",
  },
]);

/**
 * DIE BESCHLOSSENE REIHENFOLGE DER ABARBEITUNG (Auftraggeber, 2026-08-11, dem Reviewer folgend) -
 * und der Grund, aus dem sie so und nicht anders lautet:
 *
 *  - **B wird VORGEZOGEN**, nicht hinten angestellt: sie ist die einzige der sechs, die KEINEN
 *    Produktionscode anfasst, und sie holt 22,5 Minuten reine Wartezeit je Gesamtlauf zurueck.
 *    Diese Zeit zahlt unmittelbar auf jede Verifikation von A und E ein.
 *  - **C wird AUFGESPALTEN.** Die fehlende Fehlergrenze ist ein Produktionsdefekt derselben
 *    Schwereklasse wie A und gehoert VOR die Antwortform: mit Grenze wird aus "leerer body,
 *    Ursache unbekannt" ein diagnostizierbarer Fehlschlag - genau die Verwechslung, die A und C
 *    anfangs ununterscheidbar gemacht hat.
 *  - **Die Antwortform ist KEINE SPA-Reparatur.** `server.js` ist Bestand und Grundlage der
 *    Baseline; die SPA liegt fuer die Produktion richtig. Die Entscheidung lautet "Adapter im
 *    Pruefstand, oder diese Faelle in diesem Aufbau als nicht vergleichbar fuehren" - nicht
 *    "SPA anpassen".
 *
 * GEMESSEN, NICHT ANGENOMMEN: diese Liste hat SECHS Schritte, die Tabelle oben SECHS Eintraege -
 * es sind aber nicht dieselben sechs. Die Reihenfolge fuehrt nur FUENF Kennungen (A, B, C, D, E),
 * weil C zweimal vorkommt; F fehlt darin, weil F bereits in Task 13 erledigt wurde. Wer die beiden
 * Listen fuer deckungsgleich haelt, zieht den falschen Schluss.
 */
export const GANZSEITENVERGLEICH_REIHENFOLGE = Object.freeze([
  "A",
  "B",
  "E",
  "C(Fehlergrenze)",
  "C(Antwortform)",
  "D",
] as const);

/*
 * ENTFALLEN am 2026-08-11: `NUTZER_PASSWORT_RESET_MELDUNG`.
 *
 * Hier stand ein Warnhinweis, den `nutzerPasswortZuruecksetzen` unten an die Erfolgsmeldung haengte:
 * "das Passwort wurde NICHT geändert". Er war richtig, solange `PUT /api/users/:id` das Feld
 * verschluckte (Portierungsregression gegenueber dem Bestand-Worker).
 *
 * Mit der Reparatur des Endpunkts wurde er zur FALSCHAUSSAGE - und zwar in der gefaehrlicheren
 * Richtung: ein Administrator setzt ein Passwort, wird informiert, es habe nicht gewirkt, und gibt
 * das alte weiter. Das Konto ist fuer den Betroffenen unerreichbar. Deshalb faellt der Hinweis
 * ersatzlos; die Meldung lautet wieder schlicht "Nutzer aktualisiert", wie im Bestand.
 *
 * WARUM DAS HIER STEHT UND NICHT NUR IM VERLAUF: der zugehoerige Test prueft, dass der angezeigte
 * Text der importierten Konstante ENTSPRICHT. Als beide zugleich falsch wurden, blieb er gruen -
 * eine Zusicherung "Anzeige == Konstante" prueft Uebereinstimmung, nicht Wahrheit. Die Luecke ist
 * als elfte Auspraegung in `docs/superpowers/reference/2026-08-10-nachweise-die-nichts-nachweisen.md`
 * festgehalten. Wer hier wieder eine Konstante einfuehrt, die eine Tatsache ueber das Backend
 * behauptet, braucht einen zweiten Anker im Backend - nicht bloss eine zweite Kopie im Test.
 */

/**
 * Wurzelkomponente der eigentlichen Anwendung (nicht zu verwechseln mit `src/App.tsx`, dem
 * Vite-Einstiegspunkt aus Task 5 - der rendert diese Komponente und legt seit dem Merge der Tasks
 * 7-9 den `<Speicher>` darum).
 *
 * Entspricht `bootstrapSession()`/`showLogin()`/`doLogin()` (index.html:5689-5799). Die Sitzung
 * liegt seit dem Merge NICHT mehr in einem lokalen `useState` dieser Komponente, sondern im Store
 * (`zustand.nutzer`/`zustand.rechte`): der Store ist die einzige Quelle, sonst haetten Kopfzeile und
 * Datenschicht zwei Wahrheiten ueber denselben Nutzer, und der 401-Fall (siehe unten) muesste beide
 * treffen. Ohne Sitzung wird ausschliesslich `<Anmeldung>` gezeigt, mit Sitzung `<Kopfzeile>` +
 * Seiteninhalte.
 *
 * `#page-board` mit mindestens einem Kind ist dabei kein optischer Anspruch, sondern eine
 * notwendige Bruecke zum gemeinsamen Test-Harness (Task 6): `harness.ts#anmelden` wartet nach dem
 * Login unbedingt auf `document.querySelector("#page-board")?.children.length > 0` - genau die
 * Bedingung, die im Original `renderCurrentData()` (index.html:5887) bei JEDEM Laden erfuellt,
 * unabhaengig von der gerade aktiven Seite. Ohne diesen Platzhalter wuerde jeder Playwright-Test,
 * der sich ueber `anmelden()` anmeldet, auf unbestimmte Zeit haengen bleiben.
 *
 * NOCH NICHT GEBAUT (gemeldet, nicht heimlich ersetzt): `<main>` traegt im Original SECHS
 * Seitencontainer (`#page-board`, `-timeline`, `-overview`, `-payments`, `-costs`, `-users`,
 * index.html:3681-3734). Hier stehen die fuenf, deren Inhalt es gibt - das Bestellboard (Task 3
 * dieses Plans), der Zeitstrahl (Task 6), die Uebersicht (Task 7), die Kostenseite (Task 9, vor
 * diesem Task bereits eingehaengt) und seit diesem Task (8) die Zahlungsseite. Der eine uebrige
 * (Nutzer) gehoert dem noch nicht gebauten Seitentask; ihn hier vorwegzunehmen hiesse, fremde
 * Dateien zu belegen. Der Ganzseitenvergleich (`test/visual/seiten/kosten.spec.ts`, seit diesem
 * Task (8) zusaetzlich `test/visual/seiten/zahlungen.spec.ts` - dessen selbstscharfe Bedingung
 * `#page-payments` ist mit diesem Einhaengepunkt jetzt immer erfuellt, der Vergleich laeuft also
 * wirklich statt sich zu ueberspringen) sieht diese Luecke - siehe Merge-Bericht.
 *
 * `#page-board` UND `#page-overview` werden ANDERS behandelt als `#page-costs`/`#page-timeline`: sie
 * werden IMMER gezeichnet, unabhaengig von `seite` (`renderCurrentData()`, index.html:5887-5889 -
 * `renderBoard()` und `renderOverview()` laufen bei JEDEM Laden ungefragt; nur `renderTimelinePage`/
 * `renderPayPage`/`renderCostPage` haengen an der gerade aktiven Seite, :5890-5893). Nur `className`
 * steuert bei den ersten beiden `active`. Das ist keine Nachlaessigkeit, sondern derselbe Grund wie
 * oben unter "Bruecke zum Test-Harness" beschrieben, jetzt mit echtem Inhalt statt Platzhalter-Text.
 */

/**
 * Die Meldung fuer eine Bestellung, die es nicht (mehr) gibt - EINE Stelle fuer beide Formen
 * (mit und ohne Kennung), vorher an zwei Aufrufern wortgleich aufgebaut (Befund "Gering" 3 der
 * Pruefrunde 3).
 *
 * `kennung` ist NICHT immer abwesend, wenn `treffer` in `uebersichtOeffnen` fehlt: DREI
 * Aufrufer erreichen den Nicht-gefunden-Zweig, mit UNTERSCHIEDLICHEN Voraussetzungen -
 * `Uebersicht-Handlungsbedarf`/`NutzerSeite`s Audit-Protokoll kennen nur die rohe id (meist
 * eine UUID, `kennungVon` ist dort nicht anwendbar - kein `Order`, an dem sie haengen koennte),
 * `Terminaenderungen.tsx` dagegen HAT eine lesbare Kennung zur Hand
 * (`bestellungAnzeige(bestellung)` - seit Auftrag "be-*-Bestellnummer statt jtl_order_id in
 * Terminaenderungen-Pills" (01.09.2026) bevorzugt diese Funktion die ECHTE JTL-Bestellnummer
 * `orderNo` (`cEigeneBestellnummer`, z. B. "BE-10281") und faellt nur ohne sie auf `bestellnummer`
 * (`orders.jtl_order_id` als Text) zurueck - `null` bleibt weiterhin nur ohne verknuepfte
 * JTL-Bestellung moeglich: siehe `backend/src/http/routes/terminaenderungen.ts:35-46`
 * (`BestellungsBezugAntwort`)) und reicht sie ueber `uebersichtOeffnen`s dritten, optionalen
 * Parameter `kennungHinweis` durch (Befund 1 der Pruefrunde 4). Eine UUID im Fliesstext waere
 * das Gegenteil dessen, was `anzeigenameZu` weiter unten fuer Nutzer-ids begruendet -
 * `kennung === null` heisst deshalb wirklich "keine lesbare Kennung vorhanden", nicht "keiner
 * hat sie mitgegeben".
 *
 * GEMESSENER DOM-WEG (Befund 1 der Pruefrunde 4 - eine fruehere Fassung dieses Kommentars
 * behauptete, es gebe KEINEN DOM-Weg zum Nicht-gefunden-Zweig, weil jede Zeile mit ihrem
 * Datensatz verschwinde. Das gilt fuer ZWEI der drei Aufrufer, nicht fuer alle: die Ausnahme
 * ist genau `Terminaenderungen.tsx` - die Komponente laedt ihre Zeilen SELBST
 * (`GET /api/dsv/terminaenderungen`) in lokalen Zustand, GENAU EINMAL, ohne Nachziehen (siehe
 * dortiger Kopfkommentar, "istAktiveSeite") - eine Zeile mit einer im Store inzwischen toten
 * id bleibt deshalb anklickbar stehen. `adresse.test.tsx` faehrt diesen Weg ueber die echte
 * `<Terminaenderungen>`-Komponente, nicht nur ueber einen Unit-Test dieser Funktion.
 */
export function meldungBestellungNichtGefunden(kennung: string | null): string {
  return kennung ? `Bestellung ${kennung} wurde nicht gefunden.` : "Die Bestellung wurde nicht gefunden.";
}

/**
 * Rechteanforderung je `AktiveSeite` (Befund K-2 der Pruefrunde 1): deckt "suppliers" ZUSAETZLICH
 * zu `SEITEN_RECHTE` (`berechtigungen.ts`) ab, das nur `Seite` kennt - `berechtigungen.ts` bleibt
 * unangetastet (Parity-Test gegen `index.html`, siehe TASK-11-Kommentar oben an `App()`).
 *
 * `undefined` heisst FREI, nicht gesperrt: eine Seite ohne Eintrag hat KEINE Rechteanforderung, sie
 * ist nicht automatisch gesperrt. `lieferantenOeffnen` unten hat bewusst KEINEN Rechte-Waechter
 * (siehe dessen Kopfkommentar - "Lesen steht allen Rollen offen"). Der vorherige
 * `SEITEN_RECHTE[ansicht.seite as Seite]`-Cast hat genau diese Luecke zugedeckt: der Typfehler, den
 * er unterdrueckte, haette direkt auf die fehlende Zuordnung fuer "suppliers" hingewiesen.
 */
function rechtFuerSeite(seite: AktiveSeite): string | undefined {
  // "suppliers" und "paymentTemplates": Lesen steht allen Rollen offen; das Schreiben sperrt die Seite selbst (manage_suppliers).
  // "activities": das Protokoll hat keine Rechteanforderung (wie der Reiter in `Navigation.tsx`).
  // "kiErlauben", "kiZugaenge": jedes Konto verbindet und widerruft seine eigenen KI-Zugaenge; "Alle Verbindungen"
  // sperrt die Seite selbst (`manage_users`). Ein Rueckfall wuerde zudem die Anfrage der Erlauben-Seite aus der Adresse werfen.
  // "dashboard": die Seite rechnet aus dem Bestand, den die App ohnehin hat; keine Rechteanforderung (wie der Reiter in `Navigation.tsx`).
  if (
    seite === "suppliers" || seite === ZAHLUNGSVORLAGEN_SEITE || seite === AKTIVITAETEN_SEITE || seite === DASHBOARD_SEITE ||
    seite === KI_ERLAUBEN_SEITE || seite === KI_ZUGAENGE_SEITE
  ) {
    return undefined;
  }
  if (seite === LIQUIDITAET_SEITE) return LIQUIDITAET_RECHT;
  return SEITEN_RECHTE[seite];
}

/**
 * Klartext je Seite fuer Meldungen (Rechte-Rueckfall unten) - wortgleich aus `Navigation.tsx`s
 * `REITER` (`:62-68`) uebernommen, damit die Meldung dasselbe Wort benutzt wie der Reiter, auf den
 * sie sich bezieht, PLUS `suppliers` (kein Reiter, nur ueber das Marken-Menue erreichbar - Befund
 * K-2 der Pruefrunde 1: `Record<AktiveSeite, string>`, nicht `Record<Seite, string>`, sonst kann
 * eine Meldung wieder das Wort "undefined" zeigen). `berechtigungen.ts` fuehrt selbst keine
 * Klartexttabelle (dort steht nur die Rechtezuordnung, keine Beschriftung).
 */
const SEITEN_NAMEN: Record<AktiveSeite, string> = {
  board: "Bestellboard",
  timeline: "Zeitstrahl",
  dashboard: "Dashboard",
  overview: "Übersicht",
  activities: "Aktivitäten",
  payments: "Zahlungen & Kurssicherung",
  liquidity: "Liquiditätsplanung",
  costs: "Kosten",
  users: "Nutzerverwaltung",
  suppliers: "Lieferanten",
  paymentTemplates: "Zahlungsvorlagen",
  kiErlauben: "KI-Zugang erlauben",
  kiZugaenge: "Meine KI-Zugänge",
};

/**
 * Die Seiten, die den Bestand (`zustand.bestellungen` und was daraus abgeleitet wird) lesen - und auf
 * denen bei einem gescheiterten Erstladen die Ladefehler-Flaeche den Seiteninhalt ersetzt (Fehleranzeige
 * Paket 2, Entscheidung E12): sonst zeigten sie "0 Bestellungen" und Leertexte wie ein leerer Bestand.
 *
 * Gemessen an den Props, die `App()` unten an die Seiten reicht, nicht aus dem Kopfkommentar von
 * `Speicher.tsx` abgelesen:
 *   board, timeline, overview, payments, costs, liquidity, dashboard - bekommen `bestellungen={zustand.bestellungen}`.
 * NICHT dabei:
 *   suppliers (`LieferantenSeite`: laedt ihre Uebersicht selbst, `lieferantenUebersichtLaden`),
 *   paymentTemplates, activities (laden selbst), users (`NutzerSeite` liest `bestellungen` nur fuer
 *   `auditLogEintraege`; `Order.audit` setzt im Produktionsweg niemand, die Liste ist dort ohnehin leer).
 *
 * Eine neue Seite, die `zustand.bestellungen` liest, gehoert hier hinein.
 */
export const SEITEN_MIT_BESTAND: ReadonlySet<AktiveSeite> = new Set<AktiveSeite>([
  "board",
  "timeline",
  "overview",
  "payments",
  "costs",
  LIQUIDITAET_SEITE,
  DASHBOARD_SEITE,
]);

export function App(): JSX.Element {
  const { zustand, versende, bestandNeuLaden, bestandVollNeuLaden, bestaetigterFilter, lueckeNeuLaden, lueckeSchliessen } =
    useSpeicher();
  /*
   * TASK 11: `AktiveSeite` (= `Seite | "suppliers"`), NICHT `Seite`. Die Lieferantenflaeche ist eine
   * SPA-eigene Seite ohne Vorbild im Bestand; `state/berechtigungen.ts` bleibt deshalb unangetastet
   * (`test/parity/berechtigungen.test.ts` haelt es wortgleich gegen `index.html`) - die Union lebt in
   * `Navigation.tsx`, wo auch der Reiter sitzt. `ersteErlaubteSeite` liefert weiterhin nur `Seite`
   * und kann die Lieferantenseite folglich nie zur Startseite machen; das ist beabsichtigt.
   *
   * TIEFLINKS (Aufgabe 3): Die Seite lebt seither in der ADRESSE, nicht mehr in einem eigenen
   * `useState` - `useAnsicht` liest sie aus `useLocation`/`useNavigate` (`state/useAnsicht.ts`).
   * `setSeite` bleibt als NAME erhalten, damit die uebrigen `setSeite(...)`-Aufrufstellen in dieser
   * Datei unveraendert bleiben.
   */
  const { ansicht, setzeAnsicht } = useAnsicht();
  const seite = ansicht.seite;
  const setSeite = (neu: AktiveSeite): void => setzeAnsicht({ seite: neu });
  // Erstladen des Bestands gescheitert (`Speicher.tsx` setzt `bestandLadefehler` nur, solange noch kein
  // Stand uebernommen ist): auf den Bestandsseiten steht dann die Ladefehler-Flaeche statt des Inhalts.
  const bestandFlaeche = zustand.bestandLadefehler !== null && SEITEN_MIT_BESTAND.has(seite);

  useBoardBestellzustand(
    zustand.nutzer, ansicht.seite, ansicht.board.states, bestandNeuLaden, setzeAnsicht, bestaetigterFilter,
  );

  // Dashboard: der Download einer Packliste (`onDateiLaden` der Seite). Die Seite liegt im nachgeladenen Chunk und darf weder
  // den Fehlerstapel noch diesen Hook importieren (AGENTS.md, „Rolldown zerlegt das Eintrittsbuendel“); sie verlaesst sich darauf,
  // dass der Rueckruf NIE ablehnt. Der Hook erfuellt das: er faengt jeden Fehler des Abrufs und meldet ihn im Stapel
  // („Die Packliste konnte nicht geladen werden. <Ursache>“).
  const { herunterladen: packlisteLaden } = useDateiDownloadMitStapel("Die Packliste konnte nicht geladen werden.");

  // `toast()` (index.html:5264-5270), Task 2. `null` ist der Ruhezustand ohne Meldung - der
  // Zeitvertrag (Setzen, `clearTimeout`-Reset, Dauer) lebt in `Toast.tsx#useMeldungsAnzeige`
  // (Fix-Runde 1: eigenstaendige, exportierte Einheit statt einer Kopie hier - siehe dort).
  const [meldung, zeigeMeldung] = useMeldungsAnzeige();
  // Hinweise OHNE Zeitablauf (Task 4 "Uploads bis 100 MB", `StehendeHinweise.tsx`): bleiben stehen,
  // bis der Nutzer sie schliesst, mehrere stapeln sich. `zeigeHinweis` steht fuer die Uploadwege
  // (Task 5 bis 7) bereit und hat bis dahin keinen Aufrufer.
  const { hinweise, zeigeHinweis, schliesseHinweis } = useStehendeHinweise();
  // Sperre und Fortschrittszeile der Upload-Stellen (Spec "Upload-Sperren", 2026-10-20): ein Store ueber den
  // Komponenten, die hochladen, damit sie Reiter- und Bestellungswechsel ueberleben. Referenzstabil und OHNE
  // App-State: `onprogress` feuert ungedrosselt, die App zeichnet bei einem Fortschritt nicht neu (die Leser
  // abonnieren den Store selbst). Die Weitergabe folgt dem Weg von `zeigeHinweis`.
  const uploadStellen = useUploadStellen();
  const { zahlungenSicherungBinden, zahlungenSicherungLoesen } =
    useFxZuordnungen(zustand.fxDeals, versende);
  const {
    fxOverview, fxOverviewLadefehler, fxOverviewLadeursache, fxOverviewNeuLaden, fxOverviewNachSchreibenLaden,
    fxOverviewKontoAktualisieren,
  } =
    useFxOverview(zustand.nutzer, ansicht.seite, zustand.rechte, zustand.fxDeals);

  // Entspricht `initTheme()` (index.html:14936): unbedingt, unabhaengig vom Login-Status, damit auch
  // der Anmeldebildschirm das gespeicherte Theme zeigt. `ThemeSchalter.tsx` uebernimmt danach jeden
  // weiteren Wechsel eigenstaendig (siehe dortiger Kopfkommentar).
  useEffect(() => {
    let gespeichert = "light";
    try {
      gespeichert = localStorage.getItem(THEME_SPEICHER_SCHLUESSEL) || "light";
    } catch { // still: wie im Original darf ein blockiertes localStorage den Start nicht verhindern
    }
    document.documentElement.dataset.theme = gespeichert === "dark" ? "dark" : "light";
  }, []);

  /**
   * `sitzungWirdGeprueft`: waehrend ein gespeichertes Token noch gegen den Server geprueft wird
   * (Bootstrap-Effekt unten), ist die Anmeldemaske NICHT erreichbar - 1:1 zu `index.html:3995`, wo
   * `#login` beim Laden KEIN `show` traegt (erst `showLogin()` blendet es ueberhaupt ein, und das
   * laeuft entweder SOFORT ohne Token oder erst NACH dem Fehlschlag). Portiert die BESTANDSREIHEN-
   * FOLGE, statt den Versand nachtraeglich abzusichern (Review 2026-08-12, Critical 1):
   *
   * OHNE diese Sperre stand `<Anmeldung>` die ganze Pruefzeit ueber bedienbar da (Minor 5 desselben
   * Reviews - ein unbenanntes Aufblitzen bei jedem F5) UND ermoeglichte damit einen echten Wettlauf:
   * Nutzer A's noch gueltiges Token liegt in `hubSession`, `/api/auth/me` antwortet langsam; waehrend
   * die Antwort unterwegs ist, meldet sich B ueber genau diese sichtbare Maske an (Token B, Store B);
   * dann trifft A's veraltete Antwort ein und versendet `"angemeldet"` mit A - die Oberflaeche und
   * die clientseitigen Rechte gehoeren wieder A, waehrend Token und Ereignisstrom laengst auf B
   * laufen. GEMESSEN: "zeigt Anna Alt: true, zeigt Bert Neu: false, Token: token-von-B".
   *
   * Initialwert: SYNCHRON beim ersten Rendern `true`, wenn zu diesem Zeitpunkt ein Token in
   * `localStorage` liegt - `sitzungAusSpeicherUebernehmen()` uebernimmt es dabei GLEICH als
   * aktuelles Token (`api/basis.ts`), dieselbe fruehe Uebernahme wie
   * `let SESSION_TOKEN = localStorage.getItem('hubSession') || '';` am Skript-Toplevel des
   * Originals (`index.html:5273`) - hier ueber den Lazy-Initializer von `useState`, weil das der
   * fruehstmoegliche Zeitpunkt einer Komponente ist, VOR jedem Rendern von `<Anmeldung>`. OHNE
   * gespeichertes Token: `false`, die Anmeldemaske erscheint dann SOFORT, ohne Wartezeit - wie im
   * Original (`if (!SESSION_TOKEN) return showLogin();`, `index.html:5787`). Ein zusaetzlicher
   * Ladezustand fuer diesen (haeufigsten) Fall waere eine NEUE Abweichung.
   */
  const [sitzungWirdGeprueft, setSitzungWirdGeprueft] = useState<boolean>(
    () => sitzungAusSpeicherUebernehmen() !== null,
  );

  /**
   * Zweite, redundante Lage gegen denselben Wettlauf (Review 2026-08-12: "als zweite Lage
   * willkommen, aber nicht als einzige" - heilt sonst nur das Symptom). Haelt den JEWEILS AKTUELLEN
   * `zustand.nutzer` bereit, damit der Bootstrap-Effekt unten ihn beim Eintreffen der Antwort lesen
   * kann, OHNE dass der Effekt selbst von `zustand.nutzer` abhinge (er liefe sonst bei jeder
   * Anmeldung erneut los). Ein `useEffect([zustand.nutzer])` waere hierfuer zu spaet dran - die
   * Zuweisung waehrend des Renderns selbst ist der frueheste Zeitpunkt, an dem der Ref den Wert
   * dieses Renderns tragen kann.
   */
  const nutzerBeiRenderZeit = useRef(zustand.nutzer);
  nutzerBeiRenderZeit.current = zustand.nutzer;

  /**
   * Der Hinweis, den die Anmeldemaske beim Start in ihrer Fehlerzeile zeigt, wenn die Pruefung der gespeicherten Anmeldung
   * scheiterte (Bootstrap-Effekt unten; `Anmeldung.startHinweis`). Er gilt nur fuer diese eine Maske: sobald jemand
   * angemeldet ist - ob ueber die Maske oder, bei einer spaet eintreffenden Fehlerantwort, schon vorher -, faellt er
   * weg, damit die Maske nach einem spaeteren Sitzungsverlust nicht mit einem veralteten Satz erscheint.
   */
  const [sitzungsHinweis, setSitzungsHinweis] = useState<string | null>(null);
  useEffect(() => {
    if (zustand.nutzer) setSitzungsHinweis(null);
  }, [zustand.nutzer, sitzungsHinweis]);

  /**
   * Ist der KI-Zugang im Bestellhub eingeschaltet (`MCP_AKTIV`)? Aus der Antwort von `GET /api/auth/me`
   * (Bootstrap-Effekt unten) bzw. der Anmeldung (`angemeldet`); nur `=== true` zaehlt, ein fehlendes Feld (aelteres Backend)
   * heisst aus. Steuert allein den Eintrag "Meine KI-Zugänge" im Marken-Menue - Spec: Schalter aus = Rollout ohne Wirkung.
   * Die Seiten selbst bleiben per Adresse erreichbar und melden dann "nicht eingeschaltet". Bewusst lokaler Zustand statt
   * eines Feldes im Store (`Speicherzustand`): es haengt an keiner Bestellung und wird nur hier gelesen.
   */
  const [kiZugangAktiv, setKiZugangAktiv] = useState(false);

  /**
   * Sitzungswiederaufnahme beim Start (Befund B17, Nachmessung R5) - 1:1 zu `bootstrapSession()`
   * (`index.html:5786-5798`), aufgerufen als LETZTE Anweisung des Originalskripts (`:14937`, direkt
   * nach `initTheme()` an `:14936` - deshalb hier ebenfalls direkt nach dem Theme-Effekt oben):
   *
   * ```js
   * async function bootstrapSession() {
   *   if (!SESSION_TOKEN) return showLogin();
   *   try {
   *     const r = await api('/api/auth/me');
   *     const data = await r.json();
   *     applyAuthPayload(data);
   *     updateUserUi();
   *     $('#login').classList.remove('show');
   *     await load();
   *     startAutoRefresh();
   *   } catch (e) {
   *     if (e.message !== 'unauthorized') showLogin();
   *   }
   * }
   * ```
   *
   * Hier als eigener Effekt statt einer Skript-Toplevel-Anweisung - dieselbe Uebersetzung wie bei
   * `sitzungAusSpeicherUebernehmen()` (`api/basis.ts`), die SPA kennt keinen Toplevel, der vor jeder
   * Komponente laeuft. Das TOKEN-UEBERNEHMEN selbst ist bereits im `useState`-Initializer oben
   * geschehen (fruehstmoeglich, VOR diesem Effekt) - hier bleibt nur noch die Pruefung UND, in
   * `finally`, das Schliessen der Sperre von oben (Critical 1): `sitzungWirdGeprueft` wird so oder
   * so wieder `false`, egal ob die Pruefung gelingt oder scheitert - andernfalls bliebe die
   * Anmeldemaske nach einem gescheiterten Token fuer immer unerreichbar.
   *
   * `[]` als Abhaengigkeitsliste (nicht `[versende]` wie zuvor): dieser Effekt prueft GENAU EINMAL
   * das Token, das beim Mounten bereits feststand - `versende` selbst ist ohnehin stabil
   * (`useReducer`-Dispatch), ein Wechsel wuerde den Effekt also nie erneut ausloesen.
   *
   * OHNE TOKEN: der `useState`-Initializer hat bereits `false` gesetzt, dieser Effekt hat nichts zu
   * tun - `<Anmeldung>` ist ab dem ersten Rendern erreichbar (siehe Render-Zweig unten).
   *
   * MIT TOKEN, ERFOLGREICH: `eigenesProfil()` (`GET /api/auth/me`) liefert Nutzer und Rechte, der
   * Store bekommt sie ueber denselben `"angemeldet"`-Fall wie ein frischer Login - `bestandLaden`
   * (haengt an `angemeldet` in `state/Speicher.tsx`) laedt den Anfangsbestand hinterher. Die
   * zweite Lage (`nutzerBeiRenderZeit`) greift NUR, wenn INZWISCHEN doch schon jemand angemeldet
   * ist - unter der Sperre von oben eigentlich unerreichbar, siehe deren Kopfkommentar.
   *
   * MIT TOKEN, FEHLGESCHLAGEN (abgelaufenes/ungueltiges Token): DIE FEINHEIT AUS DEM ORIGINAL - bei
   * einem 401 tut `anfrage()` (`api/basis.ts`) bereits ALLES Noetige selbst (Token samt
   * `localStorage['hubSession']` raeumen, `sitzungsverlustHandler` -> der Hook direkt darunter
   * versendet `"angemeldet": null`) UND wirft einen `SitzungsFehler`. Ein zweiter Versand hier waere
   * doppelt - deshalb die Fallunterscheidung, wortgleich zum `if (e.message !== 'unauthorized')` des
   * Originals. `finally` schliesst die Sperre in beiden Zweigen - die eigentliche Wirkung des
   * Originalzweigs (kein zweites `showLogin()`) liegt bereits VOLLSTAENDIG in `anfrage()`
   * (`api/basis.ts:200-206`). Die Unterscheidung bleibt stehen (Minor 4, Review 2026-08-12:
   * vorher ohne beobachtbaren Unterschied), denn die beiden Zweige tun jetzt Verschiedenes:
   * beim 401 gibt es keinen Hinweis (die Maske ist die ganze Auskunft), bei jedem ANDEREN
   * Fehler (Netz weg, 5xx, abgerissene Antwort) bleibt es beim Anfangszustand
   * `null` - dieselbe Wirkung wie `showLogin()` im Original -, aber die Fehlerzeile der Maske
   * sagt, dass die gespeicherte Anmeldung nicht geprueft werden konnte, samt Ursache
   * (Fehleranzeige Paket 2, `sitzungsHinweis`). Das Token bleibt erhalten (nur ein 401 verwirft
   * es): F5 prueft erneut, einen eigenen Knopf gibt es nicht. IN BEIDEN FAELLEN bleibt die
   * Anmeldemaske stehen, nie eine leere Seite: ein Fehlschlag laesst den Store exakt dort, wo er
   * ohne gespeichertes Token auch stuende.
   */
  useEffect(() => {
    if (!sitzungWirdGeprueft) return;
    let verworfen = false;
    eigenesProfil()
      .then((daten) => {
        if (verworfen) return;
        if (nutzerBeiRenderZeit.current) return; // zweite Lage, siehe deren Kopfkommentar oben.
        setKiZugangAktiv(daten.kiZugangAktiv === true);
        versende({ art: "angemeldet", nutzer: daten.user, rechte: daten.permissions });
      })
      .catch((fehler: unknown) => {
        if (fehler instanceof SitzungsFehler) return; // anfrage() hat die Abmeldung bereits versendet.
        if (verworfen) return;
        // Jeder andere Fehler: der Anfangszustand (nutzer: null) IST die Anmeldemaske, die Maske sagt nur noch, warum
        // sie da ist. `null` (Abbruch) ergibt keinen Hinweis.
        setSitzungsHinweis(fehlertext(fehler, "Die gespeicherte Anmeldung konnte nicht geprüft werden."));
      })
      .finally(() => {
        if (!verworfen) setSitzungWirdGeprueft(false);
      });
    return () => {
      verworfen = true;
    };
  }, []); // absichtlich leer, nicht [versende] - Begruendung im Kommentar oben ("[] als Abhaengigkeitsliste").

  /**
   * Der dritte Teil der Anmeldeverdrahtung und der einzige, der sich nicht von selbst bemerkbar
   * macht: `api/basis.ts` verwirft bei einem 401 das Token und ruft genau diesen Haken
   * (1:1 zu index.html:5317-5324, `showLogin()`/`updateUserUi()`). OHNE ihn passiert nach einem
   * abgelaufenen Token GAR NICHTS - die Oberflaeche bleibt mit ihrem alten Nutzer stehen, jeder
   * weitere Aufruf scheitert still, und der Benutzer bekommt nie eine Anmeldemaske zu sehen.
   * Die Rueckgabe von `beiSitzungsverlust` hebt die Registrierung wieder auf; ohne sie bliebe ein
   * Haken auf eine abgebaute Komponente stehen.
   */
  useEffect(() => {
    return beiSitzungsverlust(() => {
      versende({ art: "angemeldet", nutzer: null, rechte: [] });
    });
  }, [versende]);

  /**
   * Die aktiven JTL-Warenlager (`GET /api/jtl/warehouses`) - die Grundlage der Lager-Auswahl in
   * der Anlagemaske (`lib/lager.ts#lagerAuswahl`). RICHTIGGESTELLT (Nachtrag "Bestellfelder nach
   * Anlage sperren", Abschnitt 5, 2026-08-25): der Drawer (`MainTab/Stammdaten.tsx`) zeigt das
   * Ziellager seither nur noch schreibgeschuetzt an und braucht diese Liste nicht mehr - die
   * Anlagemaske bleibt der einzige Aufrufer. EINMAL beim Start geladen, dort,
   * wo die SPA ihre uebrigen Stammdaten zieht - genau wie `bestandLaden` in `state/Speicher.tsx`
   * haengt auch dieser Abruf an der Anmeldung (`zustand.nutzer !== null`), nicht an der blossen
   * Montage: `<Speicher>` (und damit `<App>`) umschliesst auch die Anmeldemaske, und die Route
   * verlangt eine Sitzung (401 ohne sie).
   *
   * Scheitert der Abruf, bleibt die Liste leer - KEINE Ersatzliste im Code (dieselbe Haltung wie
   * `jtlBestellungenRohLaden` mit `bestellungenZusammensetzen`, siehe `state/Speicher.tsx`: "ein halber
   * Bestand, dem man das nicht ansieht, ist schlechter als ein sichtbarer Ausfall"). `meldeFehler` macht den Ausfall laut,
   * damit der Nutzer merkt, dass die Auswahl gerade nicht bestueckt ist, statt eine leere Auswahl
   * fuer eine vollstaendige zu halten.
   */
  const [jtlLager, setJtlLager] = useState<readonly JtlLager[]>([]);
  useEffect(() => {
    if (!zustand.nutzer) return;
    let verworfen = false;
    jtlLagerLaden()
      .then((liste) => {
        if (!verworfen) setJtlLager(liste);
      })
      .catch((fehler: unknown) => {
        if (!verworfen) meldeFehler(fehler, "Die Lagerliste aus JTL konnte nicht geladen werden.");
      });
    return () => {
      verworfen = true;
    };
  }, [Boolean(zustand.nutzer)]);

  function angemeldet(ergebnis: AngemeldeterNutzer): void {
    // 1. Token: `Anmeldung.tsx` (Task 7) spricht mit einem eigenen, kleinen `fetch` direkt gegen
    //    `/api/auth/login` und nicht ueber `api/auth.ts#anmelden` - das Token landet deshalb nicht
    //    von selbst in `api/basis.ts`. Ohne diese Zeile traegt KEIN einziger spaeterer API-Aufruf
    //    einen `Authorization`-Kopf, und der SSE-Strom bekaeme dauerhaft 401.
    setzeSitzungsToken(ergebnis.token);
    // 2. Nutzer und Rechte in den Store. Der Rechte-Rueckfall (§ 5.1/§ 5.2) laeuft NICHT mehr hier
    // inline, sondern im Effekt unten (Befund K-1 der Pruefrunde 1) - der greift auf JEDEM Weg, auf
    // dem eine Sitzung entsteht oder sich die Adresse aendert, nicht nur bei diesem einen.
    versende({ art: "angemeldet", nutzer: ergebnis.user, rechte: ergebnis.permissions });
    // Der Schalter des KI-Zugangs steht in derselben Antwort wie in `/me`; ohne das Feld bleibt der Eintrag aus.
    setKiZugangAktiv(ergebnis.kiZugangAktiv === true);
  }

  /**
   * Der Rechte-Rueckfall (§ 5.1/§ 5.2) fuer JEDEN Weg, auf dem eine Sitzung entsteht oder sich die
   * Adresse aendert - Formular-Login (`angemeldet` oben), wiederaufgenommene Sitzung
   * (Bootstrap-Effekt, `eigenesProfil()`), Vor/Zurueck, Tieflink (Befund K-1 der Pruefrunde 1).
   *
   * VORHER stand diese Pruefung nur inline in `angemeldet()`, dem Rueckruf des Anmeldeformulars -
   * die wiederaufgenommene Sitzung versendet `"angemeldet"` DIREKT im Bootstrap-Effekt oben und lief
   * nie durch `angemeldet()`, prueft also nie. Vor dieser Aufgabe (Tieflinks) konnte `seite` nur aus
   * `Navigation.tsx:73` kommen (filtert selbst nach `SEITEN_RECHTE`) oder aus
   * `nutzerverwaltungOeffnen` (eigener Riegel) - seit die Adresse `seite` bestimmt, gibt es einen Weg
   * daran vorbei, den ein einzelner Aufrufer nicht schliessen kann. Ein Effekt, der auf
   * `zustand.rechte` UND `ansicht.seite` hoert, trifft dagegen JEDEN Weg gemeinsam - das ist die
   * Zusicherung, nicht der Mechanismus (so vom Pruefer vorgegeben).
   *
   * `zustand.nutzer` in der Bedingung: OHNE Sitzung ist nichts zu pruefen - die Anmeldemaske
   * entscheidet selbst, und ein Tieflink darf sein Ziel behalten, bis sich jemand angemeldet hat
   * (§ 5.1, siehe `adresse.test.tsx` "behaelt das Ziel ueber die Anmeldung").
   *
   * `rechtFuerSeite` (Modulkopf) statt `SEITEN_RECHTE[ansicht.seite as Seite]`: kein `as`-Cast mehr,
   * der eine fehlende Zuordnung verdeckt (Befund K-2 der Pruefrunde 1) - `rechtFuerSeite` deckt
   * `AktiveSeite` VOLLSTAENDIG ab und liefert fuer eine Seite ohne Rechteanforderung `undefined`
   * statt eines stillen `hatRecht(rechte, undefined) === false`.
   */
  /**
   * Merkt sich, wohin der letzte Rueckfall FUEHRTE, UND unter welchem Rechtesatz (Befund 1 der
   * Pruefrunde 3 - `zuletztRueckfallSeite` allein, Kleinbefund der Pruefrunde 2, riss K-1 fuer
   * genau den Weg wieder auf, den diese Aufgabe schliessen sollte: meldet sich IN DERSELBEN
   * Montage ein ZWEITES Konto an (`abgemeldet()`/eine neue Anmeldung ruehrt die Adresse nicht
   * an, `app.test.tsx`s `BertMeldetSichZwischendurchAn` faehrt genau das), aendert sich
   * `zustand.rechte` auf ein NEUES Set, `ansicht.seite` bleibt aber oft dieselbe (die vom
   * ERSTEN Rueckfall) - die Ref allein verwechselte dann "wir selbst haben hierhin
   * umgeleitet" mit "ein voellig neuer Vorgang landet zufaellig auf derselben Seite" und
   * verschluckte Meldung UND Rueckfall fuer das zweite Konto komplett.
   *
   * `zuletztRueckfallRechte` haelt die IDENTITAET des Sets fest, unter dem der letzte Rueckfall
   * geschah - `zustand.rechte` bekommt im Reduzierer bei JEDEM `"angemeldet"`-Dispatch ein
   * NEUES `Set` (`new Set(aktion.rechte)`), bleibt sonst referenzgleich stehen. Nur wenn BEIDE
   * - Seite UND Rechte-Set - mit dem letzten Rueckfall uebereinstimmen, ist es wirklich
   * derselbe Vorgang (Kleinbefund der Pruefrunde 2, unveraendert: ein Nutzer OHNE JEDES
   * Seitenrecht bekaeme sonst ZWEI Meldungen fuer denselben Anlauf, weil `ersteErlaubteSeite`
   * mangels Rechten auf "board" faellt und "board" GENAUSO gesperrt ist).
   *
   * EIN SCHMALER SEITENEFFEKT BLEIBT (Befund 3 der Pruefrunde 4 - dieser Satz war in Runde 2
   * hier schon einmal wortgleich formuliert und ist beim Umbau in Runde 3 ersatzlos
   * verschwunden, obwohl der Effekt selbst unveraendert blieb): faehrt DIESELBE Person mit
   * DEMSELBEN Rechte-Set SPAETER erneut genau die Rueckfallseite an, bleibt die zweite
   * Meldung stumm - sie sitzt ohnehin schon dort fest, dieselbe Ref-Kombination erkennt genau
   * das als denselben Vorgang wieder. Harmlos (nur beim vollstaendig rechtlosen Konto);
   * benannt, damit die Zusicherung im Text nicht staerker klingt als der Code haelt.
   */
  const zuletztRueckfallSeite = useRef<AktiveSeite | null>(null);
  const zuletztRueckfallRechte = useRef<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (!zustand.nutzer) return;
    const erforderlich = rechtFuerSeite(ansicht.seite);
    if (!erforderlich || hatRecht(zustand.rechte, erforderlich)) return;
    if (
      zuletztRueckfallSeite.current === ansicht.seite &&
      zuletztRueckfallRechte.current === zustand.rechte
    ) {
      return;
    }
    meldeFehler(`Für die Seite ${SEITEN_NAMEN[ansicht.seite]} fehlt dir die Berechtigung.`);
    // Der Drawer bleibt OFFEN - die Bestellung ist das Ziel, die Seite dahinter nur Kulisse.
    const rueckfall = ersteErlaubteSeite(zustand.rechte);
    zuletztRueckfallSeite.current = rueckfall;
    zuletztRueckfallRechte.current = zustand.rechte;
    setzeAnsicht({ seite: rueckfall }, { ersetzen: true });
  }, [zustand.nutzer, zustand.rechte, ansicht.seite]);

  /**
   * Anzeigename zu einer Nutzer-**id**. `Konflikt.geaendertVon` traegt eine id - beide Quellen
   * liefern eine (SSE `actor`, 409 `current.updatedBy`, beide im Backend ausdruecklich als id
   * begruendet); `KonfliktBanner` erwartet laut seinem Kopfkommentar einen ANZEIGENAMEN. Ohne diese
   * Aufloesung stuende dem Benutzer eine UUID im Satz.
   *
   * Bekannt ist an dieser Stelle genau EIN Name: der des angemeldeten Nutzers. Ein Verzeichnis
   * aller Nutzer fuehrt der Store nicht, und `GET /api/users` steht ausschliesslich Konten mit
   * `manage_users` offen (`backend/src/http/routes/users.ts`) - fuer die Mehrzahl der Rollen waere
   * es also gar nicht abrufbar. Fuer jede fremde id steht deshalb eine ehrliche Rueckfallanzeige
   * statt der rohen id. Sobald ein spaeterer Task ein Nutzerverzeichnis in den Store zieht
   * (Task 26, Nutzerverwaltung), ist das hier die eine Stelle, die davon erfaehrt.
   */
  function anzeigenameZu(nutzerId: string): string {
    if (zustand.nutzer && zustand.nutzer.id === nutzerId) return zustand.nutzer.displayName;
    return UNBEKANNTER_AENDERNDER;
  }

  // ================= Marken-Menue und Nutzerpfad (Task 10, `App/MarkenMenue.tsx`/`Users/`) =======
  //
  // Herkunft: `openPasswordDialog`/`closePasswordDialog`/`changeOwnPassword` (index.html:5737-5780),
  // `logout` (:5726-5735), `openUserManagement`/`openManagedUser` (:5445-5455), `loadBrandUsers`
  // (:5409-5432, dort in `MarkenMenue.tsx` selbst verdrahtet - siehe dessen Kopfkommentar), `renderUsers`
  // (:6228-6323, hier die AUFRUFER-Haelfte: Laden + Reichen an `<NutzerSeite>`), `showPage`s
  // `users`-Zweig (:6327-6331).

  /**
   * `selectedManagedUserId` (index.html:5275) - das ausgewaehlte Konto der Nutzerverwaltung. Liegt
   * in der ADRESSE (`ansicht.nutzer.id`, `adresse.ts:62`), nicht in einem eigenen `useState` (Befund
   * W-2 der Pruefrunde 1 - stand hier vorher noch als `useState` und blieb dadurch der einzige Teil
   * der `Ansicht`-Grammatik ohne Leser/Schreiber). Kein eigener `set...`-Name mehr noetig: der
   * einzige Schreiber ist `nutzerverwaltungOeffnen` unten, direkt ueber `setzeAnsicht`.
   */
  const ausgewaehlteNutzerId = ansicht.nutzer.id;

  /**
   * `#taskTemplateOverlay.open` (index.html:5522/5527) - ANDERS als `listenEditorOffen` und die drei
   * `NewOrder/`-Masken KEIN eigener `useState` mehr (Tieflinks, Erweiterung "Vorlagen-Tieflink"):
   * der Dialog ist zwar eine Ueberlagerung, KEINE Seite - er hat weiterhin keinen `AktiveSeite`-
   * Eintrag -, aber anders als die uebrigen Ueberlagerungen soll er verschickbar sein
   * (`?dialog=vorlagen`, `adresse.ts#Ansicht.dialog`). Offen ist er, wenn `ansicht.dialog ===
   * "vorlagen"` UND das Recht vorliegt - siehe die Rendering-Stelle unten fuer den Rechte-Rueckfall
   * (§ 3.4 Regel 2 des Tieflinks-Entwurfs: ein Link ohne Recht zeigt den Dialog still nicht, keine
   * Fehlermeldung).
   */
  const taskTemplateOffen = ansicht.dialog === "vorlagen" && hatRecht(zustand.rechte, "manage_templates");

  /**
   * Raeumt `?dialog=vorlagen` aus der Adresse, wenn das Recht fehlt (Fix-Runde zu Befund 1 der
   * Pruefrunde gegen Commit b93d2a7). `taskTemplateOffen` oben verhindert bereits das ANZEIGEN
   * (§ 3.4 Regel 2 des Tieflinks-Entwurfs, still) - das allein raeumt aber die ADRESSE nicht auf:
   * eine Folgenavigation schreibt ueber `setzeAnsicht` nur das jeweils geaenderte Feld, `dialog`
   * bleibt unangetastet stehen (gemessen: `lesen("/board", "dialog=vorlagen")`, danach
   * `setzeAnsicht({ seite: "timeline" })` ergibt `/zeitstrahl?dialog=vorlagen` - der Parameter
   * ueberlebt jede Seite). Meldet sich im selben Tab spaeter ein ANDERES Konto MIT dem Recht an
   * (Abmelden/Anmelden ruehrt die Adresse laut § 5.1 nicht an, siehe `zuletztRueckfallSeite`
   * oben), bekaeme dieses zweite Konto den Dialog sonst ungefragt aufgezogen.
   *
   * STUMM wie die "Drawer-Reiter nicht erlaubt"-Regel (§ 5.2 Regel 2) - keine Meldung, nur
   * `replaceState`: ein rechtloser Dialoglink ist eine Nebensache, keine gescheiterte Navigation
   * wie ein Seiten- oder Bestellungslink.
   */
  useEffect(() => {
    if (ansicht.dialog === "vorlagen" && !hatRecht(zustand.rechte, "manage_templates")) {
      setzeAnsicht({ dialog: null }, { ersetzen: true });
    }
  }, [ansicht.dialog, zustand.rechte]);

  const {
    nutzerVerzeichnis,
    jtlLoginOptionen,
    jtlLoginStatus,
    jtlLoginFehler,
    nutzerAnlegenAufruf,
    nutzerRolleAendern,
    nutzerAktivAendern,
    nutzerAnzeigenameAendern,
    nutzerJtlLoginAendern,
    nutzerPasswortZuruecksetzen,
  } = useNutzerverwaltung({
    nutzer: zustand.nutzer,
    ausgewaehlteNutzerId,
    seite,
    nutzerseiteErlaubt: seite === "users" && !!zustand.nutzer && hatRecht(zustand.rechte, "manage_users"),
    rechte: zustand.rechte,
    versende,
    zeigeMeldung,
  });

  /**
   * `showPage('users')` (index.html:6327-6331), woertlich MIT dem Rechte-Waechter: JEDER
   * Seitenwechsel auf "users" prueft `manage_users` erneut - Verteidigung in der Tiefe, genau wie im
   * Bestand, obwohl der einzige Weg hierher (Marken-Menue) das Recht schon selbst voraussetzt (siehe
   * `MarkenMenue.tsx`, `kannNutzerVerwalten`). Ohne das Recht faellt der Wechsel auf
   * `ersteErlaubteSeite` zurueck, wie jeder andere `showPage`-Aufruf ohne Berechtigung.
   *
   * `openManagedUser(id)` ruft mit einer id, `openUserManagement()` mit `null`
   * (`selectedManagedUserId = null`, index.html:5451-5455). Das ansichtsgebundene Laden übernimmt
   * der Hook-Aufruf direkt darüber, damit auch Navigation ohne diesen Aufrufer vollständig bleibt.
   */
  function nutzerverwaltungOeffnen(id: string | null): void {
    if (!hatRecht(zustand.rechte, "manage_users")) {
      setSeite(ersteErlaubteSeite(zustand.rechte));
      return;
    }
    // EIN `setzeAnsicht` statt zwei getrennter Aufrufe (Seite + ausgewaehlte id) - dieselbe
    // Begruendung wie bei `uebersichtOeffnen`/W-3: zwei Aufrufe gaeben zwei Verlaufseintraege.
    setzeAnsicht({ seite: "users", nutzer: { id } });
  }

  /**
   * Oeffnet die Lieferantenflaeche - der Rueckruf des Marken-Menue-Eintrags "Lieferanten öffnen"
   * (Fix-Runde 1 zu Task 11, Entscheidung des Auftraggebers vom 2026-08-11).
   *
   * OHNE RECHTE-WAECHTER, und das ist der Unterschied zu `nutzerverwaltungOeffnen` direkt darueber:
   * jener prueft `manage_users` ein zweites Mal, weil `showPage('users')` es im Bestand ebenfalls
   * tut (index.html:6327-6331). Hier gibt es nichts zu pruefen - `GET /api/suppliers/:kLieferant`
   * traegt `null`-Rechte (`backend/src/http/endpunktMatrix.ts:105`, Global Constraint 9: "Lesen
   * steht allen Rollen offen"). Ein Waechter hier waere kein zusaetzlicher Schutz, sondern eine
   * Sperre, die es serverseitig nicht gibt.
   *
   * Und es wird NICHTS geladen: die Flaeche hat keinen Katalog (es gibt kein `GET /api/suppliers`),
   * sie beginnt leer und laedt erst auf eine Suche hin - siehe `Suppliers/LieferantenSeite.tsx`.
   */
  function lieferantenOeffnen(): void {
    setSeite(LIEFERANTEN_SEITE);
  }

  /** Oeffnet die Seite "Zahlungsvorlagen" (Marken-Menue); ohne Rechte-Waechter wie `lieferantenOeffnen`. */
  function zahlungsvorlagenOeffnen(): void {
    setSeite(ZAHLUNGSVORLAGEN_SEITE);
  }

  /** Oeffnet "Meine KI-Zugänge" (Marken-Menue); ohne Rechte-Waechter wie `lieferantenOeffnen`. */
  function kiZugaengeOeffnen(): void {
    setSeite(KI_ZUGAENGE_SEITE);
  }

  /**
   * `openTaskTemplateManager()` (index.html:5510-5524) - der Rueckruf des Marken-Menue-Eintrags
   * "Task-Template verwalten". Bis zum 2026-08-12 gab es ihn nicht: der Knopf war gezeichnet und
   * ohne Wirkung (siehe `MarkenMenue.tsx`, "benannte Luecke"), gemeldet vom Auftraggeber.
   *
   * MIT Rechte-Waechter und damit wie `nutzerverwaltungOeffnen`, nicht wie `lieferantenOeffnen`:
   * der Bestand prueft `manage_templates` beim OEFFNEN (`:5511`, mit Toast) UND beim Speichern
   * (`:5634`) - Verteidigung in der Tiefe, obwohl der einzige Weg hierher das Recht schon selbst
   * voraussetzt (`MarkenMenue.tsx#kannVorlagenVerwalten`). Serverseitig steht derselbe Riegel:
   * schreiben darf nur `manage_templates` (`endpunktMatrix.ts:122/126-128`).
   *
   * Der Dialog laedt selbst, es wird hier also nichts vorbereitet - anders als bei der
   * Nutzerverwaltung, deren Liste `App.tsx` haelt.
   *
   * TIEFLINKS (Erweiterung "Vorlagen-Tieflink"): oeffnet ueber `setzeAnsicht({ dialog: "vorlagen"
   * })` statt eines lokalen `setTaskTemplateOffen(true)` - der Dialog ist damit verschickbar
   * (`?dialog=vorlagen`). Die Rechtepruefung bleibt UNVERAENDERT hier stehen, mit Meldung: dieser
   * Pfad ist der Menue-Klick, der ein bestehendes Recht schon voraussetzt (siehe oben) - anders als
   * bei einem direkten Adressaufruf ohne Recht (§ 3.4 Regel 2 des Tieflinks-Entwurfs, still, siehe
   * Rendering-Stelle unten), wo eine Meldung reiner Laerm waere.
   */
  function taskTemplateOeffnen(): void {
    if (!hatRecht(zustand.rechte, "manage_templates")) {
      meldeFehler("Für das Task-Template fehlt die Berechtigung");
      return;
    }
    setzeAnsicht({ dialog: "vorlagen" });
  }

  /**
   * `logout()` (index.html:5726-5735) - Rueckruf sowohl fuer das Marken-Menue als auch fuer den
   * gleichnamigen Knopf in `NutzerSeite`. `abmelden()` (`api/auth.ts`) traegt bereits
   * `setzeSitzungsToken(null)` in seinem eigenen `finally` - hier trotzdem noch einmal EXPLIZIT
   * gesetzt: wortgetreu zur Drei-Schritt-Aufzaehlung des Task-Briefs, und weil dieser Aufrufer sich
   * nicht auf ein Implementierungsdetail von `abmelden()` verlassen soll.
   */
  function abgemeldet(): void {
    abmelden()
      .catch(() => { // still: wie im Bestand (`logout`, index.html:5727) darf ein gescheiterter Logout-Aufruf (Netz weg, Sitzung serverseitig schon fort) die lokale Abmeldung nicht verhindern
      })
      .finally(() => {
        setzeSitzungsToken(null);
        versende({ art: "angemeldet", nutzer: null, rechte: [] });
      });
  }

  /** `#passwordDialogOverlay`s Sichtbarkeit (index.html:5741/5744), ausgeloest vom Marken-Menue. */
  const [passwortDialogOffen, setPasswortDialogOffen] = useState(false);
  const [fxKontenDialogOffen, setFxKontenDialogOffen] = useState(false);
  const [fxKonten, setFxKonten] = useState<readonly FxAccount[]>([]);
  // Die Bankauswahl der Kurssicherungen liest den Kontenstamm (auch inaktive Konten): beim Betreten der
  // Zahlungsseite und nach jedem Schliessen des Kontendialogs (dort entstehen oder ruhen Konten) frisch
  // laden. Nur mit manage_fx: die Route antwortet sonst 403, dann gar nicht erst anfragen - die Bank
  // bleibt dann eine Anzeige. Scheitert das Laden, bleibt sie es ebenfalls: die Zahlungsseite hat keine Flaeche fuer
  // die Konten, deshalb steht ein Eintrag im Fehlerstapel (Fehleranzeige Paket 2, L2) - mit demselben Recht wie das
  // Laden selbst.
  const [fxKontenBereit, setFxKontenBereit] = useState(false);
  const darfFxKonten = hatRecht(zustand.rechte, "manage_fx");
  useEffect(() => {
    if (!zustand.nutzer || ansicht.seite !== "payments" || fxKontenDialogOffen) return;
    if (!darfFxKonten) { setFxKontenBereit(false); return; }
    let verworfen = false;
    fxKontenLaden()
      .then((konten) => { if (!verworfen) { setFxKonten(konten); setFxKontenBereit(true); } })
      .catch((fehler: unknown) => {
        if (verworfen) return;
        setFxKontenBereit(false);
        meldeFehler(fehler, "Die USD-Konten konnten nicht geladen werden – die Bank einer Kurssicherung lässt sich gerade nicht ändern.");
      });
    return () => { verworfen = true; };
  }, [zustand.nutzer?.id, ansicht.seite, fxKontenDialogOffen, darfFxKonten]);
  /** `#passwordError`s SERVER-Anteil (index.html:5773-5776) - der Client-Anteil bleibt in `PasswortDialog` selbst (Submit-Validierung). */
  const [passwortFehler, setPasswortFehler] = useState("");

  /** `openPasswordDialog()` (index.html:5737-5744). */
  function passwortDialogOeffnen(): void {
    setPasswortFehler("");
    setPasswortDialogOffen(true);
  }

  function fxKontenDialogOeffnen(): void {
    if (!hatRecht(zustand.rechte, "manage_fx")) {
      meldeFehler("Für USD-Konten fehlt die Berechtigung");
      return;
    }
    fxKontenLaden()
      .then((konten) => {
        setFxKonten(konten);
        setFxKontenDialogOffen(true);
      })
      .catch((fehler: unknown) => {
        meldeFehler(fehler, "USD-Konten konnten nicht geladen werden.");
      });
  }

  /**
   * `changeOwnPassword` NACH bestandener Client-Pruefung (index.html:5767-5780) - fuer das
   * Marken-Menue: bei einem Fehlschlag bleibt der Dialog OFFEN, der Server-Fehler erscheint INLINE
   * (`serverFehler`) statt als Toast - wortgetreu zum Bestand ("der Knopf bleibt klickbar").
   */
  function eigenesPasswortAendern(daten: PasswortDialogDaten): void {
    passwortAendern(daten.currentPassword, daten.newPassword)
      .then(() => {
        setPasswortDialogOffen(false);
        zeigeMeldung("Passwort wurde geändert");
      })
      .catch((fehler: unknown) => {
        // R2: inline bleibt inline; R1: der Text kommt vom Uebersetzer. `null` (401) meldet die Anmeldemaske.
        setPasswortFehler(fehlertext(fehler, "Das Passwort konnte nicht geändert werden.") ?? "");
      });
  }

  /**
   * Derselbe API-Aufruf fuer `NutzerSeite`s EIGENEN, dort bereits gebauten Dialog (Task-Brief:
   * "onSpeichern -> passwortAendern"). Dessen Dialog schliesst sich schon VOR diesem Aufruf selbst
   * (`NutzerSeite.tsx`: `onSpeichern={(daten) => { setDialogOffen(false); onSpeichern(daten); }}`) -
   * es gibt dort keinen Inline-Fehlerkanal mehr. Ein Fehlschlag kann deshalb nur noch ueber
   * `meldeFehler` gemeldet werden - eine vom Marken-Menue-Pfad ABWEICHENDE, durch die bereits
   * bestehende Komponentenarchitektur erzwungene Fehlerbehandlung (`NutzerSeite.tsx` liegt
   * ausserhalb der Files-Liste dieses Tasks und wird nicht angefasst).
   */
  function eigenesPasswortAendernAufSeite(daten: PasswortDialogDaten): void {
    passwortAendern(daten.currentPassword, daten.newPassword)
      .then(() => zeigeMeldung("Passwort wurde geändert"))
      .catch((fehler: unknown) => {
        console.error("Eigenes Passwort konnte nicht geaendert werden:", fehler);
        meldeFehler(fehler, "Das Passwort konnte nicht geändert werden.");
      });
  }

  // Der Zahlungsziel-Endpunkt liefert nur Raten/Snapshot, nicht den ganzen Bestellstand. Die Ref
  // beansprucht deshalb VOR dem ersten PUT synchron jeden Zahlungs-Schreibkanal. Scheitert dieses
  // PUT, wird sie wieder freigegeben; nach Erfolg bleibt sie bis zum Kontrollabruf bestehen.
  const zahlungsbearbeitungNachladenNoetigRef = useRef(new Set<string>());
  const [zahlungsbearbeitungNachladenNoetig, setZahlungsbearbeitungNachladenNoetig] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const zahlungsbearbeitungSperren = (id: string): void => {
    zahlungsbearbeitungNachladenNoetigRef.current.add(id);
    setZahlungsbearbeitungNachladenNoetig((bisher) => new Set(bisher).add(id));
  };
  const zahlungsbearbeitungFreigeben = (id: string): void => {
    zahlungsbearbeitungNachladenNoetigRef.current.delete(id);
    setZahlungsbearbeitungNachladenNoetig((bisher) => {
      const naechster = new Set(bisher);
      naechster.delete(id);
      return naechster;
    });
  };

  // Ein unaufloesbarer Zahlungsziel-Callback darf nach dem Aushaengen weder den Store noch Drawer,
  // Navigation oder Meldung anfassen. Der Request selbst bleibt bewusst unabgebrochen.
  const istEingehaengt = useMontiertRef("passiv");

  // ================= Drawer (Task 4, `Drawer/Drawer.tsx`) - externe Naht ========================
  //
  // `openDrawer`/`closeDrawer` (index.html:9243-9313, :9631-9640). Der Zustand traegt NUR `id` +
  // `tab` (Zielbild, Verdrahtungsplan) - der Datensatz selbst kommt bei jedem Rendern frisch aus
  // `zustand.bestellungen`, damit ein Nachladen automatisch beim offenen Drawer ankommt, ohne dass
  // hier eine zweite Kopie mitgefuehrt werden muesste.
  //
  // TIEFLINKS (Aufgabe 3): ebenfalls aus der Adresse, nicht mehr aus einem eigenen `useState`. Die
  // Form `{ id, tab }` bleibt, damit die rund zehn Leser unveraendert bleiben; `id` ist hier die
  // AUFGELOESTE UUID, nicht die Kennung aus der URL (§ 3.7) - `bestellungNachKennung` (Modulkopf)
  // uebersetzt zwischen beiden.
  const drawer = useMemo(() => {
    const b = ansicht.bestellung;
    if (!b) return null;
    const treffer = bestellungNachKennung(zustand.bestellungen, b.kennung);
    return darfBestellungOeffnen(treffer)
      ? { id: String(treffer.id), tab: b.tab, abschnitt: b.abschnitt ?? null }
      : null;
  }, [ansicht.bestellung, zustand.bestellungen]);

  const geloeschteBestellungSchliessen = useCallback(() => {
    setzeAnsicht({ bestellung: null }, { ersetzen: true });
    versende({ art: "geoeffnet", verweis: null });
  }, [setzeAnsicht, versende]);

  useEffect(() => {
    const kennung = ansicht.bestellung?.kennung;
    if (!kennung) return;
    const bestellung = bestellungNachKennung(zustand.bestellungen, kennung);
    if (darfBestellungOeffnen(bestellung) || bestellung === undefined) return;
    geloeschteBestellungSchliessen();
  }, [ansicht.bestellung?.kennung, zustand.bestellungen, geloeschteBestellungSchliessen]);

  /**
   * Die Freigabeliste ist kein globaler Vorlagenkatalog: beim Oeffnen und bei jedem
   * Bestellwechsel wird daher der aktuelle Satz dieses Lieferanten geladen. Die Abhaengigkeit ist
   * bewusst nur die Drawer-Id, damit ein `freigabenGesetzt`-Dispatch keinen Fetch-Kreis ausloest.
   */
  useEffect(() => {
    if (!drawer) return;
    const bestellung = zustand.bestellungen.find((b) => b.id === drawer.id);
    const kLieferant = Number(bestellung?.kLieferant);
    if (!Number.isSafeInteger(kLieferant) || kLieferant <= 0) return;
    let verworfen = false;
    ladeFreigaben(kLieferant).then(
      (freigaben) => {
        if (!verworfen) versende({ art: "freigabenGesetzt", kLieferant, freigaben });
      },
      (fehler: unknown) => {
        if (verworfen) return;
        console.error(`Freigaben fuer Lieferant ${kLieferant} konnten nicht geladen werden:`, fehler);
        meldeFehler(fehler, "Die Zahlungsziel-Optionen konnten nicht geladen werden.");
      },
    );
    return () => {
      verworfen = true;
    };
  }, [drawer?.id]);

  /**
   * Die aktuelle Kennung, waehrend des Renderns nachgefuehrt (kein Effekt - ein Effekt liefe der
   * Aktion in `drawerPositionAendern`/`drawerLoeschen` unten hinterher, siehe dort). Bewusst NUR
   * fuer eine BILLIGE Vorpruefung gedacht, nicht fuer die eigentliche Korrektheit - die bleibt bei
   * der FUNKTIONALEN Form von `setzeAnsicht` (`useAnsicht.ts#aktuellerStand`, hier nicht
   * erreichbar). Ohne diese Vorpruefung navigierte `drawerPositionAendern` bei JEDEM Id-Wechsel,
   * auch wenn laengst eine ANDERE Bestellung offen ist oder der Drawer laengst zu ist (Befund
   * "Gering" der Pruefrunde 1).
   *
   * EHRLICH BENANNTE GRENZE (Kleinbefund der Pruefrunde 2, `:2213`): diese Ref haengt am RENDERN
   * (aktualisiert sich nur, wenn die Komponente neu laeuft), `useAnsicht.ts#aktuellerStand` dagegen
   * SYNCHRON an jedem `setzeAnsicht`-Aufruf, noch bevor React ueberhaupt neu rendert. Hinkt ein
   * Rendern einer bereits ausgeloesten, aber noch nicht committeten Aenderung hinterher, kann diese
   * Vorpruefung deshalb - in engem Zeitfenster - eine Aktualisierung UEBERSPRINGEN, die die
   * massgebliche Quelle (`aktuellerStand`) als noetig ansaehe. Frueher stand hier die Behauptung,
   * das koenne "hoechstens seltener unnoetig navigieren, nie mehr falsch" sein - das stimmt nicht:
   * eine uebersprungene Aktualisierung IST ein falsches (fehlendes) Ergebnis, nur ein SELTENES. Die
   * Vorpruefung an dieselbe Quelle zu haengen ginge nur ueber `useAnsicht.ts` selbst - das ist nicht
   * diese Aufgabe (`App.tsx` gehoert Aufgabe 3, `useAnsicht.ts` Aufgabe 2, siehe Fixbericht).
   */
  const aktuelleKennungRef = useRef(ansicht.bestellung?.kennung ?? null);
  aktuelleKennungRef.current = ansicht.bestellung?.kennung ?? null;
  const aktuelleBestellungenRef = useRef(zustand.bestellungen);
  aktuelleBestellungenRef.current = zustand.bestellungen;

  /**
   * Die zuletzt ERFOLGREICH aufgeloeste Kennung, waehrend sie offen war (Befund W-4 der Pruefrunde
   * 1). `BrowserRouter` legt jede Adressaenderung in `React.startTransition`
   * (react-router, `navigate`), `versende` ist DRINGEND - in `drawerLoeschen` faellt
   * "Datensatz weg" (dringend, Store) deshalb manchmal einen Rendertakt FRUEHER als "Adresse leer"
   * (Transition, `drawerSchliessen`). Im Zwischenstand ist `kennung` noch gesetzt, `drawer` aber
   * schon `null` - GENAU die Bedingung des "nicht gefunden"-Effekts unten. Diese Ref unterscheidet
   * "war nie da" (kaputter Tieflink) von "war da und ist gerade WEG" (eine Loeschung, die WIR selbst
   * ausgeloest haben) - nur Ersteres darf die Meldung zeigen.
   */
  const zuletztOffeneKennung = useRef<string | null>(null);
  useEffect(() => {
    if (drawer && ansicht.bestellung) {
      zuletztOffeneKennung.current = ansicht.bestellung.kennung;
    } else if (!ansicht.bestellung) {
      zuletztOffeneKennung.current = null;
    }
  }, [drawer, ansicht.bestellung]);

  /**
   * Die Kennung einer Bestellung, die WIR SELBST geloescht haben (Befund 1 der Pruefrunde 2) -
   * gesetzt in `drawerLoeschen`, unmittelbar bevor dessen Erfolgs-Dispatch feuert. Unterscheidet die
   * beiden Faelle, die `zuletztOffeneKennung` allein NICHT auseinanderhaelt:
   *
   *   - EIGENE Loeschung (`drawerLoeschen`): der "nicht gefunden"-Effekt unten darf NICHT melden -
   *     `drawerSchliessen()` raeumt die Adresse bereits selbst (PER PUSH, § 4).
   *   - FREMDE Loeschung (eine andere Sitzung loescht die offene Bestellung; SSE macht daraus einen
   *     Konflikt, `reduzierer.ts:368`, der Nutzer klickt am Banner "neu laden", `konfliktUebernehmen`
   *     stellt ein Nachladen ein, das mit 404 endet - `anfrageOderNull`/`basis.ts` liefert dafuer
   *     `null`, `Speicher.tsx` versendet `{art:"nachgeladen", datensatz:null}`): DIESE Ref bleibt
   *     leer, der Effekt unten MUSS melden und die Adresse selbst raeumen - sonst bliebe
   *     `/board/bestellung/<geloescht>` fuer immer stehen (gemessen: genau das tat der erste
   *     W-4-Fix aus Fixrunde 1 noch, weil er die beiden Faelle nicht unterschied).
   */
  const eigeneLoeschung = useRef<string | null>(null);

  /**
   * Kennungen, fuer die der Reparaturweg zu Befund 1 (`bestellungNachKennungNachladen` oben) bereits
   * LIEF - der Effekt unten darf fuer sie kein zweites Mal nachladen, sondern muss (falls dann immer
   * noch kein Treffer im Store steht) "nicht gefunden" melden. `useState`, nicht `useRef`: der
   * Abschluss eines Versuchs muss das erneute Pruefen ausloesen, und nur ein State-Wechsel erzwingt
   * dafuer einen weiteren Durchlauf des Effekts - `zustand.bestellungen` aendert sich bei einem
   * Fehlschlag ja gerade NICHT.
   *
   * BEFUND W1 (Nachbesserung Runde 3): DIE ABBRUCHBEDINGUNG IST "EIN VERSUCH IST GELAUFEN", NICHT
   * "DER VERSUCH BLIEB OHNE TREFFER IM STORE" - vorher wurde dieser Zustand NUR im Fehlschlagzweig
   * gesetzt (`gefunden` falsy), nicht auch nach einem ERFOLGREICHEN Nachladen. Server
   * (`cEigeneBestellnummer = @nummer`, SQL-Server-Standardkollation) vergleicht case-INsensitiv,
   * `bestellungNachKennung` (Modulkopf, `String(b.orderNo) === kennung`) exakt: bei einer Adresse mit
   * abweichender Gross-/Kleinschreibung (z. B. `/bestellung/bst-2026-archiv`) fand der Server einen
   * Treffer und `versende({ art: "nachgeladen", ... })` fuegte ihn (mit KORREKTER Schreibung) in den
   * Store ein - der lokale Abgleich (`drawer`, `bestellungNachKennung`) fand ihn danach trotzdem
   * NICHT (andere Schreibung), dieser Zustand blieb `null`, und JEDER folgende Render loeste denselben
   * Nachladeversuch ERNEUT aus: gemessen 78 Anfragen in 1,5 s ohne Netzlatenz - in Produktion ohne
   * Ende, solange der Tab offen bleibt. Der Name bleibt (er beschreibt weiterhin den ueberwiegenden
   * Fall), die Semantik ist seither "der letzte ABGESCHLOSSENE Versuch fuer diese Kennung" - siehe
   * unten, wo er nach JEDER Antwort gesetzt wird, nicht nur nach einer erfolglosen.
   *
   * `nachladeLaeuft` (Ref, kein State) sperrt zusaetzlich WAEHREND ein Versuch unterwegs ist - ohne
   * sie stiesse jedes Rendern in der Lauflaufzeit denselben Abruf ein zweites Mal an (React StrictMode
   * ODER ein Zustandswechsel durch ein unabhaengiges SSE-Ereignis waehrenddessen).
   */
  const [nachladeOhneTreffer, setNachladeOhneTreffer] = useState<string | null>(null);
  const nachladeLaeuft = useRef<Set<string>>(new Set());

  /**
   * Der Tieflink auf eine Bestellung, die es nicht (mehr) gibt.
   *
   * DIE BEDINGUNG `bestellungenGeladen` IST DER GANZE PUNKT (Spezifikation § 5.3): beim ersten
   * Rendern ist die Liste leer, weil sie noch laedt. Ohne diese Sperre meldet JEDER gueltige
   * Tieflink einmal faelschlich "nicht gefunden" - und zwar nur beim Tieflink, nie beim Klicken
   * innerhalb der Anwendung. Beim Durchklicken faellt das deshalb niemandem auf.
   *
   * `reduzierer.ts` fuehrt KEIN eigenes Ladeflag fuer `bestellungen` (gesucht ueber
   * `laedt|geladen|laden` - kein Treffer). Der ehrliche Ersatz ist `zustand.bestellungen.length > 0`
   * - das bleibt bei einer WIRKLICH leeren Datenbank stumm (keine Bestellung meldet sich je als
   * "nicht gefunden"), ein Fall, den es in der Produktion nicht gibt, der aber benannt gehoert statt
   * verschwiegen.
   *
   * `zuletztOffeneKennung`/`eigeneLoeschung` oben (Befund W-4, verfeinert in Befund 1 der
   * Pruefrunde 2): war die Kennung schon einmal offen UND WIR haben sie selbst geloescht, ist ihr
   * Verschwinden eine ERWARTETE Loeschung - `drawerSchliessen()` raeumt die Adresse bereits selbst
   * PER PUSH (§ 4 - Schliessen ist eine echte Navigation), dieser Effekt wartet dann nur, bis sie
   * ankommt, statt ein zweites Mal - und mit der FALSCHEN Politik (Ersetzen statt Push) - zu
   * navigieren. War die Kennung offen und ist sie WEG, OHNE dass wir selbst geloescht haben (fremde
   * Loeschung, siehe `eigeneLoeschung` oben), ist das KEIN Fall zum Schweigen: der Effekt meldet UND
   * raeumt die Adresse, genau wie bei einem kaputten Tieflink.
   *
   * REPARATURWEG BEFUND 1 (Nachbesserung "Serverseitiger Archivfilter + Ladeanzeige"): bevor
   * gemeldet wird, EIN gezielter Versuch, die Bestellung ueber `bestellungNachKennungNachladen`
   * nachzuladen - der Archivfilter (Baustein A desselben Auftrags) verkleinert seither
   * `zustand.bestellungen` serverseitig; eine archivierte Bestellung fehlt dort, obwohl sie
   * existiert. Erst wenn dieser Versuch fuer GENAU DIESE Kennung bereits GELAUFEN ist
   * (`nachladeOhneTreffer`, Befund W1 - siehe Kommentar oben), faellt der Effekt auf die bisherige
   * Meldung zurueck.
   *
   * DREI AUSGAENGE (Fehleranzeige Paket 2, Task 7; `AufloesungsErgebnis` in `bestellungsAufloesung.ts`):
   * `treffer` (ggf. mit `hubFehler`: die Bestellung oeffnet, dazu ein Eintrag, dass der Hub-Teil fehlt),
   * `keinTreffer` (der Server kennt die Kennung nicht: der Weg ueber `nachladeOhneTreffer` zu "nicht gefunden")
   * und `ladefehler` (Netz, 5xx ...: Eintrag "konnte nicht geladen werden" mit Ursache, Adresse geraeumt,
   * `nachladeOhneTreffer` bleibt leer - ein erneutes Oeffnen des Links versucht es wieder).
   */
  useEffect(() => {
    const kennung = ansicht.bestellung?.kennung;
    const bestellungenGeladen = zustand.bestellungen.length > 0;
    if (!kennung || !bestellungenGeladen || drawer) return;
    const bestaetigteBestellung = bestellungNachKennung(zustand.bestellungen, kennung);
    if (bestaetigteBestellung && !darfBestellungOeffnen(bestaetigteBestellung)) {
      geloeschteBestellungSchliessen();
      return;
    }
    if (kennung === zuletztOffeneKennung.current && kennung === eigeneLoeschung.current) {
      eigeneLoeschung.current = null;
      return;
    }
    if (kennung !== nachladeOhneTreffer && !nachladeLaeuft.current.has(kennung)) {
      nachladeLaeuft.current.add(kennung);
      void bestellungNachKennungNachladen(kennung).then((ergebnis) => {
        nachladeLaeuft.current.delete(kennung);
        if (ergebnis.art === "ladefehler") {
          // Ein gescheitertes Laden ist KEIN "ohne Treffer" (Fehleranzeige Paket 2, Task 7): der Nutzer erfaehrt, dass
          // die Bestellung nicht geladen werden konnte, und nicht, dass es sie nicht gibt. `nachladeOhneTreffer` bleibt
          // unberuehrt - ein erneutes Oeffnen desselben Links versucht es wieder. Abbruch und 401 (`fehlertext` = null)
          // bleiben still und lassen die Adresse als Ziel stehen, die Anmeldemaske uebernimmt. Wer den Link inzwischen
          // verlassen hat (L6), bekommt weder Eintrag noch eine geraeumte Adresse. Dasselbe gilt, wenn die Bestellung
          // inzwischen auf anderem Weg im Store steht (Liste neu geladen, Archivfilter umgeschaltet; Fix-Runde 1, G1): der
          // Drawer ist dann offen, das Ergebnis ueberholt - ein Eintrag "konnte nicht geladen werden" waere falsch, und die
          // Raeumung der Adresse schlosse einen funktionierenden Drawer.
          const meldung = fehlertext(ergebnis.ursache, `Bestellung ${kennung} konnte nicht geladen werden.`);
          if (meldung === null || aktuelleKennungRef.current !== kennung) return;
          if (bestellungNachKennung(aktuelleBestellungenRef.current, kennung)) return;
          meldeFehler(meldung);
          setzeAnsicht({ bestellung: null }, { ersetzen: true });
          return;
        }
        const gefunden = ergebnis.art === "treffer" ? ergebnis.bestellung : null;
        if (gefunden && !darfBestellungOeffnen(gefunden)) {
          const aktuelleBestellung = bestellungNachKennung(aktuelleBestellungenRef.current, kennung);
          if (aktuelleKennungRef.current === kennung && !darfBestellungOeffnen(aktuelleBestellung)) {
            geloeschteBestellungSchliessen();
          }
          setNachladeOhneTreffer(kennung);
          return;
        }
        if (gefunden) {
          versende({
            art: "nachgeladen",
            entitaet: BESTELLUNG_ENTITAET,
            id: String(gefunden.id),
            datensatz: gefunden,
          });
        }
        if (ergebnis.art === "treffer" && "hubFehler" in ergebnis) {
          // Die Bestellung oeffnet mit den JTL-Daten; ohne die Hub-Zeile fehlen Archivstand, Aufgaben und Zahlungen,
          // und sie erschiene faelschlich als aktiv (L2: keine eigene Flaeche, also ein Eintrag).
          meldeFehler(
            ergebnis.hubFehler,
            `Die Hub-Daten der Bestellung ${bestellbezeichnung(ergebnis.bestellung, kennung)} konnten nicht geladen werden – ` +
              "Archivstand, Aufgaben und Zahlungen fehlen in der Anzeige. Bitte neu laden.",
          );
        }
        // Befund W1 (Nachbesserung Runde 3): IMMER setzen, nicht nur im `else`-Zweig oben - "ein
        // Versuch ist gelaufen" ist die Abbruchbedingung, nicht "kein Treffer im Store danach". Bei
        // einem EXAKTEN Treffer (Normalfall) findet `drawer` (Modulkopf, `bestellungNachKennung`) die
        // frisch eingefuegte Bestellung im naechsten Render ohnehin ueber `drawer`, und der fruehe
        // `if (... || drawer) return;` oben verhindert dann jede weitere Wirkung dieses Zustands. Bei
        // einer Gross-/Kleinschreibungs-Abweichung (Server case-insensitiv, `bestellungNachKennung`
        // exakt) bleibt `drawer` dagegen leer - OHNE diese Zeile loeste das einen neuen Versuch bei
        // JEDEM Render aus (siehe Kommentar oben, gemessen: 78 Anfragen in 1,5 s).
        setNachladeOhneTreffer(kennung);
      });
      return;
    }
    // Ein Abruf fuer diese Kennung ist noch unterwegs (Fix-Runde 1 zu Task 7, W2): der Effekt lief nur erneut, etwa weil
    // sich `zustand.bestellungen` durch eine ANDERE Bestellung geaendert hat (SSE). Das ist kein "ohne Treffer" - der
    // Abschluss setzt `nachladeOhneTreffer` (das loest den Effekt erneut aus) bzw. meldet den Ladefehler selbst.
    if (nachladeLaeuft.current.has(kennung)) return;
    // Hierher gelangt nur "ohne Treffer" (der Versuch ist gelaufen und fand nichts, oder es gab nichts nachzuladen): ein
    // Ladefehler hat oben bereits seine eigene Meldung bekommen und die Adresse geraeumt.
    meldeFehler(meldungBestellungNichtGefunden(kennung));
    setzeAnsicht({ bestellung: null }, { ersetzen: true });
  }, [ansicht.bestellung?.kennung, zustand.bestellungen, drawer, nachladeOhneTreffer, versende, geloeschteBestellungSchliessen]);

  /**
   * Der Tieflink auf ein Dokument, das es an dieser Bestellung nicht (mehr) gibt (Spezifikation
   * § 3.9/§ 5.4). Dieselbe Wartepflicht wie der Effekt oben, nur eine Ebene tiefer: ERST wenn
   * `drawer` die Bestellung aufgeloest hat (der Effekt oben ist dann schon durchgelaufen), DANN
   * gegen ihre `pdfs[]` pruefen - sonst meldete jeder Dokumentlink einmal faelschlich, waehrend die
   * Bestellung selbst noch laedt.
   *
   * `DokumenteTab.tsx` bekommt die Auswahl nur als PROP (`hervorgehoben`, siehe dort) - dieser Reiter
   * wird auch OHNE Router direkt getestet (`dokumenteTab.test.tsx`) und kennt `useAnsicht` deshalb
   * nicht. Die Meldung UND die Bereinigung gehoeren deshalb hierher, wie beim Effekt oben.
   *
   * `pdfsGeladen` statt `pdfs.length === 0` (derselbe Befund wie bei den Aufgaben, Aufgabe 6/W-3,
   * "mach ihn nicht noch einmal"): eine Bestellung OHNE Dokumente darf die Meldung nicht
   * verschlucken, eine, deren `pdfs` schlicht noch nicht eingetroffen sind, darf sie nicht
   * VERFRUEHT zeigen.
   *
   * `doc_type nicht an dieser Bestellung` und `doc_type ist gar kein bekannter Typ` bekommen laut
   * Spezifikation (§ 5.4) dieselbe Meldung - eine Unterscheidung hülfe dem Empfaenger nicht weiter.
   *
   * BERICHTIGT (Fixrunde 1, Befund K-2): der Treffer-Test war `pdfs.some((d) => String(d.type ?? "")
   * === dokument)` - "steht der Typ in `pdfs`?" statt "gibt es eine Zeile dafuer?". Damals konnten
   * vier echte Dokumenttypen (`zahlungsbeleg_anzahlung`, `zahlungsbeleg_restzahlung`, `ankunftsavis`,
   * `seefracht_buchungsbestaetigung`; die zwei Split-Typen sind seit Asana 1220057765558651 als
   * `anzahlungsbeleg`/`restzahlungsbeleg` Checklistenzeilen) an einer Bestellung liegen, ohne dass der Reiter je
   * eine Zeile fuer sie zeichnet - ein Tieflink darauf fand trotzdem "etwas in `pdfs`" und blieb
   * STUMM. `istGezeichneterDokumentTyp` (`DokumenteTab.tsx`) fragt die richtige Frage - siehe dort
   * (Fixrunde 2, Befund 4) zur genauen Beziehung zu den drei Einbaustellen des Reiters.
   *
   * BERICHTIGT (Fixrunde 2, Befund 3): der Wortlaut selbst - "… gibt es an dieser Bestellung
   * nicht." (wörtlich aus der Spezifikation, § 5.4-Tabelle) - trifft den K-2-Fall NICHT: den Beleg
   * GIBT es (er liegt in `pdfs`), der Reiter zeichnet nur nirgends eine Zeile dafür. Der alte
   * Wortlaut schickt den Empfänger auf die falsche Fährte ("dann lade ich ihn eben hoch" - ein
   * zweites Mal, unnötig). Der neue Wortlaut ("wird in diesem Reiter nicht angezeigt") behauptet
   * nichts über die Existenz und stimmt deshalb für BEIDE Fälle der Tabelle - den echten, aber
   * ungezeichneten Beleg UND den wirklich unbekannten `doc_type`.
   */
  useEffect(() => {
    const dokument = ansicht.bestellung?.dokument;
    if (!dokument || !drawer) return;
    const bestellung = zustand.bestellungen.find((b) => b.id === drawer.id);
    if (!bestellung) return;
    const pdfsGeladen = bestellung.pdfs !== undefined;
    if (!pdfsGeladen) return;
    if (istGezeichneterDokumentTyp(bestellung, dokument)) return;
    meldeFehler(`Das Dokument „${dokument}“ wird in diesem Reiter nicht angezeigt.`);
    setzeAnsicht(
      { bestellung: ansicht.bestellung && { ...ansicht.bestellung, dokument: null } },
      { ersetzen: true },
    );
  }, [ansicht.bestellung, drawer, zustand.bestellungen]);

  /**
   * `openDrawer(id, initialTab)` (index.html:9243-9313), auf den Rahmen beschraenkt (Kopf, Reiter,
   * Fussleiste - die INHALTE der fuenf Reiter folgen mit Task 5, siehe `Drawer.tsx`-Kopfkommentar,
   * "Die INHALTE der fuenf Reiter ... sind NICHT Teil dieses Tasks").
   *
   * `versende({ art: "geoeffnet", … })` ist die eigentliche Wirkung dieses Tasks: sie schaltet den
   * Reduzierer-Fall 2 (`reduzierer.ts`, Fall `"sse"`) fuer DIESEN Datensatz scharf - ein SSE-Ereignis
   * dazu wird ab jetzt als Konflikt vorgemerkt statt gezielt nachgeladen (siehe dortiger
   * Kopfkommentar). Ohne diese Zeile bliebe der Fall fuer immer toter Codepfad, wie vor diesem Task
   * hier vermerkt war.
   *
   * TIEFLINKS (Aufgabe 3): schreibt seither die Adresse statt eines `useState`, der `versende`-Aufruf
   * bleibt UNVERAENDERT stehen (Risiko 3 der Spezifikation) - er ist die SSE-Konflikterkennung, keine
   * Navigation.
   *
   * `useCallback` (Folgefix zu Auftrag "Bestellboard-Filter — Renderausfall durch fehlende
   * Memoisierung beheben", Commit 663102d8): diese Funktion geht unveraendert als `onOeffnen` an
   * `<BoardSeite>` (siehe deren Aufruf weiter unten) - `BoardSeite.tsx#zeileAngeklickt` fuehrt die
   * Prop bereits in IHREM eigenen Abhaengigkeitsarray (siehe dortiger Kommentar). Eine bei JEDEM
   * App-Render frisch erzeugte Fassung HIER wuerde `zeileAngeklickt` (und damit `Bestellzeile`s
   * `React.memo`) bei JEDER App-State-Aenderung neu ausloesen, unabhaengig davon, ob sich am Board
   * selbst etwas geaendert hat - GENAU die Luecke, die der Pruefer der zweiten Runde des
   * Vorgaengerauftrags gefunden hat. Abhaengigkeiten: `zustand.bestellungen` (gelesen fuer die
   * Nachschlage), `setzeAnsicht`/`versende` (beide gelesen, gerufen) - `kennungVon` ist ein
   * modulweiter Import und braucht keinen Eintrag.
   */
  const drawerOeffnen = useCallback(
    (bestellungId: string, ziel?: DrawerTabId | DrawerZiel): void => {
      // Gezielter Sprung (Auftrag "Klick auf nicht aenderbare Grid-Zelle oeffnet die Bestellung"):
      // `ziel` ist entweder der nackte Reiter (alle bisherigen Aufrufer) oder `{ tab, abschnitt? }`.
      const tab = typeof ziel === "string" ? ziel : ziel?.tab;
      const abschnitt = typeof ziel === "object" ? ziel.abschnitt : undefined;
      const bestellung = zustand.bestellungen.find((b) => String(b.id) === bestellungId);
      // `:9244 if (!o) return` - eine id ohne (mehr) geladenen Datensatz oeffnet keinen Drawer.
      if (!darfBestellungOeffnen(bestellung)) return;
      // Task 7: der Drawer haelt entweder eine Bestellung ODER eine Sammelsendung, nie beides.
      setSammelsendungOffen(null);
      if (abschnitt) {
        setSprung((alt) => ({ nr: (alt?.nr ?? 0) + 1, bestellungId: String(bestellung.id), tab: tab ?? "main", abschnitt }));
      }
      const kennung = kennungVon(bestellung, bestellungId);
      setzeAnsicht({
        bestellung: {
          kennung,
          tab: tab ?? "main",
          aufgabe: null,
          dokument: null,
          ...(abschnitt ? { abschnitt } : {}),
        },
      });
      versende({ art: "geoeffnet", verweis: { entitaet: BESTELLUNG_ENTITAET, id: bestellungId } });
    },
    [zustand.bestellungen, setzeAnsicht, versende],
  );

  // ================= Sammelsendungs-Drawer (Task 7) ==============================================
  //
  // Lokaler Zustand statt Adresse: die Sammelsendung hat (noch) keine eigene Kennung in der URL.
  // Gegenseitiger Ausschluss mit dem Bestell-Drawer: `sammelsendungOeffnen` schliesst die Bestellung,
  // `drawerOeffnen` (oben) und der Effekt darunter (Tieflink, Zurueck-Taste) schliessen die Sammelsendung.
  // Sprung-Zaehler fuer den Drawer (Prop `sprung`, Zaehler plus Ziel): macht den erneuten Klick auf dieselbe Zelle zu
  // einem neuen Sprung. Bewusst App-Zustand, nicht Adresse; der Setter ist stabil (keine Dep).
  const [sprung, setSprung] = useState<DrawerSprung | undefined>(undefined);
  const [sammelsendungOffen, setSammelsendungOffen] = useState<{ shipmentId: string } | null>(null);
  const offeneBestellungKennung = ansicht.bestellung?.kennung ?? null;
  useEffect(() => {
    if (offeneBestellungKennung !== null) setSammelsendungOffen(null);
  }, [offeneBestellungKennung]);
  const sammelsendungOeffnen = useCallback(
    (shipmentId: string): void => {
      setSammelsendungOffen({ shipmentId });
      if (aktuelleKennungRef.current !== null) {
        setzeAnsicht({ bestellung: null });
        versende({ art: "geoeffnet", verweis: null });
      }
    },
    [setzeAnsicht, versende],
  );
  const sammelsendungSchliessen = useCallback((): void => setSammelsendungOffen(null), []);
  /** Nach einem zentralen Schreiben tragen die Kinder neue Werte: jede Mitgliedsbestellung nachladen. */
  function sammelsendungGeaendert(parent: { memberOrderIds: string[] }): void {
    // Wie der Sammel-Statuswechsel: `sammelEintragNachladen` liefert je Bestellung ein Ergebnis; ein
    // Fehlschlag wird sichtbar gemeldet, statt veraltete Kinder still stehen zu lassen. R4 (Task 10): gespeichert ist
    // gespeichert - die Ursache der ERSTEN gescheiterten Bestellung steht hinter dem Satz, ausser sie raet zum Wiederholen.
    Promise.all(parent.memberOrderIds.map((orderId) => sammelEintragNachladen(orderId))).then((ergebnisse) => {
      if (!istEingehaengt.current) return;
      meldeNachladeFehlschlaege(
        ergebnisse,
        (anzahl) => `Die Sammelsendung wurde gespeichert, aber ${anzahl} Bestellung(en) konnten nicht neu geladen werden. Bitte die Seite neu laden.`,
      );
    });
  }

  /**
   * Das Faelligkeitsdatum direkt an der Elternzeile im Board (Plan 2026-10-31, Task 6; `BoardSeite#onSammelFaelligkeitSetzen`):
   * zentral schreiben (`wert` "YYYY-MM-DD" oder `null` zum Leeren), danach wie nach dem Speichern im Drawer die Kinder
   * nachladen (`sammelsendungGeaendert`).
   *
   * Scheitert das Schreiben, steht EIN Satz im Fehlerstapel und die Kinder werden trotzdem nachgeladen: nach einem Konflikt
   * zeigt die Zeile sonst weiter den Stand, auf dem die Zelle stand, und die naechste Eingabe liefe in denselben 409. Beim
   * Konflikt (`aktuelleSammelsendungAusKonflikt`) erklaert der Satz sich selbst ("... zwischenzeitlich geaendert") und die
   * Mitgliederliste kommt aus dem aktuellen Stand des Servers (Mitglieder koennen hinzugekommen sein); jeder andere Fehler
   * haengt seine Ursache an und laedt die bekannten Mitglieder aus dem Store nach. Die Nummer fuer den Satz steht im Parent
   * der Kinder, die der Store kennt.
   *
   * NICHT ueber `sammelsendungGeaendert` im Fehlerpfad: dessen Nachladefehler sagt "Die Sammelsendung wurde gespeichert, aber ...",
   * und hier wurde NICHTS gespeichert. Scheitert das Nachladen nach einem gescheiterten Schreiben, steht stattdessen der Satz zur
   * Anzeige. Er geht ueber `meldeNachladeFehlschlaege` (die Ursache der ersten gescheiterten Bestellung steht dahinter, ausser
   * sie raet zum Wiederholen: der Satz sagt schon "neu laden"); der Fehlerwaechter verlangt diesen Helfer fuer jeden Wortlaut
   * "nicht aktualisiert werden".
   */
  function sammelFaelligkeitSetzen(shipmentId: string, rowVersion: number, wert: string | null): void {
    const kinder = zustand.bestellungen.filter((b) => b.shipmentParent?.shipmentId === shipmentId);
    const nummer = kinder[0]?.shipmentParent?.dsvShipmentNo ?? shipmentId;
    const satz = `Das Fälligkeitsdatum der Sammelsendung ${nummer} konnte nicht gespeichert werden.`;
    gemeinsameWerteSchreiben(shipmentId, rowVersion, { listDueDate: wert }).then(
      (neu) => sammelsendungGeaendert(neu),
      (fehler: unknown) => {
        const aktuell = aktuelleSammelsendungAusKonflikt(fehler);
        if (aktuell) meldeFehler(`${satz} Die Sammelsendung wurde zwischenzeitlich geändert.`);
        else meldeFehler(fehler, satz);
        const orderIds = aktuell?.memberOrderIds ?? kinder.map((k) => String(k.id));
        Promise.all(orderIds.map((orderId) => sammelEintragNachladen(orderId))).then((ergebnisse) => {
          if (!istEingehaengt.current) return;
          meldeNachladeFehlschlaege(
            ergebnisse,
            () => `Die Anzeige der Sammelsendung ${nummer} konnte nicht aktualisiert werden – bitte neu laden.`,
          );
        });
      },
    );
  }

  /**
   * Sammelsendung aufgeloest (`SammelsendungDrawer`, Spec 2026-10-04-sammelsendung-aufloesen): Drawer zu, dann die
   * getrennten Bestellungen NACHEINANDER nachladen (Schleife mit `await`, hinter dem letzten Dispatch der vorigen) -
   * parallele `nachgeladen`-Dispatches ueberschreiben sich im Store (AGENTS.md, "Store-Wettlauf").
   */
  function sammelsendungAufgeloest(orderIds: string[]): void {
    setSammelsendungOffen(null);
    zeigeMeldung("Sammelsendung aufgelöst.");
    void (async () => {
      const ergebnisse: NachladeErgebnis[] = [];
      for (const orderId of orderIds) {
        ergebnisse.push(await sammelEintragNachladen(orderId));
      }
      zeitstrahlListeNeuLaden();
      // R4 (Task 10): aufgeloest ist aufgeloest - die Ursache der ERSTEN gescheiterten Bestellung steht hinter dem Satz,
      // ausser sie raet zum Wiederholen.
      if (istEingehaengt.current) {
        meldeNachladeFehlschlaege(
          ergebnisse,
          (anzahl) => `Die Sammelsendung wurde aufgelöst, aber ${anzahl} Bestellung(en) konnten nicht neu geladen werden. Bitte die Seite neu laden.`,
        );
      }
    })();
  }

  /**
   * Eine Bestellung aus der Sammelsendung herausnehmen: Verknuepfung loesen (wirft bei Fehlschlag, der Dialog zeigt
   * ihn), dann die Bestellung nachladen. Den Parent laedt der Drawer danach selbst neu und meldet ihn ueber `onGeaendert`.
   */
  async function sammelsendungHerausnehmen(shipmentId: string, orderId: string): Promise<void> {
    await verknuepfungLoesen(orderId, shipmentId);
    // Der Dialog schliesst nach dem Server-Erfolg: das Nachladen laeuft danach, ohne ihn festzuhalten.
    void sammelEintragNachladen(orderId).then((ergebnis) => {
      // R4 (Task 10): herausgenommen ist herausgenommen - die Ursache steht hinter dem Satz, ausser sie raet zum Wiederholen.
      if (!ergebnis.ok && istEingehaengt.current) {
        meldeNachladeFehler(
          ergebnis.ursache,
          "Die Bestellung wurde herausgenommen, konnte aber nicht neu geladen werden. Bitte die Seite neu laden.",
        );
      }
    });
    zeitstrahlListeNeuLaden();
  }

  /**
   * `onLineageOeffnen` (`Drawer.tsx#LineageHinweis`, Task 12; Pflichtreview-Befund 1 zu Commit
   * `5e27997c`): ANDERS als jeder bisherige Aufrufer von `drawerOeffnen` zeigt eine
   * `LineageHinweis`-Zeile eine `orderId`, die NICHT zwangslaeufig im (seit dem Archivfilter
   * verkleinerten) `zustand.bestellungen` steht - eine Quelle "gesplittet von X" oder ein frisch
   * angelegtes Ziel "aufgeteilt in Y" sind beide oft NICHT geladen (gemessen: 2044 von 2144
   * archivierten Bestellungen betroffen; eine frisch gesplittete Zielzeile bekommt zudem kein
   * eigenes Nachladen/Broadcast). `drawerOeffnen` selbst kehrt in diesem Fall still zurueck
   * (`:9244 if (!o) return`, siehe dort) - ein Klick aus dem `LineageHinweis` waere damit ein toter
   * Klick.
   *
   * REPARATUR: derselbe Weg wie beim Tieflink (§ 3.7, der "nicht gefunden"-Effekt oben, Reparaturweg
   * Befund 1 der Nachbesserung "Serverseitiger Archivfilter + Ladeanzeige"), statt eines neu
   * erfundenen. Steht die Bestellung bereits im Store, bleibt es EXAKT bei `drawerOeffnen` (lesbare
   * Kennung, SSE-Konflikterkennung). Sonst wird `orderId` (eine echte UUID, `orders.id` - jede
   * Lineage-Quelle/-Ziel traegt sie so) DIREKT als Kennung in die Ansicht geschrieben - der
   * bestehende "nicht gefunden"-Effekt oben erkennt daraufhin selbst, dass `drawer` (noch) leer ist,
   * und uebernimmt: EIN Versuch ueber `bestellungNachKennungNachladen`, bei Erfolg fuegt er die
   * Bestellung in den Store ein (worauf der Drawer im naechsten Rendern oeffnet, genau wie bei einem
   * direkten Tieflink), sonst zeigt er `meldungBestellungNichtGefunden` und raeumt die Ansicht
   * wieder auf - kein zweiter, hier neu gebauter Nachladeweg noetig.
   */
  function lineageOeffnen(orderId: string): void {
    oeffnenOderNachladen(
      zustand.bestellungen.find((b) => String(b.id) === orderId),
      orderId,
    );
  }

  /**
   * Der gemeinsame Kern von `lineageOeffnen` und `aktivitaetOeffnen` (Aktivitaeten-Seite): `bestellung`
   * ist der Treffer im Store (oder `undefined`), `kennung` die Kennung, die bei einem Fehltreffer in
   * die Adresse geschrieben wird. Die Aufrufer unterscheiden sich nur in der SUCHE im Store.
   *
   * - im Store und oeffenbar: `drawerOeffnen` (lesbare Kennung, SSE-Konflikterkennung);
   * - im Store, aber in JTL geloescht: nichts (`darfBestellungOeffnen`);
   * - nicht im Store: `kennung` direkt in die Ansicht - der "nicht gefunden"-Effekt weiter oben laedt
   *   sie nach (`bestellungNachKennungNachladen`), statt sofort "nicht gefunden" zu melden.
   */
  function oeffnenOderNachladen(bestellung: Order | undefined, kennung: string): void {
    if (darfBestellungOeffnen(bestellung)) {
      drawerOeffnen(String(bestellung.id));
      return;
    }
    if (bestellung) return;
    setzeAnsicht({ bestellung: { kennung, tab: "main", aufgabe: null, dokument: null } });
  }

  /**
   * Klick auf eine Zeile der Seite "Aktivitaeten" (`AktivitaetenSeite.onBestellungOeffnen`). Dieselbe
   * Frage wie bei `lineageOeffnen` oben: die Bestellung steht NICHT zwangslaeufig in `zustand.bestellungen`
   * (der Archivfilter verkleinert den Store serverseitig, und das Protokoll reicht Jahre zurueck).
   *
   * Kennung: die Hub-UUID (`wo.orderId`), sonst `jtl-<jtlOrderId>`, sonst `wo.kennung` (der Server
   * liefert dort genau eines von beiden). Steht die Bestellung im Store, bleibt es bei `drawerOeffnen`
   * (lesbare Kennung in der Adresse, SSE-Konflikterkennung). Sonst wird die Kennung direkt in die
   * Ansicht geschrieben - der "nicht gefunden"-Effekt weiter oben laedt sie nach
   * (`bestellungNachKennungNachladen`), statt sofort "Bestellung nicht gefunden" zu melden. Eine in
   * JTL geloeschte Bestellung oeffnet nichts (`darfBestellungOeffnen`); die Seite zeigt sie ohnehin
   * nicht als Knopf.
   */
  function aktivitaetOeffnen(wo: AktivitaetWo): void {
    const kennung = wo.orderId ?? (wo.jtlOrderId != null ? abgeleiteteKennung(wo.jtlOrderId) : wo.kennung);
    oeffnenOderNachladen(bestellungNachKennung(zustand.bestellungen, kennung), kennung);
  }

  /**
   * Klick auf eine Zeile der Seite "Aktivitaeten", deren Objekt eine eigene Seite hat (Phase 2, Task 11B,
   * `AktivitaetenSeite.onSeiteOeffnen`). Welche Objekte anklickbar sind und ob das Recht fuer die Zielseite da ist,
   * entscheidet `aktivitaetenAnzeige.ts#klickZiel` schon in der Tabelle; hier wird nur gewechselt, ueber dieselben Wege wie
   * die Marken-Menue-Eintraege (Nutzerverwaltung mit ihrem Rechte-Waechter, Lieferanten, Zahlungsvorlagen):
   * - `users`: `nutzerverwaltungOeffnen(id)` waehlt das Konto aus (`nutzer.id`);
   * - `suppliers`: die Lieferantenseite mit `lieferanten.id` (die JTL-Lieferantennummer), die Suche leer;
   * - `paymentTemplates`: die Seite Zahlungsvorlagen mit der Vorlage als Auswahl (`/zahlungsvorlagen/<id>`; `objekt_id`
   *   einer Zahlungsvorlage ist ihre Id). Gibt es die Vorlage nicht mehr, sagt die Seite es selbst.
   * - `payments`: die Seite Zahlungen (FX-Konten, -Deals und -Zuordnungen haben dort ihren Platz).
   */
  function aktivitaetSeiteOeffnen(ziel: SeitenZiel): void {
    switch (ziel.seite) {
      case "users":
        nutzerverwaltungOeffnen(ziel.id);
        return;
      case "suppliers":
        setzeAnsicht({ seite: LIEFERANTEN_SEITE, lieferanten: { q: "", id: ziel.id } });
        return;
      case "paymentTemplates":
        setzeAnsicht({ seite: ZAHLUNGSVORLAGEN_SEITE, zahlungsvorlagen: { id: ziel.id } });
        return;
      case "payments":
        setSeite("payments");
        return;
    }
  }

  /**
   * `closeDrawer()` (index.html:9631-9640).
   *
   * GEFUNDENER BEFUND (Woertlichkeitsregel - gemeldet, nicht repariert): der Bestand ruft am Ende
   * zusaetzlich `renderSuppliers()` (`:9639`) - werfender toter Code (Befund 15: `renderSuppliers`
   * schreibt in ein nicht existierendes `#page-suppliers`, siehe Verdrahtungsplan, Abschnitt
   * "Entscheidungen, Blockaden, bewusst Ungetanes"). Wird hier NICHT nachgebaut.
   */
  function drawerSchliessen(): void {
    setzeAnsicht({ bestellung: null });
    versende({ art: "geoeffnet", verweis: null });
  }

  const {
    sendung, zeitstrahl, vorschlag, historie, belege, einbuchungen, einbuchungenFehlgeschlagen,
    speditionsSuche, speditionVerknuepfen, speditionLoesen, speditionLaeuft, belegeNeuLaden,
  } = useDrawerSpedition(
    drawer, zustand.bestellungen, versende,
    () => zeitstrahlListeNeuLaden(), NACHLADEN_FEHLGESCHLAGEN_MELDUNG,
  );

  /**
   * Positionsdokumente sind Drawer-Lokal: ein Wechsel darf nie die Liste der vorherigen Bestellung zeigen. Seit Fehleranzeige
   * Paket 2 (Task 13, Spec E4) haben sie drei Zustaende - laedt, geladen, gescheitert (`lib/positionsDokumenteZustand.ts`) -, damit
   * ein gescheitertes Laden nicht mehr wie "keine Dokumente" aussieht (Reiter Dokumente: Flaeche mit Grund und "Erneut laden",
   * Raster: "nicht geladen"; in beiden keine Upload-Knoepfe ohne geladenen Stand). Der Stand traegt die Bestellkennung, zu der er
   * gehoert; fuer jede andere Bestellung gilt `laedt`.
   */
  interface PositionsDokumenteStand {
    bestellungId: string;
    zustand: PositionsDokumenteZustand;
  }
  const [positionsDokumenteStand, setPositionsDokumenteStand] =
    useState<PositionsDokumenteStand | null>(null);
  const positionsDokumenteZustand: PositionsDokumenteZustand =
    positionsDokumenteStand !== null && positionsDokumenteStand.bestellungId === drawer?.id
      ? positionsDokumenteStand.zustand
      : POSITIONSDOKUMENTE_LAEDT;
  // Die Lightbox zeigt Bilder, die es nur in einem geladenen Stand gibt; sonst gibt es nichts zu zeigen.
  const geladeneProduktionsbilder = geladeneDokumente(positionsDokumenteZustand);
  const [lightboxStartLinkId, setLightboxStartLinkId] = useState<string | null>(null);
  const lightboxAusloeserRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setLightboxStartLinkId(null);
  }, [drawer?.id]);

  function produktionsbildLightboxOeffnen(linkId: string, ausloeser: HTMLElement): void {
    lightboxAusloeserRef.current = ausloeser;
    setLightboxStartLinkId(linkId);
  }

  const drawerRef = useRef(drawer);
  drawerRef.current = drawer;
  const positionsDokumenteLadefolge = useRef(0);

  /**
   * Die EINE Stelle, an der der Ausgang eines Abrufs der Positionsdokumente in den Stand kommt - fuer das Laden beim Oeffnen, fuer
   * "Erneut laden" und fuer das Nachladen nach einem Schreiben (Review Task 13, G4). Wer einen Abruf startet, zaehlt die Ladefolge hoch
   * und reicht sie hier mit zurueck: eine Antwort, die nach einem Drawer-Wechsel oder einem neueren Abruf eintrifft, wird verworfen
   * (L6) - Liste wie Fehlschlag.
   *
   * Fehlschlag: `zustandNachFehlschlag` (404 = "keine", sonst `gescheitert` mit der Ursache). Nach einem Schreiben (`nachSchreiben`) gilt
   * zusaetzlich R-M16: die Aenderung steht schon, `meldeNachladeFehler` haengt den Wiederholungsrat der Ursache (Netz, 5xx) nicht an
   * ("bitte gleich noch einmal versuchen" hinter einem gespeicherten Upload fuehrte zum doppelten Upload). Ein geladener Altstand bleibt
   * dann stehen (L3). Andernfalls - `laedt` oder `gescheitert` - wird der Fehlschlag zum Zustand: das Nachladen hat das Laden beim
   * Oeffnen mit seiner Ladefolge ueberholt und dessen Ergebnis verworfen, und `laedt` bliebe sonst fuer immer stehen, ohne Flaeche und
   * ohne "Erneut laden" (G2).
   */
  const positionsDokumenteAnwenden = useCallback((
    bestellungId: string,
    ladefolge: number,
    ausgang: PositionsDokumenteAusgang,
    nachSchreiben = false,
  ): void => {
    if (drawerRef.current?.id !== bestellungId) return;
    if (positionsDokumenteLadefolge.current !== ladefolge) return;
    if (ausgang.art === "dokumente") {
      setPositionsDokumenteStand({ bestellungId, zustand: { art: "geladen", dokumente: ausgang.dokumente } });
      return;
    }
    const zustandNeu = zustandNachFehlschlag(ausgang.fehler);
    if (nachSchreiben || zustandNeu.art === "gescheitert") {
      console.error(`Positionsdokumente zu "${bestellungId}" konnten nicht geladen werden:`, ausgang.fehler);
    }
    if (!nachSchreiben) {
      // L1: die Flaeche im Reiter Dokumente nennt den Grund (kein Stapeleintrag daneben), das Raster sagt "nicht geladen".
      setPositionsDokumenteStand({ bestellungId, zustand: zustandNeu });
      return;
    }
    // Der Rueckruf lehnt NICHT ab, er meldet selbst; die Aufrufer sehen nur Erfolg.
    meldeNachladeFehler(
      ausgang.fehler,
      "Die Änderung ist gespeichert, die Positionsdokumente konnten aber nicht aktualisiert werden. Bitte neu laden.",
    );
    setPositionsDokumenteStand((alt) => (
      alt !== null && alt.bestellungId === bestellungId && alt.zustand.art === "geladen"
        ? alt
        : { bestellungId, zustand: zustandNeu }
    ));
  }, []);

  /**
   * Nachladen NACH einem Schreiben (Upload und Ersatz in `Positionsdokumente` und im Grid, Uebernehmen, Loeschen, Wiederherstellen).
   * Das Laden beim Oeffnen des Drawers geht im Effekt unten, "Erneut laden" in `positionsDokumenteErneutLaden`.
   */
  const positionsDokumenteNeuLaden = useCallback((): Promise<void> => {
    const bestellungId = drawerRef.current?.id;
    if (!bestellungId) return Promise.resolve();
    const ladefolge = ++positionsDokumenteLadefolge.current;
    return positionsDokumenteLaden(bestellungId)
      .then((dokumente) => positionsDokumenteAnwenden(bestellungId, ladefolge, { art: "dokumente", dokumente }))
      .catch((fehler: unknown) => positionsDokumenteAnwenden(bestellungId, ladefolge, { art: "fehlschlag", fehler }, true));
  }, [positionsDokumenteAnwenden]);

  /**
   * „Erneut laden“ der Flaeche im Reiter Dokumente (Task 13, L1): laedt die Positionsdokumente des Drawers, der JETZT offen ist (nicht
   * den, bei dem es zuerst scheiterte). LEHNT NIE AB: `<Ladefehler>` faengt ein abgelehntes Promise nicht, es ginge an den globalen
   * Auffaenger und stuende als Eintrag im Stapel neben der Flaeche (Doppel). Die Flaeche bleibt waehrend des Ladens im Baum, deshalb ist
   * das Promise erst nach dem Laden erfuellt (sonst waere der Knopf nur eine Mikrotask lang gesperrt).
   */
  const positionsDokumenteErneutLaden = useCallback(async (): Promise<void> => {
    const bestellungId = drawerRef.current?.id;
    if (!bestellungId) return;
    const ladefolge = ++positionsDokumenteLadefolge.current;
    let ausgang: PositionsDokumenteAusgang;
    try {
      ausgang = { art: "dokumente", dokumente: await positionsDokumenteLaden(bestellungId) };
    } catch (fehler) {
      ausgang = { art: "fehlschlag", fehler };
    }
    positionsDokumenteAnwenden(bestellungId, ladefolge, ausgang);
  }, [positionsDokumenteAnwenden]);

  useEffect(() => {
    if (!drawer) {
      positionsDokumenteLadefolge.current += 1;
      setPositionsDokumenteStand(null);
      return;
    }
    const bestellungId = drawer.id;
    const abbruch = new AbortController();
    const ladefolge = ++positionsDokumenteLadefolge.current;
    // Jeder Besuch beginnt ohne Auskunft (G1): der Stand eines frueheren Besuchs - auch der einer Flaeche - steht bei der Rueckkehr
    // (A -> B -> A) nicht mit Knoepfen da, und scheitert das neue Laden, wird daraus kein "nicht geladen" ueber einer sichtbaren Liste.
    setPositionsDokumenteStand({ bestellungId, zustand: POSITIONSDOKUMENTE_LAEDT });

    Promise.resolve()
      .then(() => positionsDokumenteLaden(bestellungId, abbruch.signal))
      .then((dokumente) => positionsDokumenteAnwenden(bestellungId, ladefolge, { art: "dokumente", dokumente }))
      .catch((fehler: unknown) => {
        // Abbruch (Wechsel, Schliessen) erzeugt weder Flaeche noch Eintrag (L5). Ein 401 ebenso: dann ergibt `fehlertext` keinen Text,
        // die Flaeche zeichnet sich nicht, und die Anmeldemaske uebernimmt.
        if (abbruch.signal.aborted) return;
        positionsDokumenteAnwenden(bestellungId, ladefolge, { art: "fehlschlag", fehler });
      });

    return () => abbruch.abort();
  }, [drawer?.id, positionsDokumenteAnwenden]);

  const [drawerAendern, drawerFeldAendern] = useDrawerBestellfelder(
    zustand.bestellungen, versende,
  );

  /**
   * Auftrag C7 (2026-08-22): zwei Register FUER `drawerJtlKopfAendern`/`fuehreKopfSchreibvorgangAus`
   * unten, keins davon der Renderzustand - siehe dort, "DIE VERSION KOMMT AUS DER LETZTEN ANTWORT".
   *
   * `letzteBekannteBRowversion`: je Bestellung die zuletzt BESTAETIGTE `bRowversion`. "Bestaetigt"
   * heisst entweder aus einer EIGENEN erfolgreichen Antwort (unten im Schreibvorgang SOFORT
   * geschrieben, VOR `versende(...)` - React committet den daraus folgenden Renderzustand erst
   * asynchron, ein sofort folgender zweiter Schreibvorgang saehe den sonst noch nicht) oder aus dem
   * Abgleich-Effekt direkt darunter, der JEDE Aenderung von `zustand.bestellungen` nachzieht (SSE,
   * ein normales Neuladen, ein Schreibvorgang eines ANDEREN Feldes). Ohne diesen Abgleich bliebe die
   * Karte auf dem letzten SELBST geschriebenen Stand haengen und erzwaenge nach einer FREMDEN,
   * laengst im Zustand angekommenen Aenderung einen unnoetigen 409, den der Renderzustand allein gar
   * nicht mehr ausloesen wuerde - die Karte ist dadurch NIE aelter als der Renderzustand, nur bei
   * einer laufenden eigenen Schreibkette bereits einen Schritt weiter.
   *
   * `kopfSchreibKette`: eine Zusagen-Kette JE BESTELLUNG (Schluessel: `id`), NICHT global -
   * Schreibvorgaenge VERSCHIEDENER Bestellungen bleiben parallel (Auftragstext). Der eigentliche
   * Fehler, den dieser Auftrag behebt: zwei Schreibvorgaenge DERSELBEN Bestellung kurz hintereinander
   * lasen BEIDE die `bRowversion` aus dem Renderzustand - der ist zwischen Absenden und Antwort noch
   * der alte. Der zweite PATCH schickte deshalb deterministisch dieselbe (beim Server inzwischen
   * VERBRAUCHTE) Version wie der erste und lief in einen 409 - ausgeloest vom EIGENEN Bedienen
   * (Feld verlassen, sofort wieder hinein und nachgebessert; oder Notiz-Blur unmittelbar gefolgt von
   * einer Lageraenderung), nicht von einem echten Fremdzugriff.
   *
   * WARUM EINE PROMISE-KETTE UND KEIN blosser "wird gerade geschrieben"-Schalter: ein Schalter
   * muesste selbst nachhalten, WANN er wieder freigibt (Erfolg UND Fehlschlag) und wie ein DRITTER
   * Aufruf waehrenddessen wartet statt verworfen zu werden. Eine Kette aus `Promise`s erledigt das
   * eingebaut: `bisherige.then(naechste)` haengt sich an, EGAL in welchem Zustand `bisherige` gerade
   * ist, und `naechste` startet garantiert erst, wenn `bisherige` abgeschlossen ist.
   *
   * EIN FEHLGESCHLAGENER PATCH DARF DIE KETTE NICHT BLOCKIEREN: `fuehreKopfSchreibvorgangAus` unten
   * faengt jeden Fehler intern ab (dieselbe Fehlerbehandlung wie vor diesem Auftrag, nur verschoben)
   * und LOEST IHR EIGENES PROMISE IMMER AUF, nie ab - `schreibeKopfSerialisiert`s `.catch(() => {})`
   * ist nur ein zweites Sicherheitsnetz. Ohne diese Zusicherung bliebe jeder KUENFTIGE Schreibvorgang
   * derselben Bestellung fuer immer haengen, sobald ein einziger PATCH scheitert (`.then()` auf einem
   * abgelehnten Promise ueberspringt den Erfuellungs-Handler) - siehe `drawerJtlKopfAendern.test.tsx`,
   * "ein fehlgeschlagener Schreibvorgang blockiert die Kette nicht".
   *
   * EIN PATCH, DER NIE ANTWORTET, IST DASSELBE PROBLEM IN NEU (Auftrag E): der Absatz oben deckt nur
   * ab, was geschieht, wenn `jtlBestellkopfAendern` ABLEHNT. `fetch` selbst kennt aber keinen
   * eingebauten Timeout - eine Verbindung, die einfach nie antwortet (totes WLAN, ein Proxy, der
   * still verwirft, ein eingeschlafener Tab), laesst das zurueckgegebene Promise fuer IMMER offen.
   * `.then()` feuert dann nie, weder erfuellt noch abgelehnt - GENAU der Fall, den der Absatz oben
   * ausdruecklich ausschliessen will, nur dass hier gar kein Fehler eintritt, der ihn ausloesen
   * koennte. Die Serialisierung selbst hat dieses Risiko erst geschaffen: VOR ihr blieb ein
   * haengender Schreibvorgang folgenlos fuer alle SPAETEREN Aenderungen (jede feuerte ihren eigenen,
   * unabhaengigen `fetch`); seither teilen sich ALLE Aenderungen derselben Bestellung genau EIN
   * Promise, das die haengende Verbindung nie aufloest. GEMESSEN (siehe Bericht): zwei weitere
   * Speicherversuche ohne jeden `fetch`-Aufruf, ohne Banner - der Nutzer haelt seine Aenderungen fuer
   * gespeichert. `JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS` (siehe dort) schliesst die Luecke: ein
   * `AbortSignal.timeout(...)` an genau diesem `fetch`-Aufruf laesst ihn nach spaetestens dieser Zeit
   * ABLEHNEN statt fuer immer offen zu bleiben - danach greift der Absatz oben unveraendert.
   */
  /**
   * Auftrag E, P4 (Kleinigkeit 3): diese Karte bekommt einen Eintrag fuer JEDE je in dieser Sitzung
   * geladene Bestellung (der Abgleich-Effekt direkt darunter laeuft ueber `zustand.bestellungen`
   * insgesamt) und RAEUMT NIE auf - kein `delete` fuer eine Bestellung, die aus dem Board
   * verschwindet (Filter, Status wechselt aus der geladenen Menge heraus, o.ae.). Ein frueherer
   * Bericht nannte das "kein Zuruecksetzen noetig"; das ist zu kurz gegriffen, denn die Karte WAECHST
   * MONOTON, solange die Seite offen bleibt. Bewusst dennoch keine Aufraeumung eingebaut:
   *
   * - Die Zahl der Eintraege ist durch die Zahl UNTERSCHIEDLICHER Bestellungen begrenzt, die diese
   *   Sitzung je gesehen hat, nicht durch die Zeit - kein unbegrenztes Wachstum bei GLEICHBLEIBENDER
   *   Bestellmenge (SSE-Ereignisse fuer dieselbe Bestellung UEBERSCHREIBEN ihren Eintrag, sie legen
   *   keinen neuen an).
   * - GEMESSEN (2026-08-22, siehe Kopfkommentar `api/jtlBestellungen.ts`): 94 offene Bestellungen im
   *   gesamten Bestand. Selbst ein Arbeitstag mit mehrfachem Filterwechsel ueber den gesamten Bestand
   *   bliebe bei einer niedrigen dreistelligen Anzahl kurzer Zeichenketten - keine Groessenordnung,
   *   die messbar Speicher kostet.
   * - Ein voller Seitenneuaufbau (Login, Reload) verwirft die Karte ohnehin (sie lebt in `useRef`,
   *   nicht in `localStorage`), sie ueberlebt also nie laenger als eine Sitzung.
   *
   * Eine Aufraeumung (z. B. beim Verlassen des Boards eine Bestellung entfernen, die nicht mehr in
   * `zustand.bestellungen` steht) waere jederzeit nachruestbar, ist bei diesem Umfang aber ungenutzte
   * Vorsicht ohne belegten Nutzen - kein Umfangszuwachs.
   */
  const letzteBekannteBRowversion = useRef<Map<string, string>>(new Map());
  /**
   * Fehleranzeige Paket 2, Task 7, Fix-Runde 1 (W1): je Bestellung die `bRowversion`, die ein GELUNGENER Schreibvorgang
   * verbraucht hat, dessen Antwort keine neue Version trug (der Stummel, siehe `drawerJtlKopfAendern`). Das Nachladen
   * danach kann scheitern - `bestellungLaden` lehnt seit Task 7 bei einem JTL-Ausfall ab, statt eine Zeile ohne
   * `bRowversion` zu liefern - oder `null` ergeben; der Store behaelt dann seine volle Zeile mit der verbrauchten Version.
   * Ohne diese Liste fiele der naechste Schreibvorgang auf genau sie zurueck (den Renderzustand) und liefe in einen
   * ECHTEN 409 "von einem anderen Benutzer geaendert". Mit ihr gilt die Version als unbekannt, und der Aufruf lehnt mit
   * `JTL_KOPF_OHNE_ROWVERSION_MELDUNG` ab - wie vor Task 7, als die halbe Zeile keine Version trug.
   *
   * Freigegeben wird sie auf zwei Wegen: ein Nachladen, das NACH dem gelungenen Schreiben startete und eine JTL-Version
   * brachte, gibt sie frei, auch bei GLEICHER Version (Fix-Runde 2, N1: die Positionsprozeduren aendern den Kopf nicht).
   * Eine Zeile mit einer ANDEREN Version (Live-Nachladen, Schreibantwort eines anderen Felds) macht sie ueber den Wertvergleich
   * hinfaellig - der Abgleich-Effekt direkt darunter raeumt sie dann ab.
   */
  const verbrauchteBRowversion = useRef<Map<string, string>>(new Map());
  const kopfSchreibKette = useRef<Map<string, Promise<void>>>(new Map());

  useEffect(() => {
    for (const b of zustand.bestellungen) {
      const roh = (b as { bRowversion?: unknown }).bRowversion;
      if (typeof roh !== "string") continue;
      const id = b.id as string;
      // Pflege der Karte, nicht die Regel: ob eine Version schreibbar ist, entscheidet `schreibbareBRowversion` (sie vergleicht
      // mit der Markierung selbst). Hier wird nur vermieden, dass die verbrauchte Version aus dem Store in die Karte zurueckkehrt,
      // und die Markierung abgeraeumt, sobald eine ANDERE Version ankommt. Bei GLEICHER Version raeumt dieser Effekt bewusst NICHT:
      // ein vor dem Schreiben gestartetes Live-Nachladen koennte die alte Version noch bringen. Das Freigeben nach einem Frischlesen
      // NACH dem Schreiben besorgen die Erfolgszweige des Nachladens in den beiden Schreibvorgaengen (N1).
      if (verbrauchteBRowversion.current.get(id) === roh) continue;
      verbrauchteBRowversion.current.delete(id);
      letzteBekannteBRowversion.current.set(id, roh);
    }
  }, [zustand.bestellungen]);

  /**
   * Die `bRowversion`, gegen die ein Schreibvorgang auf `id` laufen darf: die Karte hat Vorrang, der beim Aufruf gelesene
   * Renderzustand ist der Rueckfall (siehe "DIE VERSION KOMMT AUS DER LETZTEN ANTWORT"). `undefined`, wenn keine bekannt ist
   * ODER die bekannte die verbrauchte ist (`verbrauchteBRowversion`, W1) - beide Faelle lehnt der Aufrufer ab.
   */
  function schreibbareBRowversion(id: string, bestellung: Order): string | undefined {
    const renderzustand = (bestellung as { bRowversion?: unknown }).bRowversion;
    const bekannt =
      letzteBekannteBRowversion.current.get(id) ?? (typeof renderzustand === "string" ? renderzustand : undefined);
    return bekannt !== undefined && bekannt === verbrauchteBRowversion.current.get(id) ? undefined : bekannt;
  }

  /**
   * Reiht `aufgabe` in die Schreibkette der Bestellung `id` ein - siehe Kopfkommentar oben an
   * `letzteBekannteBRowversion`. `aufgabe` MUSS ihr Promise immer AUFLOESEN (nie ablehnen), sonst
   * haette das zweite Sicherheitsnetz hier (`.catch(() => {})`) zwar etwas zu tun, aber eine bereits
   * VOR diesem Aufruf wartende, weitere Aufgabe haengt sich an `eigene`, nicht an dessen `.catch` -
   * `fuehreKopfSchreibvorgangAus` ist entsprechend gebaut.
   */
  function schreibeKopfSerialisiert(id: string, aufgabe: () => Promise<void>): void {
    const bisherige = kopfSchreibKette.current.get(id);
    // OHNE laufende Schreibkette (der Regelfall - auch fuer die ERSTE Aenderung NACH einer bereits
    // abgeschlossenen vorherigen, siehe die Aufraeumung unten): `aufgabe()` startet SOFORT, synchron
    // im selben Durchlauf - `fuehreKopfSchreibvorgangAus` ruft `jtlBestellkopfAendern(...)` VOR
    // seinem ersten `await` auf, der PATCH geht also unveraendert zum bisherigen Verhalten sofort
    // raus (siehe `drawerJtlKopfAendern.test.tsx`, "schreibt ueber jtlBestellkopfAendern ... " -
    // dort wird GENAU DAS ohne jedes `waitFor` geprueft). Erst wenn fuer `id` bereits etwas laeuft,
    // haengt sich `aufgabe` ueber `.then` an und startet nachweislich ERST, wenn `bisherige`
    // abgeschlossen ist.
    const eigene = bisherige ? bisherige.then(aufgabe) : aufgabe();
    const gesichert = eigene.catch(() => {}); // still: Sicherheitsnetz der Kette - jede Aufgabe meldet ihren Fehler selbst und loest immer auf, die Kette darf nie ablehnen
    kopfSchreibKette.current.set(id, gesichert);
    // Aufraeumung: eine abgeschlossene Kette bleibt sonst FUER IMMER in der Karte stehen (Promises
    // "vergessen" nicht, dass sie schon erfuellt sind) und zwaenge JEDEN kuenftigen, laengst
    // unabhaengigen Aufruf fuer dieselbe Bestellung unnoetig durch `.then` - eine Verzoegerung um
    // (mindestens) einen Mikrotask ohne jeden Nutzen, siehe der Fall oben. Nur entfernen, wenn seither
    // NICHTS Neueres eingereiht wurde (`kopfSchreibKette.current.get(id) === gesichert` gilt dann
    // nicht mehr) - sonst risse dieser Aufraeumschritt die Kette einer zwischenzeitlich gestarteten
    // WEITEREN Aufgabe heraus.
    gesichert.then(() => {
      if (kopfSchreibKette.current.get(id) === gesichert) kopfSchreibKette.current.delete(id);
    });
  }

  /**
   * Auftrag C2b: schreibt einen TEIL des JTL-Bestellkopfs - `MainTab/typen.ts#onJtlKopfAendern`,
   * Aufrufer sind `BestellungTab.tsx` (interner Kommentar, `cInternerKommentar`) und
   * `Stammdaten.tsx` (Bezugsauftrag, `cBezugsAuftragsNummer`, seit Auftrag „Bezugsauftrag
   * schreibbar machen ueber den generischen Weg", 2026-08-25).
   *
   * RICHTIGGESTELLT (Nachtrag "Bestellfelder nach Anlage sperren", Abschnitt 5, 2026-08-25):
   * `Stammdaten.tsx` rief hierueber bis dahin auch das Lager (`kLager`) - seither ist das Lager nach
   * der Anlage unbedingt gesperrt (Backend-Riegel `http/routes/jtlSchreiben.ts#alsTeilKopf`),
   * `Stammdaten.tsx` schreibt `kLager` seither ueberhaupt nicht mehr darueber. Die beiden Aenderungen
   * (Lager-Sperre, Bezugsauftrag schreibbar) sind unabhaengig voneinander entstanden.
   *
   * EIGENER KANAL neben `drawerAendern`, nicht dessen Erweiterung: JTL ist
   * fuehrend, `order_extras.warehouse`/`.notes` sind hier NICHT im Spiel (siehe Kopfkommentar
   * `api/jtlBestellungen.ts`, "WELCHE SEITE GEWINNT") - `bestellungAendern` (Hub-Overlay) kennt
   * `cInternerKommentar`/`cBezugsAuftragsNummer` ohnehin nicht als Feldnamen und liefe in
   * `FeldOhneGruppeFehler`.
   *
   * `jtlOrderId` fehlt praktisch nie: beide Aufrufer sitzen an Feldern, die NUR an einer JTL-
   * Bestellung ueberhaupt gezeichnet werden (`zusammenfuehren` setzt `jtlOrderId` an jeder aus JTL
   * gelesenen Zeile). Der Fruehausstieg unten ist trotzdem kein Totcode, sondern dieselbe
   * Verteidigung wie ueberall sonst in diesem Modul: ein `Order`-Objekt ist `unknown`-typisiert
   * (`lib/types.ts`), ein manipulierter/veralteter Zustand darf nicht in einen Aufruf mit
   * `NaN`/`0` als JTL-Kennung laufen. Bewusst SYNCHRON und NICHT Teil der Schreibkette unten: diese
   * Pruefung haengt nur von den Eingaben DIESES Aufrufs ab, nicht vom Ausgang eines vorherigen
   * Schreibvorgangs - eine Verzoegerung brachte keinen Nutzen, nur eine spaetere Fehlermeldung.
   *
   * ================= DER STAND NACH ERFOLGREICHEM SCHREIBEN (Auftrag C2b, Punkt 4) =================
   *
   * `jtlBestellkopfAendern` liefert den VOLLSTAENDIGEN, frisch aus JTL gelesenen Kopf samt neuer
   * `bRowversion` und ALLEN Positionen zurueck - `zusammenfuehren(neu, bestellung)` fuehrt ihn mit
   * der (noch lokalen) Hub-Zeile zusammen, GENAU wie beim Anfangsbestand und bei
   * `bestellungLaden`/`jtlBestellungLaden` (siehe deren Kopfkommentare). OHNE diese Uebernahme
   * bliebe die im Zustand gefuehrte `bRowversion` auf dem VOR dem Schreiben gelesenen Stand stehen -
   * eine zweite Aenderung derselben Bestellung schickte dann eine bereits UEBERHOLTE Version mit und
   * scheiterte deterministisch an einem 409, obwohl niemand sonst die Bestellung angefasst hat.
   *
   * ================= DER 409-FALL (Auftrag C2b, Punkt 5) =================
   *
   * Dieselbe Anzeige wie beim Hub-Overlay-Konflikt: der Reduzierer-Fall `"konflikt"`
   * (`state/reduzierer.ts`) merkt den Datensatz vor, `<KonfliktBanner>` (`App/KonfliktBanner.tsx`)
   * zeichnet "wurde von X geaendert". BEWUSST OHNE `serverstand`: der 409-Koerper von
   * `aendereBestellkopfTeilweise` (`backend/src/jtl/schreiben/kopfLesen.ts`) traegt nur die 29
   * Kopf-Felder der Prozedursignatur PLUS `bRowversion` - NICHT die restlichen `Order`-Felder
   * (Lieferant, Positionen, Board-Status, …), die der Store bei "neu laden" sonst vollstaendig
   * ERSETZEN wuerde (`mitErsetztemDatensatz`/`listeErsetzen`, `state/reduzierer.ts`). Ein Uebernehmen
   * dieses Teilkoerpers risse genau die Felder heraus, die JETZT schon fehlten - "neu laden" loest
   * stattdessen ganz normal `nachladenVormerken` aus (derselbe Weg wie beim SSE-Fall) und holt damit
   * den VOLLSTAENDIGEN, korrekt zusammengefuehrten Stand ueber `bestellungLaden`. Die Eingabe des
   * Nutzers wird so oder so NICHT uebernommen - das ist der ganze Zweck des Banners.
   *
   * ================= OHNE `bRowversion` WIRD NICHT GESCHRIEBEN (Auftrag C2c, M2) =================
   *
   * `jtlBestellkopfAendern` laesst das Feld bei `undefined` ganz weg, und die Route schreibt dann OHNE
   * Konfliktpruefung (Rueckwaertskompatibilitaet, siehe deren Kopfkommentar) - fuer DIESEN Aufrufer
   * waere das ein ungesicherter Schreibvorgang auf einem ERP-Beleg. Der Pfad dorthin war real:
   * `bestellungLaden` fing einen Fehlschlag der JTL-Anreicherung ab und lieferte die reine Hub-Zeile
   * (`api/bestellungen.ts`) - ohne `bRowversion`/`lagerName`/`internerKommentar`. Seit Fehleranzeige
   * Paket 2 (Task 7) lehnt es stattdessen ab, die Sperre bleibt als Absicherung. Sie ist das
   * Abnahmekriterium dieses Schreibwegs; fehlt sie, wird abgelehnt statt ungeprueft geschrieben.
   *
   * ================= DER STUMMEL OHNE `positionen` (Auftrag C2c, M3) =================
   *
   * `PATCH /api/jtl/orders/:k` antwortet mit `{ kLieferantenBestellung }` OHNE `positionen`, wenn das
   * Zuruecklesen NACH einem bereits erfolgreichen Schreibvorgang ausfaellt (`backend/src/http/routes/
   * jtlSchreiben.ts`, "ein zwischenzeitlicher Lesefehler darf den bereits erfolgreichen Schreibvorgang
   * nicht als Fehlschlag erscheinen lassen"). `zusammenfuehren` griffe dabei auf `jtl.positionen.map`
   * zu und wuerfe eine TypeError - im `.then` unbehandelt liefe sie in dasselbe generische `.catch`
   * wie ein echter Fehlschlag und drehte die Absicht des Backends genau um. Statt zusammenzufuehren:
   * normal nachladen, derselbe Weg wie ueberall sonst in dieser Datei (`bestellungLaden` +
   * `versende({ art: "nachgeladen", … })`, siehe `speditionBestellungNachladen` oben). Scheitert AUCH
   * dieses Nachladen, ist der Schreibvorgang trotzdem gelungen - die Meldung ist deshalb
   * `NACHLADEN_FEHLGESCHLAGEN_MELDUNG`, nicht die generische Fehlermeldung unten.
   *
   * ========== DIE VERSION KOMMT AUS DER LETZTEN ANTWORT, NICHT AUS DEM RENDERZUSTAND (Auftrag C7) ==
   *
   * Siehe Kopfkommentar an `letzteBekannteBRowversion` oben fuer das WARUM. `fuehreKopfSchreibvorgangAus`
   * unten liest die Version deshalb NICHT mehr aus `bestellung.bRowversion` (dem beim AUFRUF
   * gelesenen Renderzustand), sondern zuerst aus dieser Karte - nur wenn dort noch nichts fuer `id`
   * steht (die allererste Aenderung dieser Bestellung in dieser Sitzung), faellt sie auf den beim
   * Aufruf gelesenen Renderzustand zurueck. Und: der eigentliche NETZWERKAUFRUF samt dieser Ermittlung
   * ist ueber `schreibeKopfSerialisiert` an die Bestellung GEBUNDEN - ein zweiter Aufruf fuer
   * dieselbe Bestellung wartet, bis der erste (Erfolg ODER Fehlschlag) abgeschlossen ist, und ermittelt
   * die Version ERST DANN. Ohne diese Bindung laese ein sofort folgender zweiter Aufruf dieselbe,
   * noch nicht aktualisierte Karte wie der erste - die Reihenfolgesicherung ist kein Komfort
   * obendrauf, sie ist die VORAUSSETZUNG dafuer, dass die Karte bis dahin ueberhaupt die neue Version
   * traegt (MUTATIONSNACHWEIS siehe Bericht: dreht man diese Quelle probeweise zurueck auf den
   * Renderzustand, wird `drawerJtlKopfAendern.test.tsx`, "zwei Speichervorgaenge kurz hintereinander"
   * rot).
   */
  function drawerJtlKopfAendern(id: string, kopf: Record<string, unknown>): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === id);
    if (!bestellung) return;
    const jtlNummer = Number((bestellung as { jtlOrderId?: unknown }).jtlOrderId);
    if (!Number.isSafeInteger(jtlNummer) || jtlNummer <= 0) {
      console.error(`Bestellung "${id}" hat keine JTL-Nummer - Lager/Notiz/Bezugsauftrag sind hier nicht schreibbar:`, bestellung);
      meldeFehler("Die Bestellung konnte nicht geändert werden.");
      return;
    }
    schreibeKopfSerialisiert(id, () => fuehreKopfSchreibvorgangAus(id, kopf, jtlNummer, bestellung));
  }

  /**
   * Der eigentliche Schreibvorgang hinter `drawerJtlKopfAendern` - AUSGELAGERT, damit
   * `schreibeKopfSerialisiert` ihn erst BEI SEINER REIHE startet, nicht schon beim Aufruf (siehe
   * Kopfkommentar an `letzteBekannteBRowversion`). Loest IMMER auf, nie ab - das haelt die Kette am
   * Laufen, auch wenn dieser Versuch scheitert.
   */
  async function fuehreKopfSchreibvorgangAus(
    id: string,
    kopf: Record<string, unknown>,
    jtlNummer: number,
    bestellung: Order,
  ): Promise<void> {
    // Die Karte hat Vorrang - siehe Kopfkommentar an `letzteBekannteBRowversion` und "DIE VERSION
    // KOMMT AUS DER LETZTEN ANTWORT" an `drawerJtlKopfAendern`. Der beim AUFRUF gelesene
    // Renderzustand ist nur der Rueckfall fuer die allererste Aenderung dieser Bestellung in dieser
    // Sitzung, bevor die Karte je einen Eintrag fuer `id` hatte. Eine VERBRAUCHTE Version (W1, siehe
    // `verbrauchteBRowversion`) zaehlt als unbekannt.
    const bRowversion = schreibbareBRowversion(id, bestellung);
    if (bRowversion === undefined) {
      // Siehe Kopfkommentar `drawerJtlKopfAendern`, "OHNE `bRowversion` WIRD NICHT GESCHRIEBEN".
      console.error(
        `Bestellung "${id}" traegt keine bRowversion - Lager/Notiz/Bezugsauftrag sind ungesichert, kein Schreibvorgang:`,
        bestellung,
      );
      meldeFehler(JTL_KOPF_OHNE_ROWVERSION_MELDUNG);
      return;
    }
    try {
      // Auftrag E, P1: `AbortSignal.timeout(...)` laesst diesen `fetch` spaetestens nach
      // `JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS` ABLEHNEN, statt fuer immer offen zu bleiben - siehe
      // Kopfkommentar an `letzteBekannteBRowversion`, "EIN PATCH, DER NIE ANTWORTET, IST DASSELBE
      // PROBLEM IN NEU". Die Ablehnung faellt unten in den generischen `catch`, NICHT in
      // `KonfliktFehler` - eigens erkannt und mit einer eigenen Meldung beantwortet.
      const neu = await jtlBestellkopfAendern(
        jtlNummer,
        kopf,
        bRowversion,
        AbortSignal.timeout(JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS),
      );
      if (typeof neu.bRowversion === "string") {
        // SOFORT, VOR `versende(...)` - siehe Kopfkommentar an `letzteBekannteBRowversion`: ein
        // sofort folgender zweiter Schreibvorgang derselben Bestellung muss diese Version schon
        // sehen, bevor React den daraus folgenden Renderzustand committet hat.
        letzteBekannteBRowversion.current.set(id, neu.bRowversion);
      } else {
        // Auftrag E, P2: der PATCH ist GELUNGEN (wir sind hinter `await jtlBestellkopfAendern`
        // ohne Fehler), aber diese Antwort traegt KEINE neue Version - der Stummelfall unten
        // ("DER STUMMEL OHNE `positionen`"). Die bisher gemerkte Version wurde vom Server durch
        // diesen Schreibvorgang bereits VERBRAUCHT; sie einfach stehenzulassen hiesse, dem
        // NAECHSTEN Schreibvorgang eine Version mitzugeben, die serverseitig laengst ueberholt ist
        // - der liefe deterministisch in einen ECHTEN 409 ("wurde von einem anderen Benutzer
        // geaendert"), obwohl niemand sonst die Bestellung angefasst hat (GEMESSEN, siehe Bericht).
        // Der Eintrag wird deshalb ENTFERNT, nicht stehengelassen, und die verbrauchte Version MARKIERT
        // (Fix-Runde 1 zu Task 7, W1): das Loeschen allein genuegte nur, solange ein gescheitertes
        // Nachladen eine Zeile OHNE Version in den Store schrieb. Seit `bestellungLaden` bei einem
        // JTL-Ausfall ablehnt (oder `null` ergibt), behaelt der Store seine volle Zeile - und mit ihr die
        // verbrauchte Version, auf die der Renderzustand-Rueckfall sonst zurueckfiele (echter 409).
        // Mit der Markierung gilt sie als unbekannt: der naechste Versuch lehnt mit der verstaendlichen
        // Meldung "OHNE `bRowversion` WIRD NICHT GESCHRIEBEN" oben ab, statt in einen falschen Konflikt
        // zu laufen. Bringt das Nachladen unten eine JTL-Version (auch dieselbe), gibt es die Markierung frei
        // und setzt die Karte neu.
        letzteBekannteBRowversion.current.delete(id);
        verbrauchteBRowversion.current.set(id, bRowversion);
      }
      if (!Array.isArray(neu.positionen)) {
        // Siehe Kopfkommentar `drawerJtlKopfAendern`, "DER STUMMEL OHNE `positionen`".
        try {
          const frisch = await bestellungLaden(id);
          if (frisch === null) {
            // Auftrag C7, Kleinigkeit 2: bis hierher VOELLIG STUMM - kein Nachladen, keine Meldung.
            // Der Nutzer sah seinen Entwurf weiter im Feld und hielt ihn faelschlich fuer den
            // Serverstand. Der Schreibvorgang IST gelungen (siehe Kopfkommentar oben, "DER STUMMEL
            // OHNE `positionen`") - dieselbe Meldung wie bei jedem anderen gescheiterten Nachladeweg
            // in dieser Datei (`NACHLADEN_FEHLGESCHLAGEN_MELDUNG`, siehe dessen Kopfkommentar).
            meldeNachladeSatz(NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
            return;
          }
          // Ein Frischlesen, das NACH dem gelungenen Schreiben gestartet wurde, ist massgeblich - auch wenn die Version gleich
          // bleibt: die Markierung der verbrauchten Version faellt weg (Fix-Runde 2, N1). Ohne diese Freigabe waere die
          // Bestellung bis zum Neuaufbau gesperrt, sobald ein gelungenes Nachladen dieselbe Version liefert. Nur ein
          // Nachladen MIT Version gibt frei; eine Zeile ohne `bRowversion` laesst die Markierung stehen.
          const frischeVersion = (frisch as { bRowversion?: unknown }).bRowversion;
          if (typeof frischeVersion === "string") {
            verbrauchteBRowversion.current.delete(id);
            letzteBekannteBRowversion.current.set(id, frischeVersion);
          }
          // Auftrag C7, Kleinigkeit 3: faellt die JTL-Anreicherung INNERHALB dieses Nachladens ebenfalls
          // aus, lehnt `bestellungLaden` seit Fehleranzeige Paket 2 (Task 7) ab (Kopfkommentar dort) und
          // der `catch` unten meldet es (R4: der Schreibvorgang gelang). Bis dahin lieferte es die reine
          // Hub-Zeile OHNE `internerKommentar`/`bRowversion`/`lagerName`, und der Reduzierer ERSETZTE den
          // Datensatz damit - das Notizfeld sah scheinbar geleert aus, OBWOHL JTL den Text laengst hielt.
          //
          // RICHTIGGESTELLT (Fix-Runde 1 zu Task 7, W1): der Store behaelt seit der Ablehnung seine volle
          // Zeile samt der verbrauchten `bRowversion`. Dass ein erneuter Schreibversuch trotzdem in die
          // verstaendliche Ablehnung oben ("OHNE `bRowversion` WIRD NICHT GESCHRIEBEN") laeuft und nicht in
          // einen ECHTEN 409 ("wurde von einem anderen Benutzer geaendert"), leistet die Markierung
          // `verbrauchteBRowversion` im `else`-Zweig oben (zusammen mit dem Loeschen aus der Karte),
          // nicht mehr das Fehlen der Version in der Zeile. Sie ist gesetzt, BEVOR dieser Block ueberhaupt
          // erreicht wird, und deckt so auch ein `null` (Zweig "frisch === null") und eine Ablehnung (`catch`)
          // ab; freigegeben hat sie oben nur ein Nachladen, das eine Version brachte.
          versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: frisch });
        } catch (fehler) {
          console.error(`Bestellung "${id}" konnte nach dem Schreiben nicht neu geladen werden:`, fehler);
          // R4 (R-Z2): geschrieben ist geschrieben - kein Wiederholungsrat der Ursache hinter dem Erfolgssatz.
          meldeNachladeFehler(fehler, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
        }
        return;
      }
      versende({
        art: "nachgeladen",
        entitaet: BESTELLUNG_ENTITAET,
        id,
        datensatz: zusammenfuehren(neu, bestellung),
      });
    } catch (fehler) {
      if (fehler instanceof KonfliktFehler) {
        // Siehe Kopfkommentar `drawerJtlKopfAendern`, "DER 409-FALL": bewusst OHNE `serverstand` -
        // "neu laden" holt den vollstaendigen Stand ueber `nachladenVormerken`, nicht den
        // unvollstaendigen 409-Koerper.
        versende({
          art: "konflikt",
          entitaet: BESTELLUNG_ENTITAET,
          id,
          geaendertVon: JTL_KOPF_KONFLIKT_AKTEUR,
        });
        return;
      }
      if (fehler instanceof DOMException && fehler.name === "TimeoutError") {
        // Auftrag E, P1: `AbortSignal.timeout(JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS)` oben laesst `fetch`
        // (`api/basis.ts#anfrage`) mit genau diesem Fehler ablehnen, wenn keine Antwort innerhalb der
        // Frist ankam - siehe Kopfkommentar an `letzteBekannteBRowversion` und `JTL_KOPF_ZEITLIMIT_
        // MELDUNG`. EIGENE Meldung statt der generischen unten: der Ausgang ist hier ungewiss (der
        // Server hat den Schreibvorgang womoeglich trotzdem ausgefuehrt), waehrend die generische
        // Meldung einen erkennbaren, abgeschlossenen Fehlschlag suggeriert.
        console.error(
          `Bestellung "${id}" konnte nicht innerhalb von ${JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS} ms in JTL geaendert werden (Zeitlimit):`,
          fehler,
        );
        meldeFehler(JTL_KOPF_ZEITLIMIT_MELDUNG);
        return;
      }
      console.error(`Bestellung "${id}" konnte nicht in JTL geaendert werden:`, fehler);
      meldeFehler(fehler, "Die Bestellung konnte nicht geändert werden.");
    }
  }

  /**
   * Schreibt kArtikel/cArtNr/cName EINER bestehenden Position - `Positionen.tsx#onArtikelTauschen`,
   * `PATCH /api/jtl/orders/:k/positions/:pos/artikel` (`api/jtlBestellungen.ts
   * #jtlPositionArtikelTauschen`). Design: docs/superpowers/specs/
   * 2026-09-01-artikeltausch-bestehende-position-design.md.
   *
   * Reiht sich in DIESELBE Schreibkette wie `drawerJtlKopfAendern` ein
   * (`schreibeKopfSerialisiert`/`letzteBekannteBRowversion`) - beide schreiben gegen dieselbe,
   * kopf-bRowversion-gesperrte JTL-Ressource (die Bestellung), ein Kopf-Schreibvorgang und ein
   * Artikeltausch derselben Bestellung dürfen deshalb nicht gleichzeitig mit derselben (dann
   * überholten) Version laufen.
   */
  function drawerArtikelTauschen(
    bestellungId: string,
    jtlPosId: number,
    aenderung: { kArtikel: number; cArtNr: string; cName: string | null },
  ): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === bestellungId);
    if (!bestellung) return;
    const jtlNummer = Number((bestellung as { jtlOrderId?: unknown }).jtlOrderId);
    if (!Number.isSafeInteger(jtlNummer) || jtlNummer <= 0) {
      console.error(`Bestellung "${bestellungId}" hat keine JTL-Nummer - Artikeltausch nicht moeglich:`, bestellung);
      meldeFehler("Die Position konnte nicht gespeichert werden.");
      return;
    }
    schreibeKopfSerialisiert(bestellungId, () =>
      fuehreArtikelTauschAus(bestellungId, jtlPosId, aenderung, jtlNummer, bestellung),
    );
  }

  /**
   * Gemeinsamer Schreibvorgang hinter ALLEN VIER `fuehre...Aus`-Funktionen der Positionsschreibkette
   * (Artikeltausch, Mengen-/Preisaenderung, neue Position, Loeschen). Extrahiert in der Fix-Welle
   * nach der finalen Zweig-Pruefung (2026-09-04, I1+I4): die vier Funktionen standen bis dahin
   * wortgleich nebeneinander (~230 Zeilen, mechanisch als identisch bis auf den API-Aufruf und drei
   * Substantive bestaetigt) - der I1-Fix (unten, der `ApiFehler`-Zweig mit Status 400) haette sonst
   * VIERMAL angebracht werden muessen.
   *
   * `aufruf` ist der eigentliche, bereits mit allen positionsspezifischen Parametern geschlossene
   * API-Aufruf - bekommt `bRowversion` und ein frisches Zeitlimit-Signal gereicht, liefert den
   * vollen, frisch aus JTL gelesenen Bestellkopf. `bezeichnung` traegt die beiden Textbausteine, die
   * sich je Aufrufer unterscheiden: `aktion` (das Substantiv fuer "X nicht moeglich"/"X ungesichert,
   * kein Schreibvorgang"/"X nicht innerhalb von N ms (Zeitlimit)"/"X fehlgeschlagen") und
   * `nachLadenPhrase` (traegt die Praeposition/den Artikel fuer "konnte <Phrase> nicht neu geladen
   * werden" GESONDERT, weil das grammatische Geschlecht wechselt: "nach dem Artikeltausch"/"nach der
   * Mengen-/Preisaenderung"/"nach der neuen Position"/"nach dem Loeschen" - ein einzelnes Substantiv
   * allein liesse sich nicht grammatikalisch korrekt einsetzen).
   *
   * Loest IMMER auf, nie ab - dasselbe Muster wie `fuehreKopfSchreibvorgangAus`.
   */
  async function fuehrePositionsSchreibvorgangAus(
    id: string,
    bestellung: Order,
    aufruf: (bRowversion: string, signal: AbortSignal) => Promise<JtlBestellungRoh>,
    bezeichnung: { aktion: string; nachLadenPhrase: string },
  ): Promise<void> {
    const bRowversion = schreibbareBRowversion(id, bestellung);
    if (bRowversion === undefined) {
      console.error(
        `Bestellung "${id}" traegt keine bRowversion - ${bezeichnung.aktion} ungesichert, kein Schreibvorgang:`,
        bestellung,
      );
      meldeFehler(JTL_KOPF_OHNE_ROWVERSION_MELDUNG);
      return;
    }
    try {
      const neu = await aufruf(bRowversion, AbortSignal.timeout(JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS));
      if (typeof neu.bRowversion === "string") {
        letzteBekannteBRowversion.current.set(id, neu.bRowversion);
      } else {
        // Derselbe Grund wie in `fuehreKopfSchreibvorgangAus` ("Auftrag E, P2"): der Schreibvorgang
        // IST gelungen, aber diese Antwort traegt keine neue Version - die bisher gemerkte ist vom
        // Server bereits VERBRAUCHT. Stehenlassen liefe im naechsten Schreibvorgang derselben
        // Bestellung deterministisch in einen falschen 409 - deshalb geloescht UND markiert (W1, siehe
        // `verbrauchteBRowversion`): der Store behaelt bei gescheitertem Nachladen seine volle Zeile mit
        // dieser Version.
        letzteBekannteBRowversion.current.delete(id);
        verbrauchteBRowversion.current.set(id, bRowversion);
      }
      if (!Array.isArray(neu.positionen)) {
        // DER STUMMEL OHNE `positionen`: `backend/src/http/routes/jtlSchreiben.ts:828-834`
        // antwortet bei gescheitertem Ruecklesen NACH erfolgreichem Schreiben mit
        // `{ kLieferantenBestellung }` - ohne `positionen`. `zusammenfuehren(neu, bestellung)`
        // liefe dann in `bezahlteSkusJeBestellung(jtl.positionen)` (Iteration ueber `undefined`)
        // und wuerfe einen TypeError, der unten im generischen `catch` landete - ein Toast
        // "konnte nicht gespeichert werden" fuer einen Schreibvorgang, der TATSAECHLICH gelungen
        // ist. Dasselbe Muster wie `fuehreKopfSchreibvorgangAus`: statt `zusammenfuehren` hier
        // ein volles Nachladen ueber `bestellungLaden`.
        try {
          const frisch = await bestellungLaden(id);
          if (frisch === null) {
            meldeNachladeSatz(NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
            return;
          }
          // Wie im Kopfweg (N1): das Frischlesen nach dem Schreiben gibt die Version frei. Hier ist es der Regelfall, dass sie
          // gleich bleibt - `spLieferantenBestellungPos*` aendern `dbo.tLieferantenBestellung` nicht -, und die Markierung
          // sperrte die Bestellung sonst fuer jede weitere Kopf- und Positionsaenderung. Nur mit Version.
          const frischeVersion = (frisch as { bRowversion?: unknown }).bRowversion;
          if (typeof frischeVersion === "string") {
            verbrauchteBRowversion.current.delete(id);
            letzteBekannteBRowversion.current.set(id, frischeVersion);
          }
          versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: frisch });
        } catch (fehler) {
          console.error(
            `Bestellung "${id}" konnte ${bezeichnung.nachLadenPhrase} nicht neu geladen werden:`,
            fehler,
          );
          meldeNachladeFehler(fehler, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
        }
        return;
      }
      versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: zusammenfuehren(neu, bestellung) });
    } catch (fehler) {
      if (fehler instanceof KonfliktFehler) {
        versende({ art: "konflikt", entitaet: BESTELLUNG_ENTITAET, id, geaendertVon: JTL_KOPF_KONFLIKT_AKTEUR });
        return;
      }
      if (fehler instanceof DOMException && fehler.name === "TimeoutError") {
        console.error(
          `Bestellung "${id}" - ${bezeichnung.aktion} nicht innerhalb von ${JTL_KOPF_SCHREIBEN_ZEITLIMIT_MS} ms (Zeitlimit):`,
          fehler,
        );
        meldeFehler(JTL_KOPF_ZEITLIMIT_MELDUNG);
        return;
      }
      // I1 (Fix-Welle nach der finalen Zweig-Pruefung, 2026-09-04): eine FACHLICHE Ablehnung des
      // Backend-Riegels (Status 400 - z. B. "Position 3 traegt bereits eine gelieferte Menge (40) -
      // ..." oder die Mengenuntergrenze/der Verknuepfungsfehler) ist KEIN Konflikt und KEIN
      // Zeitlimit, sondern eine ECHTE Erklaerung, die der Bediener sehen MUSS - die Design-Spec
      // verlangt woertlich "Sperrmeldung ... als Klartext im Positions-Panel (keine
      // Konflikt-Behandlung, sondern eine fachliche Ablehnung mit Erklaertext)". Hausmuster wie
      // `jtlLoeschenBestaetigt` oben ("Review-Fund Important #2 zu Task 12"). Seit der Fehleranzeige
      // (Paket 1) liefert der Uebersetzer (`fehlertext`) den Erklaertext des Servers hinter dem Kontext;
      // die rohe `.message` wird nicht mehr angezeigt. Ein `ApiFehler` mit Status 400 -
      // `KonfliktFehler`/`SitzungsFehler` sind zwar auch `ApiFehler`, aber mit Status 409/401 und
      // bereits oben bzw. in der Anmeldemaske behandelt.
      if (fehler instanceof ApiFehler && fehler.status === 400) {
        console.error(`Bestellung "${id}" - ${bezeichnung.aktion} fachlich abgelehnt:`, fehler);
        meldeFehler(fehler, "Die Position konnte nicht gespeichert werden.");
        return;
      }
      console.error(`Bestellung "${id}" - ${bezeichnung.aktion} fehlgeschlagen:`, fehler);
      meldeFehler(fehler, "Die Position konnte nicht gespeichert werden.");
    }
  }

  /** Der eigentliche Schreibvorgang hinter `drawerArtikelTauschen` - ruft die gemeinsame
   *  `fuehrePositionsSchreibvorgangAus` (oben) mit dem Artikeltausch-Aufruf und seinen
   *  Textbausteinen. */
  async function fuehreArtikelTauschAus(
    id: string,
    jtlPosId: number,
    aenderung: { kArtikel: number; cArtNr: string; cName: string | null },
    jtlNummer: number,
    bestellung: Order,
  ): Promise<void> {
    return fuehrePositionsSchreibvorgangAus(
      id,
      bestellung,
      (bRowversion, signal) => jtlPositionArtikelTauschen(jtlNummer, jtlPosId, aenderung, bRowversion, signal),
      { aktion: "Artikeltausch", nachLadenPhrase: "nach dem Artikeltausch" },
    );
  }

  /** Menge/Preis EINER bestehenden Position - `Positionen.tsx#onMengePreisAendern`. Design:
   *  docs/superpowers/specs/2026-09-04-positionen-crud-design.md. Reiht sich in dieselbe
   *  Schreibkette wie `drawerArtikelTauschen` ein (`schreibeKopfSerialisiert`). */
  function drawerMengePreisAendern(
    bestellungId: string,
    jtlPosId: number,
    aenderung: { qty?: number; price?: number },
  ): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === bestellungId);
    if (!bestellung) return;
    const jtlNummer = Number((bestellung as { jtlOrderId?: unknown }).jtlOrderId);
    if (!Number.isSafeInteger(jtlNummer) || jtlNummer <= 0) {
      console.error(`Bestellung "${bestellungId}" hat keine JTL-Nummer - Mengen-/Preisaenderung nicht moeglich:`, bestellung);
      meldeFehler("Die Position konnte nicht gespeichert werden.");
      return;
    }
    schreibeKopfSerialisiert(bestellungId, () =>
      fuehreMengePreisAenderungAus(bestellungId, jtlPosId, aenderung, jtlNummer, bestellung),
    );
  }

  /** Ruft `fuehrePositionsSchreibvorgangAus` (siehe dort) mit dem Mengen-/Preisaenderungs-Aufruf. */
  async function fuehreMengePreisAenderungAus(
    id: string,
    jtlPosId: number,
    aenderung: { qty?: number; price?: number },
    jtlNummer: number,
    bestellung: Order,
  ): Promise<void> {
    return fuehrePositionsSchreibvorgangAus(
      id,
      bestellung,
      (bRowversion, signal) => jtlPositionMengePreisAendern(jtlNummer, jtlPosId, aenderung, bRowversion, signal),
      { aktion: "Mengen-/Preisaenderung", nachLadenPhrase: "nach der Mengen-/Preisaenderung" },
    );
  }

  /**
   * Neue Position an einer bestehenden Bestellung - `Positionen.tsx#onPositionHinzufuegen`. `cName`
   * kommt aus `Positionen.tsx` als `string | null` (Typ des unveraendert durchgereichten Rueckrufs,
   * `PositionenProps.onPositionHinzufuegen`), `jtlPositionHinzufuegen` (`api/jtlBestellungen.ts`)
   * verlangt dagegen ein Pflichtfeld `cName: string` - der `uebernehmen`-Zweig in `Positionen.tsx`
   * setzt einen Kandidaten ohnehin nur bei einem echten, getrimmten Namen (`cName !== ""`), `null`
   * kommt hier also praktisch nie an. Der Fallback auf `""` unten macht das trotzdem TYPSICHER, ohne
   * die (absichtlich weiter gefasste) Prop-Signatur zu verengen.
   */
  function drawerPositionHinzufuegen(
    bestellungId: string,
    neuePosition: {
      kArtikel: number; cArtNr: string; cName: string | null;
      qty: number; price: number; mwstProzent: number;
    },
  ): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === bestellungId);
    if (!bestellung) return;
    const jtlNummer = Number((bestellung as { jtlOrderId?: unknown }).jtlOrderId);
    if (!Number.isSafeInteger(jtlNummer) || jtlNummer <= 0) {
      console.error(`Bestellung "${bestellungId}" hat keine JTL-Nummer - neue Position nicht moeglich:`, bestellung);
      meldeFehler("Die Position konnte nicht gespeichert werden.");
      return;
    }
    schreibeKopfSerialisiert(bestellungId, () =>
      fuehrePositionHinzufuegenAus(bestellungId, neuePosition, jtlNummer, bestellung),
    );
  }

  /** Ruft `fuehrePositionsSchreibvorgangAus` (siehe dort) mit dem Anlage-Aufruf. `cName ?? ""` siehe
   *  Kopfkommentar an `drawerPositionHinzufuegen` oben. */
  async function fuehrePositionHinzufuegenAus(
    id: string,
    neuePosition: {
      kArtikel: number; cArtNr: string; cName: string | null;
      qty: number; price: number; mwstProzent: number;
    },
    jtlNummer: number,
    bestellung: Order,
  ): Promise<void> {
    return fuehrePositionsSchreibvorgangAus(
      id,
      bestellung,
      (bRowversion, signal) =>
        jtlPositionHinzufuegen(jtlNummer, { ...neuePosition, cName: neuePosition.cName ?? "" }, bRowversion, signal),
      { aktion: "neue Position", nachLadenPhrase: "nach der neuen Position" },
    );
  }

  /** Eine bestehende Position loeschen - `Positionen.tsx#onPositionLoeschen`. */
  function drawerPositionLoeschen(bestellungId: string, jtlPosId: number): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === bestellungId);
    if (!bestellung) return;
    const jtlNummer = Number((bestellung as { jtlOrderId?: unknown }).jtlOrderId);
    if (!Number.isSafeInteger(jtlNummer) || jtlNummer <= 0) {
      console.error(`Bestellung "${bestellungId}" hat keine JTL-Nummer - Loeschen nicht moeglich:`, bestellung);
      meldeFehler("Die Position konnte nicht gespeichert werden.");
      return;
    }
    schreibeKopfSerialisiert(bestellungId, () =>
      fuehrePositionLoeschenAus(bestellungId, jtlPosId, jtlNummer, bestellung),
    );
  }

  /** Ruft `fuehrePositionsSchreibvorgangAus` (siehe dort) mit dem Loesch-Aufruf. */
  async function fuehrePositionLoeschenAus(
    id: string,
    jtlPosId: number,
    jtlNummer: number,
    bestellung: Order,
  ): Promise<void> {
    return fuehrePositionsSchreibvorgangAus(
      id,
      bestellung,
      (bRowversion, signal) => jtlPositionLoeschen(jtlNummer, jtlPosId, bRowversion, signal),
      { aktion: "Loeschen", nachLadenPhrase: "nach dem Loeschen" },
    );
  }

  const [drawerZahlungszielSetzen, drawerPlanIndividualisieren, drawerPlanSpeichern] = useDrawerZahlungsplan(
    zustand.bestellungen, versende, zahlungsbearbeitungNachladenNoetigRef,
    zahlungsbearbeitungSperren, zahlungsbearbeitungFreigeben, istEingehaengt, drawerRef, drawerSchliessen,
    NACHLADEN_FEHLGESCHLAGEN_MELDUNG, zeigeMeldung,
  );

  function drawerZahlstatusZuruecksetzen(id: string): void {
    if (zahlungsbearbeitungNachladenNoetigRef.current.has(id)) return;
    zahlungsbearbeitungSperren(id);
    void zahlstatusZuruecksetzen(id)
      .then(
        (ergebnis) => {
          // Hinweis des Servers (z. B. zentrale Vorlage unbrauchbar) im Fehlerstapel, VOR dem Nachladen: er gehoert zum
          // gelungenen Reset und darf nicht verschwinden, wenn das Nachladen scheitert (G8).
          if (ergebnis?.warnung) meldeFehler(ergebnis.warnung);
          return bestellungLaden(id).then(
            (neu) => {
              if (neu !== null) {
                versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
                zahlungsbearbeitungFreigeben(id);
                return;
              }
              console.error(`Bestellung "${id}" konnte nach dem Zahlstatus-Reset nicht geladen werden.`);
              meldeNachladeSatz(NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
            },
            (nachladeFehler: unknown) => {
              console.error(`Bestellung "${id}" konnte nach dem Zahlstatus-Reset nicht geladen werden:`, nachladeFehler);
              meldeNachladeFehler(nachladeFehler, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
            },
          );
        },
        (fehler: unknown) => {
        zahlungsbearbeitungFreigeben(id);
        console.error(`Zahlstatus der Bestellung "${id}" konnte nicht zurückgesetzt werden:`, fehler);
        meldeFehler(fehler, "Der Zahlstatus konnte nicht zurückgesetzt werden.");
        },
      );
  }

  /** Die lesbare Kennung (Bestellnummer, sonst die Id) zu einer Bestellungs-Id: der Bezug der Beleg-Hinweise. */
  const bestellKennungZu = (bestellungId: string): string => {
    const bestellung = zustand.bestellungen.find((b) => String(b.id) === bestellungId);
    return bestellung ? kennungVon(bestellung, bestellungId) : bestellungId;
  };

  const [drawerRatenSynchronisieren, istZahlungHandler] = useDrawerZahlungen(
    versende, zahlungsbearbeitungNachladenNoetigRef,
    NACHLADEN_FEHLGESCHLAGEN_MELDUNG, zeigeHinweis, bestellKennungZu,
    drawer?.id ?? null, zustand.bestellungen,
  );

  const {
    drawerAufgabeAnlegen, drawerAufgabeAendern, drawerAufgabeLoeschen, drawerAufgabenSammelLoeschen,
    drawerPhaseErledigen, drawerVorlageAnwenden, drawerZahlungAnstossen,
  } = useDrawerTasks({
    drawerId: drawer?.id ?? null, bestellungen: zustand.bestellungen, rechte: zustand.rechte,
    versende, zeigeMeldung, drawerAendern, drawerArchivUmschalten,
    nachladeFehlerMeldung: NACHLADEN_FEHLGESCHLAGEN_MELDUNG,
  });

  /**
   * `moveOrder(o.id, value)` (index.html:7764) - `onStatus` des Reiters "Bestellung"
   * (`MainTab/BestellungTab.tsx`, Abschnitt `main-status`).
   *
   * Verdrahtet am 2026-08-15. Bis dahin war das die aelteste der fuenf benannten Luecken dieses
   * Reiters (Fix-Runde zu Task 5): das `<select>` stand seit Task 5 gezeichnet und nicht gesperrt im
   * Drawer, `App.tsx` reichte `onStatus` aber nicht durch - acht waehlbare Status, und die Wahl fiel
   * ins Leere. Zusammen mit dem ebenfalls unverdrahteten `onBearbeiten` des Boards
   * (`BoardSeite`-Aufruf unten, weiterhin offen) gab es damit UEBERHAUPT keinen Weg, den Status zu
   * setzen: das Ziehen zwischen den Board-Gruppen schreibt `boardGroupOverride`, nicht den Status.
   *
   * EIGENER KANAL, nicht `drawerAendern`: jeder Wechsel braucht zusaetzlich einen
   * `order_history`-Eintrag (`setzeBoardStatus`, `backend/src/domaene/extras.ts`), den
   * `feldgruppenHandler`/`schreibeExtras` nicht leisten. `board_status` steht in `feldgruppen.ts`
   * deshalb in `NICHT_SCHREIBBAR` und hat mit `PATCH /api/orders/:id/board-status` einen eigenen
   * Endpunkt; `gruppeUndFelder` wiese den Feldgruppenweg ab (`KEIN_FELDGRUPPEN_WEG`, Eintrag
   * `boardStatus`). Der Riegel wird also nicht umgangen, sondern auf seinem eigenen Weg bedient.
   *
   * Die VERSION ist dennoch dieselbe wie bei `drawerAendern`: `setzeBoardStatus` schreibt gegen
   * `order_extras.row_version`, NICHT gegen die Kopfversion - samt Rueckfall `0`, siehe die
   * ausfuehrliche Begruendung in `drawerAendern` oben (hier nicht wiederholt, damit die Stellen
   * nicht auseinanderlaufen).
   *
   * KEINE Rechtepruefung hier: `Drawer.tsx` sperrt beim Reiterwechsel jedes Bedienelement des
   * Reiters, wenn `darfTabBearbeiten(rechte, "main")` falsch ist (`drawerRechte.ts`: main ->
   * `edit_orders`) - die Auswahl ist davon erfasst, nachgewiesen in
   * `test/komponenten/App/verdrahtungDrawer.test.tsx`. Das Backend verlangt dasselbe Recht ein
   * zweites Mal (`requireEndpunkt`), die Sperre ist also Anzeige und nicht der Riegel.
   *
   * Eigene Meldung statt der Sammelmeldung aus `drawerAendern`: hier ist genau EIN Feld im Spiel,
   * die Meldung darf das sagen.
   */
  function drawerStatusAendern(id: string, status: string): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === id);
    if (!bestellung) return;
    const extrasRowVersion = Number((bestellung as { extrasRowVersion?: unknown }).extrasRowVersion) || 0;
    boardStatusAendern(id, status, extrasRowVersion)
      .then((neu) => {
        versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
      })
      .catch((fehler: unknown) => {
        // siehe `drawerAendern` oben - derselbe Grund: der Fehler selbst ins Protokoll, damit ein
        // 409 (Versionskonflikt) vom 404 (keine order_extras-Zeile) unterscheidbar bleibt.
        console.error(`Status der Bestellung "${id}" konnte nicht geaendert werden:`, fehler);
        // R4 (Ruling R-T7a): der PATCH kam an, nur das Nachladen scheiterte - der Status IST gesetzt. Die neue
        // `extrasRowVersion` aus dem Fehler uebernimmt hier (wie bei `drawerAendern`/`boardGruppeAendern`) niemand in den
        // Store; die Meldung raet deshalb zum Neuladen.
        if (fehler instanceof NachladenNachSchreibenFehler) {
          meldeNachladeFehler(fehler.ursache, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
        } else {
          meldeFehler(fehler, "Der Status konnte nicht geändert werden.");
        }
      });
  }

  /**
   * Schreibt EINE Position der offenen Bestellung - `EkTab.tsx#onPositionAendern`, neu am
   * 2026-08-13. Eigener Kanal neben `drawerAendern`, weil `order_item_extras` eine eigene Ressource
   * mit einer EIGENEN `rowVersion` je Position ist (zusammengesetzter Schluessel order_id +
   * jtl_pos_id); die Bestellversion sagt ueber sie nichts aus.
   *
   * Nachgeladen wird die GANZE Bestellung, nicht die Position: der Store fuehrt Positionen nicht als
   * eigene Entitaet, sondern als `items[]` an der Bestellung - dieselbe Nachlade-Form wie bei
   * `drawerDokumentHochladen`. Das Backend sendet aus demselben Grund `entity: "order"` als
   * Ereignis (`http/routes/orders.ts:367-373`).
   *
   * `sortIndex` (Task 11, optional): unveraendert an `positionAendern` durchgereicht - siehe dort.
   *
   * `PositionsfeldNichtSchreibbarFehler` wirft SYNCHRON, also vor dem Promise - deshalb der
   * try/catch um den Aufruf herum und nicht nur ein `.catch()`. Ohne ihn liefe ein unbekannter
   * Feldname (etwa `qty`, das die EK-Tabelle anbietet, das aber in JTL lebt) als unbehandelte
   * Ausnahme durch das Ereignis und riss die Oberflaeche mit.
   *
   * ================= DER ID-WECHSEL (Task 11) =================
   *
   * `bestellungId` ist bei einer rein JTL-gefuehrten Bestellung die ABGELEITETE Kennung `jtl-<n>`
   * (`api/jtlBestellungen.ts#abgeleiteteKennung`) - es gibt (noch) keine Hub-Zeile. Der PATCH oben
   * LEGT SIE AN, WENN NOETIG (`loeseKennungZumSchreibenAuf`, `backend/src/domaene/bestellungKennung.ts`),
   * und ab da traegt dieselbe Bestellung eine ECHTE uuid - `bestellungLaden(bestellungId)` findet die
   * Zeile weiterhin (der Leseweg loest `jtl-<n>` ueber `jtl_order_id` auf, unabhaengig von der
   * eigentlichen `id`), aber das Ergebnis `neu.id` ist jetzt eine ANDERE Zeichenkette als
   * `bestellungId`.
   *
   * GEMESSEN, nicht angenommen: `mitErsetztemDatensatz`/`listeErsetzen` (`state/reduzierer.ts`) sucht
   * den zu ersetzenden Eintrag ueber `verweis.id` - hier bewusst weiterhin `bestellungId` (die ALTE
   * Kennung, unter der der Datensatz im Store steht), NICHT `neu.id`. Das trifft den vorhandenen
   * Eintrag am selben Index und ERSETZT ihn (kein zweiter, doppelter Eintrag unter der neuen Kennung -
   * `listeErsetzen` haengt nur an, wenn der gesuchte Index NICHT gefunden wird).
   *
   * Zwei weitere Stellen fuehren dieselbe alte Kennung aber WEITER, und OHNE das Nachziehen unten
   * reisst das den Drawer lautlos ab - GEPRUEFT durch Zuschauen, nicht angenommen:
   *
   *   `drawer` (lokaler `useState` oben)         `offeneBestellung = zustand.bestellungen.find(b =>
   *                                               b.id === drawer.id)` (weiter unten) findet nach dem
   *                                               Ersetzen NICHTS mehr unter der alten `jtl-<n>`-
   *                                               Kennung - der Drawer verschwindet, OHNE dass
   *                                               `drawerSchliessen()` je lief. Der zweite Klick auf
   *                                               dasselbe Kaestchen faende gar keine Zeile mehr vor.
   *   `zustand.geoeffnet` (Store, via `versende({art:"geoeffnet"})` bei `drawerOeffnen`)
   *                                               haelt die SSE-Sonderbehandlung fuer "der offene
   *                                               Datensatz wird nicht automatisch nachgeladen,
   *                                               sondern als Konflikt vorgemerkt" scharf
   *                                               (`reduzierer.ts`, Fall `sse`, Fall 2). Broadcasts zu
   *                                               dieser Bestellung tragen ab jetzt die NEUE uuid
   *                                               (`orders.ts` broadcastet mit der aufgeloesten `id`) -
   *                                               bliebe `geoeffnet.id` auf der alten Kennung stehen,
   *                                               griffe die Sonderbehandlung nie wieder fuer diesen
   *                                               offenen Datensatz.
   *
   * Beide werden NUR nachgezogen, wenn sie im Moment des Nachladens WIRKLICH noch die alte Kennung
   * tragen - ein zwischenzeitliches Schliessen des Drawers oder ein Wechsel auf eine andere Bestellung
   * darf nicht rueckgaengig gemacht werden. TIEFLINKS (Aufgabe 3): `drawer` selbst ist seither aus der
   * Adresse ABGELEITET (`ansicht.bestellung.kennung`, siehe Modulkopf); nachgezogen wird deshalb die
   * ADRESSE, ueber die FUNKTIONALE Form von `setzeAnsicht` (liest den WIRKLICH aktuellen Stand,
   * `useAnsicht.ts#aktuellerStand`, nicht den Stand des Closures beim Aufruf) - genau die Kennung, mit
   * der `drawerOeffnen` diesen Drawer geoeffnet hat, ist hier die JTL-abgeleitete `jtl-<n>`-Form (kein
   * `name` bis zum ersten Schreiben, § 3.7), sonst haette sich der Drawer laengst selbst geheilt.
   * `geoeffnet` prueft es im Reduzierer selbst (`bestellungIdGewechselt`, `state/reduzierer.ts`) - aus
   * demselben Grund: nur der Reduzierer kennt den Stand im Moment der Aktion, nicht das (womoeglich
   * veraltete) `zustand` dieses Funktionsabschlusses.
   */
  function drawerPositionAendern(
    bestellungId: string,
    jtlPosId: number,
    aenderung: Record<string, unknown>,
    rowVersion: number,
    sortIndex?: number,
  ): void {
    // R4 (Fehleranzeige Paket 1): scheitert erst das Nachladen NACH dem gelungenen PATCH, ist die Position
    // gespeichert - die Meldung sagt dann "neu laden" statt "konnte nicht gespeichert werden".
    let gespeichert = false;
    try {
      positionAendern(bestellungId, jtlPosId, aenderung, rowVersion, sortIndex)
        .then(() => {
          gespeichert = true;
          return bestellungLaden(bestellungId);
        })
        .then((neu) => {
          if (neu) {
            versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id: bestellungId, datensatz: neu });
            const neueId = neu.id as string | undefined;
            if (typeof neueId === "string" && neueId !== bestellungId) {
              // Billige Vorpruefung (Befund "Gering" der Pruefrunde 1) - vermeidet die Ersetz-
              // Navigation im HAEUFIGEN Nichtstun-Fall (eine ANDERE Bestellung ist offen, oder gar
              // keine). Die eigentliche Korrektheit liegt weiterhin in der Funktionsform unten.
              if (aktuelleKennungRef.current === bestellungId) {
                setzeAnsicht(
                  (alt) =>
                    alt.bestellung && alt.bestellung.kennung === bestellungId
                      ? { bestellung: { ...alt.bestellung, kennung: neueId } }
                      : {},
                  { ersetzen: true },
                );
              }
              versende({ art: "bestellungIdGewechselt", alteId: bestellungId, neueId });
            }
          }
        })
        .catch((fehler: unknown) => {
          console.error("Position konnte nicht gespeichert werden:", fehler);
          if (gespeichert) meldeNachladeFehler(fehler, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
          else meldeFehler(fehler, "Die Position konnte nicht gespeichert werden.");
        });
    } catch (fehler) {
      // `PositionsfeldNichtSchreibbarFehler` (synchron): ein Programmierfehler mit Entwicklertext, nicht fuer den
      // Nutzer - der Uebersetzer macht daraus den allgemeinen Satz, das Original steht im Protokoll.
      console.error("Position konnte nicht gespeichert werden:", fehler);
      meldeFehler(fehler, "Die Position konnte nicht gespeichert werden.");
    }
  }

  const [dokumentBestellungNachladen, drawerDokumentHochladen] = useDrawerDokumente(
    versende,
  );

  /**
   * Der Dialog „Zollinstruktionen anstoßen“ hat an der Bestellung erzeugt (`SpeditionTab.onZollErzeugt`, Plan „Zollinstruktionen
   * im Hub“, Task 11): die Datei liegt unter Dokumente → Zollanweisung, und der Task „Zollinstruktionen an Spedition senden
   * (Outlook)“ ist abgehakt - beides am Server, in einem Schritt. EINE Nachladung der ganzen Bestellung (`bestellungLaden`, ein
   * Dispatch `nachgeladen`) bringt `pdfs` UND `tasks` in den Store: Plakette im Reiter, „Erneut anstoßen“ und die Board-Spalte
   * ziehen daraus nach. Zwei Nachladungen (Dokumente, dann Aufgaben) waeren zwei Dispatches derselben Bestellung, bei denen der
   * spaetere gewinnt (AGENTS.md, „Store-Wettlauf“) - hier unnoetig, weil die Antwort ohnehin beides traegt.
   *
   * Scheitert nur das Nachladen, ist alles gespeichert (R4): der Satz sagt es, die Ursache haengt `meldeNachladeFehler` nur an,
   * wenn sie nicht zum Wiederholen raet - sonst erzeugte der Nutzer die Datei ein zweites Mal.
   */
  function zollErzeugt(bestellungId: string): void {
    dokumentBestellungNachladen(bestellungId).catch((fehler: unknown) => {
      console.error(`Bestellung "${bestellungId}" konnte nach den Zollinstruktionen nicht neu geladen werden:`, fehler);
      meldeNachladeFehler(fehler, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
    });
  }

  /**
   * `transferToJtl()` (`index.html:9964`). BIS ZUM 2026-08-15 schickte diese Funktion
   * `POST /api/jtl/orders` mit dem Koerper `{ id }` und verschluckte jeden Fehler:
   *
   *     anfrage("/api/jtl/orders", { methode: "POST", koerper: { id } })
   *       .catch(() => zeigeMeldung("Die Übertragung nach JTL ist noch nicht verfügbar."));
   *
   * Beides war falsch. Der Endpunkt verlangt `kLieferant`/`lagerId`/`lieferdatum`/`waehrung`/
   * `positionen` - `{ id }` endete zwingend in einem 400. Und der Kommentar, der hier stand
   * ("existiert im Backend NOCH NICHT"), stimmte seit Phase 5 nicht mehr; nachgezogen wurde das
   * Frontend nie. Sichtbar war davon nichts ausser der Meldung "noch nicht verfügbar" - der
   * Grund, aus dem am 2026-08-14 zwei Bestellungen im Hub standen und nie in JTL ankamen.
   *
   * Ein ehrlicher Nachtrag ist nicht moeglich: `POST /api/orders` speichert von der Maske nur den
   * Lieferanten (siehe `ANLAGEMASKE_VERWORFENE_FELDER`), einer so entstandenen Bestellung fehlen
   * also Lager, Lieferdatum UND alle Positionen - genau die Pflichtangaben von JTL. Statt einen
   * Aufruf abzusetzen, der nur scheitern kann, sagt diese Funktion, was Sache ist. Neue
   * Bestellungen gehen seit heute direkt aus der Anlagemaske nach JTL (`neueBestellungAnlegen`).
   */
  /**
   * `EkTab.tsx#onJtlUebertragen` - der Knopf "In JTL übertragen" im EK-Reiter.
   *
   * Er zieht den Artikelstamm auf die Preise DIESER Bestellung nach: für jede Position wird
   * `dbo.tliefartikel.fEKNetto` beim Lieferanten der Bestellung auf den Belegpreis gesetzt
   * (Entscheidung des Auftraggebers, 2026-08-15). Nicht der kalkulierte Landed Cost - der steht in
   * EUR, während der Lieferantenartikel bei 981 von 997 offenen Positionen in USD geführt wird.
   *
   * DER AUFRUF SENDET KEINE PREISE. Er nennt nur die Bestellung; die Beträge liest das Backend aus
   * dem Beleg. Damit gibt es keinen Weg, über diesen Knopf einen frei gewählten Wert in den
   * produktiven Artikelstamm zu bringen - auch nicht über ein manipuliertes Formular.
   *
   * Bis zum 2026-08-15 stand hier nur eine Meldung, dieser Bestellung fehlten "Lager, Lieferdatum
   * und Positionen". Das galt, solange das Board aus der tool-eigenen Tabelle kam; seit der
   * Umstellung auf Echtzeit-Lesen liegen alle drei vor.
   */
  async function drawerJtlUebertragen(bestellung: Order): Promise<void> {
    const kennung = Number(bestellung.jtlOrderId);
    if (!Number.isSafeInteger(kennung) || kennung < 1) {
      meldeFehler(
        "Diese Bestellung hat keine JTL-Nummer - ihr Einkaufspreis lässt sich nicht übertragen.",
      );
      return;
    }
    try {
      const ergebnis = await anfrage<{
        geschrieben: number;
        zurueckgenommen: boolean;
        grund: string | null;
        unveraendert: number;
      }>(`/api/jtl/orders/${kennung}/ek`, { methode: "POST", koerper: {} });
      // Nur die Form des Trockenlaufs: bei einer echten Ruecknahme antwortet der Server mit 409
      // (`backend/src/http/routes/jtlSchreiben.ts`), das faengt das `catch` unten; ein 200 mit `zurueckgenommen: true` gibt
      // es nur beim Trockenlauf, und den schickt dieser Aufrufer nie (`koerper: {}`). Der Zweig bleibt als Spiegel des
      // Bestands: kaeme eine solche Antwort doch, waere die Absicht NICHT ausgefuehrt und die Bestaetigung unten falsch -
      // der Grund geht dann in den Fehlerstapel.
      if (ergebnis.zurueckgenommen) {
        const kontext = "Die Einkaufspreise wurden nicht nach JTL übertragen.";
        if (ergebnis.grund) meldeFehler(ergebnis.grund, kontext);
        else meldeFehler(kontext);
        return;
      }
      // Beide Zahlen nennen, auch die Null: "0 übertragen, 52 unverändert" ist eine Auskunft,
      // "nichts passiert" wäre eine Ratlosigkeit.
      zeigeMeldung(
        `Einkaufspreise nach JTL übertragen: ${ergebnis.geschrieben} geändert, ` +
          `${ergebnis.unveraendert} bereits aktuell.`,
      );
    } catch (fehler) {
      meldeFehler(fehler, "Die Einkaufspreise konnten nicht nach JTL übertragen werden.");
    }
  }

  /**
   * `module` von `TasksTabProps`/`ZahlungTabProps` - Ableitung entspricht `activeTaskTemplateEntity()
   * .responsibilityModules` (`index.html:5506-5508, 8834-8836`). VEREINFACHT gegenueber dem Bestand
   * (siehe Task-Bericht): der Bestand normalisiert die gefundene Vorlage (`normalizeTaskTemplateEntity`)
   * und faellt ohne eine gespeicherte Vorlage auf eine eingebaute Standardvorlage samt
   * `DEFAULT_RESPONSIBILITY_MODULES` zurueck (`defaultTaskTemplateEntity`) - beide sind in diesem Baum
   * NICHT portiert (kein `lib/`-Modul fuehrt `DEFAULT_RESPONSIBILITY_MODULES`/`DEFAULT_ORDER_TASK_PHASES`-
   * gestuetzte Vorlagen-Normalisierung; das ist eine eigene Portierungsaufgabe, nicht Teil dieses
   * Tasks). Hier deshalb die reine Ableitung ohne Normalisierung/Rueckfallvorlage: die Vorlage mit
   * `id === "default-order-task-template"` aus `zustand.taskVorlagen` (Task 1, `GET
   * /api/task-templates`), deren `responsibilityModules` - oder `[]`, wenn (noch) keine Vorlage mit
   * dieser id geladen ist.
   */
  const taskVorlagenModule: readonly ResponsibilityModule[] = useMemo(() => {
    const vorlage = (zustand.taskVorlagen as ReadonlyArray<{ id?: unknown; responsibilityModules?: unknown }>).find(
      (t) => t && typeof t === "object" && t.id === "default-order-task-template",
    );
    return Array.isArray(vorlage?.responsibilityModules)
      ? (vorlage.responsibilityModules as ResponsibilityModule[])
      : [];
  }, [zustand.taskVorlagen]);

  /**
   * `jtlArtikelSuche` von `BestellungTabProps` (Task-Brief: "wird aus `api/jtlArtikelSuche.ts#jtlSuche`
   * erzeugt"). `useMemo` mit leerer Abhaengigkeitsliste: `jtlSuche(...)` haelt Debounce-Timer, Cache
   * und laufende Anfragen in seinem Closure (siehe dortiger Kopfkommentar) - eine neue Instanz je
   * Zeichnen wuerfe Cache und Debounce bei jedem Tastendruck weg. `grenze` (Top-50, serverseitig)
   * reist NICHT mit: `GET /api/jtl/articles` (`backend/src/http/routes/jtl.ts`) kennt ausschliesslich
   * den Suchparameter `q`, keinen Trefferlimit-Parameter - die Begrenzung liegt serverseitig bereits
   * in `sucheArtikel`.
   */
  const jtlArtikelSuche = useMemo(
    () =>
      jtlSuche<JtlArtikel>({
        abruf: (begriff, { signal, kontext }) =>
          anfrage<JtlArtikel[]>(
            `/api/jtl/articles?q=${encodeURIComponent(begriff)}` +
              (kontext ? `&kLieferant=${encodeURIComponent(kontext)}` : ""),
            { signal },
          ),
      }),
    [],
  );

  /**
   * `lieferantenSuche`/`artikelSuche` der Anlagemaske (Task 9, `Anlagemaske.tsx:65-66`) - ZWEI
   * WEITERE, eigenstaendige `jtlSuche`-Instanzen (Debounce/Cache/Single-Flight je Instanz - siehe
   * `api/jtlSuche.ts`-Kopfkommentar: "je Suchfeld ... wird jtlSuche(...) genau EINMAL aufgerufen"),
   * NICHT die `jtlArtikelSuche`-Instanz oben: die haengt an `BestellungTab`, einem ANDEREN Suchfeld
   * mit eigenem Cache. Beide Endpunkte sind damit ZWEIFACH belegt - `GET /api/jtl/articles` bereits
   * durch `jtlArtikelSuche` oben, `GET /api/jtl/suppliers` durch die Anlagemaske hier zum ersten Mal
   * - eine dokumentierte Doppelbelegung (Task-Brief woertlich: "beide Routen belegt - Abweichung 4"),
   * keine neue Route.
   *
   * GEMESSEN, NICHT ANGENOMMEN: die JSON-Form BEIDER Routen (`backend/src/jtl/typen.ts`,
   * `JtlLieferantTreffer{kLieferant,firma}` bzw. `JtlArtikelTreffer{kArtikel,artikelNummer,name,
   * lieferantenArtikelNummer}`) traegt WEDER `id` NOCH die Feldnamen, die `JtlSuchfeld`/
   * `PositionsEditor`/`Anlagemaske` verlangen (`JtlTreffer.id: string` ist Pflicht,
   * `PositionsEditor#leerePosition` liest `treffer.sku`/`treffer.price`). Ein roher Durchreich haette
   * ein Suchfeld gebaut, dessen Auswahl JEDE Zeile mit leerem `supplierId`/`sku` anlegt. Die
   * Abbildung unten macht daraus die erwartete Form:
   *   - `id`    <- `kLieferant`/`kArtikel`, als Text (`JtlTreffer.id: string`)
   *   - `name`  <- `firma`/`name`
   *   - `sku`   <- `artikelNummer` (JTL-Artikelnummer)
   *   - `eur`   BENANNTE LUECKE: die Lieferantensuche liefert keine Waehrung (nur `kLieferant`/
   *             `firma`) - bleibt IMMER `undefined`, `Boolean(undefined) === false`: jeder neu
   *             angelegte Lieferant gilt vorerst als USD, bis `GET /api/jtl/suppliers/:kLieferant`
   *             (der Einzelabruf, NICHT die Suche) das Feld an anderer Stelle ergaenzt.
   *   - `price` BENANNTE LUECKE: die Artikelsuche liefert keinen Preis - bleibt leer, der Preis ist
   *             im Original ohnehin frei editierbar (`PositionsEditor.tsx`-Kopfkommentar).
   */
  const anlagemaskeLieferantenSuche = useMemo(
    () =>
      jtlSucheGenerisch<JtlLieferantTreffer>({
        abruf: (begriff) =>
          anfrage<Array<{ kLieferant: number; firma: string; aktiv: unknown; waehrung: string | null }>>(
            `/api/jtl/suppliers?q=${encodeURIComponent(begriff)}`,
          ).then((treffer) =>
            // `aktiv` reist seit dem 2026-08-13 mit (`backend/src/jtl/typen.ts`): die Maske
            // kennzeichnet stillgelegte Lieferanten. ROH weitergereicht - eingeordnet wird in
            // `AktivMarke`/`lib/jtlAktiv.ts`, an EINER Stelle fuer beide Suchfelder.
            //
            // `eur` seit dem 2026-08-15 aus `waehrung` (tlieferant.cWaehrungISO). Bis dahin las die
            // Anlagemaske ein Feld `eur`, das diese Abbildung nie gesetzt hat - `Boolean(undefined)`
            // ist `false`, also ging JEDE Bestellung als USD heraus, auch die an einen deutschen
            // Lieferanten mit EUR-Preisen. Der Vergleich ist bewusst streng auf "EUR": eine
            // unbekannte oder fehlende Waehrung soll NICHT stillschweigend zu Euro werden.
            treffer.map((t) => ({
              id: String(t.kLieferant),
              name: t.firma,
              aktiv: t.aktiv,
              eur: t.waehrung === "EUR",
            })),
          ),
      }),
    [],
  );

  /**
   * Der rohe Serverabruf fuer die Artikelsuche - herausgezogen aus `anlagemaskeArtikelSuche` unten
   * (Auftrag "JTL-Autoabgleich verknuepft nur einen Bruchteil der SKUs", 2026-09-04) als STABILE,
   * eigene Funktion (`useCallback`, leere Deps - dieselbe Bauform wie `speditionsSuche` oben), damit
   * sie auch OHNE die Debounce-Drossel `jtlSuche(...)` weitergereicht werden kann. Nimmt seit Auftrag
   * "Verknuepfungsstatus in der Anlagemaske" (2026-08-28) einen zweiten, optionalen Parameter
   * `kontext` entgegen (siehe `jtlSuche()`-Kopfkommentar - Cache- und Single-Flight-Guard schluesseln
   * danach getrennt, damit ein Lieferantenwechsel nicht den Verknuepfungsstand des VORHERIGEN
   * Lieferanten aus dem Cache serviert). `Anlagemaske.tsx` reicht hier die gewaehlte
   * Lieferanten-Kennung als `kontext` durch (`kLieferant`), ausschliesslich wenn ein Lieferant
   * gewaehlt ist - ohne Lieferant sucht die Route weiterhin ohne `kLieferant`, unveraendert zum
   * bisherigen Verhalten.
   */
  const anlagemaskeArtikelAbruf = useCallback(
    (begriff: string, kontext?: string): Promise<JtlArtikelTreffer[]> =>
      anfrage<
        Array<{
          kArtikel: number;
          artikelNummer: string;
          name: string | null;
          ekNetto: number | null;
          mwstProzent: number | null;
          hatBild: boolean;
          verknuepft: boolean | null;
        }>
      >(
        `/api/jtl/articles?q=${encodeURIComponent(begriff)}` +
          (kontext ? `&kLieferant=${encodeURIComponent(kontext)}` : ""),
      ).then((treffer) =>
        // ekNetto/mwstProzent seit dem 2026-08-15: sie kommen aus `tliefartikel` (nStandard = 1)
        // und sind Pflichtangaben jeder JTL-Bestellposition. Vorher endete das Mapping bei
        // `name` - der Preis blieb leer und ein Steuersatz existierte gar nicht.
        // hatBild seit Auftrag "hatBild vorab mitgeben" (2026-08-27): dieses Mapping baut ein
        // NEUES Objektliteral (anders als `jtlArtikelSuche` oben, das die Antwort roh
        // durchreicht) - ein neues Backend-Feld muss deshalb EXPLIZIT genannt werden, sonst
        // faellt es hier lautlos unter den Tisch.
        treffer.map((t) => ({
          id: String(t.kArtikel),
          sku: t.artikelNummer,
          name: t.name ?? "",
          price: t.ekNetto,
          mwstProzent: t.mwstProzent,
          hatBild: t.hatBild,
          // EXPLIZIT aufgenommen, wie hatBild es hier schon vormacht - sonst faellt das Feld
          // beim Bau des neuen Objektliterals lautlos unter den Tisch.
          verknuepft: t.verknuepft,
        })),
      ),
    [],
  );

  /**
   * Die interaktive, GEDROSSELTE Fassung fuers Tippfeld der Anlagemaske - `jtlSucheGenerisch`
   * (Constraint 16: Debounce 300 ms, 60s-Cache, Single-Flight) um `anlagemaskeArtikelAbruf` herum,
   * unveraendert zum bisherigen Stand. `Anlagemaske.tsx` bekommt ZUSAETZLICH die rohe
   * `anlagemaskeArtikelAbruf` selbst als eigene Prop (`artikelSucheRoh` unten) - siehe deren
   * Kopfkommentar, warum der Bestellexcel-Import NICHT ueber diese gedrosselte Fassung laufen darf.
   */
  const anlagemaskeArtikelSuche = useMemo(
    () => jtlSucheGenerisch<JtlArtikelTreffer>({ abruf: anlagemaskeArtikelAbruf }),
    [anlagemaskeArtikelAbruf],
  );

  /**
   * `deleteOrder(o.id)` (`index.html:9291`) ueber die REST-Route - vom Task-Brief wörtlich benannt
   * (`onLoeschen → bestellungLoeschen`), anders als `onArchivUmschalten`/`onTeillieferung` unten.
   * Schliesst den Drawer nach erfolgreichem Loeschen mit (kein Formular fuer einen Datensatz, den
   * es nicht mehr gibt) und nimmt den Datensatz per `datensatz: null` aus der Liste
   * (`reduzierer.ts#listeErsetzen`, dieselbe Regel wie ein 404 beim Nachladen).
   */
  function drawerLoeschen(id: string): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === id);
    if (!bestellung) return;
    // task-rowversion-befunde.md: ANDERS als `drawerAendern`/`boardGruppeAendern` oben prueft `DELETE
    // /api/orders/:id` `orders.row_version` (backend/src/http/routes/orders.ts:429-435), also die
    // KOPF-Version - `bestellung.rowVersion` bleibt hier deshalb bewusst unveraendert, keine
    // Verwechslung mit `extrasRowVersion`.
    const rowVersion = Number((bestellung as { rowVersion?: unknown }).rowVersion) || 0;
    // Befund 1 der Pruefrunde 2: die Kennung SYNCHRON merken, VOR dem Netzaufruf - `eigeneLoeschung`
    // (oben am `drawer`-Block) ist die einzige verlaessliche Verbindung zwischen diesem Aufruf und
    // dem "nicht gefunden"-Effekt, der sonst nicht wüsste, dass GENAU DIESE Loeschung von uns kam.
    const geloeschteKennung = aktuelleKennungRef.current;
    bestellungLoeschen(id, rowVersion)
      .then(() => {
        versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: null });
        if (geloeschteKennung) eigeneLoeschung.current = geloeschteKennung;
        drawerSchliessen();
      })
      .catch((fehler: unknown) => {
        // Nacharbeit "Löschen-Merge" (Pruefrunde, Gering 1): der 409-Fall `BestellungJtlVerknuepftError`
        // (`backend/src/http/routes/orders.ts`, greift, wenn die Bestellung zwischen Laden und Klick
        // mit JTL verknuepft wurde) traegt eine konkrete, auf die JTL-Loeschroute verweisende Meldung
        // im Antwortkoerper - der Uebersetzer (`fehlertext`) stellt sie hinter den Kontext, so geht die
        // Serverbegruendung nicht verloren und die rohe `.message` wird nicht mehr angezeigt.
        meldeFehler(fehler, "Die Bestellung konnte nicht gelöscht werden.");
      });
  }

  /**
   * Startet den zweistufigen Bestaetigungsdialog (Task 11, `JtlLoeschDialog`) - der eigentliche
   * Aufruf von `jtlBestellungLoeschen` folgt erst in `jtlLoeschenBestaetigt` unten, nach der
   * Eintipp-Bestaetigung. Aufgerufen NUR noch aus `loeschenAusloesen` unten (Auftrag
   * "Löschen-Buttons zusammenlegen", 2026-09-01) - vorher gab es einen eigenen Drawer-Knopf
   * ("EK-Bestellung in JTL löschen", `onJtlLoeschen`) und einen eigenen Kontextmenue-Eintrag
   * ("jtlDelete"), die diese Funktion direkt anstiessen; beide sind seither im EINEN
   * "Löschen"-Knopf/-Menuepunkt aufgegangen (siehe `loeschenAusloesen`).
   *
   * `id` ist die Hub-`id` der Bestellung, NICHT die JTL-Kennung. Sie reist im Dialog-State mit,
   * weil `jtlLoeschenBestaetigt` sie fuer `versende({ art: "nachgeladen", ... })` braucht
   * (`reduzierer.ts#listeErsetzen` sucht ueber GENAU diesen Wert, nicht ueber `jtlOrderId`).
   */
  function jtlLoeschenStarten(id: string, jtlOrderId: number, orderNo: string): void {
    setJtlLoeschDialog({ id, jtlOrderId, orderNo });
  }

  /**
   * Die EINE Verzweigung hinter dem EINEN "Löschen"-Knopf/-Menuepunkt (Auftrag "Löschen-Buttons
   * zusammenlegen", 2026-09-01, verbindliche Entscheidung des Auftraggebers) - gemeinsam genutzt von
   * `kontextAktion` ("delete", Rechtsklick-Kontextmenue) UND vom Drawer-Fuss (`onLoeschen` unten,
   * siehe Einhaengung). Vorher fuehrten beide Oberflaechen ZWEI getrennte Knoepfe/Eintraege
   * ("Bestellung löschen" -> `drawerLoeschen`/"jtlDelete" -> `jtlLoeschenStarten`) mit eigener
   * Sichtbarkeitspruefung je Komponente (`KontextMenue.tsx`/`Drawer.tsx`) - die AUSLOESUNG war schon
   * damals dieselbe Ableitung an zwei Stellen dupliziert (siehe die alte `kontextAktion`-Fassung,
   * "jtlDelete"-Zweig). Diese Funktion buendelt genau das an EINER Stelle, WOERTLICH dieselbe
   * Ableitung von `jtlOrderId`/`hatJtlOrderId` wie zuvor (`Drawer.tsx:327-329`/ehem.
   * `KontextMenue.tsx:167-169`).
   *
   * Ein JTL-Link (`jtlOrderId` gueltig) faehrt IMMER ueber den bestehenden JTL-Loeschweg
   * (`jtlLoeschenStarten` - Grund-Dialog, `pruefeLoeschbar`, JTL-Beleg UND Hub-Zeile verschwinden
   * zusammen, siehe `jtlLoeschenBestaetigt`) - UNVERAENDERT gegenueber vorher. Ohne JTL-Link bleibt
   * es beim reinen Hub-Loeschpfad (`drawerLoeschen`, 1:1 wiederverwendet statt dupliziert - deckt
   * auch dessen `rowVersion`-Handling ab).
   *
   * Die Sichtbarkeit des Knopfes selbst (kein Knopf mehr fuer eine verknuepfte Bestellung OHNE das
   * Recht `delete_jtl_order`, Entscheidungspunkt 2 des Auftrags) entscheiden `KontextMenue.tsx`/
   * `Drawer.tsx` weiterhin SELBST (sie kennen `rechte`) - diese Funktion wird dann ohnehin nicht mehr
   * erreicht.
   *
   * NACHARBEIT "Löschen-Merge" (Pruefrunde, Wichtig 2): der reine Hub-Loeschpfad (kein JTL-Link)
   * loeste bis dahin OHNE JEDE Rueckfrage aus, sobald "Löschen" geklickt wurde - anders als der
   * JTL-Weg oben, der bereits ueber den mehrstufigen Grund-Dialog (`JtlLoeschDialog`) laeuft. Ein
   * Fehlklick im Kontextmenue (kein geoeffnetes Formular, kein Kontext) entfernte die Bestellung
   * sofort und endgueltig - es gibt fuer den Hub-Loeschpfad KEINEN Archiv-Sicherungssatz
   * (`geloeschteBestellungenArchiv.ts` schreibt nur auf dem JTL-Weg). Die Rueckfrage steht HIER, an
   * der EINEN Stelle, die Kontextmenue UND Drawer-Fussleiste gemeinsam durchlaufen (siehe
   * Kopfkommentar oben) - keine zweite, duplizierte Pruefung in `KontextMenue.tsx`/`Drawer.tsx`
   * (die hatten vorher auch keine). Muster wie das Lieferanten-Loeschen oben:
   * rohes `confirm(...)`, keine eigene Dialog-Komponente im Projekt fuer diesen Fall. Der Titel
   * folgt demselben Rueckfall wie `KontextMenue.tsx` (Eigenheit 1): Bestellnummer, sonst Name, sonst
   * "Bestellung".
   */
  function loeschenAusloesen(id: string): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === id);
    if (!bestellung) return;
    const jtlOrderId = Number(bestellung.jtlOrderId);
    const hatJtlOrderId = Number.isSafeInteger(jtlOrderId) && jtlOrderId > 0;
    if (hatJtlOrderId) {
      // `(bestellung.orderNo as string) ?? ""`, GETRIMMT geprueft - woertlich dieselbe Ableitung wie
      // `Drawer.tsx:414-421` (dortiger Kommentar begruendet das Trimmen ausfuehrlich).
      const orderNoOderKennung = ((bestellung.orderNo as string) ?? "").trim() || String(jtlOrderId);
      jtlLoeschenStarten(id, jtlOrderId, orderNoOderKennung);
      return;
    }
    const titel = (bestellung.orderNo as string) || (bestellung.name as string) || "Bestellung";
    if (!confirm(`„${titel}“ wirklich löschen?`)) return;
    drawerLoeschen(id);
  }

  /**
   * Bestaetigter Grund aus `JtlLoeschDialog` (Task 11) - loescht die Bestellung SAMT JTL
   * (`jtlBestellungLoeschen`, Task 10, `DELETE /api/jtl/orders/:kLieferantenBestellung`, Task 9).
   *
   * ANDERS als `drawerLoeschen` oben prueft diese Route keine `rowVersion` - der Server ermittelt den
   * JTL-Beleg frisch ueber `kLieferantenBestellung` und braucht dafuer keinen optimistischen
   * Sperrwert vom Client (siehe `api/jtlBestellungen.ts#jtlBestellungLoeschen`s Kopfkommentar).
   *
   * Erfolgspfad: Dialog schliessen, die Hub-Zeile (falls vorhanden) per `datensatz: null` aus der
   * Liste nehmen (`reduzierer.ts#listeErsetzen` - wirkungslos, wenn `id` ohnehin keine Zeile trifft,
   * z. B. bei einer rein-JTL-Bestellung ohne Hub-Zeile) UND den Drawer schliessen (kein Formular fuer
   * einen Beleg, den es nicht mehr gibt) - dieselbe Reihenfolge wie `drawerLoeschen`.
   *
   * Fehlerpfad (Review-Fund Important #2 zu Task 12): das Backend (`pruefeLoeschbar`) lehnt sehr
   * konkret ab - "... hat den JTL-Status \"Teilweise geliefert\" (30) - nur Bestellungen mit
   * Status ..." oder "Position 4711 traegt bereits eine gelieferte Menge (12) - die Bestellung ist
   * bebucht ...". Diese Meldung kommt bereits im Antwortkoerper an (`api/basis.ts`), und der
   * Uebersetzer (`fehlertext`) stellt sie hinter den Kontext - eine feste Ersatzzeichenkette wuerfe sie fort
   * und liesse die Anwenderin nie erfahren, WARUM abgelehnt wurde; der haeufigste echte Fall ist genau eine
   * teilgelieferte Bestellung. Dasselbe Muster wie `teillieferungAnlegen` unten.
   *
   * Nachlese-Hinweis (Fixwelle nach der finalen Gesamtpruefung, 2026-08-26, Punkt 2): JTL wurde bei
   * Erfolg dieses Aufrufs bereits unwiderruflich geloescht - die Antwort traegt zusaetzlich
   * `hubZeileStatus`/`ordnerStatus` (`api/jtlBestellungen.ts`, dreiwertig seit derselben Fixwelle).
   * `"fehlgeschlagen"` bedeutet: ein Aufraeumschritt NACH der JTL-Loeschung ist danebengegangen (z. B.
   * ein Postgres-Aussetzer) - kein Grund, den Erfolg zu verschweigen (die Bestellung IST in JTL weg),
   * aber ein Grund, es zu SAGEN, statt es unter den Tisch fallen zu lassen. `"nicht_noetig"` (der
   * haeufige Normalfall - die meisten Bestellungen haben gar keine Hub-Zeile) loest KEINEN Hinweis
   * aus, sonst saehe die Anwenderin bei JEDER Loeschung eine Meldung, die nichts zu bedeuten hat.
   */
  function jtlLoeschenBestaetigt(grund: string): void {
    if (!jtlLoeschDialog) return;
    const { id, jtlOrderId } = jtlLoeschDialog;
    jtlBestellungLoeschen(jtlOrderId, grund)
      .then((ergebnis) => {
        setJtlLoeschDialog(null);
        versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: null });
        // Nachtrag (Rechtsklick-Kontextmenue, 2026-09-01): `drawerSchliessen()` NUR, wenn GENAU
        // DIESE Bestellung gerade im Drawer offen ist - dieselbe Bedingung wie
        // `drawerArchivUmschalten` oben (`drawer?.id === id`). Ausgeloest ueber das Kontextmenue
        // ist NIE ein Drawer offen; ein bedingungsloser Aufruf schob dort einen zusaetzlichen,
        // ununterscheidbaren Verlaufseintrag (`useAnsicht.ts#fahre`, kein Zweitaufruf-Schutz).
        if (drawer?.id === id) drawerSchliessen();
        if (ergebnis.hubZeileStatus === "fehlgeschlagen" || ergebnis.ordnerStatus === "fehlgeschlagen") {
          meldeFehler(
            "Die Bestellung wurde in JTL gelöscht, ein Aufräumschritt im Hub ist aber fehlgeschlagen – bitte im Serverlog nachsehen.",
          );
        }
      })
      .catch((fehler: unknown) => {
        meldeFehler(fehler, "Die Bestellung konnte nicht in JTL gelöscht werden.");
      });
  }

  /**
   * `toggleArchive(o.id)` (`index.html:9288-9289`). Bis 2026-08-27 eine BENANNTE, nicht-stille
   * Luecke (siehe Historie unten) - seither ueber `drawerAendern` (denselben Kanal wie
   * `drawerFeldAendern`/`onFeld` oben) an `PATCH /api/orders/:id/extras` verdrahtet: `archived`
   * steht in `FELDGRUPPEN_SPALTEN.extras` (`api/bestellungen.ts`), kein zweiter Schreibweg noetig.
   *
   * Zustand (`archived`, ueber ihn indirekt `extrasRowVersion`) kommt aus der schon geladenen
   * Bestellung (`zustand.bestellungen`, dasselbe Muster wie `kontextmenueOeffnen` oben) - kein
   * Nachladen VOR dem Umschalten. `drawerAendern` selbst loest `extrasRowVersion` erneut auf (siehe
   * dort), damit bleibt auch ein zwischenzeitlich veraenderter Stand die Grundlage der Version, nicht
   * ein hier zwischengespeicherter.
   *
   * `!bestellung.archived` statt eines expliziten Booleans: `archived` ist in `Order` nur ueber den
   * Index-Signature-Fallback (`lib/types.ts`) typisiert (`unknown`), `!` liefert trotzdem sauber das
   * Gegenteil des aktuellen (ggf. `undefined`en) Werts - dieselbe Umformung wie `KontextMenue.tsx`s
   * `!!bestellung.archived` fuer die Icon-/Textwahl.
   *
   * NACHTRAG Pruefrunde 2026-08-27 (`toggleArchive`, `index.html:14186-14195`, woertlich): der
   * Bestand tut MEHR als nur das Flag kippen - er schliesst bei offenem Drawer DERSELBEN Bestellung
   * den Drawer und meldet per Toast, WAS gerade passiert ist. Ohne beides verschwaende die Zeile beim
   * Archivieren lautlos aus der Aktiv-Ansicht, waehrend der Drawer offen haengen bliebe. `onErfolg`
   * (an `drawerAendern` uebergeben, laeuft NACH `versende(...)` - siehe dort) bildet das nach:
   *   - `drawerSchliessen()` NUR, wenn GENAU DIESE Bestellung gerade im Drawer offen ist (`drawer?.id
   *     === id`, dieselbe Bedingung wie `openId === id` im Bestand) - ein Klick aus dem Kontextmenue
   *     (`kontextAktion` unten) oeffnet nie einen Drawer, dort bleibt die Bedingung folgerichtig
   *     falsch und nichts schliesst.
   *   - `zeigeMeldung(...)` mit dem WOERTLICHEN Bestandstext, in beide Richtungen unterschiedlich:
   *     Archivieren nennt ausdruecklich, dass die Historie erhalten bleibt (der haeufigste
   *     Verwechslungsgrund mit "Loeschen"); Wiederherstellen braucht das nicht.
   *
   * HISTORIE: Der Task-Brief nannte fuer diesen Knopf kein Ziel (anders als `onFeld`/`onLoeschen`
   * oben) - er blieb deshalb, wie schon `drawerOeffnen` vor Task 4 und `kontextAktion` unten, ein
   * benannter, aber wirkungsloser `console.warn`. `splitOrder(o.id)` (`:9290`, dieselbe Fussleiste)
   * ist SEIT Task 9 verdrahtet, siehe `drawerTeillieferungOeffnen` unten.
   */
  function drawerArchivUmschalten(id: string): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === id);
    if (!bestellung) return;
    const naechsterZustand = !bestellung.archived;
    drawerAendern(id, { archived: naechsterZustand }, () => {
      if (drawer?.id === id) drawerSchliessen();
      zeigeMeldung(
        naechsterZustand
          ? "Bestellung archiviert – in der Historie weiterhin sichtbar"
          : "Bestellung wiederhergestellt",
      );
    });
  }

  // Eine automatische Archivierung ist nie eine Handlung des Menschen (hat er die Fertigstellung selbst
  // ausgeloest, wurde sein ausloesender PATCH schon gezaehlt): PATCH und beide Nachlade-GETs gehen als
  // Hintergrundanfragen hinaus (`x-bestellhub-hintergrund`, sechstes Argument von `drawerAendern`). Der
  // Hook und seine Tests bleiben bei der Zwei-Argument-Form; die Markierung steht an dieser einen Stelle.
  useAutoArchivierung(zustand.bestellungen, zustand.rechte, (id, aenderung) =>
    drawerAendern(id, aenderung, undefined, undefined, undefined, true),
  );

  /**
   * `splitOrder(o.id)` (`index.html:9290`) - der Dialog selbst (`TeillieferungDialog.tsx`) ist
   * Task 23, das Anschalten dieser Naht Task 9 (Verdrahtungsplan). `id` haelt NUR die id, der
   * Datensatz kommt beim Zeichnen frisch aus `zustand.bestellungen` (siehe `teilBestellung` unten) -
   * dasselbe Muster wie `drawer` oben.
   */
  function drawerTeillieferungOeffnen(id: string): void {
    setTeilenId(id);
  }

  // `zeigeMeldung` (aus `useMeldungsAnzeige` oben, entspricht `toast(msg)`, index.html:5264-5270):
  // Task 2 baute nur das Gegenstueck (`Toast.tsx`) - "Alle folgenden Tasks konsumieren
  // `zeigeMeldung` als `onMeldung`/`onHinweis`-Prop" (Task-Brief). Task 3 (dieser Task) ist der
  // erste tatsaechliche Aufrufer: das Board reicht sie als `onMeldung` an `BoardSeite` durch
  // (Umzugsmeldung, index.html:6634) und der Listen-Editor-Ersatzpfad benutzt sie fuer die
  // woertliche Erfolgsmeldung (siehe `listenSpaltenSpeichern` unten).

  // ================= Board: Arbeitsplatz-Einstellungen (Task 3, `state/arbeitsplatz.ts`) ========
  //
  // `ORDER_LIST_WIDTHS`/`BOARD_SECTION_COLLAPSED` (index.html:4635/4639) - beide leben NUR in
  // `localStorage`, nicht im Store: sie sind Client-lokale Einstellungen, kein Serverzustand (Spec).
  // `useState(() => …)` liest `localStorage` genau EINMAL, beim ersten Rendern.
  const [breiten, setBreiten] = useState<Breiten>(() => leseBreiten());
  const [eingeklappt, setEingeklappt] = useState<Abschnitte>(() => leseBoardAbschnitte());

  /**
   * Laenderfilter (NEUBAU ohne Bestandsvorbild) - bis hierher lokaler Zustand von `BoardSeite.tsx`
   * (dort per `useState`), seither app-weit: die Fähnchen sitzen in der Kopfzeile
   * (`Kopfzeile.tsx`, neben "Neue Bestellung"), und der Filter wirkt auf allen fuenf
   * bestellungsfuehrenden Seiten (siehe `lib/laender.ts#istBestellungNachLaenderFilterSichtbar`).
   * Dasselbe Muster wie `breiten`/`eingeklappt` zwei Zeilen darueber: lazy `useState`, Initialwert
   * aus `localStorage`, sofortiges Zurueckschreiben bei jeder Aenderung.
   */
  const [laenderFilter, setzeLaenderFilterState] = useState<LaenderFilter>(() => leseLaenderFilter());
  const laenderFilterAendern = (naechster: LaenderFilter): void => {
    setzeLaenderFilterState(naechster);
    schreibeLaenderFilter(naechster);
  };

  /**
   * Luftfracht/Seefracht-Filter (Auftrag „Luftfracht/Seefracht-Filter im Bestellhub", 2026-08-27) -
   * dasselbe Muster wie `laenderFilter` zwei Zeilen darueber: lazy `useState`, Initialwert aus
   * `localStorage`, sofortiges Zurueckschreiben bei jeder Aenderung. Die Chips sitzen in der
   * Kopfzeile LINKS von EU/China (`Kopfzeile.tsx`), der Filter wirkt auf denselben fuenf
   * bestellungsfuehrenden Seiten (`lib/transportart.ts#istBestellungNachTransportartFilterSichtbar`).
   */
  const [transportartFilter, setzeTransportartFilterState] = useState<TransportartFilter>(() =>
    leseTransportartFilter(),
  );
  const transportartFilterAendern = (naechster: TransportartFilter): void => {
    setzeTransportartFilterState(naechster);
    schreibeTransportartFilter(naechster);
  };

  /** `toggleBoardSection(id)` (index.html:6694-6698). */
  function boardAbschnittUmschalten(gruppenId: string): void {
    setEingeklappt((vorher) => {
      const naechste = kippeAbschnitt(vorher, gruppenId);
      schreibeBoardAbschnitte(naechste);
      return naechste;
    });
  }

  /** `startOrderListColumnResize(event, key)` (index.html:7545) - Bedienung aus `SpaltenBreiten.ts`. */
  function boardBreiteZiehen(ereignis: React.PointerEvent<HTMLElement>, schluessel: string): void {
    starteBreitenZiehen(ereignis, schluessel, { breiten, onBreiten: setBreiten });
  }

  /** `resizeOrderListColumnByKey(event, key)` (index.html:7568). */
  function boardBreiteTaste(ereignis: React.KeyboardEvent<HTMLElement>, schluessel: string): void {
    bediendeBreitenTaste(ereignis, schluessel, { breiten, onBreiten: setBreiten });
  }

  /** `resetOrderListWidths()` (index.html:7576). */
  function boardBreitenZuruecksetzen(): void {
    const naechste = zurueckgesetzteBreiten();
    setBreiten(naechste);
    schreibeBreiten(naechste);
  }

  /**
   * `ITEM_TABLE_WIDTHS` (index.html:4638) - die Spaltenbreiten der Artikeltabelle im Drawer, seit
   * dem 2026-08-15 hier statt in `MainTab/Positionen.tsx` gehalten. Der Grund steht in
   * `state/arbeitsplatz.ts` ("DER FUENFTE"): die Komponente las den Wert mit einer eigenen
   * `localStorage`-Klammer und einmalig beim Einhaengen - ein Zuruecksetzen konnte ihn nie
   * erreichen. Dieselbe Bauart wie `breiten` fuer die Bestellliste eine Zeile darueber.
   */
  const [artikelBreiten, setArtikelBreiten] = useState<Breiten>(() => leseArtikelBreiten());

  /**
   * `resetItemTableWidths()` (index.html:6871), vollstaendig: Wert leeren, arbeitsplatzlokal
   * schreiben, neu zeichnen, melden. Der Toast ist woertlich - `boardBreitenZuruecksetzen`
   * daneben hat KEINEN, weil `resetOrderListWidths` (:7576) im Bestand ebenfalls keinen hat.
   *
   * GEMELDET, nicht repariert: das Ziehen der Spaltentrenner der Artikeltabelle ist in diesem Baum
   * nicht verdrahtet (`Positionen.tsx`, "NICHT portiert"). Innerhalb einer Sitzung kann sich also
   * keine Breite aendern - dieser Knopf wirkt heute nur auf Werte, die aus `index.html` oder einer
   * frueheren Sitzung stammen. Das macht ihn nicht wirkungslos, aber selten.
   */
  function drawerArtikelBreitenZuruecksetzen(): void {
    setArtikelBreiten({});
    schreibeArtikelBreiten({});
    zeigeMeldung("Artikel-Spaltenbreiten zurückgesetzt");
  }

  /**
   * `openPortal()` (index.html:9316), woertlich einschliesslich seiner drei Eigenheiten:
   *
   * 1. Der leere Link wird gemeldet, nicht still ignoriert - der Knopf bleibt bedienbar. Die Absicht (das Portal
   *    oeffnen) wird nicht ausgefuehrt, deshalb steht die Meldung im Fehlerstapel und nicht im Toast (Fehleranzeige
   *    Paket 1); der Bestand zeigte hier einen Toast.
   * 2. Ein Link ohne Schema bekommt `https://` vorangestellt (`/^https?:\/\//i`), damit der Browser
   *    ihn nicht als relativen Pfad der eigenen Seite auffasst.
   * 3. `_blank`, also ein neues Fenster.
   *
   * EINE ERGAENZUNG gegenueber dem Bestand: `noopener,noreferrer`. Ohne sie bekommt die geoeffnete
   * Seite ueber `window.opener` einen Griff auf dieses Fenster und kann es umleiten - bei einem
   * Link, den ein Speditionsportal liefert, ist das kein theoretisches Risiko. Beobachtbar ist der
   * Unterschied fuer den Nutzer nicht.
   *
   * QUELLE UMGESTELLT (Auftrag „Sendungs-Dropdown, Portal-Link, Konfliktdialog", 2026-08-21, Ziel 1):
   * bis dahin `bestellung.forwarderUrl` - ein Sendungsmerkmal ohne Schreibweg
   * (`KEIN_FELDGRUPPEN_WEG`), das der Mitarbeiter also nie tatsaechlich befuellen konnte (die
   * benannte Luecke bleibt, siehe `MainTab/Stammdaten.tsx` und `ShipTab/Referenzen.tsx`s
   * Kopfkommentar). Der Knopf liest jetzt `sendung.portalUrl` - die verknuepfte Sendung DIESES
   * Drawers (`sendung` oben, abgeleitet aus `speditionZustand` fuer `drawer?.id`). `id` bleibt
   * Parameter (Aufrufform von `ReferenzenProps.onPortal` unveraendert); `sendung` ist ein
   * Komponentenzustand fuer den GESAMTEN offenen Drawer, keine Umschau je `id` noetig, weil dieser
   * Rueckruf nur fuer die AKTUELL offene Bestellung erreichbar ist.
   */
  function drawerPortalOeffnen(id: string): void {
    // `id` bleibt Teil der Signatur (Aufrufform `ReferenzenProps.onPortal`), wird aber nicht mehr
    // gebraucht - siehe Kopfkommentar.
    const roh = String(sendung?.portalUrl ?? "").trim();
    if (!roh) {
      // Siehe Punkt 1 im Kopfkommentar: die Absicht wird nicht ausgefuehrt - Fehlerstapel, nicht Toast.
      meldeFehler("Kein Portal-Link hinterlegt");
      return;
    }
    const ziel = /^https?:\/\//i.test(roh) ? roh : `https://${roh}`;
    window.open(ziel, "_blank", "noopener,noreferrer");
  }

  // ================= Board: Kontextmenue (Task 15, `Board/KontextMenue.tsx`) - externe Naht =====
  //
  // `BoardSeite`s `onKontextmenue` reicht Ereignis und Id nur durch (siehe dortiger Kopfkommentar);
  // das OB und WO des Menues entscheidet dieser Aufrufer. `null` heisst geschlossen.
  const [kontextmenue, setKontextmenue] = useState<{ bestellung: Order; x: number; y: number } | null>(
    null,
  );

  /**
   * Auftrag "Sammel-Statuswechsel" (2026-09-01): `ausgewaehlt` (die Menge markierter Ids) bleibt
   * laut Architekturentscheidung LOKALER State in `BoardSeite.tsx` - keine Verschiebung hierher.
   * Diese kleine, EIGENE Kopie dient AUSSCHLIESSLICH dazu, dass `KontextMenue` (externe Naht, kennt
   * `BoardSeite`s internen Zustand nicht) weiss, ob die rechtsgeklickte Zeile Teil einer laufenden
   * Mehrfachauswahl ist (`istInAuswahl`-Prop unten) - und damit `kontextAktion` beim neuen Fall
   * `"bulkStatus"` weiss, WELCHE Ids gemeint sind (die Aktion selbst traegt nur die `id` der
   * rechtsgeklickten Zeile, siehe `OrderKontextAktion`). Synchron gehalten ueber `BoardSeite`s Prop
   * `onAuswahlGeaendert`, gerufen an JEDER Stelle, an der die eigentliche Auswahl sich dort aendert.
   */
  const [ausgewaehlteBestellungen, setzeAusgewaehlteBestellungen] = useState<readonly string[]>([]);

  /**
   * `openOrderContextMenu(event, id)` (index.html:7145-7146).
   *
   * `useCallback` (Folgefix zu Auftrag "Bestellboard-Filter — Renderausfall durch fehlende
   * Memoisierung beheben") - dieselbe Begruendung wie bei `drawerOeffnen` oben: die Funktion geht
   * unveraendert als `onKontextmenue` an `<BoardSeite>` und von dort DIREKT (ohne eigenen
   * `useCallback`-Zwischenschritt) an jede `Bestellzeile` weiter - eine frische Referenz bei JEDEM
   * App-Render wuerde also JEDE Zeile treffen. `setKontextmenue` ist ein `useState`-Setter (von React
   * garantiert referenzstabil, siehe Konvention in `BoardSeite.tsx`, z. B. `setzeAnker` dort) und
   * braucht deshalb keinen Eintrag.
   */
  const kontextmenueOeffnen = useCallback(
    (ereignis: React.MouseEvent, id: string): void => {
      ereignis.preventDefault();
      const bestellung = zustand.bestellungen.find((b) => b.id === id);
      // Befund 5, Option C (2026-08-11) im Geiste: eine id ohne (mehr) geladenen Datensatz oeffnet
      // kein Menue, das ueber sie rechnen wollte (`orderNo`/`name`/`archived`/`boardGroupOverride`).
      if (!bestellung) return;
      setKontextmenue({ bestellung, x: ereignis.clientX, y: ereignis.clientY });
    },
    [zustand.bestellungen],
  );

  /** `closeOrderContextMenu()` (index.html:7137-7143). */
  function kontextmenueSchliessen(): void {
    setKontextmenue(null);
  }

  /**
   * `runOrderContextAction(action)` (index.html:7178-7186). Die fuenf Reiter-Aktionen oeffnen den
   * Drawer (Task 4: `drawerOeffnen`) - `split`/`auto`/`archive`/`delete` sind Aktionen des
   * KONTEXTMENUES, ein anderer Aufrufpfad als die Drawer-Fussleiste (dort seit Task 4 verdrahtet,
   * siehe `drawerArchivUmschalten`/`drawerLoeschen`/`drawerTeillieferungOeffnen` oben). `"auto"`
   * bleibt, wie `drawerOeffnen` es vor Task 4 war, eine BENANNTE, nicht-stille Luecke - kein Task
   * dieses Plans nennt diesen Pfad ausdruecklich.
   *
   * `"split"` ist seit Task 10 (Verdrahtungsplan "Teillieferung/Historie") verdrahtet - DERSELBE
   * Aufruf wie die Drawer-Fussleiste (`drawerTeillieferungOeffnen` oben, kein zweiter Oeffnungsweg):
   * das Kontextmenue kennt nur die `id`, `drawerTeillieferungOeffnen` setzt lediglich `teilenId`, der
   * Datensatz kommt beim Zeichnen frisch aus `zustand.bestellungen` (siehe dortiger Kopfkommentar).
   *
   * `"archive"` ist seit 2026-08-27 verdrahtet - DERSELBE Aufruf wie die Drawer-Fussleiste
   * (`drawerArchivUmschalten` oben, kein zweiter Schreibweg): das Kontextmenue kennt nur die `id`
   * (`KontextMenue.tsx`), `drawerArchivUmschalten` liest `archived`/`extrasRowVersion` selbst aus
   * `zustand.bestellungen` - dieselbe Bauform wie `main`/`tasks`/`docs`/`pay`/`calc` zwei Zeilen
   * drunter, die ebenfalls nur die `id` weitergeben.
   *
   * `"delete"` ist seit dem Auftrag "Löschen-Buttons zusammenlegen" (2026-09-01) EBENFALLS
   * verdrahtet - vorher ein `console.warn`-Platzhalter (Aktion nie ausgeloest), UND es gab
   * zusaetzlich eine EIGENSTAENDIGE zweite Aktion `"jtlDelete"` fuer den frueheren separaten
   * "EK-Bestellung in JTL löschen"-Eintrag (Asana-Unterticket zu Task 12). Beide sind seither zu
   * DIESER EINEN Aktion verschmolzen (verbindliche Entscheidung des Auftraggebers: ein
   * Löschen-Knopf/-Menuepunkt statt zwei) - `loeschenAusloesen` oben entscheidet an EINER Stelle,
   * ob der bestehende JTL-Loeschweg (verknuepfte Bestellung) oder der reine Hub-Loeschpfad (keine
   * Verknuepfung) laeuft; `KontextMenue.tsx` entscheidet selbst, ob der Menuepunkt fuer eine
   * verknuepfte Bestellung OHNE das Recht `delete_jtl_order` ueberhaupt erscheint (dann wird diese
   * Aktion gar nicht erst ausgeloest).
   *
   * `"bulkStatus"` ist seit dem Auftrag "Sammel-Statuswechsel" (2026-09-01) ebenfalls verdrahtet -
   * IGNORIERT das mitgegebene `id` bewusst: die Aktion meint nicht die eine rechtsgeklickte Zeile,
   * sondern die GESAMTE Mehrfachauswahl (`ausgewaehlteBestellungen` oben, synchron gehalten ueber
   * `BoardSeite`s `onAuswahlGeaendert`) - `KontextMenue` zeigt den Eintrag ohnehin nur, wenn die
   * rechtsgeklickte Zeile TEIL dieser Auswahl ist (`istInAuswahl`, siehe dortige Props). Oeffnet
   * denselben Dialog wie der Werkzeugleiste-Knopf, siehe `onSammelStatusAnfordern` an `<BoardSeite>`.
   */
  function kontextAktion(aktion: OrderKontextAktion, id: string): void {
    if (aktion === "main" || aktion === "tasks" || aktion === "docs" || aktion === "pay" || aktion === "calc") {
      drawerOeffnen(id);
      return;
    }
    if (aktion === "archive") {
      drawerArchivUmschalten(id);
      return;
    }
    if (aktion === "delete") {
      loeschenAusloesen(id);
      return;
    }
    if (aktion === "bulkStatus") {
      setSammelStatusDialogOffen(true);
      return;
    }
    if (aktion === "split") {
      drawerTeillieferungOeffnen(id);
      return;
    }
    if (aktion === "split-undo") {
      setRueckgaengigId(id);
      return;
    }
    if (aktion === "duplicate") {
      bestellungDuplizierenOeffnen(id);
      return;
    }
    // offen: Paket 3 – Scheinfunktion: "Automatisch einsortieren" ist nicht verdrahtet und tut nichts als protokollieren (Inventar E)
    console.warn(`Kontextmenü-Aktion "${aktion}" fuer Bestellung "${id}" noch nicht verdrahtet.`);
  }

  // ========== Board: Sammel-Statuswechsel (Auftrag "Sammel-Statuswechsel", 2026-09-01) ==========
  // `Board/SammelStatusDialog.tsx` - externe Naht wie `KontextMenue`/`ListenEditor`. Ausgeloest
  // sowohl von `onSammelStatusAnfordern` (`<BoardSeite>` unten, Werkzeugleiste-Knopf) als auch vom
  // `kontextAktion`-Fall `"bulkStatus"` oben - beide setzen nur diesen Zustand, die betroffenen Ids
  // liegen bereits in `ausgewaehlteBestellungen`.
  const [sammelStatusDialogOffen, setSammelStatusDialogOffen] = useState(false);

  // ========== Massenupload (Plan "Massenupload fuer Artwork und Produktionsbilder", 2026-10-22) ==========
  // Die Ablage im Reiter Dokumente (`MassenuploadAblage`) ruft `massenuploadStarten`; der Dialog haengt als Geschwister des
  // Drawers weiter unten. Er liest den Auftrag nur beim Einhaengen (Momentaufnahme): der `key` aus dem Laufzaehler sorgt dafuer,
  // dass ein spaeterer, neuer Auftrag einen neuen Dialog einhaengt und nicht den alten weiterlaufen laesst. Ein neuer Drop bei
  // offenem Dialog ist ausgeschlossen (das Modal verdeckt den Reiter), der Schutz gilt trotzdem.
  const [massenuploadAuftrag, setMassenuploadAuftrag] = useState<MassenuploadAuftrag | null>(null);
  const [massenuploadLauf, setMassenuploadLauf] = useState(0);
  const massenuploadStarten = useCallback((auftrag: MassenuploadAuftrag): void => {
    setMassenuploadAuftrag(auftrag);
    setMassenuploadLauf((lauf) => lauf + 1);
  }, []);
  // Zählt die Schließungen des Dialogs. Seine Zusammenfassung führt einen eigenen Prüfstand der Artworks; „Gesehen“ und „Erneut
  // prüfen“ dort ändern, was der Reiter Dokumente (andere Hook-Instanz) sonst weiter als altes Ergebnis zeigte. Der Zähler geht
  // über `DokumenteTab` in den Ladeschlüssel des Reiters und lässt ihn nachladen (Review Task 11, W2).
  const [massenuploadGeschlossen, setMassenuploadGeschlossen] = useState(0);
  const massenuploadSchliessen = useCallback((): void => {
    setMassenuploadAuftrag(null);
    setMassenuploadGeschlossen((zahl) => zahl + 1);
  }, []);
  // Abmeldung oder Sitzungsverlust (Ruling 55, N4): Die Anmeldemaske ersetzt die Oberflaeche, der Dialog haengt aus, aber der
  // Auftrag bliebe im Zustand. Nach einer neuen Anmeldung haengte er sich mit ihm wieder ein und luede hoch, was niemand mehr
  // im Blick hat, womoeglich unter einem anderen Konto. Darum faellt er mit der Sitzung.
  useEffect(() => {
    if (!zustand.nutzer) setMassenuploadAuftrag(null);
  }, [Boolean(zustand.nutzer)]);
  // Fokus nach dem Schliessen: der Dialog gibt ihn an seinen Ausloeser zurueck, solange der noch im Dokument steht. Hat der Drawer
  // seinen Rumpf inzwischen neu aufgebaut (Rechtesatz, Reiter), ist der Knopf weg und der Fokus laege auf `body`; dann geht er an
  // die Ablage des aktuellen Drawers. Der Effekt laeuft NACH dem Aufraeumen des Dialogs (React raeumt vor jedem Neu-Einrichten auf),
  // der Dialog hat seine Rueckgabe also schon versucht.
  const massenuploadWarOffen = useRef(false);
  useEffect(() => {
    const offen = massenuploadAuftrag !== null;
    if (massenuploadWarOffen.current && !offen) fokusZurAblage();
    massenuploadWarOffen.current = offen;
  }, [massenuploadAuftrag]);
  /**
   * NACHBESSERUNG 2 (Pruefung 97e5bffb, Wichtig 1): das Leeren-Signal an `BoardSeite` (siehe deren
   * Kopfkommentar zur Prop `auswahlLeerenSignal` fuer den vollstaendigen Befund und die Begruendung
   * der Zaehler-Bauart). Hochgezaehlt in `useBoardSammelaktionen`, NACH jedem Sammel-Statuswechsel
   * mit MINDESTENS einer erfolgreichen Aenderung.
   */
  const [auswahlLeerenSignal, setAuswahlLeerenSignal] = useState(0);
  const {
    drawer: drawerGruppe,
    ansicht: gruppenAnsicht,
    setAnsicht: setGruppenAnsicht,
    gruppenId: gruppenIdDerOffenenBestellung,
    hinzufuegen: gruppenMitgliedHinzufuegen,
    loesen: gruppenMitgliedLoesen,
    aufloesen: gruppeAufloesen,
    sendungSetzen: gruppenSendungSetzen,
    erstellen: bestellgruppeErstellen,
  } = useBestellgruppen(
    drawer?.id ?? null,
    zustand.bestellungen,
    ansicht.board.states,
    bestandVollNeuLaden,
    setAuswahlLeerenSignal,
  );

  /**
   * NACHLADE-HELFER zu `useBoardSammelaktionen.ts#sammelStatusAendern` (Nachbesserung, Pruefung e9f0edb,
   * Kritisch+Wichtig1) - GENAU dasselbe Muster wie `aufgabenNachladen` (`useDrawerTasks.ts`)/`ratenNachladen` (`useDrawerZahlungen.ts`)
   * (`bestellungLaden` + `versende({ art: "nachgeladen", ... })`, siehe dortiger Kopfkommentar "kein
   * Zeitproblem, ein Bindungsproblem"): ein Schreibfehler DIESES einzelnen Nachladeschritts wird
   * SELBST abgefangen und protokolliert, statt die umgebende `Promise.all` (siehe unten) fuer ALLE
   * anderen, erfolgreich nachgeladenen Ids mit abzulehnen - ein einzelner 404 (die Bestellung fiel
   * zwischen Schreiben und Nachladen weg) darf die uebrigen Erfolge nicht in einen falschen
   * "nicht geaendert"-Fehlertoast verwandeln.
   *
   * NACHBESSERUNG 2 (Pruefung 97e5bffb, Wichtig 2): liefert seither ein Ergebnis statt `Promise<void>` - dieselbe Bauart
   * wie `aufgabenNachladen` (`useDrawerTasks.ts`; Ausgang `geladen | weg | gescheitert | verworfen`) und `ratenNachladen`
   * (`useDrawerZahlungen.ts`; `geladen | weg | gescheitert`): der Ausgang kommt als Wert zurueck, die Funktion wirft nicht.
   * Anders als dort kennt dieses Ergebnis nur zwei Faelle: Erfolg (AUCH wenn `neu === null`, die Bestellung also
   * serverseitig inzwischen verschwunden ist - dort heisst das `weg`; hier gibt es dafuer nichts anzuzeigen, und es ist kein
   * FEHLSCHLAG DIESES Nachladeschritts), Fehlschlag NUR wenn `bestellungLaden` selbst wirft.
   *
   * FEHLERANZEIGE PAKET 2, TASK 10: das Ergebnis ist ein `NachladeErgebnis` (`{ ok: true }` oder
   * `{ ok: false, ursache }`) statt eines `boolean`. Die Ursache wird nicht mehr verschluckt, sondern dem Aufrufer
   * gereicht: jeder Aufrufer meldet den Fehlschlag selbst (Sammelaktionen und Sammelsendung EINE Sammelmeldung,
   * Herausnehmen, Teilung und Split rueckgaengig je einen eigenen Satz) und haengt die Ursache der ERSTEN gescheiterten
   * Bestellung ueber `meldeNachladeFehler` an - ausser sie raet zum Wiederholen (R4: die Aktion ist gelungen). Hier
   * selbst wird nicht gemeldet; eine Sammelaktion mit 40 Zeilen soll nicht 40 Eintraege schreiben. Ein `null` aus
   * `bestellungLaden` bleibt `{ ok: true }`: die Bestellung gibt es nicht mehr, es ist nichts nachzuladen.
   * `useBoardSammelaktionen.ts#sammelStatusAendern` sammelt diese Ergebnisse und unterscheidet in der Meldung
   * einen Nachlade-Fehlschlag EHRLICH von einem Schreibkonflikt (siehe dortiger Kommentar): der
   * Server hat den neuen Status in beiden Faellen unterschiedlich behandelt - beim Nachladefehler HAT
   * er gespeichert, nur die hiesige ANZEIGE weiss noch nichts davon; beim Konflikt hat er GAR NICHT
   * geschrieben. Anders als `aufgabenNachladen`/`ratenNachladen` zeigt diese Funktion selbst KEINEN
   * eigenen Toast (`zeigeMeldung` faellt hier bewusst weg) - eine Sammelaktion mit z. B. 40 Zeilen und
   * mehreren fehlgeschlagenen Nachladevorgaengen soll nicht denselben Meldungs-Slot mehrfach
   * ueberschreiben; die EINE zusammenfassende Meldung baut `useBoardSammelaktionen` stattdessen aus der
   * Anzahl der gescheiterten Ergebnisse.
   */
  function sammelEintragNachladen(id: string): Promise<NachladeErgebnis> {
    return bestellungLaden(id)
      .then((neu): NachladeErgebnis => {
        if (neu !== null) {
          versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
        }
        return { ok: true };
      })
      .catch((fehler: unknown): NachladeErgebnis => {
        // NACHBESSERUNG (Pruefung 78d5a074, Gering): der Text lautete hier "... nach dem
        // Sammel-Statuswechsel ..." - diese Funktion wird seit `boardGruppeSammelAendern` (unten)
        // AUCH vom Sammel-Bereichswechsel gerufen, nicht mehr nur vom Statuswechsel. Verallgemeinert,
        // damit ein Nachladefehler nach einem DnD-Sammelzug nicht mit einer Statusaenderung
        // verwechselt wird. Seit dem Schutz gegen doppelte Teilung ruft auch `teillieferungAnlegen`
        // sie (Quelle und neue Bestellung nach dem Split) - der Text bleibt deshalb neutral.
        console.error(`Bestellung "${id}" konnte nach der Änderung nicht neu geladen werden:`, fehler);
        return { ok: false, ursache: fehler };
      });
  }

  const {
    statusAendern: sammelStatusAendern,
    gruppeAendern: boardGruppeSammelAendern,
    laeuft: sammelStatusLaeuft,
  } = useBoardSammelaktionen(
    zustand.bestellungen,
    ausgewaehlteBestellungen,
    sammelEintragNachladen,
    setSammelStatusDialogOffen,
    setAuswahlLeerenSignal,
    zeigeMeldung,
  );

  // ================= Board: Listen-Editor (Task 14, `Board/ListenEditor.tsx`) - externe Naht ====
  const [listenEditorOffen, setListenEditorOffen] = useState(false);

  /**
   * `saveOrderListEditor` (index.html:7483-7497) OHNE die Server-Persistenz: `konfigurationSchreiben
   * ("orderListColumns", …)` (`api/konfiguration.ts`) braucht den zuletzt GELESENEN `rowVersion` -
   * `GET /api/config/:key` existiert im Backend noch nicht (siehe `api/konfiguration.ts`,
   * Kopfkommentar), es gibt also keinen Stand, von dem aus zu schreiben waere. BENANNTE NAHT (Brief):
   * bis die Leseroute steht, bleibt es bei der woertlichen Erfolgsmeldung des Bestands
   * (`toast('Bestellliste aktualisiert')`, index.html:7500) und einer rein LOKALEN Wirkung im Store
   * (`bestandGeladen`-Patch) - ohne Persistenz zum Server ueberlebt die Aenderung keinen Neuladen.
   */
  function listenSpaltenSpeichern(spalten: OrderListColumn[], entfernteSchluessel: string[]): void {
    versende({
      art: "bestandGeladen",
      bestand: {
        orderListSpalten: spalten,
        ...(entfernteSchluessel.length
          ? { bestellungen: bereinigteBestellungen(zustand.bestellungen, entfernteSchluessel) }
          : {}),
      },
    });
    setListenEditorOffen(false);
    // offen: Paket 3 – Scheinmeldung: die Aenderung bleibt lokal und ueberlebt kein Neuladen (GET /api/config/:key fehlt, Inventar E)
    zeigeMeldung("Bestellliste aktualisiert");
  }

  /**
   * `moveOrderToBoardGroup(id, groupId)` (index.html:6629-6636) ueber die REST-Route: `BoardSeite`
   * liefert nur den reinen Patch (`{ boardGroupOverride }`, siehe dortiger Kopfkommentar) - Schreiben
   * und Melden bleiben beim Aufrufer. `bestellungAendern` verlangt zusaetzlich eine Versionsnummer -
   * die steht nicht im Patch, sondern am Datensatz selbst und wird hier nachgeschlagen.
   *
   * Befund 5, Option C (2026-08-11): eine Bestellung, die nicht (mehr) im Speicher steht, kann nicht
   * geaendert werden - ohne diesen Fund liefe `Number(undefined?.extrasRowVersion)` zu `NaN` und die
   * Anfrage schriebe eine bedeutungslose Versionsnummer.
   *
   * task-rowversion-befunde.md: `bestellungAendern` schreibt wie `drawerAendern` oben ueber `PATCH
   * .../<gruppe>` gegen `order_extras.row_version` - hier gilt deshalb DIESELBE Begruendung fuer
   * `extrasRowVersion` samt Rueckfall `0`, siehe dortiger Kommentar (nicht wiederholt, um die beiden
   * Stellen nicht auseinanderlaufen zu lassen).
   */
  function boardGruppeAendern(id: string, patch: { boardGroupOverride: string }): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === id);
    if (!bestellung) return;
    const extrasRowVersion = Number((bestellung as { extrasRowVersion?: unknown }).extrasRowVersion) || 0;
    bestellungAendern(id, patch, extrasRowVersion)
      .then((neu) => {
        versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id, datensatz: neu });
      })
      .catch((fehler: unknown) => {
        // siehe `drawerAendern` oben - derselbe Grund.
        console.error(`Bestellung "${id}" konnte nicht in die Gruppe umgehaengt werden:`, fehler);
        // R4: der PATCH kam an, nur das Nachladen scheiterte - kein Fehlschlag der Aenderung.
        if (fehler instanceof NachladenNachSchreibenFehler) {
          meldeNachladeFehler(fehler.ursache, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
        } else {
          meldeFehler(fehler, "Die Bestellung konnte nicht geändert werden.");
        }
      });
  }

  /**
   * `setStatusDefinitions(DB.boardColumns)` (index.html:5902, in `load()`). Im Original ein
   * Seiteneffekt auf drei Modul-Globals, hier eine reine Funktion, deren Ergebnis jede abhaengige
   * Flaeche als Argument bekommt (siehe `lib/status.ts`, "Der eine bewusste Umbau"). `useMemo`,
   * weil `activeReportingOrders` es bei jedem Rendern liest und die Ableitung ueber alle
   * Board-Spalten laeuft.
   *
   * VORGEZOGEN (Folgefix zu Auftrag "Bestellboard-Filter — Renderausfall durch fehlende Memoisierung
   * beheben"): stand bis dahin NACH `boardZelleAendern` - das ging nur, weil `boardZelleAendern` eine
   * `function`-Deklaration war (vollstaendig gehoben, `definitionen` war beim tatsaechlichen AUFRUF
   * laengst zugewiesen). Der `useCallback` unten wertet sein Abhaengigkeitsarray dagegen SOFORT, an
   * der Stelle seiner eigenen Zeile, aus - `definitionen` muesste dort schon in der zeitlichen
   * Zugriffssperre (TDZ) eines `const` stehen, bliebe es hinter `boardZelleAendern`. Reine
   * Umsortierung, keine Verhaltensaenderung.
   */
  const definitionen = useMemo(() => setStatusDefinitions(zustand.boardSpalten), [zustand.boardSpalten]);

  /**
   * `commitOrderListEdit` -> `patchOrderListField` (index.html:7628/7690) als Verteiler: `BoardSeite`
   * liefert Schluessel und Rohtext, `orderListSchreibweg` entscheidet daraus, WAS geschrieben wird,
   * und hier steht nur noch, WOHIN.
   *
   * Vier Ausgaenge, weil der Bestand vier Faelle kennt (siehe `lib/orderListEditor.ts`):
   *   `felder`  -> `drawerAendern`, also `bestellungAendern` samt Feldgruppen-Ermittlung
   *   `status`  -> `drawerStatusAendern`, der eigene Endpunkt mit Verlaufseintrag
   *   `nichts`  -> unveraendert oder unbekannt: der Bestand zeichnet nur neu, hier faellt auch das weg
   *   `fehler`  -> die woertliche Meldung des Bestands ("Bitte einen gültigen Bestellwert eingeben")
   *
   * KEIN eigener Schreibkanal: beide Ausgaenge sind dieselben Funktionen, die der Drawer benutzt -
   * samt ihrer `extrasRowVersion`-Ermittlung, ihrem Nachladen und ihrer Fehlerbehandlung. Ein
   * zweiter Kanal waere die naechste Stelle, an der die Versionsnummer auseinanderlaeuft.
   *
   * `useCallback` (Folgefix zu Auftrag "Bestellboard-Filter — Renderausfall durch fehlende
   * Memoisierung beheben") - dieselbe Begruendung wie bei `drawerOeffnen`/`kontextmenueOeffnen` oben:
   * geht unveraendert als `onZelleAendern` an `<BoardSeite>`, dort in `bearbeitungBeenden`s eigenem
   * Abhaengigkeitsarray. Abhaengigkeiten: `zustand.bestellungen` (eigene Nachschlage) und
   * `definitionen` (an `orderListSchreibweg` gereicht) - beide aendern sich nur, wenn sich am Board
   * selbst etwas aendert.
   *
   * BEWUSST NICHT in der Abhaengigkeitsliste: `drawerAendern`/`drawerStatusAendern`. Beide sind PLAIN
   * Funktionsdeklarationen im Komponentenkoerper von `App` (keine eigenen `useCallback`, ausserhalb des
   * Auftragsumfangs dieses Folgefixes) und wuerden bei JEDEM App-Render neu entstehen - stuenden sie hier,
   * verlöre `boardZelleAendern` seine Referenzstabilitaet fuer GENAU DIE Faelle, derentwegen dieser Fix
   * existiert, und die benannten Zeilen waeren wirkungslos. Das ist HIER folgenlos, weil beide ihrerseits
   * nur `zustand.bestellungen` (bereits eigene Abhaengigkeit dieses `useCallback`s - jede tatsaechliche
   * Aenderung erzwingt also ohnehin eine neue `boardZelleAendern`-Fassung, die wieder die JEWEILS
   * AKTUELLEN Fassungen dieser Funktionen schliesst) und `versende` (Dispatch aus `useReducer`, laut
   * React-Vertrag ueber die gesamte Komponenteninstanz hinweg REFERENZGLEICH) lesen/rufen - keine liest je
   * einen Wert, der sich AENDERN koennte, ohne dass `zustand.bestellungen` es ebenfalls tut. `meldeFehler`
   * (`state/fehlerStapel.ts`) ist eine Modulfunktion und braucht ohnehin keinen Eintrag.
   */
  const boardZelleAendern = useCallback(
    (id: string, key: string, wert: string): void => {
      const bestellung = zustand.bestellungen.find((b) => b.id === id);
      if (!bestellung) return;
      const weg = orderListSchreibweg(bestellung, key, wert, definitionen);
      if (weg.art === "felder") drawerAendern(id, weg.aenderung);
      else if (weg.art === "status") drawerStatusAendern(id, weg.status);
      else if (weg.art === "fehler") meldeFehler(weg.meldung);
    },
    [zustand.bestellungen, definitionen],
  );

  const { zeitstrahlSendungen, zeitstrahlListeNeuLaden } =
    useZeitstrahlSendungen(seite, zustand.bestellungen, definitionen);

  /**
   * Nachtrag 2026-08-11 (Auftraggeber-Entscheidung, Task-3-Brief): abweichend vom Bestand (dort
   * bleibt der `settings-2`-Ausloeser der Werkzeugleiste OHNE `manage_settings` bedienbar, die
   * Meldung kommt erst beim Uebernehmen im Dialog, `index.html:7484`) wird der Ausloeser hier ohne
   * das Recht `disabled` mit `title="fehlende Berechtigung"` gerendert. Siehe
   * `Werkzeugleiste.tsx`/`BoardSeite.tsx` (Durchreichung) und `test/komponenten/rechte/matrix.test.tsx`
   * (Matrixeintrag, `art: "gesperrt"`).
   */
  const spaltenEditorGesperrt = !zustand.rechte.has("manage_settings");
  // Kontotyp "Nur lesen" (Task 6): EINMAL hier entschieden und durch Board, Kontextmenue und Uebersicht gereicht
  // (Kopfzeile und Kurssicherung-Detail leiten sich aus ihren `rechte` selbst ab). Siehe `state/nurLeser.ts`.
  const nurLeser = istNurLeser(zustand.rechte);

  // ================= Anlagemaske und Teillieferung (Task 9, `NewOrder/`) - externe Naht ==========
  //
  // Herkunft: `newOrder()`/`saveNewOrder` (index.html:7910-7939, 8090-8158), `splitOrder`-Ausloeser
  // (`:9283`, Drawer-Fussleiste - der Handler selbst steht oben bei `drawerTeillieferungOeffnen`),
  // `openConsolidationDialog` (`:7046`, Werkzeugleisten-Knopf - siehe `Werkzeugleiste.tsx`).

  /** `newOrderDraft`/die Sichtbarkeit von `#newOrderOverlay` (index.html:7914, 7936). */
  const [neueBestellungOffen, setNeueBestellungOffen] = useState(false);
  const [duplikat, setDuplikat] = useState<AnlagemaskeDuplikat | undefined>();
  const { anlegen: neueBestellungAnlegen, laeuft: neueBestellungLaeuft } = useBestellungAnlegen(
    jtlLager, versende, setNeueBestellungOffen, zeigeMeldung, zeigeHinweis,
  );

  /**
   * `newOrder()` (index.html:7910-7939), auf den Rechtewaechter und das Oeffnen beschraenkt (der
   * INHALT der Maske selbst ist Task 23, `Anlagemaske.tsx`). Woertlich ZUERST der Wächter (`:7911`):
   * `if (!hasPermission('edit_orders')) return toast('Du darfst keine Bestellungen anlegen');` - OHNE
   * das Recht bleibt die Maske zu, MIT der wörtlichen Meldung. Der Knopf selbst bleibt in
   * `Kopfzeile.tsx` für alle Konten mit mindestens einem Schreibrecht unbedingt sichtbar (gemeldeter
   * Befund, siehe dortiger Kopfkommentar) - NICHT repariert, der Wächter sitzt hier im Handler, genau wie im
   * Bestand im Funktionskopf. Einzige Ausnahme seit Task 6 des Plans „Kontotyp Nur lesen": ein Konto ohne
   * jedes Schreibrecht (`state/nurLeser.ts#istNurLeser`) bekommt den Knopf nicht; für dieses Konto ist der
   * Wächter hier ein zweiter Riegel hinter der Kopfzeile.
   */
  function neueBestellungOeffnen(): void {
    if (!hatRecht(zustand.rechte, "edit_orders")) {
      meldeFehler("Du darfst keine Bestellungen anlegen");
      return;
    }
    setDuplikat(undefined);
    setNeueBestellungOffen(true);
  }

  function bestellungDuplizierenOeffnen(id: string): void {
    const quelle = zustand.bestellungen.find((bestellung) => bestellung.id === id);
    const lieferantId = String(quelle?.supplierId ?? "").trim();
    const lieferantName = String(quelle?.supplier ?? "").trim();
    const lager = String(quelle?.lagerName ?? "").trim();
    if (!quelle || !lieferantId || !lieferantName || !lager) {
      meldeFehler("Diese Bestellung kann ohne Lieferant und Lager nicht dupliziert werden.");
      return;
    }
    const positionen = Array.isArray(quelle.items)
      ? quelle.items.map((position) => ({
          sku: position.sku,
          name: position.name,
          qty: position.qty,
          price: position.price,
          kArtikel: position.kArtikel,
          mwstProzent: position.mwstProzent,
        }))
      : [];
    const quellenLagerId = Number(quelle.kLager);
    // Die Waehrung stammt bei einer Neuanlage aus dem aktuellen JTL-Lieferantentreffer, nicht aus
    // einem alten Bestellkopf. Die Kopie nimmt deshalb nur die stabile Kennung als Zuordnung mit und
    // laedt den Treffer ueber denselben Pfad neu.
    anlagemaskeLieferantenSuche(lieferantName)
      .then((treffer) => {
        const lieferant = treffer.find((eintrag) => String(eintrag.id) === lieferantId);
        if (!lieferant) {
          meldeFehler("Der aktuelle Lieferant konnte nicht für die Duplizierung geladen werden.");
          return;
        }
        setDuplikat({
          lieferant,
          lager,
          lagerId: Number.isInteger(quellenLagerId) && quellenLagerId > 0 ? quellenLagerId : undefined,
          positionen,
        });
        setNeueBestellungOffen(true);
      })
      .catch((fehler: unknown) =>
        meldeFehler(fehler, "Der aktuelle Lieferant konnte nicht für die Duplizierung geladen werden."),
      );
  }


  /** `splitOrder(o.id)`s Zustand (index.html:8165, `partialDeliveryDraft.sourceId`). `null` heisst
   *  geschlossen; der Datensatz kommt beim Zeichnen frisch aus `zustand.bestellungen` (siehe
   *  `teilBestellung` unten) - dasselbe Muster wie `drawer` oben. */
  const [teilenId, setTeilenId] = useState<string | null>(null);

  /**
   * `JtlLoeschDialog`s Zustand (Task 12) - `null` heisst geschlossen. ANDERS als `teilenId` haelt
   * dieser State den kompletten, zum Klickzeitpunkt in `Drawer.tsx` bereits aufgeloesten Datensatz
   * (`id`/`jtlOrderId`/`orderNo`) statt nur einer id: der Dialog selbst braucht `orderNo` fuer seine
   * Eintipp-Bestaetigung (Task 11), `jtlLoeschenBestaetigt` unten braucht `id` UND `jtlOrderId` fuer
   * zwei VERSCHIEDENE Zwecke (`versende`/`jtlBestellungLoeschen`) - ein erneutes Nachschlagen in
   * `zustand.bestellungen` beim Bestaetigen waere hier, anders als bei `teilBestellung` unten,
   * ueberfluessig, weil keines der drei Felder sich waehrend der offenen Bestaetigung sinnvoll
   * aendern kann.
   */
  const [jtlLoeschDialog, setJtlLoeschDialog] = useState<
    { id: string; jtlOrderId: number; orderNo: string } | null
  >(null);

  /**
   * `TeillieferungDialogProps.onTeilen` (`TeillieferungDialog.tsx:73`) - `splitOrder`/
   * `savePartialDelivery` (index.html:8161-8243) OHNE die dort erzeugte LOKALE Kopie
   * (Verhaltensaenderung 4, siehe `TeillieferungDialog.tsx`-Kopfkommentar): geschrieben wird nach
   * JTL, ueber `POST /api/jtl/orders/:kLieferantenBestellung/split` (`jtlSchreiben.ts:357-384`).
   *
   * `:kLieferantenBestellung` ist `bestellung.jtlOrderId` (`orders.jtl_order_id`, ueber
   * `oeffentlich()` camelCase - gemessen: `test/komponenten/api/bestellungen.test.ts` fuehrt
   * dasselbe Feld an einer echten Antwort vor). Eine gerade erst ueber die Anlagemaske angelegte
   * Bestellung traegt hier NOCH keinen Wert (siehe `neueBestellungAnlegen` oben) - der Riegel weist
   * das unten laut ab, das ist beabsichtigt, nicht uebersehen.
   *
   * LUECKE GESCHLOSSEN (Task 10, Plan "Teillieferung/Historie"): die vorige Fassung dieses
   * Kommentars beschrieb hier eine ZWEIFACHE benannte Luecke gegenueber `alsSplitKoerper`
   * (`jtlSchreiben.ts:209-229`) - `lagerId` fehlte GANZ, und `mengen` trug je Position nur `qty`,
   * NIE `kLieferantenBestellungPos`. Beide Haelften sind seit diesem Task geschlossen:
   *   - `kLieferantenBestellungPos` kommt seit Task 9 als `TeillieferungMenge.posId` bereits FERTIG
   *     abgeleitet herein (`TeillieferungDialog.tsx#absenden`, ueber `schreibwegVon`) - diese
   *     Funktion muss den Wert nur noch durchreichen, keine eigene Herleitung/Validierung mehr.
   *   - `lagerId`: ERSTE Fassung dieses Fixes griff ueber `lib/lager.ts#lagerKennung(bestellung.
   *     warehouse, jtlLager)` - ein NAMENS-Umweg, der fuer eine BESTEHENDE Bestellung praktisch immer
   *     `undefined` lieferte, weil `order_extras.warehouse` seit Migration 017 (2026-08-22) entfernt
   *     ist und kein `Order`-Objekt aus dem Hub seither einen `warehouse`-Wert traegt (Befund aus der
   *     Pruefung dieses Tasks, RICHTIGGESTELLT direkt im Anschluss). Der Umweg ist ueberfluessig: die
   *     JTL-Lagerkennung liegt an der geladenen Bestellung bereits VOR, unter `kLager` (`bk.kLager`,
   *     `dbo.tLieferantenBestellung`, `backend/src/jtl/bestellungen.ts:151`, ueber `zusammenfuehren`
   *     1:1 nach `Order.kLager` gereicht, `api/jtlBestellungen.ts:430`/`lib/types.ts:15`) - `lagerId`
   *     liest deshalb jetzt DIREKT `bestellung.kLager`, ganz ohne `lagerKennung`/`jtlLager`
   *     (die beide fuer die NEUANLAGE, `neueBestellungAnlegen` oben, unveraendert gebraucht werden -
   *     DORT gibt es keine bestehende Bestellung mit `kLager`, sondern nur den vom Nutzer aus einer
   *     Namens-Auswahlliste gewaehlten `daten.warehouse`, ein ANDERER Fall).
   *
   * KEINE VALIDIERUNG HIER (Auftrag woertlich, "reines Durchreichen"): ein fehlendes `jtlOrderId`
   * (bleibt `NaN` in der URL, siehe `kennung` unten - unveraendert seit Fix-Runde 1) wird hier NICHT
   * abgefangen - der Riegel des Backends (`alsSplitKoerper`) weist das mit einer eigenen, ueber
   * `meldeFehler` sichtbaren Meldung ab (Kontext plus Servertext). Ein fehlendes `kLager` (z. B. eine noch
   * nicht ueber JTL angelegte Bestellung) faellt aus demselben Grund unter dieselbe Ablehnung -
   * `Number(undefined)` ist `NaN`, kein stillschweigend erfundener Vorgabewert.
   */

  /**
   * Erzwingt ein Nachladen von `LineageHinweis` im OFFENEN Quell-Drawer nach einem erfolgreichen
   * Split (Endabnahme-Fixrunde, Pflichtreview-Befund 2): `teillieferungAnlegen` unten schliesst nach
   * Erfolg nur den Dialog (`setTeilenId(null)`) - der Quell-Drawer selbst bleibt offen,
   * `bestellung.id` aendert sich dabei NICHT, und `LineageHinweis`s `useEffect` haengt bislang
   * ausschliesslich an `orderId`. Ohne einen zusaetzlichen Ausloeser sieht der Nutzer im offenen
   * Quell-Drawer nach dem Splitten schlicht NICHTS (die Zeile "aufgeteilt in ..." erscheint erst
   * beim naechsten Oeffnen/Schliessen des Drawers) - KEINE rohe UUID, wie eine fruehere, vom
   * Koordinator ausdruecklich richtiggestellte Fehldiagnose im Ledger vermerkte.
   *
   * Dieselbe Zaehler-Bauart wie `auswahlLeerenSignal` oben (siehe dortiger Kopfkommentar): ein reiner
   * Zaehler, bei jedem erfolgreichen Split um eins erhoeht, als zusaetzliche Abhaengigkeit bis zu
   * `LineageHinweis` durchgereicht (`App.tsx` -> `Drawer.tsx` -> `LineageHinweis.tsx`) - dessen
   * `useEffect` laedt dadurch neu, sobald sich der Zaehler aendert, unabhaengig davon, ob `orderId`
   * gleich bleibt. Anders als `auswahlLeerenSignal` (das einen Ref-Vergleich braucht, um die Auswahl
   * nicht bei jedem Rendern erneut zu leeren) genuegt hier die blosse Aufnahme in die
   * Abhaengigkeitsliste: `LineageHinweis`s Effekt ist ohnehin idempotent (laedt einfach erneut), kein
   * Leerungszustand, der sich versehentlich wiederholen koennte.
   */
  const [lineageVersion, setLineageVersion] = useState(0);

  /**
   * Schutz gegen doppelte Teilung (Plan 2026-10-20-doppelte-teilung-schutz): `teilenLaeuft` sperrt den
   * Knopf im Dialog (Anzeige), `teilenLaeuftRef` ist der synchrone Riegel dahinter - zwei Klicks im
   * selben Takt erreichen beide den Handler, bevor React `disabled` gesetzt hat (Muster:
   * `neueBestellungLaeuftRef` in `useBestellungAnlegen.ts`, `rueckgaengigLaeuft` unten).
   * `teilenStand` setzt nach einem 409 die Mengeneingaben des offenen Dialogs zurueck (Prop
   * `eingabenStand`): sie haengen an der Zeilenposition und beziehen sich auf den veralteten Stand.
   * Die Kopieroptionen bleiben dabei erhalten - deshalb KEIN `key` am Dialog, das setzte sie zurueck.
   */
  const [teilenLaeuft, setTeilenLaeuft] = useState(false);
  const teilenLaeuftRef = useRef(false);
  const [teilenStand, setTeilenStand] = useState(0);

  function teillieferungAnlegen(
    bestellungId: string,
    mengen: TeillieferungMenge[],
    copyOptions: { dokumente: boolean; tasks: boolean },
  ): void {
    if (teilenLaeuftRef.current) return;
    teilenLaeuftRef.current = true;
    setTeilenLaeuft(true);
    const bestellung = zustand.bestellungen.find((b) => b.id === bestellungId);
    const kennung = Number((bestellung as { jtlOrderId?: unknown } | undefined)?.jtlOrderId);
    const lagerId = Number((bestellung as { kLager?: unknown } | undefined)?.kLager);
    anfrage<{ neueOrderId?: unknown; positionsdokumente?: { nichtUebernommen?: unknown[] } } | undefined>(`/api/jtl/orders/${encodeURIComponent(String(kennung))}/split`, {
      methode: "POST",
      koerper: {
        lagerId,
        teilmengen: mengen.map((m) => ({ kLieferantenBestellungPos: m.posId, menge: m.qty })),
        // Die Mengen, die der Dialog angezeigt hat: der Hub liest die Quelle in der Schreibsperre frisch
        // und weist die Teilung mit 409 ab, wenn sie davon abweicht (versehentlich wiederholt, oder
        // zweiter Tab mit altem Stand).
        angezeigteMengen: mengen.map((m) => ({ kLieferantenBestellungPos: m.posId, menge: m.angezeigteMenge })),
        copyOptions,
      },
    })
      .then(async (antwort) => {
        // Quelle UND neue Bestellung einzeln nachladen: der Split sendet kein SSE-Ereignis, ohne
        // Nachladen zeigte der Dialog weiter die alte Menge, und eine bewusste zweite Teilung bekaeme
        // vom Hub ein 409. Zwei VERSCHIEDENE Bestellungen, also kein Store-Wettlauf (AGENTS.md); der
        // Dialog schliesst erst danach, damit der naechste Aufruf den frischen Stand sieht.
        const neueOrderId = typeof antwort?.neueOrderId === "string" ? antwort.neueOrderId : null;
        const nachgeladen = await Promise.all(
          [bestellungId, ...(neueOrderId ? [neueOrderId] : [])].map((id) => sammelEintragNachladen(id)),
        );
        setTeilenId(null);
        // Endabnahme-Fixrunde, Pflichtreview-Befund 2: siehe Kopfkommentar `lineageVersion` oben -
        // ohne diesen Zaehler bleibt `LineageHinweis` im offenen QUELL-Drawer (die Bestellung, aus der
        // gerade gesplittet wurde) nach einem erfolgreichen Split auf ihrem alten, noch leeren Stand
        // stehen, weil weder `orderId` noch sonst etwas sich fuer sie aendert.
        setLineageVersion((v) => v + 1);
        // R4 wie bei der Rueckfuehrung: die Teillieferung IST angelegt, scheitert nur das Nachladen, ist die Anzeige
        // veraltet - das sagt der Stapel (der Toast bestaetigt die Teilung). Mit dem alten Stand bekaeme eine bewusste
        // zweite Teilung vom Hub ein 409. Task 10: die Ursache der ERSTEN gescheiterten Bestellung (Quelle vor neuer
        // Bestellung) steht hinter dem Satz, ausser sie raet zum Wiederholen - "Bitte erneut versuchen" hinter "angelegt"
        // legte eine zweite Teillieferung an (R-T7d).
        if (istEingehaengt.current) {
          meldeNachladeFehlschlaege(nachgeladen, () => "Teillieferung angelegt – die Anzeige ist aber nicht mehr aktuell. Bitte neu laden.");
        }
        // Regel B (Positionsdokumente bei Teilung): die Teilung gilt, auch wenn der Hub einzelne Artwork-/Produktionsbild-
        // Ketten nicht an die neue Bestellung uebernehmen konnte (`positionsdokumente.nichtUebernommen`, siehe
        // `backend/src/domaene/splitPositionsdokumente.ts`). Sie bleiben in der Ursprungsbestellung; hier steht nur, WIE VIELE.
        // Ohne das Feld (aeltere Hub-Fassung, "Dokumente uebernehmen" abgewaehlt) oder bei leerer Liste gibt es keine Meldung.
        const nichtUebernommen = antwort?.positionsdokumente?.nichtUebernommen;
        const anzahlNichtUebernommen = Array.isArray(nichtUebernommen) ? nichtUebernommen.length : 0;
        if (anzahlNichtUebernommen > 0 && istEingehaengt.current) {
          meldeFehler(
            anzahlNichtUebernommen === 1
              ? "Teillieferung angelegt – 1 Positionsdokument konnte nicht übernommen werden und bleibt in der Ursprungsbestellung."
              : `Teillieferung angelegt – ${anzahlNichtUebernommen} Positionsdokumente konnten nicht übernommen werden und bleiben in der Ursprungsbestellung.`,
          );
        }
        zeigeMeldung("Teillieferung angelegt");
      })
      .catch(async (fehler: unknown) => {
        // `null` heisst Abbruch oder 401: still, wie bei `meldeFehler`.
        const grund = fehlertext(fehler);
        if (grund === null) return;
        // Ungewisser Ausgang (R-T7d): die Antwort ging verloren, JTL kann die Teillieferung trotzdem angelegt haben.
        // Dazu gehoert der mehrdeutige Schreibfehler des Backends (R-T6f, R-Z4): die Split-Route legt ueber
        // `legeBestellungAn` an und kann den `JtlMehrdeutigeErstellungError` (502, eigener Text in Umschrift und
        // Entwicklerton) werfen. Auf dieser Route kommt ein Backend-502 nur daher; ein Proxy-502 landet ohnehin in
        // `ausgangUnklar`. Der Nutzer bekommt in beiden Faellen den eigenen Satz, nicht den Servertext.
        if (ausgangUnklar(grund) || (fehler instanceof ApiFehler && fehler.status === 502)) meldeFehler(TEILLIEFERUNG_AUSGANG_UNKLAR_MELDUNG);
        // Jeder uebrige Fall - eindeutige Ablehnung (400 mit Servertext usw.): der feste Satz als Kontext, die Ursache dahinter.
        else meldeFehler(`Die Teillieferung konnte nicht angelegt werden. ${grund}`);
        if (fehler instanceof KonfliktFehler) {
          // 409: der Hub hat NICHTS geschrieben, die angezeigten Mengen waren veraltet. Die Quelle
          // nachladen, damit der offene Dialog (und ein erneuter Versuch) den frischen Stand zeigt.
          // Scheitert das Nachladen, sagt die 409-Meldung oben schon "Bitte neu laden".
          await sammelEintragNachladen(bestellungId);
          setTeilenStand((v) => v + 1);
        }
      })
      .finally(() => {
        teilenLaeuftRef.current = false;
        setTeilenLaeuft(false);
      });
  }

  // ================= Split rueckgaengig (Task 7, `NewOrder/SplitRueckgaengigDialog.tsx`) ===========

  /** Die Teillieferung, deren Rueckfuehrung der Dialog zeigt (nur die id; der Datensatz kommt frisch aus dem Store). */
  const [rueckgaengigId, setRueckgaengigId] = useState<string | null>(null);
  const [rueckgaengigLaeuft, setRueckgaengigLaeuft] = useState(false);

  function splitRueckgaengigBestaetigt(auswahl: RueckfuehrungsAuswahl): void {
    // `rueckgaengigKennung` leitet die JTL-Kennung des Kindes aus dem Store ab (null: nicht geladen/ohne Kennung).
    const kennung = rueckgaengigKennung;
    if (!rueckgaengigId || kennung === null) return;
    const kindId = rueckgaengigId;
    // Wie `drawerLoeschen`: die Kennung der offenen Kindbestellung merken, damit der "nicht gefunden"-Effekt
    // das eigene Loeschen nicht meldet (nur, wenn genau das Kind im Drawer offen ist).
    const offeneKennung = drawer?.id === kindId ? aktuelleKennungRef.current : null;
    setRueckgaengigLaeuft(true);
    splitRueckgaengigAusfuehren(kennung, auswahl)
      .then((ergebnis) => {
        setRueckgaengigLaeuft(false);
        setRueckgaengigId(null);
        if (ergebnis.stand === "abgeschlossen") {
          // Das Kind ist in JTL und im Hub geloescht: wie nach einer JTL-Loeschung aus dem Store nehmen.
          // Die Split-Marke "aufgeteilt" der Quelle verschwindet von selbst (`splitKinderIndex` zaehlt nur geladene Kinder).
          versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id: kindId, datensatz: null });
          if (offeneKennung) eigeneLoeschung.current = offeneKennung;
          lineageOeffnen(ergebnis.quelleOrderId);
          setLineageVersion((v) => v + 1);
          // Die Quelle traegt jetzt die zurueckgefuehrten Mengen: einzeln nachladen, damit der Drawer nicht die
          // alten zeigt. HINTER dem Dispatch oben (Store-Wettlauf, siehe AGENTS.md): zwei `nachgeladen`-Dispatches
          // derselben Bestellung ueberschreiben sich, der spaetere gewinnt.
          // R4 (Ruling R-T7b): der Split IST aufgeloest, scheitert nur das Nachladen der Quelle, ist ihre Anzeige veraltet -
          // das sagt der Stapel (der Toast unten bestaetigt den Split). Das Ergebnis wird nicht verworfen.
          // Task 10: die Ursache steht hinter dem Satz, ausser sie raet zum Wiederholen (`meldeNachladeFehler`).
          void sammelEintragNachladen(ergebnis.quelleOrderId).then((nachgeladen) => {
            if (!nachgeladen.ok && istEingehaengt.current) {
              meldeNachladeFehler(
                nachgeladen.ursache,
                "Split aufgelöst – die Anzeige der Ausgangsbestellung ist aber nicht mehr aktuell. Bitte neu laden.",
              );
            }
          });
          const quelleNummer = ergebnis.quelleNummer
            || String(zustand.bestellungen.find((b) => b.id === ergebnis.quelleOrderId)?.orderNo ?? "");
          zeigeMeldung(`Split aufgelöst – Mengen in ${quelleNummer || "der Ursprungsbestellung"} zurückgeführt`);
        } else {
          // Teilfehler: Zustand ist in JTL/Hub teilweise geaendert, die Liste neu laden.
          bestandVollNeuLaden(ansicht.board.states);
          setLineageVersion((v) => v + 1);
          meldeFehler(ergebnis.fehler ?? "Die Rückführung ist nicht abgeschlossen.");
        }
      })
      .catch((fehler: unknown) => {
        setRueckgaengigLaeuft(false);
        setRueckgaengigId(null);
        meldeFehler(fehler, "Der Split konnte nicht rückgängig gemacht werden.");
      });
  }

  // ================= Uebersicht (Task 7, `OverviewPage/UebersichtSeite.tsx`) - externe Naht ======

  /**
   * `openOverviewAction(id, kind)`/`openFromOverview(id)` (index.html:14494-14499) - der Aufrufer
   * der Uebersicht-Seite: `showPage('board'); openDrawer(id)`, plus die Reiterwahl aus dem
   * Handlungsbedarf-/Aufgaben-Klick (`kind==='payment'` -> Tab "pay", `'task'` -> Tab "tasks", sonst
   * der Standardreiter "main"). Fuer den Klick auf eine EINZELNE Aufgabenzeile steht seit dem
   * 2026-08-25 `uebersichtAufgabeOeffnen` daneben (`onAufgabeOeffnen`, siehe dort).
   *
   * DIE REITERWAHL AM AUFGABENTYP GILT UNVERAENDERT WEITER (Fixrunde 1, Befund W-1 - eine
   * Produktentscheidung des Auftraggebers GEGEN eine erste Fassung dieser Aufgabe, die
   * `uebersichtAufgabeOeffnen` zunaechst IMMER auf "tasks" fuehren liess): eine Zahlungsaufgabe
   * (`isPaymentRequestTask`) fuehrt weiterhin auf den ZAHLUNGSreiter, jede andere auf den Tasks-
   * Reiter - wie im Bestand (`openOverviewTask`, index.html:14501) und wie im Rueckfall unten in
   * `UebersichtSeite.tsx:418-425`. NEU ist nur der zweite Schritt: springt der gewaehlte Reiter auf
   * "tasks", waehlt `uebersichtAufgabeOeffnen` zusaetzlich die Aufgabe selbst an - auf dem
   * Zahlungsreiter waere eine Aufgabenauswahl wirkungslos, dort sieht man die Zeile ohnehin nicht.
   *
   * `aufgabenIndex` (Fixrunde 1, Befund "Gering" - Dedup mit `uebersichtAufgabeOeffnen` daneben, die
   * bis hierhin denselben Treffer-/Kennung-/`setzeAnsicht`-Block ein zweites Mal aufbaute): optional,
   * NUR von dort gereicht. Waehlt die Funktion "tasks" als Reiter UND kennt sie den Index, traegt sie
   * die Aufgabe selbst in die Auswahl ein - sonst (jeder andere Aufrufer, `art` ohne Index) bleibt
   * `aufgabe: null` wie bisher.
   *
   * `seiteWechseln` (seit dem 2026-09-01, Auftrag "Bestellung in-place oeffnen"): Vorgabe `true` -
   * damit erzwingt `setzeAnsicht` weiterhin `seite: "board"`, wie es JEDER Aufrufer bisher erwartete.
   * Nur die Verdrahtung der Uebersichtsseite (`onOeffnen`/`onAufgabeOeffnen` an `UebersichtSeite`,
   * siehe dort) reicht `false` durch: ein Klick, der VON der Uebersichtsseite ausgeht, soll die
   * Bestellung als Drawer AUF der Uebersichtsseite oeffnen statt aufs Board wegzunavigieren - der
   * Drawer haengt ohnehin nur an `ansicht.bestellung`, nicht an `ansicht.seite` (siehe Kommentar zum
   * `drawer`-`useMemo`). Der Aufruf ueber `NutzerSeite` (unten, eigene Verdrahtung) gibt den Parameter
   * bewusst NICHT mit und bleibt damit unveraendert bei `seite: "board"`.
   */
  function uebersichtOeffnen(
    bestellungId: string,
    art: string,
    kennungHinweis?: string,
    aufgabenIndex?: number,
    seiteWechseln: boolean = true,
  ): void {
    const treffer = zustand.bestellungen.find((b) => String(b.id) === bestellungId);
    if (!treffer) {
      // Kleinbefund der Pruefrunde 2 (`:4106`): vor Aufgabe 3 wechselte ein Klick auf eine
      // unbekannte id wenigstens aufs Board, seither (die Funktion kehrt seit Fixrunde 1 VOR jeder
      // Aenderung zurueck) passiert BEIM Klick still gar nichts - kein Wechsel, keine Meldung.
      // ENTSCHIEDEN: dieselbe Meldung wie beim kaputten Tieflink (§ 5.3) statt eines stillen
      // Nichtstuns - drei Aufrufer zeigen eine id, die gerade NICHT im geladenen Bestand steht
      // (Uebersicht-Handlungsbedarf, Nutzerverwaltungs-Audit: archiviert, gefiltert, noch nicht
      // nachgeladen; `Terminaenderungen.tsx`: die id kommt aus dem DSV-Backend, nie aus dem
      // Store, Befund 1 der Pruefrunde 4 - dort war die Unmoeglichkeitsbehauptung dieses
      // Kommentars falsch, siehe `meldungBestellungNichtGefunden` am Modulkopf).
      // `kennungHinweis` (`Terminaenderungen.tsx#bestellungAnzeige(bestellung)` - seit Auftrag
      // "be-*-Bestellnummer statt jtl_order_id in Terminaenderungen-Pills" (01.09.2026) die ECHTE
      // JTL-Bestellnummer `orderNo`, nur ersatzweise `orders.jtl_order_id` als Text, siehe
      // Kopfkommentar von `meldungBestellungNichtGefunden`) zeigt die lesbare Kennung, wo sie
      // vorliegt - die uebrigen Aufrufer kennen nur die rohe id (meist eine UUID) und liefern
      // keinen Hinweis, dieselbe Klartext-Zurueckhaltung wie `anzeigenameZu` weiter unten fuer
      // Nutzer-ids begruendet.
      meldeFehler(meldungBestellungNichtGefunden(kennungHinweis ?? null));
      return;
    }
    if (!darfBestellungOeffnen(treffer)) return;
    const kennung = kennungVon(treffer, bestellungId);
    const tab = art === "payment" ? "pay" : art === "task" ? "tasks" : "main";
    const tasks = ((treffer as { tasks?: unknown }).tasks as OrderTask[] | undefined) || [];
    const taskId =
      tab === "tasks" && aufgabenIndex !== undefined ? String(tasks[aufgabenIndex]?.id ?? "") : "";
    // EIN `setzeAnsicht` fuer alle Aenderungen (Seite, Bestellung, Reiter, ggf. Aufgabe) - mehrere
    // Aufrufe hintereinander gaeben mehrere Verlaufseintraege, und die Zurueck-Taste liefe mehrfach
    // ins Leere (Spezifikation, Risiko 5).
    setzeAnsicht({
      ...(seiteWechseln ? { seite: "board" as const } : {}),
      bestellung: {
        kennung,
        tab,
        aufgabe: taskId ? { kind: "task", taskId } : null,
        dokument: null,
      },
    });
    versende({ art: "geoeffnet", verweis: { entitaet: BESTELLUNG_ENTITAET, id: bestellungId } });
  }

  /**
   * VERDRAHTET SEIT DEM 2026-08-25. Hier stand zuvor der Vermerk, `onAufgabeOeffnen` bleibe
   * ungesetzt und die Uebersicht loese den Klick selbst auf `onOeffnen(id, "task")` auf - die
   * Aufgabe wurde also nie angesprungen, nur ihr Reiter geoeffnet. Mit der Adresse als Traeger geht
   * beides in einem Zug (Spezifikation § 1.2, Nebenbefund).
   *
   * REITERWAHL AM AUFGABENTYP (Fixrunde 1, Befund W-1): eine erste Fassung setzte hier IMMER
   * `tab: "tasks"` fest und riss damit die Zahlungsaufgaben-Regel des Bestands ein - fuer
   * `payment_manager` besteht die Uebersichts-Aufgabenliste AUSSCHLIESSLICH aus Zahlungsaufgaben
   * (`UebersichtSeite.tsx:212`), dort haette JEDER Klick auf dem falschen Reiter geendet. Seit
   * derselben Fixrunde (Befund "Gering") reicht diese Funktion nur noch `art` und `aufgabenIndex` an
   * `uebersichtOeffnen` weiter, die den ganzen Rest (Treffer, Kennung, Reiter, `setzeAnsicht`)
   * uebernimmt - vorher stand hier eine zweite, fast wortgleiche Kopie dieses Blocks.
   *
   * `seiteWechseln: false` (seit dem 2026-09-01): diese Funktion wird ausschliesslich von der
   * Uebersichtsseite gerufen (`UebersichtSeite.tsx#onAufgabeOeffnen`) - ein Klick auf eine
   * Aufgabenzeile dort soll die Seite nie mehr wechseln, siehe `uebersichtOeffnen`.
   */
  function uebersichtAufgabeOeffnen(bestellungId: string, aufgabenIndex: number): void {
    const treffer = zustand.bestellungen.find((b) => String(b.id) === bestellungId);
    const tasks = ((treffer as { tasks?: unknown } | undefined)?.tasks as OrderTask[] | undefined) || [];
    const art = isPaymentRequestTask(tasks[aufgabenIndex]) ? "payment" : "task";
    uebersichtOeffnen(bestellungId, art, undefined, aufgabenIndex, false);
  }

  /**
   * `toggleOverviewTask(id, taskIndex, checked, event)` (index.html:14507-14518): setzt
   * `task.done`/`updatedAt`/`updatedBy` und schreibt danach die GESAMTE Bestellung durch (`save()`).
   *
   * VERDRAHTET SEIT DEM 2026-08-15. Hier stand zuvor eine ausfuehrliche Begruendung, warum das NICHT
   * geht: `aufgabeAendern` verlange eine Aufgaben-eigene `rowVersion`, die `OrderTask` nirgends
   * fuehre, weil `GET /api/orders/:id` die Aufgaben gar nicht mitliefere. Der zweite Halbsatz stimmt
   * seit dem 2026-08-13 nicht mehr (`backend/src/domaene/orders.ts`, Abschnitt "WARUM `tasks` UND
   * `payments` AN DER OBERSTEN EBENE STEHEN") - die Aufgaben kommen mit, durch `oeffentlich()`, also
   * samt `rowVersion`. Damit faellt die ganze Begruendung, und mit ihr der Rueckfall auf den
   * Bestellungs-Kanal, der ohnehin nur in `FeldOhneGruppeFehler` lief: das Haekchen war LAUT
   * wirkungslos.
   *
   * `updatedAt`/`updatedBy` setzt diese Funktion nicht mehr selbst - das tut der Server
   * (`updateVersioned`). Der Bestand trug sie ein, weil er seinen Datenbestand selbst hielt.
   */
  function uebersichtAufgabeUmschalten(bestellungId: string, aufgabenIndex: number, erledigt: boolean): void {
    const bestellung = zustand.bestellungen.find((b) => b.id === bestellungId);
    if (!bestellung) return;
    const tasks = ((bestellung as { tasks?: unknown }).tasks as OrderTask[] | undefined) || [];
    const bestehend = tasks[aufgabenIndex];
    if (!bestehend) return;
    drawerAufgabeAendern(bestellungId, bestehend, { done: erledigt });
  }

  // ================= Zahlungen & Kurssicherung (Task 8, `PaymentsPage/ZahlungsSeite.tsx`) - =======
  // ================= externe Naht ===================================================================
  //
  // `fxCalculatorDraft` ist im Bestand ein Modul-Global mit Lebensdauer der Seite (index.html:4596,
  // Task-Brief woertlich) - hier ein `useState` in `App`, aus demselben Grund wie `drawer`/`breiten`
  // oben: die Seite selbst bleibt eine reine Funktion ihrer Props (siehe Kopfkommentar
  // `ZahlungsSeite.tsx`, "Verhaltensunterschied, den der Prop-Umbau erzwingt"). `null` entspricht dem
  // Ausgangswert der globalen Variable und loest beim naechsten Zeichnen `ensureFxCalculatorEntwurf`
  // aus (siehe dort).
  const [fxEntwurf, setFxEntwurf] = useState<FxCalculatorDraft | null>(null);

  /**
   * `patchFxCalculator(key, value)` (index.html:13244-13257) OHNE dessen gezielte DOM-Schreibvorgaenge
   * (`set('#fxCalc...', ...)`): die Portierung zeichnet bei jeder Eingabe ohnehin neu (derselbe
   * benannte Verhaltensunterschied wie oben), die vier `set(...)`-Aufrufe waeren also nur eine zweite,
   * jetzt ueberfluessige Schreibroute zum selben Ergebnis. `ensureFxCalculatorEntwurf` bildet den
   * fehlenden Ausgangswert nach, falls noch nichts eingegeben wurde - wortgleich mit der Vorbelegung,
   * die `ZahlungsSeite` selbst zeigt, wenn `entwurf` noch `null` ist.
   */
  function fxEntwurfAendern(schluessel: string, wert: string): void {
    setFxEntwurf((vorher) => ({
      ...(vorher ?? ensureFxCalculatorEntwurf(zustand.fxDeals)),
      [schluessel]: wert,
    }));
  }

  /** `clearFxCalculator()` (index.html:13268-13277) ohne den Fokus-Seiteneffekt (setTimeout/focus). */
  function fxEntwurfLeeren(): void {
    setFxEntwurf({
      oldBalance: "",
      oldRate: "",
      newUsd: "",
      newRate: "",
      newBalanceOverride: "",
      mixedRateOverride: "",
      weightingOverride: "",
      oldShareOverride: "",
      newShareOverride: "",
      date: todayIso(),
      bank: "",
    });
  }

  /**
   * `saveFxCalculatorDeal()` (index.html:13301-13333) OHNE dessen erste (Rechte-)Zeile (13302) - die
   * sitzt seit der Nachtrag-Rechteregel im `disabled` der beiden Knoepfe selbst (siehe
   * `MischkursRechner.tsx#gesperrt`/`SicherungDetail.tsx` (frueher `SicherungsListe`)), nicht mehr im Klick-Handler. Die
   * beiden Wortlaut-Warnungen (13305-13306) sind woertlich uebernommen.
   *
   * BENANNTE LUECKE: `createFxCreditUpdateTask(deal)` (13279-13299) wird NICHT nachgebaut - sie
   * schreibt eine neue Aufgabe in `order.tasks` einer ANDEREN Bestellung. `"tasks"` traegt seit
   * Task 5a KEINEN Feldgruppen-Weg (`KEIN_FELDGRUPPEN_WEG` in `api/bestellungen.ts`) - derselbe Grund,
   * aus dem `uebersichtAufgabeUmschalten` oben schon auf den gemeinsamen Bestellungs-Kanal
   * zurueckfaellt. Die Erfolgsmeldung nennt deshalb NICHT „Task „Guthaben neu" erstellt" wie im
   * Bestand (13332) - das waere eine Behauptung ueber eine Wirkung, die nicht eintritt.
   *
   * GEMESSEN, NICHT ANGENOMMEN: `oldBalance`/`oldRate`/`newBalance`/`mixedRate`/`oldShare`/
   * `newShare`/`weighting` gehen NICHT mit hinaus. `FX_DEAL_FELDER`
   * (`backend/src/domaene/kurssicherung.ts`) fuehrt fuer `fx_deals` nur SECHS schreibbare Spalten
   * (`no, date, usd, bank, rate, docFileId`) - fuer die sieben abgeleiteten Werte des Bestands gibt
   * es dort keine Spalte. `id` und `rowVersion` vergibt der Server (`legeDealAn`, `INSERT ...
   * RETURNING *` ohne diese Spalten in der Liste). DIESELBE Luecke reisst zusaetzlich `securedAt`
   * (index.html:13323, im Bestand ein Zeitstempel neben `date`) - auch dafuer gibt es keine Spalte.
   * Beide zusammen reissen die im Bestand gemeinte KETTENVORBELEGUNG ein - siehe die Zuruecksetzung
   * unten (Fix-Runde 1 zu diesem Task, gemessen statt behauptet).
   */
  function kurssicherungSpeichern(): void {
    const entwurfWirksam = fxEntwurf ?? ensureFxCalculatorEntwurf(zustand.fxDeals);
    const werte = fxCalculatorValues(entwurfWirksam);
    if (werte.newUsd <= 0 || werte.newRate <= 0) {
      meldeFehler("Neuen Dollarbetrag und neuen Dollarkurs eintragen");
      return;
    }
    if (werte.oldBalance > 0 && werte.oldRate <= 0) {
      meldeFehler("Für den alten Kontostand fehlt der alte Dollarkurs");
      return;
    }
    // POST /api/fx-deals verlangt ein aktives, buchbares Konto. Dieser Rechner-Pfad ist nicht
    // erreichbar (MischkursRechner wird nirgends gerendert; der Dialog "Neue Sicherung" waehlt das
    // Konto selbst) und kennt deshalb nur den eindeutigen Fall: genau ein aktives Konto.
    const aktiveKonten = fxOverview?.accounts ?? [];
    const accountId = aktiveKonten.length === 1 ? aktiveKonten[0].account.id : "";
    if (!accountId) {
      meldeFehler("Sicherungen werden über „Neue Sicherung“ angelegt");
      return;
    }
    kurssicherungAnlegen({
      accountId,
      date: (entwurfWirksam.date as string) || todayIso(),
      usd: Math.round(werte.newUsd * 100) / 100,
      rate: Math.round(werte.newRate * 1000000) / 1000000,
    })
      .then((neu) => {
        const id = String((neu as { id?: unknown }).id ?? "");
        versende({ art: "nachgeladen", entitaet: KURSSICHERUNG_ENTITAET, id, datensatz: neu });
        // Entspricht dem Zuruecksetzen des Entwurfs auf die frische Sicherung (13322-13328): `null`
        // loest beim naechsten Zeichnen tatsaechlich `ensureFxCalculatorEntwurf` erneut aus - NICHT
        // aber die im Bestand gemeinte Kettenvorbelegung (Fix-Runde 1 zu diesem Task, GEMESSEN statt
        // behauptet - siehe auch die Backend-Liste oben):
        //   1. `oldBalance`/`oldRate` liest `ensureFxCalculatorEntwurf` (ZahlungsSeite.tsx:188-190)
        //      aus `latest.newBalance ?? latest.guthabenNeu` bzw. `latest.mixedRate ?? latest.rate` -
        //      KEINE dieser drei Spalten existiert im Backend. `oldBalance` bleibt deshalb LEER,
        //      `oldRate` faellt auf den einfachen `rate` der jeweiligen Sicherung zurueck - der
        //      Mischkurs, auf dem jede neue Sicherung eigentlich weiterrechnet, geht verloren.
        //   2. "juengste" stimmt nicht: sortiert wird nach `securedAt || date` - `securedAt` ist
        //      ebenfalls KEINE Spalte, kommt also nie zurueck. `listeErsetzen` (`reduzierer.ts`)
        //      haengt den soeben gespeicherten Satz HINTEN an, `Array.sort` ist stabil - bei
        //      gleichem `date` wie ein vorhandener Eintrag gewinnt der AELTERE, nicht der neue.
        // Konkret: wird FX-004 mit derselben Valuta wie FX-003 gespeichert, zeigt die zurueckgesetzte
        // Maske "Guthaben neu" leer statt des neuen Kontostands und "alter Kurs" den Einzelkurs von
        // FX-003 statt dessen Mischkurs. Nicht repariert (Schemafrage, siehe Backend-Liste oben) -
        // hier nur benannt.
        setFxEntwurf(null);
        zeigeMeldung(`${String((neu as { nummer?: unknown }).nummer ?? "Sicherung")} gespeichert`);
      })
      .catch((fehler: unknown) => {
        console.error("Kurssicherung konnte nicht gespeichert werden:", fehler);
        meldeFehler(fehler, "Die Kurssicherung konnte nicht gespeichert werden.");
      });
  }

  /**
   * Weich löschen aus der Übersicht (Register "USD-Sicherungen"): Fehler (409 mit Serverwortlaut)
   * gehen unverändert an den Dialog. Nach dem Erfolg verschwindet die Sicherung aus dem Store (wie
   * beim alten Löschweg); das lädt die Übersicht neu (Effekt auf `zustand.fxDeals`), dort steht sie
   * dann als "gelöscht (nicht übernommen)" in Register und Konto-Historie.
   */
  async function offeneKurssicherungLoeschen(deal: FxDeal, grund: string): Promise<void> {
    const id = String(deal.id ?? "");
    await kurssicherungWeichLoeschen(id, Number(deal.rowVersion) || 0, grund);
    versende({ art: "nachgeladen", entitaet: KURSSICHERUNG_ENTITAET, id, datensatz: null });
  }

  async function offeneKurssicherungAnlegen(eingabe: FxSicherungAnlegenEingabe) {
    const neu = await kurssicherungAnlegen(eingabe);
    versende({ art: "nachgeladen", entitaet: KURSSICHERUNG_ENTITAET, id: String(neu.id ?? ""), datensatz: neu });
    return neu;
  }

  /**
   * `patchFxDeal(i, key, value)` (index.html:13618) - eine Feldzeile einer Sicherung, verdrahtet am
   * 2026-08-15.
   *
   * MOEGLICH WURDE ES ERST DURCH MIGRATION 012: von den Feldern der Zeile (`date`, `oldBalance`,
   * `oldRate`, `usd`, `rate`) hatten zwei bis dahin KEINE Spalte. Sie sind Eingaben,
   * keine abgeleiteten Werte - die Begruendung, warum genau diese zwei gespeichert werden und die
   * fuenf gerechneten (newBalance, mixedRate, oldShare, newShare, weighting) nicht, steht in
   * `backend/migrations/012_fx_altbestand.sql`.
   *
   * DIE ZAHLENFELDER GEHEN ALS ZAHL HINAUS, nicht als Rohtext: die vier numerischen Spalten sind
   * `numeric`, und `""` (geleertes Feld) muss `null` werden statt 0 - eine geloeschte Eingabe ist
   * etwas anderes als eine Null. `date` bleibt Text; die Bank ist nie Text, sie folgt dem Konto (`accountId`).
   *
   * `PATCH /api/fx-deals/:dealId` verlangt `{ rowVersion, felder }` - die Versionsnummer steht am
   * Datensatz, nicht am Ereignis, und wird hier nachgeschlagen (dieselbe Form wie bei
   * `drawerAendern`; hier aber die KOPF-Version, denn `fx_deals` traegt `row_version` selbst).
   */
  function zahlungenSicherungAendern(index: number, schluessel: string, wert: string): void {
    const deal = zustand.fxDeals[index] as Record<string, unknown> | undefined;
    if (!deal) return;
    const id = String(deal.id ?? "");
    // Die Bank wird als Konto gewaehlt: accountId geht hinaus, der Server setzt `bank` auf den Kontonamen.
    if (schluessel === "accountId") {
      if (!fxKonten.some((konto) => konto.id === wert)) return;
      kurssicherungAendern(id, { accountId: wert }, Number(deal.rowVersion) || 0)
        .then((neu) => {
          fxOverviewKontoAktualisieren(id, wert);
          versende({ art: "nachgeladen", entitaet: KURSSICHERUNG_ENTITAET, id, datensatz: neu });
        })
        .catch((fehler: unknown) => {
          console.error(`Kurssicherung "${id}" konnte nicht geaendert werden:`, fehler);
          meldeFehler(fehler, "Die Kurssicherung konnte nicht geändert werden.");
        });
      return;
    }
    const zahl = schluessel !== "date";
    const wirksam = zahl ? (wert.trim() === "" ? null : Number(wert)) : wert;
    if (zahl && wirksam !== null && !Number.isFinite(wirksam as number)) return;

    kurssicherungAendern(id, { [schluessel]: wirksam }, Number(deal.rowVersion) || 0)
      .then((neu) => {
        versende({ art: "nachgeladen", entitaet: KURSSICHERUNG_ENTITAET, id, datensatz: neu });
      })
      .catch((fehler: unknown) => {
        console.error(`Kurssicherung "${id}" konnte nicht geaendert werden:`, fehler);
        meldeFehler(fehler, "Die Kurssicherung konnte nicht geändert werden.");
      });
  }

  /**
   * Der Beleg einer Sicherung (2026-08-15) - die Knoepfe „Beleg hochladen" und „Entfernen" in
   * `PaymentsPage/SicherungDetail.tsx` (frueher `SicherungsListe.tsx`).
   *
   * Die Dateiauswahl entsteht programmatisch und wird NIE eingehaengt: die Zahlungsseite hat keinen
   * versteckten `<input>`, und einer waere ein Strukturunterschied zur Baseline. (Dieselbe Bauart nutzte
   * bis Ticket H der Beleg-Upload im Reiter „Zahlung“.)
   *
   * Die Groessengrenze spiegelt Global Constraint 17 wie ueberall; Grenze und Wortlaut kommen aus `lib/upload.ts`
   * (`istZuGross`, `dateiZuGrossText`), damit nicht drei Uploadwege verschieden urteilen.
   *
   * Task 7 ("Uploads bis 100 MB"): Vorabpruefung UND Fehlschlag gehen als STEHENDER Hinweis hinaus (`zeigeHinweis`,
   * `StehendeHinweise.tsx`), nicht mehr als Toast - die Zahlungsseite hat keine Fehlerflaeche, und ein Upload von
   * 100 MB, der nach Minuten scheitert, darf nicht nach 2,2 Sekunden verschwinden. Der Text kommt aus
   * `lib/upload.ts#uploadFehlerText`. Ist der Beleg gespeichert und scheitert nur das Nachladen der Sicherung
   * danach, lehnt die Kette mit einem `NachladenNachUploadFehler` ab: der Hinweis sagt "gespeichert, die Anzeige
   * ist aber nicht mehr aktuell" statt "konnte nicht gespeichert werden".
   *
   * Fix-Runde 1 zu Task 7: jeder Hinweis traegt den Bezug `Kurssicherungsbeleg für <Sicherungsnummer>: <Text>` (beim Start
   * festgehalten), weil er getrennt vom Dialog steht.
   *
   * Upload-Sperren (Spec 2026-10-20), Task 7: Sperre und Fortschrittszeile liegen JE SICHERUNG im Store dieser Komponente
   * (Stelle `uploadStelle.kurssicherung(id)`, dieselbe `id` wie `SicherungDetail`), nicht im State des Detail-Panels. Der Lauf
   * beginnt im `change` exklusiv und synchron (`versucheBeginn`): ein zweiter Klick oder ein zweites `change` vor dem Neuzeichnen
   * findet die Stelle belegt und wird still verworfen - der erste Upload ist sichtbar. Schon der Klick fragt `istGesperrt`, bevor er
   * die Dateiauswahl oeffnet. Die Vorabpruefung `istZuGross` bleibt VOR dem Beginn (eine zu grosse Datei sperrt nichts). Der Lauf
   * endet im `finally` ueber die ganze Kette, NACH `kurssicherungLaden` und nach dem Fehlerzweig: bis dahin ist der Beleg noch
   * nicht in der Anzeige. Er gehoert dem Store und ueberlebt Reiterwechsel und Abbau der Zahlungsseite. Eine eigene Zeitgrenze gibt
   * es nicht: ein haengender Upload endet serverseitig (Node `requestTimeout` 15 min, Apache `ProxyTimeout` 300 s).
   */
  function zahlungenBelegHochladen(index: number): void {
    const deal = zustand.fxDeals[index] as Record<string, unknown> | undefined;
    if (!deal) return;
    const id = String(deal.id ?? "");
    const stelle = uploadStelle.kurssicherung(id);
    if (uploadStellen.istGesperrt(stelle)) return;
    const feld = document.createElement("input");
    feld.type = "file";
    feld.accept = ".pdf,.png,.jpg,.jpeg";
    feld.addEventListener(
      "change",
      () => {
        const datei = feld.files?.[0];
        if (!datei) return;
        // Der Bezug jedes Hinweises (Fix-Runde 1 zu Task 7): die Sicherung, so wie die Oberflaeche sie nennt
        // ("Sicherung S-12", `sicherungsNr`), beim START festgehalten - der Hinweis steht getrennt vom Dialog.
        const bezug = `Kurssicherungsbeleg für ${sicherungsNr(deal) || id}`;
        if (istZuGross(datei)) {
          zeigeHinweis(`${bezug}: ${dateiZuGrossText(datei)}`);
          return;
        }
        const lauf = uploadStellen.versucheBeginn(stelle);
        if (!lauf) return;
        // Nach dem Ende des Laufs wirkt `lauf.zeige` nicht mehr: ein verspaetetes Ereignis brachte die Zeile nicht zurueck.
        lauf.zeige({ dateiname: datei.name, phase: "hochladen", anteil: 0 });
        belegHochladen(id, datei, (anteil) => lauf.zeige({ dateiname: datei.name, phase: "hochladen", anteil }))
          .then(() =>
            // Nur das Nachladen wird umhuellt (zweites Argument): ein Fehler des Uploads laeuft unveraendert weiter.
            kurssicherungLaden(id).then(
              (neu) => {
                versende({ art: "nachgeladen", entitaet: KURSSICHERUNG_ENTITAET, id, datensatz: neu });
              },
              (ursache: unknown) => {
                throw new NachladenNachUploadFehler(
                  `Kurssicherung "${id}" konnte nach dem Hochladen von "${datei.name}" nicht neu geladen werden.`,
                  { cause: ursache },
                );
              },
            ),
          )
          .catch((fehler: unknown) => {
            console.error(`Beleg zu "${id}" konnte nicht hochgeladen werden:`, fehler);
            // Der Fehlschlag eines Uploads steht als stehender Hinweis am Ort, NICHT zusaetzlich im Fehlerstapel.
            zeigeHinweis(`${bezug}: ${uploadFehlerText(fehler, datei)}`);
          })
          .finally(() => lauf.beende());
      },
      { once: true },
    );
    feld.click();
  }

  /** Der Gegenweg - entfernt den VERWEIS, nicht die Datei (siehe `api/kurssicherung.ts`). */
  function zahlungenBelegEntfernen(index: number): void {
    const deal = zustand.fxDeals[index] as Record<string, unknown> | undefined;
    if (!deal) return;
    const id = String(deal.id ?? "");
    // R4: scheitert erst das Nachladen NACH dem gelungenen Entfernen, ist der Beleg entfernt.
    let entfernt = false;
    belegEntfernen(id, Number(deal.rowVersion) || 0)
      .then(() => {
        entfernt = true;
        return kurssicherungLaden(id);
      })
      .then((neu) => {
        versende({ art: "nachgeladen", entitaet: KURSSICHERUNG_ENTITAET, id, datensatz: neu });
      })
      .catch((fehler: unknown) => {
        console.error(`Beleg zu "${id}" konnte nicht entfernt werden:`, fehler);
        if (entfernt) meldeNachladeFehler(fehler, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
        else meldeFehler(fehler, "Der Beleg konnte nicht entfernt werden.");
      });
  }

  // Split-Kennzeichnung (`SplitMarke.tsx`): Kinder-Index und Id-Tabelle EINMAL je Bestandswechsel, nicht
  // je Zeile; der Oeffner geht ueber `lineageOeffnen` (laedt auch nicht geladene Quellen nach). Die
  // Ref haelt die jeweils aktuelle Funktion, damit der Kontextwert nicht bei jedem Render neu entsteht.
  // STEHT VOR dem Anmelde-Fruehausstieg unten: Hooks muessen in jedem Render gleich oft laufen.
  const lineageOeffnenRef = useRef(lineageOeffnen);
  lineageOeffnenRef.current = lineageOeffnen;
  const splitIndizes = useMemo(
    () => baueSplitIndizes(zustand.bestellungen),
    [zustand.bestellungen],
  );
  const splitKontext = useMemo<SplitKontextWert>(
    () => ({ ...splitIndizes, onOeffnen: (orderId) => lineageOeffnenRef.current(orderId) }),
    [splitIndizes],
  );

  if (!zustand.nutzer) {
    /**
     * Critical 1 (Review 2026-08-12): waehrend ein gespeichertes Token noch geprueft wird, ist
     * `<Anmeldung>` NICHT erreichbar - 1:1 zu `index.html:3995` (`#login` traegt beim Laden kein
     * `show`). Begruendung samt gemessenem Wettlauf am `sitzungWirdGeprueft`-Zustand oben. NUR der
     * unsichtbare `<aside id="drawer">`-Anker bleibt stehen (siehe Kommentar am zweiten Vorkommen
     * unten) - kein eigener Ladehinweis, das Original kennt an dieser Stelle ebenfalls keinen.
     *
     * BEWUSST INNERHALB von `!zustand.nutzer`, nicht DAVOR: die Sperre betrifft ausschliesslich die
     * Anmeldemaske. Wird der Store waehrend der Pruefung TROTZDEM angemeldet (Sitzungsverlust-Hook,
     * eine zweite, ausserhalb dieses Tabs laufende Anmeldung ueber denselben Store o. ae.), muss die
     * dann schon angemeldete Oberflaeche sofort erscheinen - eine Sperre VOR dieser Weiche haette
     * genau das verhindert und waere selbst wieder ein Fehler derselben Klasse gewesen.
     */
    if (sitzungWirdGeprueft) {
      return <aside id="drawer" />;
    }
    return (
      <>
        <Anmeldung onAngemeldet={angemeldet} startHinweis={sitzungsHinweis} />
        {/* siehe Kommentar am zweiten Vorkommen unten */}
        <aside id="drawer" />
      </>
    );
  }

  // Der offene Datensatz, frisch aus `zustand.bestellungen` gelesen (siehe Kommentar am
  // `drawer`-Zustand oben). `undefined`, wenn er waehrenddessen aus der Liste gefallen ist (z. B.
  // `drawerLoeschen` oben oder ein Nachladen mit 404) - dann faellt der Drawer auf den geschlossenen
  // Platzhalter zurueck, ganz ohne eigenes Zutun.
  const offeneBestellung = drawer ? zustand.bestellungen.find((b) => b.id === drawer.id) : undefined;

  // Die Bestellung der Teillieferung, frisch aus `zustand.bestellungen` gelesen (siehe Kommentar am
  // `drawer`-Zustand oben) - dasselbe Muster, aus demselben Grund.
  const teilBestellung = teilenId ? zustand.bestellungen.find((b) => b.id === teilenId) : undefined;
  // JTL-Kennung der Teillieferung im Rueckfuehrungs-Dialog (null: nicht geladen oder ohne Kennung).
  const rueckgaengigKennung = ((): number | null => {
    const k = Number(
      (rueckgaengigId ? zustand.bestellungen.find((b) => b.id === rueckgaengigId) : undefined)?.jtlOrderId,
    );
    return Number.isSafeInteger(k) && k > 0 ? k : null;
  })();

  return (
    <SplitKontextProvider value={splitKontext}>
      <Kopfzeile
        seite={seite}
        rechte={zustand.rechte}
        nutzer={zustand.nutzer}
        onNeueBestellung={neueBestellungOeffnen}
        onPasswort={passwortDialogOeffnen}
        onAbmelden={abgemeldet}
        onNutzerverwaltung={nutzerverwaltungOeffnen}
        onLieferanten={lieferantenOeffnen}
        onZahlungsvorlagen={zahlungsvorlagenOeffnen}
        onKiZugaenge={kiZugaengeOeffnen}
        kiZugangAktiv={kiZugangAktiv}
        onUsdKonten={fxKontenDialogOeffnen}
        onTaskTemplate={taskTemplateOeffnen}
        nutzerLaden={nutzerListe}
        laenderFilter={laenderFilter}
        onLaenderFilterAendern={laenderFilterAendern}
        transportartFilter={transportartFilter}
        onTransportartFilterAendern={transportartFilterAendern}
      />
      {/*
        Die Meldungen vor `<main>`: die Leiste nach einer Unterbrechung der Live-Verbindung (Lueckenausgleich, Spec
        2026-10-24) und die offenen Konflikte, in EINEM Behaelter, die Leiste zuerst. Die Leiste liegt fest unter der
        Kopfzeile (`position: fixed`, siehe `verbindungsluecke.css`), der Konfliktbanner ohne eigene CSS im Fluss an
        derselben Stelle - ohne gemeinsamen Behaelter verdeckte sie ihn, und das einzige Zeichen, dass die offene
        Bestellung anderswo geaendert wurde, waere weg. Darum gilt die Klasse `verbindungsluecke-stapel` (fester Stapel)
        NUR, solange die Leiste steht; ohne sie bleiben die Banner im Fluss wie zuvor. Ohne Leiste UND ohne Konflikt gibt
        es den Behaelter nicht (das DOM bleibt das des Bestands, Ganzseitensignatur). Beides steht ausserhalb von
        `<main>`: es behaelt seine Kinderzahl.
        Leiste: nur mit dem Merkmal, hoechstens einmal. Der Speicher zeigt es erst, wenn ein Stand uebernommen ist; die
        Leiste prueft das nicht selbst. "Neu laden" ist bei `zustand.ladend` gesperrt (ein Klick brache einen laufenden
        Teillauf ab).
        Konflikte - je Eintrag ein Banner. Sie stehen VOR `<main>` und nicht in einer
        Seite: ein Konflikt gehoert zu einem Datensatz, nicht zu der Seite, die gerade offen ist,
        und darf beim Seitenwechsel nicht verschwinden. Der Schluessel ist entitaet+id, weil ids nur
        innerhalb ihrer Entitaet eindeutig sind (dieselbe Begruendung wie in
        `konflikte.ts#istDerselbeDatensatz`).
      */}
      {zustand.verbindungsluecke !== null || zustand.konflikte.length > 0 ? (
        <div className={zustand.verbindungsluecke !== null ? "verbindungsluecke-stapel" : undefined}>
          {zustand.verbindungsluecke !== null ? (
            <VerbindungsLuecke
              ursache={zustand.verbindungsluecke.ursache}
              ladend={zustand.ladend}
              onNeuLaden={() => void lueckeNeuLaden()}
              onSchliessen={lueckeSchliessen}
            />
          ) : null}
          {zustand.konflikte.map((konflikt: Konflikt) => (
            <KonfliktBanner
              key={`${konflikt.entitaet}/${konflikt.id}`}
              entitaet={konflikt.entitaet}
              id={konflikt.id}
              geaendertVon={anzeigenameZu(konflikt.geaendertVon)}
              onUebernehmen={() =>
                versende({ art: "konfliktUebernehmen", entitaet: konflikt.entitaet, id: konflikt.id })
              }
              onVerwerfen={() =>
                versende({ art: "konfliktVerwerfen", entitaet: konflikt.entitaet, id: konflikt.id })
              }
            />
          ))}
        </div>
      ) : null}
      <main>
        {/*
          LADEFEHLER DES BESTANDS (Fehleranzeige Paket 2, Task 4, Regel L1): scheitert das ERSTE Laden, ist
          "0 Bestellungen" auf Board und den uebrigen Bestandsseiten eine Luege. Dann ersetzt EINE Flaeche
          die Seitenbehaelter der `SEITEN_MIT_BESTAND` (die uebrigen Seiten bleiben unberuehrt). Im
          Normalfall (kein Fehler) ist der Zweig unten unveraendert - `<main>` hat dieselben Kinder wie
          zuvor, die Sechs-Kinder-Invariante gilt weiter. Der Fragment-Zweig ist bewusst NICHT neu
          eingerueckt, damit der Diff die rund 350 Zeilen darunter nicht beruehrt.
          "Erneut laden" laedt mit demselben Filter wie der Erstabruf (`ersteZustandswahl`, auch die
          Zahlungsrolle) VOLL nach; das Promise wird nie abgelehnt, die Flaeche sperrt den Knopf, solange es laeuft.
          Welcher Filter damit geladen ist, fuehrt der Speicher als `bestaetigterFilter`; `useBoardBestellzustand`
          vergleicht die Wahl der Adresse mit ihm (Fix-Runde 1, G2) - ein Nachladen von einer anderen Seite aus
          laesst die Rueckkehr zum Board mit einem anderen Filter daher neu laden.
        */}
        {bestandFlaeche ? (
          <div id="page-bestand-ladefehler" className="page active">
            <Ladefehler
              was="Die Bestellungen konnten nicht geladen werden."
              ursache={zustand.bestandLadefehler}
              onErneutLaden={() => bestandVollNeuLaden(ersteZustandswahl(zustand.nutzer?.role))}
            />
          </div>
        ) : (
        <>
        <div id="page-board" className={seite === "board" ? "page active" : "page"}>
          {/*
            Woertlich aus index.html:3683-3727 und dort wie hier per CSS versteckt
            (bestand.css:1966, `#page-board > .filter-bar { display: none; }`). Nicht bedienbar,
            nicht verdrahtet (kein `onChange`/`onClick`) - sie steht hier, weil die
            Ganzseiten-Signatur der Baseline sie fuehrt (Abweichung 8, Verdrahtungsplan). Der
            Ganzseitenvergleich selbst traegt eine benannte Auslassung fuer diesen Block (Task 13,
            `test/visual/signatur.ts`) - diese Datei ist hier ausdruecklich NICHT angefasst.
          */}
          <div className="filter-bar">
            <div className="filter-grid">
              <div className="field">
                <label>Artikel / SKU</label>
                <input id="filterArticle" placeholder="Artikel oder SKU suchen" />
              </div>
              <div className="field">
                <label>Lieferant</label>
                <input id="filterSupplier" list="filterSupplierList" placeholder="Lieferant suchen" />
                <datalist id="filterSupplierList" />
              </div>
              <div className="field">
                <label>Zahlung</label>
                <select id="filterPayment">
                  <option value="">Alle Zahlungen</option>
                  <option value="overdue">Überfällig</option>
                  <option value="due7">Fällig in 7 Tagen</option>
                  <option value="open">Offen</option>
                  <option value="paid">Bezahlt</option>
                  <option value="missingPlan">Plan fehlt</option>
                </select>
              </div>
              <div className="field">
                <label>Wert ab</label>
                <input
                  id="filterValueMin"
                  className="mono"
                  type="number"
                  min={0}
                  step={1000}
                  placeholder="z. B. 50000"
                />
              </div>
              <div className="field">
                <label>Archiv</label>
                <select id="filterArchived">
                  <option value="active">Aktiv</option>
                  <option value="all">Aktiv + Archiv</option>
                  <option value="archived">Nur Archiv</option>
                </select>
              </div>
              <div className="field check">
                <label>
                  <input type="checkbox" id="filterActionOnly" /> Handlungsbedarf
                </label>
              </div>
              <div className="field check">
                <label title="Nur Bestellungen anzeigen, die jünger als 30 Tage sind">
                  <input type="checkbox" id="filterRecentOnly" /> Neu
                </label>
              </div>
              <button className="filter-reset" id="filterReset" type="button">
                <Ikone name="filter-x" />
                Zurücksetzen
              </button>
              <span className="filter-hits" id="filterHits" />
              <button
                className="board-edit-trigger"
                type="button"
                title="Spaltenbreiten zurücksetzen"
                aria-label="Spaltenbreiten zurücksetzen"
              >
                <Ikone name="columns-3" />
              </button>
            </div>
          </div>
          {/*
            ABWEICHUNG E (Task 13, behoben im Gesamtreview 2026-08-11): hier stand bis dahin ein
            EIGENES `<div id="board">` UM `<BoardSeite>`. Der Bestand kennt diese Verschachtelung
            nicht - `renderBoard` setzt die Klasse AUF den Behaelter selbst
            (`board.className = 'grouped-order-list'`, index.html:6994), und das CSS greift genau so
            (`#board.grouped-order-list`, index.html:1975). `BoardSeite` traegt die Klasse ohnehin
            an ihrer Wurzel; die `id` bekommt sie seit dieser Runde als Prop (siehe dort). Damit
            faellt der klassenlose Zwischenknoten weg, der - weil die Ganzseiten-Signatur keine `id`
            fuehrt - in JEDEM Ganzseitenvergleich die ERSTE Abweichung war. Nachgemessen an der
            Baseline: auf `div.grouped-order-list` folgt dort unmittelbar `div.order-list-shell`,
            also genau die Wurzel dieser Komponente.

            GESCHLOSSEN am 2026-08-15 - bis dahin die teuerste benannte Luecke dieses Baums
            (Gesamtreview, Important 8): `onBearbeiten` wurde hier NICHT durchgereicht, obwohl
            `Board/Bestellzeile.tsx` damit JEDE Zelle JEDER Bestellzeile umhuellt (`Bearbeitbar`,
            `<span class="order-list-editable-value" role="button" tabIndex={0}>` - sichtbar,
            fokussierbar, nicht gesperrt). Der Klick lief ins Leere, und weil `Bestellzeile` vorher
            `ereignis.stopPropagation()` ruft (woertlich aus index.html:7651), fiel er AUCH NICHT
            auf das `<tr onClick={onOeffnen}>` durch: der Nutzer klickte eine Zelle an, und es
            passierte exakt gar nichts.

            Der Anstoss des Editors liegt seither IN `BoardSeite` (Modul-Global `orderListEditCell`
            im Bestand, also Seitenzustand - siehe dortiger Kopfkommentar); hierher kommt nur noch
            das Ergebnis, ueber `onZelleAendern` -> `boardZelleAendern` oben. Die Prop
            `onBearbeiten` von `BoardSeite` gibt es deshalb nicht mehr; `Bestellzeile` fuehrt sie
            weiter, jetzt vom Board selbst belegt.

            WAS DABEI NICHT GEHT, und das ist gemessen und nicht angenommen: `name` (die
            Bestellnummer gehoert JTL) und `supplier` (kein Feldgruppen-Weg; ein Wechsel zieht
            Zahlungsziel und Ratenplan nach - das ist `onLieferant`, weiterhin offen) haben keinen
            Schreibweg, ebensowenig `etd`/`eta` bei verknuepfter DSV-Sendung. Diese Zellen bleiben
            anklickbar und antworten mit der Begruendung statt mit einem Feld, dessen Uebernahme
            scheitern muesste - die Saetze stehen in `lib/orderListEditor.ts`.
          */}
          <BoardSeite
            id="board"
            bestellungen={zustand.bestellungen}
            lieferanten={zustand.lieferanten}
            definitionen={definitionen}
            spalten={zustand.orderListSpalten}
            breiten={breiten}
            eingeklappt={eingeklappt}
            laenderFilter={laenderFilter}
            transportartFilter={transportartFilter}
            rechte={zustand.rechte}
            onOeffnen={drawerOeffnen}
            onSammelsendungOeffnen={sammelsendungOeffnen}
            onSammelFaelligkeitSetzen={sammelFaelligkeitSetzen}
            onEingeklappt={boardAbschnittUmschalten}
            onKontextmenue={kontextmenueOeffnen}
            onZelleAendern={boardZelleAendern}
            onSpaltenEditor={() => setListenEditorOffen(true)}
            onBreitenZuruecksetzen={boardBreitenZuruecksetzen}
            onBreiteZiehen={boardBreiteZiehen}
            onBreiteTaste={boardBreiteTaste}
            onAendern={boardGruppeAendern}
            // Auftrag "Sammel-Bereichswechsel per Drag-and-Drop": siehe Kopfkommentar
            // `useBoardSammelaktionen.ts#boardGruppeSammelAendern`.
            onSammelAendern={boardGruppeSammelAendern}
            onMeldung={zeigeMeldung}
            spaltenEditorGesperrt={spaltenEditorGesperrt}
            nurLeser={nurLeser}
            onAuswahlGeaendert={setzeAusgewaehlteBestellungen}
            onSammelStatusAnfordern={() => setSammelStatusDialogOffen(true)}
            onBestellgruppeAnlegen={bestellgruppeErstellen}
            // Nachbesserung (Pruefung e9f0edb, Wichtig 2): waehrend der Dialog offen ist, soll DESSEN
            // eigener Escape-Listener (`SammelStatusDialog.tsx`) den Dialog schliessen, OHNE dass
            // `BoardSeite`s globaler Escape-Listener denselben Tastendruck gleichzeitig zum Leeren der
            // Auswahl nutzt - siehe `BoardSeite.tsx`s Kopfkommentar zu `escapeAuswahlGesperrt`.
            // Fixrunde 1 zu Task 7: dasselbe fuer den Sammelsendungs-Drawer - sein Escape schliesst nur
            // ihn und laesst die Board-Mehrfachauswahl stehen.
            // Massenupload: der Dialog schliesst mit Escape und markiert die Taste als behandelt (`defaultPrevented`); der
            // Hoerer in `BoardSeite` prueft das nicht und leerte die Auswahl trotzdem.
            escapeAuswahlGesperrt={sammelStatusDialogOffen || sammelsendungOffen !== null || massenuploadAuftrag !== null}
            // Nachbesserung 2 (Pruefung 97e5bffb, Wichtig 1): siehe `BoardSeite.tsx`s Kopfkommentar
            // zu `auswahlLeerenSignal` und `useBoardSammelaktionen.ts#sammelStatusAendern`.
            auswahlLeerenSignal={auswahlLeerenSignal}
          />
        </div>
        <div id="page-timeline" className={seite === "timeline" ? "page active" : "page"}>
          {/*
            Task 6 (Verdrahtungsplan Phase 6): `renderTimelinePage` (index.html:13733), Behaelter
            `#page-timeline` (index.html:3730). Nur zeichnen, wenn die Seite auch gewaehlt ist -
            derselbe Grund wie bei `#page-costs` unten (`renderCurrentData`, index.html:5887/5893).
            Die vier `data-lucide`-Platzhalter dieser Flaeche sind auf `Ikone` umgestellt (siehe
            `TimelinePage/ZeitstrahlSuche.tsx`, `Stationsspur.tsx`, `ZeitstrahlSeite.tsx`) - die
            einzigen Fundstellen im ganzen `src/`, die niemand hydriert.
          */}
          {seite === "timeline" ? (
            <ZeitstrahlSeite
              bestellungen={zustand.bestellungen}
              lieferanten={zustand.lieferanten}
              definitionen={definitionen}
              onOeffnen={drawerOeffnen}
              sendungenNachBestellung={zeitstrahlSendungen}
              laenderFilter={laenderFilter}
              transportartFilter={transportartFilter}
            />
          ) : null}
        </div>
        <div id="page-overview" className={seite === "overview" ? "page active" : "page"}>
          {/*
            Task 7 (Verdrahtungsplan Phase 6): `renderOverview` (index.html:14561), Behaelter
            `:3731`. ANDERS als Zeitstrahl/Kosten (die nur zeichnen, wenn `seite` sie zeigt) wird
            diese Seite - wie das Board - IMMER gezeichnet, unabhaengig von `seite`
            (`renderCurrentData()`, index.html:5887-5889: `renderOverview()` steht in der Zeile mit
            `renderBoard()`, nicht mit den drei bedingt gezeichneten Seiten darunter). Kein `seite
            === "overview" ? ... : null`-Wächter wie bei `#page-timeline`/`#page-costs` also - siehe
            Kopfkommentar dieser Datei.
          */}
          <UebersichtSeite
            bestellungen={zustand.bestellungen}
            lieferanten={zustand.lieferanten}
            definitionen={definitionen}
            nutzer={zustand.nutzer}
            istZahlungsansicht={normalisierteRolle(zustand.nutzer?.role) === "payment_manager"}
            laenderFilter={laenderFilter}
            transportartFilter={transportartFilter}
            // `seiteWechseln={false}`: ein Klick von der Uebersichtsseite aus oeffnet die Bestellung
            // als Drawer AUF der Uebersichtsseite statt aufs Board wegzunavigieren, siehe
            // `uebersichtOeffnen` oben. `uebersichtAufgabeOeffnen` gibt `false` bereits selbst weiter.
            onOeffnen={(bestellungId: string, art: string, kennungHinweis?: string) =>
              uebersichtOeffnen(bestellungId, art, kennungHinweis, undefined, false)}
            onAufgabeOeffnen={uebersichtAufgabeOeffnen}
            onAufgabeUmschalten={uebersichtAufgabeUmschalten}
            // Auftrag F, `Terminaenderungen.tsx`: die Uebersicht ist wie das Board IMMER gezeichnet
            // (siehe Kommentar oben) - der Abruf des Panels haengt deshalb an der WIRKLICHEN
            // Sichtbarkeit, nicht am Mounten.
            istAktiveSeite={seite === "overview"}
            nurLeser={nurLeser}
          />
        </div>
        <div id="page-payments" className={seite === "payments" ? "page active" : "page"}>
          {/*
            Task 8 (Verdrahtungsplan Phase 6): `renderPayPage` (index.html:13504), Behaelter
            `#page-payments` (index.html:3732). Nur zeichnen, wenn die Seite auch gewaehlt ist -
            derselbe Grund wie bei `#page-timeline`/`#page-costs` (`renderCurrentData`,
            index.html:5887/5890-5893).

            Die Abschnitte "In Raten umwandeln" (Sammelaktion) und "Tasks Zahlungsauftraege"
            (`onGuthabenAufgabe`, nie verdrahtet) sind von dieser Seite ersatzlos entfernt; der
            Einzelweg im Drawer (`drawerRatenSynchronisieren`) bleibt.
              GESTRICHEN am 2026-08-15: `onBinden`/`onLoesen` haengen an
              `zahlungenSicherungBinden`/`zahlungenSicherungLoesen` oben. Dafuer waren ZWEI
              Backend-Schritte noetig, beide in derselben Runde: `ladeDeals()` liefert jetzt
              `allocations` je Deal (vorher waren die Zuordnungen unsichtbar), und
              `PATCH /api/fx-deals/:dealId/allocations/:allocationId` ist neu - ohne sie scheiterte
              jede zweite Bindung derselben Bestellung an `UNIQUE (deal_id, order_id)`.
              GESTRICHEN am 2026-08-15: `onSicherungAendern` haengt an `zahlungenSicherungAendern`
              oben. Dafuer war Migration 012 noetig: zwei der sechs bearbeitbaren Feldzeilen
              (`oldBalance`, `oldRate`) hatten in `FX_DEAL_FELDER`
              (`backend/src/domaene/kurssicherung.ts`) KEINE Spalte, seither haben sie eine.
              GESTRICHEN am 2026-08-15: `onBelegHochladen`/`onBelegEntfernen` haengen an
              `zahlungenBelegHochladen`/`zahlungenBelegEntfernen` oben. Der Preis war NICHT nur die
              neuen Routen `POST`/`DELETE /api/fx-deals/:dealId/document` -
              `backend/src/http/routes/fxDeals.ts` fuehrte bis dahin nur das PATCH-Feld
              `docFileId`, keine eigene Upload-/Loeschroute fuer Belege. Dazu kam der LESEWEG:
              `ladeDeals()` lieferte bis dahin nur die Kennung, die Oberflaeche liest aber ein
              Objekt mit `name` und `path` - `doc` steht seither im Leseweg von GET /api/fx-deals,
              dritter Fall desselben Musters nach `pdfs` und `allocations`.
          */}
          {seite === "payments" ? (
            <ZahlungsSeite
              bestellungen={zustand.bestellungen}
              deals={zustand.fxDeals}
              lieferanten={zustand.lieferanten}
              definitionen={definitionen}
              entwurf={fxEntwurf}
              onEntwurf={fxEntwurfAendern}
              rechte={zustand.rechte}
              onSpeichern={kurssicherungSpeichern}
              onOeffnen={drawerOeffnen}
              onEntwurfLeeren={fxEntwurfLeeren}
              onSicherungAendern={zahlungenSicherungAendern}
              onBelegHochladen={zahlungenBelegHochladen}
              onBelegEntfernen={zahlungenBelegEntfernen}
              onBinden={zahlungenSicherungBinden}
              onLoesen={zahlungenSicherungLoesen}
              onHinweis={zeigeMeldung}
              overview={fxOverview}
              overviewLadefehler={fxOverviewLadefehler}
              overviewLadeursache={fxOverviewLadeursache}
              onOverviewNeuLaden={fxOverviewNeuLaden}
              onOverviewNachSchreiben={fxOverviewNachSchreibenLaden}
              onSicherungAnlegen={offeneKurssicherungAnlegen}
              konten={fxKontenBereit ? fxKonten : undefined}
              onSicherungWeichLoeschen={offeneKurssicherungLoeschen}
              uploadStellen={uploadStellen}
            />
          ) : null}
        </div>
        {/*
          LIQUIDITAETSPLANUNG - wie `#page-suppliers` unten NUR bei aktiver Seite im Baum: `index.html`
          kennt nur sechs Seitenbehaelter, ein siebter, immer vorhandener haengte ein Kind mehr an
          `<main>` und machte JEDE Ganzseiten-Signatur strukturell rot. Die Seite wendet die
          Kopfzeilenfilter (Laender, Transportart) bewusst nicht an; `Kopfzeile.tsx` blendet die Chips aus.
        */}
        {seite === LIQUIDITAET_SEITE ? (
          <div id="page-liquidity" className="page active">
            <LiquiditaetsSeite
              bestellungen={zustand.bestellungen}
              deals={zustand.fxDeals}
              lieferanten={zustand.lieferanten}
              overview={fxOverview}
              overviewLadefehler={fxOverviewLadefehler}
              overviewLadeursache={fxOverviewLadeursache}
              onOverviewNeuLaden={fxOverviewNeuLaden}
              onOeffnen={drawerOeffnen}
            />
          </div>
        ) : null}
        {/*
          AKTIVITAETEN - wie `#page-liquidity` nur bei aktiver Seite im Baum (`index.html` kennt nur
          sechs Seitenbehaelter). Die Seite laedt ihre Eintraege selbst (`api/aktivitaeten.ts`); der
          Filterzustand lebt in der Adresse (`ansicht.aktivitaeten`). Kopfzeilenfilter gelten hier nicht
          (`Kopfzeile.tsx` blendet die Chips aus).
        */}
        {seite === AKTIVITAETEN_SEITE ? (
          <div id="page-activities" className="page active">
            <AktivitaetenSeite
              istAktiveSeite
              onBestellungOeffnen={aktivitaetOeffnen}
              onSeiteOeffnen={aktivitaetSeiteOeffnen}
              rechte={zustand.rechte}
              ansicht={ansicht.aktivitaeten}
              onAnsichtAendern={(aenderung) => setzeAnsicht({ aktivitaeten: aenderung }, { ersetzen: true })}
            />
          </div>
        ) : null}
        {/*
          DASHBOARD - wie `#page-activities` nur bei aktiver Seite im Baum (`index.html` kennt nur sechs Seitenbehaelter). Die
          Seite rechnet im Browser aus `zustand.bestellungen` und `zustand.lieferanten`; sie liegt in einem nachgeladenen Chunk
          (`DashboardSeiteLazy`). Kopfzeilenfilter gelten hier nicht (`Kopfzeile.tsx` blendet die Chips aus).
          - `onNeuLaden`: der Lauf behaelt die Auswahl des Boards. `bestandNeuLaden()` ohne Argument setzte den bestaetigten Filter
            still auf "nur aktiv" (`laufAbwickeln` in `Speicher.tsx`: `states ?? ["active"]`) und verwuerfe alle 15 Minuten die Wahl
            des Nutzers im Board; Vorbild ist `lueckeNeuLaden`. Noch nichts bestaetigt (`null`, etwa bei F5 oder einem Lesezeichen
            auf /dashboard, solange das Erstladen laeuft): dieselbe Auswahl wie das Erstladen, `ersteZustandswahl(rolle)` - wie bei
            "Erneut laden" oben. Nur mit gleicher Auswahl hat der Lauf im Speicher denselben Schluessel (`auswahlSchluessel`) und haengt
            sich an das laufende Erstladen; ohne Auswahl (`undefined`) bekaeme er einen anderen, ein zweiter voller Lauf verwuerfe das
            Erstladen als "ueberholt", und bei `payment_manager` (active + archived) fehlten danach die archivierten Bestellungen.
            `bestandNeuLaden` ist der TEILLAUF: nur die JTL-Liste (Einbuchstatus, Positionen). Beim Oeffnen und in den meisten Takten.
          - `onVollNeuLaden`: der VOLLE Lauf (Hub und JTL, ~25 MB; ETA, Packlisten und Sendung kommen aus dem DSV-Abgleich, der KEIN
            Ereignis sendet und sonst nie bei einer offenen Seite ankaeme): im Takt hoechstens stuendlich, um Mitternacht und bei
            "Jetzt aktualisieren" (`useDashboardFrische`, Zweig-Review W3). Mit bestaetigtem Filter `bestandVollNeuLaden(filter)`
            (`frisch: true`, dieselbe Auswahl wie das Board). Noch nichts bestaetigt (`null`, das Erstladen laeuft): trotzdem der
            Teillauf mit `ersteZustandswahl` - ein voller Lauf haengt sich nicht an das laufende Erstladen, sondern holte die ~25 MB
            ein zweites Mal (der Doppellauf aus der Fix-Runde von Task 4). Das Erstladen ist ohnehin voll.
          - `aktiveGeladen`: dreiwertig. Ist nur "archiviert" oder "geloescht" bestaetigt, fehlen die aktiven Bestellungen im
            Speicher (`"nein"`): die Seite zeigt "nicht geladen" statt einer falschen Entwarnung. `null` = noch nichts bestaetigt,
            das Erstladen laeuft (`"laedt"`): die Daten fehlen ebenso, die Seite zeigt "Bestellungen werden geladen …", "–" statt
            der Kennzahlen und keine "Ohne ETA"-Liste (Zweig-Review W1: `true` zeigte hier die ganze Ladezeit "Keine offenen
            Anlieferungen." und 0/0/0). Scheitert das Erstladen, bleibt der Filter `null`, aber `bestandFlaeche` ersetzt dann den
            ganzen Behaelter durch die gemeinsame Ladefehler-Flaeche (`SEITEN_MIT_BESTAND`): der Ladetext steht nie neben ihr.
          - `drawerOffen`: bei offenem Drawer (Bestellung oder Sammelsendung) laedt die Seite nicht von selbst nach.
          - `standLaden`: der DSV-Stand fuer die Standzeile. Hereingereicht statt im Chunk importiert: `api/dashboard.ts` zieht
            `api/basis.ts`, und dann loeste Rolldown den Zyklus `basis` / `lib/upload` als Gemeinschafts-Chunk aus dem Eintritt.
        */}
        {seite === DASHBOARD_SEITE ? (
          <div id="page-dashboard" className="page active">
            <DashboardSeiteLazy
              bestellungen={zustand.bestellungen}
              lieferanten={zustand.lieferanten}
              onBestellungOeffnen={drawerOeffnen}
              onDateiLaden={packlisteLaden}
              onNeuLaden={() => bestandNeuLaden(bestaetigterFilter() ?? ersteZustandswahl(zustand.nutzer?.role))}
              onVollNeuLaden={() => {
                const filter = bestaetigterFilter();
                return filter === null
                  ? bestandNeuLaden(ersteZustandswahl(zustand.nutzer?.role))
                  : bestandVollNeuLaden(filter);
              }}
              aktiveGeladen={() => {
                const filter = bestaetigterFilter();
                if (filter === null) return "laedt";
                return filter.includes("active") ? "ja" : "nein";
              }}
              drawerOffen={ansicht.bestellung !== null || sammelsendungOffen !== null}
              standLaden={dashboardStandLaden}
            />
          </div>
        ) : null}
        <div id="page-costs" className={seite === "costs" ? "page active" : "page"}>
          {/*
            Nur zeichnen, wenn die Seite auch gewaehlt ist - so wie im Original, wo `renderCostPage`
            ausschliesslich aus `showPage('costs')`/`renderCurrentData()` bei aktiver Kostenseite
            gerufen wird (index.html:5893, 6341) und `#page-costs` sonst leer bleibt.
          */}
          {seite === "costs" ? (
            <KostenSeite
              bestellungen={zustand.bestellungen}
              deals={zustand.fxDeals}
              lieferanten={zustand.lieferanten}
              definitionen={definitionen}
              laenderFilter={laenderFilter}
              transportartFilter={transportartFilter}
              onOeffnen={drawerOeffnen}
            />
          ) : null}
        </div>
        <div id="page-users" className={seite === "users" ? "page active" : "page"}>
          {/*
            Task 10 (Verdrahtungsplan Phase 6): `renderUsers` (index.html:6240-6288), Behaelter
            `#page-users` (index.html:3734) - der sechste und letzte Seitenbehaelter dieses Plans.
            Nur zeichnen, wenn die Seite auch gewaehlt ist, wie `#page-timeline`/`#page-payments`/
            `#page-costs` (`showPage`, index.html:6342: `if (p === 'users') renderUsers();`) - anders
            als Board/Uebersicht keine Flaeche, die `renderCurrentData()` bei jedem Laden unbedingt
            fuellt.

            Seit dem 03.11.2026 (Plan „Kontotyp Nur lesen“, Task 4) ueber `NutzerSeiteLazy`: die Seite
            liegt im Chunk `assets/NutzerSeite-*.js`, nicht im Eintrittsbuendel.
          */}
          {seite === "users" ? (
            <NutzerSeiteLazy
              nutzer={nutzerVerzeichnis}
              eigenerId={zustand.nutzer.id}
              rechte={zustand.rechte}
              onAnlegen={nutzerAnlegenAufruf}
              onSpeichern={eigenesPasswortAendernAufSeite}
              bestellungen={zustand.bestellungen}
              ausgewaehlteId={ausgewaehlteNutzerId ?? undefined}
              onRolleAendern={nutzerRolleAendern}
              onAktivAendern={nutzerAktivAendern}
              onAnzeigenameAendern={nutzerAnzeigenameAendern}
              onJtlLoginAendern={nutzerJtlLoginAendern}
              jtlLoginOptionen={jtlLoginOptionen}
              jtlLoginStatus={jtlLoginStatus}
              jtlLoginFehler={jtlLoginFehler}
              onPasswortZuruecksetzen={nutzerPasswortZuruecksetzen}
              onAlleAnzeigen={() => nutzerverwaltungOeffnen(null)}
              onAbmelden={abgemeldet}
              // Dieselbe Konstellation wie `uebersichtOeffnen` (Seite + Bestellung in EINEM
              // `setzeAnsicht`) - hier direkt wiederverwendet statt dupliziert (Befund W-3 der
              // Pruefrunde 1: zwei getrennte Aufrufe gaben vorher zwei Verlaufseintraege).
              onOeffnen={(bestellungId) => uebersichtOeffnen(bestellungId, "main")}
            />
          ) : null}
        </div>
        {/*
          TASK 11 - DER SIEBTE BEHAELTER, UND WARUM ER NICHT DAUERHAFT HAENGT (Befund 15, Option B).

          Die sechs Behaelter oben stehen IMMER im Baum, jeder mit `className="page"` bzw.
          `"page active"` - genau wie `index.html:3681-3734`. `#page-suppliers` gibt es dort NICHT
          (die Seitenliste in `showPage`, `index.html:6332`, kennt sechs Seiten; ein
          `#page-suppliers` steht nur noch in zwei toten CSS-Regeln). Haengte er hier unbedingt,
          traege `<main>` sieben Kinder statt sechs - und JEDE Ganzseiten-Signatur waere strukturell
          rot, egal wie gut portiert wurde.

          Der Behaelter wird deshalb NUR bei aktiver Lieferantenseite gezeichnet. Die
          Sechs-Kinder-Invariante ist als eigener Fall zugesichert
          (`test/komponenten/Suppliers/verwaltung.test.tsx`) - sie faellt, sobald jemand die
          Bedingung auf "immer" stellt.

          FIX-RUNDE 1 (2026-08-11): der ZUGANG zur Flaeche liegt seither im Marken-Menue
          (`MarkenMenue.tsx`, "Lieferanten öffnen") statt in einem sechsten Reiter - Entscheidung des
          Auftraggebers. Damit ist auch der zweite Teil des Problems weg, den dieser Behaelter nicht
          loesen konnte: ein sechster Reiter verschob in der ZENTRIERTEN `nav`-Gruppe alle fuenf
          Bestandsreiter und machte JEDEN Bildvergleich rot, in dem die Kopfzeile vorkommt.
        */}
        {seite === LIEFERANTEN_SEITE ? (
          <div id="page-suppliers" className="page active">
            <LieferantenSeite
              rechte={zustand.rechte}
              onFreigabenGespeichert={(kLieferant, freigaben) =>
                versende({ art: "freigabenGesetzt", kLieferant, freigaben })
              }
            />
          </div>
        ) : null}
        {seite === ZAHLUNGSVORLAGEN_SEITE ? (
          <div id="page-payment-templates" className="page active">
            <ZahlungsvorlagenSeite
              rechte={zustand.rechte}
              onVorlagenGeaendert={(vorlagen) => {
                // Auch die Freigabe-Beschriftungen im Store (Bestell-Drawer, `VorlagenAuswahl`) tragen
                // den Namen der Vorlage: sie nachziehen, sonst zeigt der Drawer bis zum naechsten
                // Laden den alten Namen.
                const namen = new Map(vorlagen.map((v) => [v.id, vorlagenName(v.comment, v.rows)]));
                const freigaben = Object.fromEntries(
                  Object.entries(zustand.freigaben).map(([lieferant, zeilen]) => [
                    lieferant,
                    zeilen.map((f) => (namen.has(f.templateId) ? { ...f, label: namen.get(f.templateId)! } : f)),
                  ]),
                );
                versende({ art: "bestandGeladen", bestand: { vorlagen, freigaben } });
              }}
            />
          </div>
        ) : null}
        {/*
          KI-ZUGANG (Stufe 1, nur lesend): die Erlauben-Seite, auf die ein KI-Werkzeug den Kollegen leitet
          (`/ki-erlauben?anfrage=<id>`), und "Meine KI-Zugaenge" (Marken-Menue). Wie `#page-payment-templates` nur bei
          aktiver Seite im Baum (`index.html` kennt nur sechs Seitenbehaelter) und als nachgeladener Chunk. Ohne Sitzung
          steht oben die Anmeldung und die Adresse bleibt: nach der Anmeldung steht hier dieselbe Anfrage.
        */}
        {seite === KI_ERLAUBEN_SEITE ? (
          <div id="page-ki-erlauben" className="page active">
            <KiErlaubenSeiteLazy anfrageId={ansicht.kiErlauben.anfrage} />
          </div>
        ) : null}
        {seite === KI_ZUGAENGE_SEITE ? (
          <div id="page-ki-zugaenge" className="page active">
            <KiZugaengeSeiteLazy rechte={zustand.rechte} />
          </div>
        ) : null}
        </>
        )}
      </main>
      {/*
        `#passwordDialogOverlay` (index.html:3737-3759) - vom Marken-Menue ausgeloest, deshalb ein
        eigener, TOP-LEVEL Dialog (nicht der interne Dialog von `NutzerSeite`, der nur ueber die
        Nutzerverwaltungsseite selbst erreichbar ist). Positionierung wie bei `KontextMenue`/den
        drei `NewOrder/`-Dialogen unten: eine `board-editor-overlay`-Ueberlagerung, DOM-Reihenfolge
        ohne Bedeutung.
      */}
      {passwortDialogOffen ? (
        <PasswortDialog
          onSpeichern={eigenesPasswortAendern}
          onSchliessen={() => setPasswortDialogOffen(false)}
          serverFehler={passwortFehler}
        />
      ) : null}
      {fxKontenDialogOffen ? (
        <FxKontenDialog
          offen={fxKontenDialogOffen}
          rechte={zustand.rechte}
          konten={fxKonten}
          onSchliessen={() => setFxKontenDialogOffen(false)}
          onMeldung={zeigeMeldung}
        />
      ) : null}
      {/*
        `#overlay`/`aside#drawer` (index.html:3815-3816). GEOEFFNET bringt `<Drawer>` beide Elemente
        selbst mit (`Drawer.tsx`-Kopfkommentar, Abschnitt "Huelle"; `rahmen.test.tsx`, "oeffnet die
        Ueberlagerung und schliesst ueber sie") - Task 4 haengt die Komponente nur ein, statt
        `#overlay` hier ein zweites Mal nachzubauen (das gaebe zwei Elemente mit dem selben `id`).
        GESCHLOSSEN bleibt NUR der leere `<aside id="drawer" />` stehen (kein `#overlay` - im Bestand
        unsichtbar ohne `.open`, hier aus demselben Grund weggelassen).

        GEFUNDENER BEFUND (Wörtlichkeitsregel - gemeldet, nicht repariert): `<aside id="drawer">`
        (index.html:3816, CSS index.html:586-595) ist auch GESCHLOSSEN sichtbarkeitswirksam - es
        sitzt per `transform: translateX(102%)` nur ~13px jenseits des rechten Viewport-Rands, und
        sein `box-shadow: -24px 0 60px rgba(35,37,43,.2)` (60px Weichzeichnung) blutet dadurch
        sichtbar in die Kopfzeile hinein (empirisch mit einer Wegwerf-Sonde nachgewiesen: Pixelwerte
        an x=1670 stimmen NUR überein, wenn dieses Element - sogar LEER, ohne Inhalt - im DOM steht;
        `main`/`#overlay` auszublenden ändert nichts, NUR `#drawer` auszublenden entfernt den
        Farbverlauf). Der geschlossene Platzhalter bleibt deshalb bestehen, byteidentisch zur
        Portierung aus Task 3.

        Die Reiter-INHALTE (`children`) sind seit diesem Task (5) eingehaengt - je `drawer.tab` GENAU
        eine der fuenf Reiterkomponenten (Tasks 17-21). `SendungsZuordnung` (Einhaengepunkt in
        `BestellungTab`, ausgewiesene Planentscheidung des Task-5-Briefs) bleibt AUSSEN VOR - siehe
        Task-Bericht: `bestellungTabStruktur.test.tsx` (Task 17, ausserhalb der Files-Liste dieses
        Tasks) vergleicht `BestellungTab` Knoten fuer Knoten OHNE jede Maskierung gegen die Baseline;
        eine neue Flaeche dort ausserhalb der zwei bereits registrierten Ausnahmen haette diesen Test
        rot gemacht. Der Brief erlaubt das Ablehnen ausdruecklich ("wer diesen Einhaengepunkt ablehnt,
        lehnt nur diesen Schritt ab, nicht den Task").
      */}
      {drawer && darfBestellungOeffnen(offeneBestellung) ? (
        <Drawer
          bestellung={offeneBestellung}
          rechte={zustand.rechte}
          tab={drawer.tab}
          abschnitt={drawer.abschnitt}
          sprung={sprung}
          onTab={(tab) => {
            // TIEFLINKS (Aufgabe 3): der Reiterwechsel schreibt die Adresse, `id` bleibt aussen vor -
            // `ansicht.bestellung.kennung` traegt weiterhin die LESBARE Kennung (§ 3.7), nicht die
            // aufgeloeste UUID aus `drawer.id`.
            //
            // ERSETZT, nicht geschoben (Endabnahme-Nachbesserung, Punkt 1) - ausdrueckliche Entscheidung
            // des Auftraggebers (§ 4, Tabelle): "Wer im Drawer vier Reiter durchklickt, will mit EINEM
            // Zurueck auf dem Board landen, nicht mit vieren." Der Reiter steht trotzdem im Link und
            // bleibt damit verschickbar - nur der Verlauf soll ihn nicht mitzaehlen. Ohne `ersetzen`
            // wuchs `history.length` bei jedem Reiterklick um eins (Test:
            // `verdrahtungDrawerReiter.test.tsx`, "Drawer-Reiter: Verlauf (§ 4)"). Das OEFFNEN/
            // SCHLIESSEN der Bestellung bleibt bewusst unveraendert ein PUSH (`drawerOeffnen`/
            // `drawerSchliessen` oben) - dieselbe Zeile bewacht auch die Gegenrichtung.
            if (!ansicht.bestellung) return;
            setzeAnsicht(
              // `abschnitt` faellt mit dem Reiterwechsel weg: der Sprung galt dem alten Reiter.
              { bestellung: { ...ansicht.bestellung, tab, aufgabe: null, dokument: null, abschnitt: null } },
              { ersetzen: true },
            );
          }}
          onSchliessen={drawerSchliessen}
          kontext={{
            lieferanten: zustand.lieferanten,
            deals: zustand.fxDeals,
            bestellungen: zustand.bestellungen,
          }}
          onFeld={(feld, wert) => drawerFeldAendern(drawer.id, feld, wert)}
          onArchivUmschalten={drawerArchivUmschalten}
          onTeillieferung={drawerTeillieferungOeffnen}
          onSplitRueckgaengig={setRueckgaengigId}
          onLineageOeffnen={lineageOeffnen}
          onSammelsendungOeffnen={sammelsendungOeffnen}
          lineageVersion={lineageVersion}
          onLoeschen={loeschenAusloesen}
          bestellgruppenBereich={
            drawerGruppe && drawerGruppe.id === gruppenIdDerOffenenBestellung ? (
              <BestellgruppenBereich
                bestellung={offeneBestellung}
                bestellungen={zustand.bestellungen}
                gruppe={drawerGruppe}
                onBestellungOeffnen={drawerOeffnen}
                onGruppenansichtOeffnen={() => setGruppenAnsicht(drawerGruppe)}
                onMitgliedHinzufuegen={gruppenMitgliedHinzufuegen}
                onMitgliedLoesen={gruppenMitgliedLoesen}
                onGruppeAufloesen={gruppeAufloesen}
              />
            ) : null
          }
        >
          {/*
            BENANNTE LUECKE, nachgetragen in der Fix-Runde zu Task 5 (Review-Befund 3): dieser
            Abschnitt fuehrte hier bis zum Nachtrag "Bestellfelder nach Anlage sperren" (2026-08-25)
            EIN SICHTBARES Bedienelement, das an einer optionalen Prop hing, die hier NICHT
            durchgereicht wurde (`onLieferant`, Lieferanten-`<select>` in `MainTab/Stammdaten.tsx`) -
            der Nutzer sah es, es war nicht gesperrt, und ein Klick tat nichts.

            GESTRICHEN mit demselben Nachtrag (G-1 aus Nachtrag 3): der Lieferant wird seither bei
            der Anlage festgelegt und danach nie mehr geaendert - das Auswahlfeld selbst ist ersatzlos
            entfallen (reine Anzeige, siehe Kopfkommentar `Stammdaten.tsx`), `onLieferant` ist damit
            keine benannte Luecke mehr, sondern schlicht fort - dieselbe Endstufe wie bei `onTracking`
            weiter unten in diesem Kommentar. Der urspruengliche Grund fuer die fehlende Verdrahtung
            steht weiterhin zur Erinnerung: `supplier` gehoert JTL (`kLieferant` am Kopf), und ein
            Wechsel waere mehr als ein Feld gewesen - `setSupplier` haette Zahlungsziel und Ratenplan
            aus den Stammdaten uebernommen und Zahlungen angelegt.

            GESTRICHEN am 2026-08-15: `onStatus` -> `drawerStatusAendern` (Anlass: der Status liess
            sich in der ganzen SPA nicht mehr setzen), `onSpaltenbreiten` ->
            `drawerArtikelBreitenZuruecksetzen`. `onPortal` stand hier ebenfalls bis zum 2026-08-20 -
            mit dem Umzug des Portal-Knopfs in den neuen Reiter "Spedition" (`ShipTab/Referenzen.tsx`,
            Auftrag "Der neue Drawer-Reiter 'Spedition' und der Feldumzug") geht `drawerPortalOeffnen`
            seither an `<SpeditionTab>` unten statt hierher - `sendungen` (ebenfalls ohne Leser seit
            demselben Umzug) faellt ERSATZLOS weg, siehe `MainTab/typen.ts`.

            `onTracking` stand hier bis zum selben Umzug (Trackingfeld -> `ShipTab/Referenzen.tsx`,
            `MainTab/typen.ts`s Kopfkommentar "UMZUG 'Reiter Spedition'") und war seither, mangels
            Leser in `Stammdaten.tsx`, keine Zeile mehr wert - diese Tabelle hier hat es beim Umzug
            versaeumt zu streichen. Nachgezogen mit dem Auftrag "Trackingfeld ablösen statt Eingabe
            vortäuschen" (2026-08-25), der den ZWEITEN, wichtigeren Rest gleich mit erledigt hat:
            `onTracking` gibt es seither an KEINER Komponente mehr - es ist damit auch keine
            benannte Luecke mehr, sondern schlicht fort (die Herleitung steht beim Reiter
            "Spedition" unten) - `trackingNo` bleibt trotzdem eine benannte Luecke in
            `api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`, weil die Spalte im neuen Modell schlicht
            nicht existiert (die Nummer WIRD eine Sendung, `shipments.dsv_shipment_no` +
            `order_shipments`, `feldinventar.ts:114`).
          */}
          {drawer.tab === "main" ? (
            <BestellungTab
              bestellung={offeneBestellung}
              lieferanten={zustand.lieferanten}
              definitionen={definitionen}
              rechte={zustand.rechte}
              onAendern={drawerAendern}
              onJtlKopfAendern={drawerJtlKopfAendern}
              onStatus={drawerStatusAendern}
              artikelBreiten={artikelBreiten}
              onSpaltenbreiten={drawerArtikelBreitenZuruecksetzen}
              jtlArtikelSuche={jtlArtikelSuche}
              onPositionAendern={(jtlPosId, aenderung, rowVersion, sortIndex) =>
                drawerPositionAendern(
                  offeneBestellung.id as string,
                  jtlPosId,
                  aenderung,
                  rowVersion,
                  sortIndex,
                )
              }
              onArtikelTauschen={(jtlPosId, aenderung) => drawerArtikelTauschen(offeneBestellung.id as string, jtlPosId, aenderung)}
              onMengePreisAendern={(jtlPosId, aenderung) => drawerMengePreisAendern(offeneBestellung.id as string, jtlPosId, aenderung)}
              onPositionHinzufuegen={(neuePosition) => drawerPositionHinzufuegen(offeneBestellung.id as string, neuePosition)}
              onPositionLoeschen={(jtlPosId) => drawerPositionLoeschen(offeneBestellung.id as string, jtlPosId)}
              positionsDokumenteZustand={positionsDokumenteZustand}
              positionsDokumenteNeuLaden={positionsDokumenteNeuLaden}
              onProduktionsbildOeffnen={produktionsbildLightboxOeffnen}
              onStehenderHinweis={zeigeHinweis}
              uploadStellen={uploadStellen}
            />
          ) : drawer.tab === "ship" ? (
            // Reiter "Spedition" (Auftrag D, 2026-08-21): `sendung`/`zeitstrahl` sind lokaler
            // State DIESER Komponente (siehe Kopfkommentar am `useEffect` oben, "Reiter Spedition") -
            // ein Store-Feld gibt es dafuer seit dem Auftrag „Sendungsabruf beim App-Start
            // entfernen" (2026-08-22) ohnehin nicht mehr (`Speicherzustand` fuehrte vorher
            // `sendungen`, ein Sammelfeld, das nie mehr als ein leeres Array war - siehe
            // Kopfkommentar `api/sendungen.ts`). Der Reiter zeigt die zur offenen Bestellung
            // VERKNUEPFTE Sendung, keine Liste. `onPortal` ist derselbe Rueckruf, den `BestellungTab`
            // bis zum Feldumzug erhielt - siehe die BENANNTE LUECKE oben.
            //
            // `onTracking` ist HIER NICHT MEHR die benannte Luecke, die sie bis zum Auftrag
            // "Trackingfeld ablösen statt Eingabe vortäuschen" (2026-08-25) war: die Prop ist mit
            // dem entfallenen Trackingabschnitt ganz weggefallen, `ShipTab/typen.ts#SpeditionTabProps`
            // kennt sie nicht mehr - der zehnte Props-Zaehler unten aendert sich dadurch NICHT,
            // `onTracking` stand nie unter den zehn Props dieses JSX-Blocks. Die Nummer zeigt seither
            // AUSSCHLIESSLICH `ShipTab/DsvSendung.tsx`, unter den Beschriftungen „DSV-Sendungsnr."
            // und „Container-Nr." direkt aus der verknuepften Sendung. `ShipTab/Referenzen.tsx`
            // zeigt sie NICHT: der dortige Abschnitt „Tracking- / Container-Nr." ist in derselben
            // Runde ersatzlos entfallen, weil er `dsvShipmentNo` ein ZWEITES Mal gezeigt und im
            // Etikett eine Container-Nr. versprochen haette, die zwei Zeilen hoeher schon unter
            // eigenem Namen steht. `referenzenTracking.test.tsx` haelt genau das fest: die Nummer
            // darf im Reiter „Spedition" genau EINMAL vorkommen (gegen die Mutation gemessen).
            <SpeditionTab
              bestellung={offeneBestellung}
              sendung={sendung}
              vorschlag={vorschlag}
              zeitstrahl={zeitstrahl}
              historie={historie}
              belege={belege}
              // Nach „Kategorie zuweisen …“ am Beleg: Belegliste UND Bestellung (`pdfs`, gruener Haken) neu
              // laden - der Reiter Dokumente haengt nicht im Baum und laedt erst beim Oeffnen selbst nach.
              onBelegeNeuLaden={() =>
                Promise.all([belegeNeuLaden(), dokumentBestellungNachladen(offeneBestellung.id as string)]).then(
                  () => undefined,
                )
              }
              rechte={zustand.rechte}
              onAendern={drawerAendern}
              onPortal={drawerPortalOeffnen}
              sendungsSuche={speditionsSuche}
              onVerknuepfen={speditionVerknuepfen}
              onLoesen={speditionLoesen}
              laeuft={speditionLaeuft}
              gruppe={drawerGruppe}
              bestellungen={zustand.bestellungen}
              onGruppenSendungSetzen={gruppenSendungSetzen}
              zollMelder={ZOLL_MELDER}
              onZollErzeugt={() => zollErzeugt(offeneBestellung.id as string)}
            />
          ) : drawer.tab === "arrival" ? (
            <Anlieferungsliste
              bestellung={offeneBestellung}
              einbuchungen={einbuchungen}
              fehlgeschlagen={einbuchungenFehlgeschlagen}
            />
          ) : drawer.tab === "tasks" ? (
            /*
              `module` steht hier seit dem 2026-08-15 NICHT mehr: `taskVorlagenModule` ist die
              Modulform des BESTANDS (Alt-Ids wie "responsibility_purchasing"),
              `order_tasks.responsibility_id` dagegen ein uuid-Fremdschluessel. Der Reiter laedt die
              echten Module selbst - siehe `TasksTab.tsx`, Abschnitt "Die Zustaendigkeitsmodule
              laedt dieser Reiter selbst". `taskVorlagenModule` bleibt fuer `ZahlungTab` unten.

              `nutzer` steht hier ebenfalls nicht mehr: `updatedBy` setzt der Server aus dem
              angemeldeten Nutzer (`updateVersioned`), der Reiter trug es zuvor selbst ein.
            */
            <TasksTab
              bestellung={offeneBestellung}
              rechte={zustand.rechte}
              onAufgabeAnlegen={drawerAufgabeAnlegen}
              onAufgabeAendern={drawerAufgabeAendern}
              onAufgabeLoeschen={drawerAufgabeLoeschen}
              onAufgabenSammelLoeschen={drawerAufgabenSammelLoeschen}
              onPhaseErledigt={drawerPhaseErledigen}
              onVorlageAnwenden={drawerVorlageAnwenden}
            />
          ) : drawer.tab === "docs" ? (
            <DokumenteTab
              bestellung={offeneBestellung}
              bestellungen={zustand.bestellungen}
              rechte={zustand.rechte}
              onAendern={drawerAendern}
              onHochladen={drawerDokumentHochladen}
              onBestellungNachladen={dokumentBestellungNachladen}
              belege={belege}
              onBelegeNeuLaden={belegeNeuLaden}
              sendung={sendung}
              positionsDokumenteZustand={positionsDokumenteZustand}
              positionsDokumenteNeuLaden={positionsDokumenteNeuLaden}
              onPositionsDokumenteErneutLaden={positionsDokumenteErneutLaden}
              onProduktionsbildOeffnen={produktionsbildLightboxOeffnen}
              onStehenderHinweis={zeigeHinweis}
              uploadStellen={uploadStellen}
              onMassenuploadStarten={massenuploadStarten}
              massenuploadGeschlossen={massenuploadGeschlossen}
              hervorgehoben={ansicht.bestellung?.dokument}
              onDokumentFreigeben={(dokument) => {
                const linkId = typeof dokument.id === "string" ? dokument.id : null;
                const rowVersion = typeof dokument.rowVersion === "number" ? dokument.rowVersion : null;
                if (
                  !linkId || rowVersion === null || !drawerGruppe || drawerGruppe.status !== "active" ||
                  dokument.viaGroupId || dokument.viaShipmentNo || dokument.jtlPosId !== undefined || dokument.positionId !== undefined
                ) return;
                const ziele = drawerGruppe.members
                  .filter((mitglied) => mitglied.membershipState === "active")
                  .map((mitglied) => zustand.bestellungen.find((eintrag) => String(eintrag.id) === mitglied.orderId))
                  .filter((eintrag): eintrag is Order => !!eintrag)
                  .map((eintrag) => String(eintrag.orderNo || eintrag.name || eintrag.id));
                if (!confirm(`„${String(dokument.name || dokument.type || "Dokument")}“ wird von dieser Bestellung zur Bestellgruppe. Sichtbar für: ${ziele.join(", ")}.`)) return;
                // R4: scheitert erst das Nachladen NACH der gelungenen Freigabe, ist das Dokument freigegeben.
                let freigegeben = false;
                anfrage<{ orderIds: string[] }>(`/api/orders/${encodeURIComponent(String(offeneBestellung.id))}/documents/${encodeURIComponent(linkId)}/share-group`, {
                  methode: "POST",
                  koerper: { rowVersion },
                })
                  .then(({ orderIds }) => {
                    freigegeben = true;
                    return Promise.all(orderIds.map((orderId) => dokumentBestellungNachladen(orderId)));
                  })
                  .catch((fehler: unknown) => {
                    console.error("Dokument konnte nicht für die Bestellgruppe freigegeben werden:", fehler);
                    if (freigegeben) meldeNachladeFehler(fehler, NACHLADEN_FEHLGESCHLAGEN_MELDUNG);
                    else meldeFehler(fehler, "Das Dokument konnte nicht für die Bestellgruppe freigegeben werden.");
                  });
              }}
            />
          ) : drawer.tab === "pay" ? (
            /*
              Ticket H (Asana „Reiter Zahlung aufräumen“, 2026-10-20): die Abschnitte „Kurssicherung ($)“, „Kurswechsel &
              Guthaben“ und „Überweisungsbelege“ sind entfernt, mit ihnen die Rückrufe `onHochladen` (Beleg-Upload),
              `onLoeschen` (Papierkorb am Zahlungsbeleg), `onZuordnung` (Sicherung binden/lösen) und `onMail`
              (vorbereitete Mail) sowie die Prop `deals`. Sicherungen binden und lösen bleiben auf der Seite
              „Zahlungen & Kurssicherung“ (`zahlungenSicherungBinden`/`-Loesen`).

              `bundle.test.ts` prueft je Komponente den eigenen JSX-Block, nicht die Datei.
            */
            <ZahlungTab
              bestellung={offeneBestellung}
              lieferanten={zustand.lieferanten}
              freigaben={zustand.freigaben[String(offeneBestellung.kLieferant)] ?? []}
              vorlagen={zustand.vorlagen}
              module={taskVorlagenModule}
              nutzer={zustand.nutzer}
              rechte={zustand.rechte}
              schreibgeschuetzt={zahlungsbearbeitungNachladenNoetig.has(offeneBestellung.id as string)}
              onAendern={(id, aenderung) => {
                // offen: Paket 3 – Sperre "Nachladen noetig": die Knoepfe sind ohne Begruendung gesperrt, die Eingabe geht still verloren (Inventar E)
                if (!zahlungsbearbeitungNachladenNoetigRef.current.has(id)) drawerAendern(id, aenderung);
              }}
              // Ticket G: `raten` kommt von „Zurück auf Vorlage" (`ersetzen`); `ZahlungTab` reicht den Rückruf unverändert an `VorlagenAuswahl` durch.
              onZahlungszielOption={(templateId: string, raten?: ZahlungszielRaten) =>
                drawerZahlungszielSetzen(offeneBestellung.id as string, templateId, raten)}
              onPlanIndividualisieren={drawerPlanIndividualisieren}
              onPlanSpeichern={drawerPlanSpeichern}
              onRatenSynchronisieren={(id, alte, neue) => {
                if (!zahlungsbearbeitungNachladenNoetigRef.current.has(id)) drawerRatenSynchronisieren(id, alte, neue);
              }}
              onZahlungAnstossen={(id, aenderung, aufgabe) => {
                if (!zahlungsbearbeitungNachladenNoetigRef.current.has(id)) {
                  drawerZahlungAnstossen(id, aenderung, aufgabe);
                }
              }}
              onZahlstatusZuruecksetzen={drawerZahlstatusZuruecksetzen}
              istZahlung={istZahlungHandler}
            />
          ) : drawer.tab === "history" ? (
            // Reiter "Historie" (Auftrag "Bestellungs-Aenderungshistorie", Task 11): reine Anzeige,
            // laedt ihre Liste selbst (`HistoryTab.tsx`, wie `TasksTab` seine Zustaendigkeitsmodule) -
            // keine Props ausser der Bestell-id noetig.
            <HistoryTab orderId={offeneBestellung.id as string} />
          ) : (
            <EkTab
              bestellung={offeneBestellung}
              deals={zustand.fxDeals}
              lieferanten={zustand.lieferanten}
              bestellungen={zustand.bestellungen}
              rechte={zustand.rechte}
              onAendern={drawerAendern}
              onPositionAendern={(jtlPosId, aenderung, rowVersion, sortIndex) =>
                drawerPositionAendern(
                  offeneBestellung.id as string,
                  jtlPosId,
                  aenderung,
                  rowVersion,
                  sortIndex,
                )
              }
              nutzer={zustand.nutzer}
              onJtlUebertragen={() => void drawerJtlUebertragen(offeneBestellung)}
            />
          )}
          {lightboxStartLinkId ? <ProduktionsbildLightbox
            orderId={offeneBestellung.id as string}
            bestellung={offeneBestellung}
            positionsDokumente={geladeneProduktionsbilder}
            startLinkId={lightboxStartLinkId}
            ausloeserRef={lightboxAusloeserRef}
            onSchliessen={() => setLightboxStartLinkId(null)}
          /> : null}
        </Drawer>
      ) : sammelsendungOffen ? (
        <SammelsendungDrawerLazy
          shipmentId={sammelsendungOffen.shipmentId}
          dsvShipmentNo={
            zustand.bestellungen.find((b) => b.shipmentParent?.shipmentId === sammelsendungOffen.shipmentId)
              ?.shipmentParent?.dsvShipmentNo
          }
          rechte={zustand.rechte}
          bestellungen={zustand.bestellungen}
          statusliste={definitionen.STATUSES}
          lieferanten={zustand.lieferanten}
          onSchliessen={sammelsendungSchliessen}
          onBestellungOeffnen={drawerOeffnen}
          onGeaendert={sammelsendungGeaendert}
          onHerausnehmen={(orderId) => sammelsendungHerausnehmen(sammelsendungOffen.shipmentId, orderId)}
          onAufgeloest={sammelsendungAufgeloest}
          onStehenderHinweis={zeigeHinweis}
          uploadStellen={uploadStellen}
          zollMelder={ZOLL_MELDER}
        />
      ) : (
        <aside id="drawer" />
      )}
      {/*
        Massenupload-Dialog: Geschwister des Drawers, nicht sein Kind - ein Klick im Dialog soll nicht durch das React-Portal zu
        den Zuhoerern des Drawers aufsteigen. Er bringt Portal, Fokusfalle, Escape und `beforeunload` selbst mit.
      */}
      {massenuploadAuftrag !== null ? (
        <MassenuploadDialogLazy
          key={massenuploadLauf}
          auftrag={massenuploadAuftrag}
          uploadStellen={uploadStellen}
          positionsDokumenteNeuLaden={positionsDokumenteNeuLaden}
          // Für die Artwork-Prüfung in der Zusammenfassung: die Fassung der hochgeladenen Artworks. Die Positionsdokumente gehören dem
          // geöffneten Drawer; steht dort eine andere Bestellung (oder keine), kennt der Dialog keine Fassung. Ohne geladenen Stand
          // (`laedt`, `gescheitert`) ist die Liste leer, wie die Vorbelegung `[]` vor Paket 2.
          positionsDokumente={drawer?.id === String(massenuploadAuftrag.bestellung.id) ? geladeneDokumente(positionsDokumenteZustand) : undefined}
          darfPruefungAendern={hatRecht(zustand.rechte, "manage_documents")}
          // „Gesehen“ / „Erneut prüfen“ in der Zusammenfassung: die Bestellung in den Store nachladen, damit die Board-Marke folgt.
          bestellungNachladen={() => dokumentBestellungNachladen(String(massenuploadAuftrag.bestellung.id))}
          onSchliessen={massenuploadSchliessen}
        />
      ) : null}
      {gruppenAnsicht ? (
        <BestellgruppenAnsicht
          gruppe={gruppenAnsicht}
          bestellungen={zustand.bestellungen}
          rechte={zustand.rechte}
          onSchliessen={() => setGruppenAnsicht(null)}
          onBestellungOeffnen={(orderId) => {
            setGruppenAnsicht(null);
            drawerOeffnen(orderId);
          }}
          onMitgliedHinzufuegen={gruppenMitgliedHinzufuegen}
          onMitgliedLoesen={gruppenMitgliedLoesen}
          onGruppeAufloesen={gruppeAufloesen}
          sendungsSuche={speditionsSuche}
          onGruppenSendungSetzen={async (shipmentId) => { await gruppenSendungSetzen(shipmentId); }}
        />
      ) : null}
      {/*
        `#orderContextMenu` (index.html:3817) steht im Bestand ausserhalb von `<main>`, als eigenes
        Geschwister direkt vor `#drawer` - Positionierung laeuft ueber `x`/`y` (siehe
        `KontextMenue.tsx`), nicht ueber die DOM-Verschachtelung. `bestellung: null` (Ruhezustand)
        rendert nichts (siehe dortige Annahme).
      */}
      <KontextMenue
        bestellung={kontextmenue?.bestellung}
        x={kontextmenue?.x ?? 0}
        y={kontextmenue?.y ?? 0}
        rechte={zustand.rechte}
        nurLeser={nurLeser}
        ausgewaehlteAnzahl={ausgewaehlteBestellungen.length}
        istInAuswahl={
          !!kontextmenue?.bestellung && ausgewaehlteBestellungen.includes(kontextmenue.bestellung.id as string)
        }
        onSchliessen={kontextmenueSchliessen}
        onAktion={kontextAktion}
      />
      {/*
        `Board/SammelStatusDialog.tsx` (Auftrag "Sammel-Statuswechsel") - wie `KontextMenue`
        UNVERAENDERT gemountet (kein bedingter Wrapper hier): `offen` entscheidet ueber das Rendern
        INNERHALB der Komponente, siehe deren Kopfkommentar/Annahme.
      */}
      <SammelStatusDialog
        offen={sammelStatusDialogOffen}
        anzahl={ausgewaehlteBestellungen.length}
        definitionen={definitionen}
        onUebernehmen={sammelStatusAendern}
        onAbbrechen={() => setSammelStatusDialogOffen(false)}
        // Nachbesserung 2 (Gering): siehe `useBoardSammelaktionen.ts#sammelStatusLaeuft`.
        laeuft={sammelStatusLaeuft}
      />
      {listenEditorOffen ? (
        <ListenEditor
          spalten={zustand.orderListSpalten}
          bestellungen={zustand.bestellungen}
          rechte={zustand.rechte}
          onSpeichern={listenSpaltenSpeichern}
          onSchliessen={() => setListenEditorOffen(false)}
        />
      ) : null}
      {/*
        "Task-Template verwalten" (`TaskTemplate/VorlagenDialog.tsx`) - dieselbe Bauart wie der
        `ListenEditor` darueber. Der Dialog laedt Module, Vorlage und Konten selbst; hier wird nur
        der Speicherstand nachgezogen, damit `zustand.taskVorlagen` nach dem Schreiben nicht auf dem
        Ladestand von `bestandLaden()` stehen bleibt.

        TIEFLINKS: `taskTemplateOffen` oben ist bereits gegen `ansicht.dialog === "vorlagen" &&
        hatRecht(...)` verengt - ein `?dialog=vorlagen` ohne Recht rendert den Dialog folglich NIE
        (§ 3.4 Regel 2 des Tieflinks-Entwurfs). `onSchliessen` schreibt `dialog: null` in die
        Adresse zurueck, statt einen lokalen Zustand zu loeschen.
      */}
      {taskTemplateOffen ? (
        <VorlagenDialog
          rechte={zustand.rechte}
          onSchliessen={() => setzeAnsicht({ dialog: null })}
          onMeldung={zeigeMeldung}
          onGespeichert={(vorlagen) =>
            versende({ art: "bestandGeladen", bestand: { taskVorlagen: [...vorlagen] } })
          }
        />
      ) : null}
      {/*
        Anlagemaske und Teillieferung (Task 9, `NewOrder/`) - zwei weitere
        `board-editor-dialog`-Ueberlagerungen, derselben Bauart wie `ListenEditor` oben: EIN
        eigener `useState` je Dialog (`neueBestellungOffen`/`teilenId`), `null`/
        `false` heisst geschlossen.

        DIE `board-editor-overlay open`-HUELLE HIER IST DER FIX VOM 2026-08-14, kein Beiwerk. Die
        drei `NewOrder/`-Komponenten geben - anders als ListenEditor, SpaltenEditor,
        VorlagenDialog, UeberschreibDialog und PasswortDialog - nur ihr `<section>` zurueck, ohne
        Huelle. Der Kommentar am PasswortDialog behauptete sogar, sie haetten eine.
        Wirkung, live gemessen: `position: fixed; inset: 0` sitzt an der HUELLE, nicht am Dialog.
        Ohne sie stand die Maske im normalen Fluss unterhalb der Seite - unsichtbar -, und der
        Browser fror ein: das Element verlaengert die Seite, der Scrollbalken erscheint, der
        Viewport wird schmaler, `width: min(1120px, 100%)` und `max-height: calc(100vh - 36px)`
        rechnen neu, der Balken verschwindet - eine Layout-Endlosschleife mit ueber 40 Sekunden je
        Bild. Sichtbar war davon nur: "der Knopf 'Neue Bestellung' tut nichts."
        Zusaetzlich greift erst mit der Huelle `.board-editor-overlay.open .board-editor-dialog
        { transform: none }` - ohne sie bliebe der Dialog auf `translateY(10px) scale(.988)` stehen.
      */}
      {neueBestellungOffen ? (
        <div className="board-editor-overlay open" role="presentation">
          <Anlagemaske
            bestellungen={zustand.bestellungen}
            jtlLager={jtlLager}
            lieferantenSuche={anlagemaskeLieferantenSuche}
            artikelSuche={anlagemaskeArtikelSuche}
            artikelSucheRoh={anlagemaskeArtikelAbruf}
            onSpeichern={neueBestellungAnlegen}
            onSchliessen={() => {
              setNeueBestellungOffen(false);
              setDuplikat(undefined);
            }}
            speichernLaeuft={neueBestellungLaeuft}
            rechte={zustand.rechte}
            duplikat={duplikat}
          />
        </div>
      ) : null}
      {teilenId && teilBestellung ? (
        <div className="board-editor-overlay open" role="presentation">
          <TeillieferungDialog
            bestellung={teilBestellung}
            bestellungen={zustand.bestellungen}
            onTeilen={teillieferungAnlegen}
            onSchliessen={() => setTeilenId(null)}
            laeuft={teilenLaeuft}
            eingabenStand={teilenStand}
          />
        </div>
      ) : null}
      {rueckgaengigId && rueckgaengigKennung !== null ? (
        <div className="board-editor-overlay open" role="presentation">
          <SplitRueckgaengigDialog
            kennung={rueckgaengigKennung}
            laeuft={rueckgaengigLaeuft}
            onAbbrechen={() => setRueckgaengigId(null)}
            onBestaetigen={splitRueckgaengigBestaetigt}
          />
        </div>
      ) : null}
      {/*
        `JtlLoeschDialog` (Task 11/12) - ANDERS als die drei Dialoge oben KEINE zusaetzliche
        `board-editor-overlay open`-Huelle hier: die Komponente baut ihre eigene Ueberlagerung samt
        `.open`-Klasse bereits selbst (`JtlLoeschDialog.tsx`, `createPortal` auf `document.body`) - ein
        zweiter Wrapper hier erzeugte zwei verschachtelte `.board-editor-overlay`-Elemente.
      */}
      {jtlLoeschDialog ? (
        <JtlLoeschDialog
          orderNo={jtlLoeschDialog.orderNo}
          onAbbrechen={() => setJtlLoeschDialog(null)}
          onBestaetigen={jtlLoeschenBestaetigt}
        />
      ) : null}
      <Toast text={meldung} />
      <StehendeHinweise hinweise={hinweise} onSchliessen={schliesseHinweis} />
      {/*
        Auftrag "Schwebender Lade-Hinweis (Spinner-Popup) statt Textzeile" (2026-08-28): loest den
        bisherigen board-lokalen `.empty-note`-Hinweis in `BoardSeite.tsx` ab (dort ersatzlos
        entfernt) - `zustand.ladend` ist GLOBALER Store-Zustand (Erstladen UND jeder spaetere
        Archivfilter-Wechsel, `bestandNeuLaden`), deshalb hier auf App-Ebene verdrahtet und nicht
        mehr board-lokal: die Rolle `payment_manager` laedt ueber denselben Zustand, unabhaengig
        davon, welche Seite gerade sichtbar ist (siehe Kopfkommentar `LadeHinweis.tsx`).
      */}
      <LadeHinweis sichtbar={zustand.ladend} />
    </SplitKontextProvider>
  );
}
